import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import './PlanningAtelier.css';
import api from '../services/api';
import permissionsApi from '../services/permissions';
import SupportRequests from './SupportRequests';
import PlanningConfig from './PlanningConfig';
import InlineStatusSelect from './InlineStatusSelect';
import InterventionModal from './InterventionModal';
import AccordClientImmoModal from './planning/AccordClientImmoModal';
import { MOTIFS_ABSENCE } from '../utils/absenceMotifs';
import { teinteCravate } from '../utils/cravate';
import { estMobileEnvoyable } from '../services/sms';
import RdvNonHonores from './planning/RdvNonHonores';
import ReceptionsStats from './planning/ReceptionsStats';
import DerniersContacts from './planning/DerniersContacts';
import ClientsSansContact from './planning/ClientsSansContact';
// Onglet 💬 Demandes clients du panneau latéral : la modale vient du
// module Demandes clients (réutilisation cross-module, pattern Takata).
import { DemandeModal, ArchiveDemandeModal } from './LeadsServices';
import { PlanificationCell } from './shared/DossierPlanification';
import BadgeMarque from './shared/BadgeMarque';
import './LeadsServices.css';
import { demandes as demandesApi } from '../services/api';
import referentielsApi from '../services/referentiels';
import sujetsApi from '../services/sujets';
// Pictos de carte : extraits dans un module partagé pour que la recherche
// globale affiche exactement les mêmes drapeaux que la grille.
import CardFlagIcon from './planning/CardFlagIcon';
import RechercheDossier from './planning/RechercheDossier';
import PiecesAlertes from './planning/PiecesAlertes';
import SuiviAtelier from './planning/SuiviAtelier';
import KanbanAtelier from './planning/KanbanAtelier';
import RappelsRdvSimulation from './planning/RappelsRdvSimulation';
import RemplissageAtelier from './planning/RemplissageAtelier';
import DemandeNumeroOr from './planning/DemandeNumeroOr';
import { useTri, EnteteTri } from './planning/TriTableau';
import { getContrastColor } from '../utils/planningColors';
import NoteJdmaPill from './planning/NoteJdmaPill';
import { formatDateFr, toYmd, jourSuivantOuvre, dateLocale } from '../utils/planningTime';
import { horairesAtelier, prochainJourOuvert as prochainJourOuvertAtelier, repartirDuree, hhmmEnMin } from '../utils/repartitionPlanning';

// Origine d'une demande de RDV en ligne (planning_rdv_online.source) → picto +
// libellé. Permet au réceptionnaire de savoir d'où vient la demande avant même
// de l'ouvrir : un RDV pris par l'agent vocal se relit dans le Call-center IA,
// pas ailleurs. 'client' (défaut) = réservation faite par le client sur le site.
const RDV_ONLINE_SOURCES = {
  remorqueur: { icon: '🚛', label: 'Véhicule amené par un remorqueur' },
  'agent-vocal': { icon: '🎙️', label: 'RDV pris par l\'agent vocal IA' },
};
const rdvOnlineSource = (source) => RDV_ONLINE_SOURCES[source] || { icon: '📥', label: 'Réservation en ligne du client' };

// Statut pièces affiché sur la carte : l'état RÉEL de la commande
// (pieces_etat_or, recalculé à chaque import du carnet et des bons de
// livraison) prime sur `statut_pieces`, valeur figée au dernier enregistrement
// de la modale — l'OR 011039389 restait « en commande » trois semaines après
// l'arrivée de sa pièce (20/10/2026). Repli sur la valeur figée quand aucune
// commande n'est suivie (dossier hors carnet, Mécaplanning).
const statutPiecesCarte = (i) => {
  switch (i.pieces_statut_livraison) {
    case 'livre': return 'disponible';
    case 'en_commande':
    case 'recu_partiellement': return 'en_commande';
    default: return i.statut_pieces || '';
  }
};

function generatePlanningData() {
  const days = [
    '2026-03-16', '2026-03-17', '2026-03-18', '2026-03-19', '2026-03-20',
    '2026-03-23', '2026-03-24', '2026-03-25', '2026-03-26', '2026-03-27',
    '2026-03-30', '2026-03-31', '2026-04-01', '2026-04-02', '2026-04-03'
  ];
  const mecaniciens = ['Paul', 'Vincent', 'Gérard', 'Nicolas', 'François'];
  const clients = [
    'M. Dupont', 'Mme Martin', 'M. Bernard', 'M. Lefebvre', 'Mme Durand',
    'M. Petit', 'Mme Moreau', 'M. Simon', 'Mme Laurent', 'M. Thomas',
    'Mme Robert', 'M. Richard', 'Mme Leroy', 'M. Morel', 'Mme Fournier',
    'M. Girard', 'Mme Bonnet', 'M. Lambert', 'Mme François', 'M. Martinez',
    'Mme Garcia', 'M. Roux', 'Mme David', 'M. Bertrand', 'Mme Mercier'
  ];
  const immats = [
    'AB-123-CD', 'EF-456-GH', 'IJ-789-KL', 'MN-012-OP', 'QR-345-ST',
    'UV-678-WX', 'YZ-901-AB', 'CD-234-EF', 'GH-567-IJ', 'KL-890-MN',
    'OP-123-QR', 'ST-456-UV', 'WX-789-YZ', 'BC-012-DE', 'FG-345-HI',
    'JK-678-LM', 'NO-901-PQ', 'RS-234-TU', 'VW-567-XY', 'ZA-890-BC',
    'DE-123-FG', 'HI-456-JK', 'LM-789-NO', 'PQ-012-RS', 'TU-345-VW'
  ];
  const tels = [
    '0612345678', '0623456789', '0634567890', '0645678901', '0656789012',
    '0667890123', '0678901234', '0689012345', '0690123456', '0701234567',
    '0712345678', '0723456789', '0734567890', '0745678901', '0756789012',
    '0767890123', '0778901234', '0789012345', '0790123456', '0601234567'
  ];
  const typeDescs = [
    { type: 'Révision',      description: 'Révision annuelle' },
    { type: 'Réparation',    description: 'Réparation moteur' },
    { type: 'Vidange',       description: 'Vidange + filtres' },
    { type: 'Diagnostic',    description: 'Diagnostic électronique' },
    { type: 'Carrosserie',   description: 'Réparation carrosserie' },
    { type: 'Pneumatiques',  description: 'Changement 4 pneus' },
    { type: 'Freinage',      description: 'Plaquettes + disques' },
    { type: 'Climatisation', description: 'Recharge climatisation' },
    { type: 'Distribution',  description: 'Remplacement courroie' },
    { type: 'Révision',      description: 'Révision 30 000 km' },
    { type: 'Réparation',    description: 'Remplacement amortisseurs' },
    { type: 'Diagnostic',    description: 'Contrôle pré-CT' },
    { type: 'Vidange',       description: 'Vidange boite de vitesses' },
    { type: 'Freinage',      description: 'Purge circuit frein' },
    { type: 'Carrosserie',   description: 'Débosselage aile avant' }
  ];
  // 3 créneaux par mécanicien par jour, avec des horaires variés
  const mecaSlots = [
    [ // Paul
      { h: '08:00', d: 2, rh: '07:30', p: 1 },
      { h: '10:30', d: 1, rh: '10:00', p: 2 },
      { h: '13:00', d: 3, rh: '12:30', p: 3 }
    ],
    [ // Vincent
      { h: '08:30', d: 2, rh: '08:00', p: 2 },
      { h: '11:00', d: 1, rh: '10:30', p: 3 },
      { h: '14:00', d: 2, rh: '13:30', p: 4 }
    ],
    [ // Gérard
      { h: '07:30', d: 2, rh: '07:00', p: 1 },
      { h: '10:00', d: 2, rh: '09:30', p: 2 },
      { h: '13:30', d: 2, rh: '13:00', p: 5 }
    ],
    [ // Nicolas
      { h: '08:00', d: 1, rh: '07:30', p: 4 },
      { h: '10:00', d: 2, rh: '09:30', p: 5 },
      { h: '14:00', d: 3, rh: '13:30', p: 1 }
    ],
    [ // François
      { h: '09:00', d: 2, rh: '08:30', p: 3 },
      { h: '11:30', d: 1, rh: '11:00', p: 4 },
      { h: '14:30', d: 2, rh: '14:00', p: 2 }
    ]
  ];

  const rdvReception = [];
  const interventions = [];
  let id = 1;

  days.forEach((date, dayIdx) => {
    mecaniciens.forEach((meca, mecaIdx) => {
      mecaSlots[mecaIdx].forEach((slot, slotIdx) => {
        const idx = dayIdx * 15 + mecaIdx * 3 + slotIdx;
        const client = clients[idx % clients.length];
        const immat = immats[idx % immats.length];
        const tel = tels[idx % tels.length];
        const { type, description } = typeDescs[idx % typeDescs.length];

        rdvReception.push({
          id,
          poste: slot.p,
          heure: slot.rh,
          date,
          client,
          tel,
          immat,
          mecanicien: meca,
          intervention_id: id
        });

        const statutOptions = ['planifie', 'planifie', 'planifie', 'en_cours', 'termine'];
        interventions.push({
          id,
          mecanicien: meca,
          heure_debut: slot.h,
          duree: slot.d,
          date,
          client,
          immat,
          type,
          description,
          heure_reception: slot.rh,
          poste_reception: slot.p,
          statut_avancement: statutOptions[idx % statutOptions.length],
          pieces: [],
          statut_pieces: ''
        });

        id++;
      });
    });
  });

  return { rdvReception, interventions };
}

const { rdvReception: seedRdv, interventions: seedInterventions } = generatePlanningData();

// ═══ Menu contextuel en colonnes ══════════════════════════════════════════
// Largeurs fixes : la colonne 1 ne doit pas se redimensionner quand les
// suivantes s'ouvrent, sinon les entrées bougent sous le curseur.
const CTX_LARGEURS = { col1: 236, col2: 240, col3: 264 };

// Temporisations du survol. Ouvrir est instantané (c'est ce qui donne la
// sensation de vitesse) ; c'est CHANGER de colonne qui est retenu, le temps de
// laisser passer une trajectoire en diagonale vers la colonne déjà ouverte.
const CTX_DELAI_COMMUT = 110;   // ms avant de basculer sur une autre action
const CTX_SURSIS_VISEE = 140;   // ms de sursis supplémentaire tant qu'on vise la colonne ouverte
const CTX_VISEE_MAX = 900;      // ms au-delà desquelles on bascule quoi qu'il arrive
const CTX_POINTEUR_FRAIS = 250; // ms au-delà desquelles le dernier mouvement n'est plus significatif
const CTX_AGE_ORIGINE = 50;     // ms : âge minimal du point servant d'origine au geste

// Carte du menu : une entrée = une action. `suites` liste ce que propose la
// 3ᵉ colonne une fois l'action appliquée ; une entrée sans `suites` ferme le
// menu (elle passe la main à une modale). Tout est déclaratif ici pour que la
// personnalisation demandée se fasse au cas par cas, sans toucher au rendu.
const CTX_ACTIONS = [
  { cle: 'statut',      icone: '🏷️', libelle: 'Statut atelier',           colonne: 'statuts',     titre: 'Statut atelier',          suites: ['parking', 'commentaire'], cap: 'dossier.statut_atelier' },
  // `suites: null` — et non `[]` — parce que la colonne des tags ne se referme
  // JAMAIS après un clic : les tags sont cumulables, proposer « et ensuite ? »
  // après le premier obligerait à rouvrir le menu pour poser le second.
  // L'entrée est masquée tant que la concession n'a aucun tag : c'est l'opt-in.
  { cle: 'tags',        icone: '🔖', libelle: 'Tags',                     colonne: 'tags',        titre: 'Tags du dossier',         suites: null, siTags: true, cap: 'dossier.tags' },
  // Les deux gestes de programmation posent la MÊME question (quel jour, quelle
  // heure, quel mécanicien, combien de temps) et partagent donc le même panneau
  // — seul l'effet diffère, et il est écrit noir sur blanc dans chacun d'eux :
  // « Déplacer » vide le créneau d'origine, « Ajouter » crée une seconde
  // intervention sur le même OR et laisse la première en place. Ils se suivent
  // dans le menu pour se lire l'un contre l'autre.
  { cle: 'planif',      icone: '↔️', libelle: "Déplacer l'intervention",   colonne: 'planif',      titre: "Déplacer l'intervention", suites: ['commentaire', 'client'], cap: 'dossier.replanifier' },
  { cle: 'ajouter',     icone: '➕', libelle: 'Ajouter une intervention',  colonne: 'ajouter',     titre: 'Ajouter une intervention', suites: ['commentaire', 'client'], cap: 'dossier.creer' },
  // Raccourci de « Ajouter une intervention » : même écriture (un complément),
  // mais en un clic — même heure, même mécanicien, seule la durée est demandée.
  { cle: 'lendemain',   icone: '⏭️', libelle: 'Ajouter au lendemain',      colonne: 'lendemain',   titre: 'Durée à ajouter',         suites: ['commentaire', 'client'], cap: 'dossier.creer' },
  { cle: 'aplanifier',  icone: '🪝', libelle: 'Mettre à planifier',        colonne: null,          titre: null,                      suites: ['commentaire'], cap: 'dossier.replanifier' },
  { separateur: true },
  { cle: 'parking',     icone: '📍', libelle: 'Position parking',          colonne: 'parking',     titre: 'Où est le véhicule ?',    suites: ['commentaire'], cap: 'reception.parking' },
  // Demande du service Pièces : déclarer en un clic que le prépicking est fait,
  // sans ouvrir le dossier — un magasinier en traite quinze d'affilée.
  { cle: 'prepicking',  icone: '🛒', libelle: 'Prépicking',                colonne: null,          titre: null,                      suites: [], cap: 'pieces.prepicking' },
  { cle: 'non_honore',  icone: '🚫', libelle: 'RDV non honoré…',           colonne: 'nonHonore',   titre: 'Rendez-vous non honoré',  suites: ['commentaire'], cap: 'dossier.modifier' },
  { cle: 'commentaire', icone: '💬', libelle: 'Commentaire',               colonne: 'commentaire', titre: 'Commentaire au dossier',  suites: [], cap: 'dossier.commentaire' },
  { cle: 'contact',     icone: '📞', libelle: 'Contacter le client',       colonne: 'contact',     titre: 'Contacter le client',     suites: null, cap: 'client.echanges_voir' },
  { separateur: true },
  { cle: 'supprimer',   icone: '🗑️', libelle: "Supprimer l'intervention…", colonne: null,          titre: null,                      suites: null, danger: true, cap: 'dossier.supprimer' },
];

// ── Portées des opérations en masse (import Mécaplanning, vidage du planning) ──
// Les deux gestes partagent la même question — « sur quoi ? » — donc le même
// vocabulaire et le même ordre, du plus étroit au plus large. Les bornes sont
// calculées en heure LOCALE (cf. plagePortee) : `toISOString()` reculerait d'un
// jour aux Antilles et l'on importerait ou supprimerait la veille.
const PORTEES = [
  { cle: 'jour',    icone: '📆', libelle: 'Jour affiché' },
  { cle: 'semaine', icone: '🗓️', libelle: 'Semaine affichée' },
  { cle: 'mois',    icone: '📅', libelle: 'Mois affiché' },
];

// Au-delà de ce nombre de lignes, la colonne accepte un filtre au clavier
// (28 parkings chez AUTODISCOUNT : les faire défiler à la souris est pénible).
const CTX_SEUIL_FILTRE = 10;

// ─── PDF thumbnail : rendu de la 1re page via pdfjs (lazy) ──────────────────
let _pdfjsLib = null;
async function getPdfjsLib() {
  if (!_pdfjsLib) {
    _pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
    _pdfjsLib.GlobalWorkerOptions.workerSrc = '//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
  }
  return _pdfjsLib;
}

function PlanningPdfThumbnail({ url }) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await getPdfjsLib();
        const pdf = await pdfjs.getDocument({ url, withCredentials: false }).promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const DPR = window.devicePixelRatio || 1;
        const vp0 = page.getViewport({ scale: 1 });
        const scale = Math.max(165 / vp0.width, 120 / vp0.height) * DPR;
        const vp = page.getViewport({ scale });
        canvas.width = vp.width;
        canvas.height = vp.height;
        canvas.style.width = Math.round(vp.width / DPR) + 'px';
        canvas.style.height = Math.round(vp.height / DPR) + 'px';
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);
  if (error) return <div className="planning-doc-icon">📄</div>;
  return <canvas ref={canvasRef} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// RDV EN LIGNE — Manager admin
// ═══════════════════════════════════════════════════════════════════════════════
function RdvOnlineManager({ user, staffList, onReload }) {
  const [rdvList, setRdvList] = useState([]);
  // Tri partagé (TriTableau) : sans clé au départ, l'ordre du serveur est conservé.
  const triRdv = useTri(null, 'asc');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('en_attente');
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmData, setConfirmData] = useState({ mecanicien_id: '', poste_reception: '' });
  const [detailModal, setDetailModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editData, setEditData] = useState({
    client_nom: '', client_prenom: '', client_tel: '', client_email: '',
    marque: '', modele: '', immatriculation: '',
    date_souhaitee: '', heure_souhaitee: '', commentaire: '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  const loadRdv = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/planning/rdv-online?statut=${filter}`);
      setRdvList(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadRdv(); }, [loadRdv]);

  // Fermeture des modales avec Échap
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (editModal) { setEditModal(null); return; }
      if (confirmModal) { setConfirmModal(null); return; }
      if (detailModal) { setDetailModal(null); return; }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [editModal, confirmModal, detailModal]);

  const handleConfirm = async () => {
    if (!confirmModal) return;
    try {
      await api.patch(`/planning/rdv-online/${confirmModal.id}/confirmer`, {
        mecanicien_id: confirmData.mecanicien_id ? parseInt(confirmData.mecanicien_id) : null,
        poste_reception: parseInt(confirmData.poste_reception) || 1
      });
      setConfirmModal(null);
      setDetailModal(null);
      loadRdv();
      if (onReload) onReload();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    }
  };

  const handleRefuse = async (id) => {
    if (!window.confirm('Refuser ce RDV en ligne ?')) return;
    try {
      await api.patch(`/planning/rdv-online/${id}/refuser`);
      setDetailModal(null);
      loadRdv();
      if (onReload) onReload();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    }
  };

  const openEditModal = (rdv) => {
    setDetailModal(null);
    setEditError('');
    setEditData({
      client_nom: rdv.client_nom || '',
      client_prenom: rdv.client_prenom || '',
      client_tel: rdv.client_tel || '',
      client_email: rdv.client_email || '',
      marque: rdv.marque || '',
      modele: rdv.modele || '',
      immatriculation: rdv.immatriculation || '',
      date_souhaitee: rdv.date_souhaitee ? rdv.date_souhaitee.slice(0, 10) : '',
      heure_souhaitee: rdv.heure_souhaitee ? rdv.heure_souhaitee.slice(0, 5) : '',
      commentaire: rdv.commentaire || '',
    });
    setEditModal(rdv);
  };

  const handleModify = async () => {
    if (!editModal) return;
    if (!editData.client_nom.trim()) { setEditError('Le nom du client est requis.'); return; }
    if (!editData.date_souhaitee) { setEditError('La date est requise.'); return; }
    setEditLoading(true);
    setEditError('');
    try {
      await api.patch(`/planning/rdv-online/${editModal.id}/modifier`, editData);
      setEditModal(null);
      loadRdv();
      if (onReload) onReload();
    } catch (e) {
      setEditError(e.response?.data?.error || 'Erreur lors de la modification.');
    }
    setEditLoading(false);
  };

  const mecaniciens = (staffList || []).filter(s => s.role === 'mecanicien' && s.actif);
  const receptionnaires = (staffList || []).filter(s => s.role === 'receptionnaire' && s.actif);

  const formatDate = (d) => (d ? formatDateFr(d) : '—');

  const formatHeure = (h) => {
    if (!h) return '—';
    return h.substring(0, 5);
  };

  // Les colonnes se trient sur la donnée brute, pas sur ce qui est affiché :
  // « 09/08 » et « 10/08 » se compareraient correctement, mais « 1 h 30 » et
  // « 45 min » non — la durée se trie donc en minutes.
  const rdvTriee = triRdv.appliquer(rdvList, {
    date: r => r.date_souhaitee,
    heure: r => r.heure_souhaitee,
    client: r => `${r.client_nom || ''} ${r.client_prenom || ''}`.trim(),
    tel: r => r.client_tel,
    vehicule: r => [r.marque, r.modele].filter(Boolean).join(' '),
    remorqueur: r => r.remorqueur_entreprise,
    assureur: r => r.remorqueur_assureur,
    prestations: r => (r.prestations || []).map(p => p.libelle).join(', ') || r.type_service,
    duree: r => Number(r.duree_totale) || 0,
  });

  const inputStyle = {
    width: '100%', padding: '8px 12px',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-input)',
    fontSize: '14px', background: 'var(--bg-input)',
    color: 'var(--text-primary)', outline: 'none',
    transition: 'border-color 0.15s',
    fontFamily: 'inherit',
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>📥 RDV à confirmer</h2>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {['en_attente', 'confirme', 'refuse'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              padding: '6px 14px', borderRadius: '6px', border: '1px solid var(--border-default)',
              background: filter === s ? 'var(--color-accent)' : 'var(--bg-surface)',
              color: filter === s ? '#fff' : 'var(--text-secondary)',
              fontWeight: filter === s ? 600 : 400, cursor: 'pointer', fontSize: '13px'
            }}
          >
            {s === 'en_attente' ? '⏳ En attente' : s === 'confirme' ? '✅ Confirmés' : '❌ Refusés'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Chargement...</div>
      ) : rdvList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
          Aucun RDV {filter === 'en_attente' ? 'en attente' : filter === 'confirme' ? 'confirmé' : 'refusé'}
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <EnteteTri tri={triRdv} cle="date">Date</EnteteTri>
                <EnteteTri tri={triRdv} cle="heure">Heure</EnteteTri>
                <EnteteTri tri={triRdv} cle="client">Client</EnteteTri>
                <EnteteTri tri={triRdv} cle="tel">Téléphone</EnteteTri>
                <EnteteTri tri={triRdv} cle="vehicule">Véhicule</EnteteTri>
                <EnteteTri tri={triRdv} cle="remorqueur">Remorqueur</EnteteTri>
                <EnteteTri tri={triRdv} cle="assureur">Assureur</EnteteTri>
                <EnteteTri tri={triRdv} cle="prestations">Prestations</EnteteTri>
                <EnteteTri tri={triRdv} cle="duree">Durée</EnteteTri>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rdvTriee.map(rdv => (
                <tr
                  key={rdv.id}
                  onClick={() => setDetailModal(rdv)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{formatDate(rdv.date_souhaitee)}</td>
                  <td>{formatHeure(rdv.heure_souhaitee)}</td>
                  <td>
                    {rdv.source && rdv.source !== 'client' && (
                      <span title={rdvOnlineSource(rdv.source).label} style={{ marginRight: 4 }}>
                        {rdvOnlineSource(rdv.source).icon}
                      </span>
                    )}
                    <strong>{rdv.client_nom}</strong> {rdv.client_prenom || ''}
                  </td>
                  <td>{rdv.client_tel || '—'}</td>
                  <td>
                    {[rdv.marque, rdv.modele].filter(Boolean).join(' ') || '—'}
                    {rdv.immatriculation ? ` (${rdv.immatriculation})` : ''}
                  </td>
                  <td>{rdv.remorqueur_entreprise || '—'}</td>
                  <td>{rdv.remorqueur_assureur || '—'}</td>
                  <td style={{ maxWidth: '200px' }}>
                    {(rdv.prestations || []).map(p => p.libelle).join(', ') || rdv.type_service}
                  </td>
                  <td>{rdv.duree_totale}h</td>
                  <td onClick={e => e.stopPropagation()}>
                    {rdv.statut === 'en_attente' ? (
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          title="Valider"
                          onClick={() => { setConfirmModal(rdv); setConfirmData({ mecanicien_id: '', poste_reception: '' }); }}
                        >✅</button>
                        <button
                          className="btn-icon rdv-btn-edit"
                          title="Modifier"
                          onClick={() => openEditModal(rdv)}
                        >✏️</button>
                        <button
                          className="btn-icon delete"
                          title="Refuser"
                          onClick={() => handleRefuse(rdv.id)}
                        >❌</button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{rdv.statut}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modale de détail RDV ── */}
      {detailModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Détail du RDV</h2>
              <button className="modal-close" onClick={() => setDetailModal(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Infos client */}
              <div className="rdv-detail-section">
                <div className="rdv-detail-row">
                  <span className="rdv-detail-label">Client</span>
                  <span><strong>{detailModal.client_nom}</strong> {detailModal.client_prenom || ''}</span>
                </div>
                <div className="rdv-detail-row">
                  <span className="rdv-detail-label">Téléphone</span>
                  <span>{detailModal.client_tel || '—'}</span>
                </div>
                {detailModal.client_email && (
                  <div className="rdv-detail-row">
                    <span className="rdv-detail-label">Email</span>
                    <span>{detailModal.client_email}</span>
                  </div>
                )}
              </div>
              {/* Infos véhicule */}
              <div className="rdv-detail-section">
                <div className="rdv-detail-row">
                  <span className="rdv-detail-label">Véhicule</span>
                  <span>{[detailModal.marque, detailModal.modele].filter(Boolean).join(' ') || '—'}</span>
                </div>
                {detailModal.immatriculation && (
                  <div className="rdv-detail-row">
                    <span className="rdv-detail-label">Immatriculation</span>
                    <span className="badge-pill badge-neutral" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {detailModal.immatriculation}
                    </span>
                  </div>
                )}
              </div>
              {/* Infos RDV */}
              <div className="rdv-detail-section">
                <div className="rdv-detail-row">
                  <span className="rdv-detail-label">Date souhaitée</span>
                  <span>📅 {formatDate(detailModal.date_souhaitee)}</span>
                </div>
                <div className="rdv-detail-row">
                  <span className="rdv-detail-label">Créneau</span>
                  <span>🕐 {formatHeure(detailModal.heure_souhaitee)}</span>
                </div>
                <div className="rdv-detail-row">
                  <span className="rdv-detail-label">Durée estimée</span>
                  <span>{detailModal.duree_totale}h</span>
                </div>
                <div className="rdv-detail-row">
                  <span className="rdv-detail-label">Prestations</span>
                  <span style={{ maxWidth: '260px', textAlign: 'right' }}>
                    {(detailModal.prestations || []).map(p => p.libelle).join(', ') || detailModal.type_service || '—'}
                  </span>
                </div>
              </div>
              {/* Commentaire client */}
              <div className="rdv-detail-commentaire">
                <div className="rdv-detail-label" style={{ marginBottom: '6px' }}>💬 Commentaire client</div>
                {detailModal.commentaire && detailModal.commentaire.trim() ? (
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {detailModal.commentaire.trim()}
                  </p>
                ) : (
                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Aucun commentaire
                  </p>
                )}
              </div>
            </div>
            {/* Actions conditionnelles selon statut */}
            {detailModal.statut === 'en_attente' ? (
              <div className="modal-footer" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <button className="btn-secondary" onClick={() => setDetailModal(null)}>Fermer</button>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    className="btn-primary"
                    onClick={() => { setDetailModal(null); setConfirmModal(detailModal); setConfirmData({ mecanicien_id: '', poste_reception: '' }); }}
                  >✅ Valider</button>
                  <button
                    style={{ padding: '10px 20px', background: 'var(--color-info-light)', color: 'var(--color-info-dark)', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => openEditModal(detailModal)}
                  >✏️ Modifier</button>
                  <button className="btn-danger" onClick={() => handleRefuse(detailModal.id)}>❌ Refuser</button>
                </div>
              </div>
            ) : (
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setDetailModal(null)}>Fermer</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modale de confirmation (valider + assigner) ── */}
      {confirmModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmer le RDV</h2>
              <button className="modal-close" onClick={() => setConfirmModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '12px' }}>
                <strong>{confirmModal.client_nom} {confirmModal.client_prenom || ''}</strong> — {formatDate(confirmModal.date_souhaitee)} à {formatHeure(confirmModal.heure_souhaitee)}
              </p>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                {(confirmModal.prestations || []).map(p => p.libelle).join(', ')} — {confirmModal.duree_totale}h
              </p>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Mécanicien</label>
                <select
                  className="page-select"
                  value={confirmData.mecanicien_id}
                  onChange={e => setConfirmData({ ...confirmData, mecanicien_id: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="">— Non assigné —</option>
                  {mecaniciens.map(m => (
                    <option key={m.id} value={m.id}>{m.prenom} {m.nom}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Réceptionnaire</label>
                <select
                  className="page-select"
                  value={confirmData.poste_reception}
                  onChange={e => setConfirmData({ ...confirmData, poste_reception: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="">— Non assigné —</option>
                  {receptionnaires.map(r => (
                    <option key={r.id} value={r.id}>{r.prenom} {r.nom}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setConfirmModal(null)}>Annuler</button>
              <button className="btn-primary" onClick={handleConfirm}>Confirmer le RDV</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale d'édition RDV ── */}
      {editModal && (
        <div className="modal-overlay">
          <div className="modal-content rdv-edit-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Modifier le RDV</h2>
              <button className="modal-close" onClick={() => setEditModal(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

              {/* ── Section Client ── */}
              <div>
                <div className="rdv-detail-label" style={{ marginBottom: '10px' }}>Client</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)' }}>
                      Nom <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>*</span>
                    </label>
                    <input type="text" value={editData.client_nom}
                      onChange={e => setEditData({ ...editData, client_nom: e.target.value })}
                      style={inputStyle} placeholder="Nom" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)' }}>Prénom</label>
                    <input type="text" value={editData.client_prenom}
                      onChange={e => setEditData({ ...editData, client_prenom: e.target.value })}
                      style={inputStyle} placeholder="Prénom" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)' }}>Téléphone</label>
                    <input type="tel" value={editData.client_tel}
                      onChange={e => setEditData({ ...editData, client_tel: e.target.value })}
                      style={inputStyle} placeholder="06 xx xx xx xx" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)' }}>Email</label>
                    <input type="email" value={editData.client_email}
                      onChange={e => setEditData({ ...editData, client_email: e.target.value })}
                      style={inputStyle} placeholder="email@exemple.fr" />
                  </div>
                </div>
              </div>

              {/* ── Section Véhicule ── */}
              <div>
                <div className="rdv-detail-label" style={{ marginBottom: '10px' }}>Véhicule</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)' }}>Marque</label>
                    <input type="text" value={editData.marque}
                      onChange={e => setEditData({ ...editData, marque: e.target.value })}
                      style={inputStyle} placeholder="Ex : Peugeot" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)' }}>Modèle</label>
                    <input type="text" value={editData.modele}
                      onChange={e => setEditData({ ...editData, modele: e.target.value })}
                      style={inputStyle} placeholder="Ex : 308" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)' }}>Immatriculation</label>
                    <input type="text" value={editData.immatriculation}
                      onChange={e => setEditData({ ...editData, immatriculation: e.target.value.toUpperCase() })}
                      style={{ ...inputStyle, fontFamily: 'monospace', fontWeight: 600 }}
                      placeholder="AA-000-BB" />
                  </div>
                </div>
              </div>

              {/* ── Section RDV ── */}
              <div>
                <div className="rdv-detail-label" style={{ marginBottom: '10px' }}>Date & créneau</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)' }}>
                      Date <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>*</span>
                    </label>
                    <input type="date" value={editData.date_souhaitee}
                      onChange={e => setEditData({ ...editData, date_souhaitee: e.target.value })}
                      style={inputStyle} required />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)' }}>Créneau horaire</label>
                    <input type="time" value={editData.heure_souhaitee}
                      onChange={e => setEditData({ ...editData, heure_souhaitee: e.target.value })}
                      style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* ── Commentaire client ── */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  💬 Commentaire client
                </label>
                <textarea value={editData.commentaire}
                  onChange={e => setEditData({ ...editData, commentaire: e.target.value })}
                  rows={3} placeholder="Remarques, demandes particulières…"
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '80px', lineHeight: '1.5' }} />
              </div>

              {editError && (
                <div style={{ padding: '10px 14px', background: 'var(--color-error-light)', border: '1px solid light-dark(#fecaca, var(--border-strong))', borderRadius: 'var(--radius-input)', color: 'var(--color-error-dark)', fontSize: '13px' }}>
                  {editError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setEditModal(null)} disabled={editLoading}>Annuler</button>
              <button className="btn-primary" onClick={handleModify} disabled={editLoading}>
                {editLoading ? 'Enregistrement…' : '💾 Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Fallbacks utilisés uniquement quand l'API staff renvoie une liste vide (déploiement vierge).
// Définis au niveau module pour ne pas être recréés à chaque render du composant.

// Date de mise à jour d'une position parking, en clair et sans l'année quand
// c'est l'année courante — l'information sert à juger de la fraîcheur.
const formatMajParking = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const memeAnnee = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', ...(memeAnnee ? {} : { year: 'numeric' }) })
    + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

// Réponse du client au lien du SMS de confirmation (2026-10-22). L'annulation
// NE retire PAS la carte (décision utilisateur) : elle reste, barrée d'une
// bande rouge, jusqu'à ce que l'équipe rappelle le client et décide
// (non honoré, replanification, ou mention retirée depuis la fiche).
// La CONFIRMATION s'affiche depuis le 2026-10-29 (demande utilisateur, qui
// revient sur le choix du 22/10) : bande et contour verts « Confirmé par le
// client », retirés dès que le client s'enregistre à la borne ou que le dossier
// passe à « Réceptionné » ou au-delà (`confirmeVisible`, calculé par le
// composant). La dernière réponse du client prime : le service efface l'autre
// date à chaque réponse.
const heureReponseClient = (iso) => (iso
  ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '');
function MarqueReponseClient({ intv, confirmeVisible = false }) {
  if (!intv) return null;
  if (intv.client_annule_at) {
    return (
      <div className="card-client-annule" title={`Annulé par le client via le lien du SMS, le ${heureReponseClient(intv.client_annule_at)}`}>
        Annulé par le client
      </div>
    );
  }
  if (confirmeVisible) {
    return (
      <div className="card-client-confirme" title={`Confirmé par le client via le lien du SMS, le ${heureReponseClient(intv.client_confirme_at)}`}>
        Confirmé par le client
      </div>
    );
  }
  return null;
}

function PlanningAtelier({ user, editMode = false, setHeaderAction, onChoisirConcession = null, concessionsChoix = null }) {
  const [view, setView] = useState('atelier');
  const [rdvOnlineCount, setRdvOnlineCount] = useState(0);
  // Rendez-vous non honorés ACTIFS (déclarés, pas encore traités) — alimente la
  // section du panneau, le badge Tableaux et l'onglet par défaut de la vue.
  // Déclaré ICI (avant l'effet du header) : le badge lit nonHonores.length.
  const [nonHonores, setNonHonores] = useState([]);
  const [ctxNonHonore, setCtxNonHonore] = useState({ motif: null, commentaire: '' });
  // Nouveautés Pièces : compteur du menu Tableaux + jeton de rafraîchissement.
  // Le compteur est chargé même quand la vue n'est pas ouverte — c'est tout
  // l'intérêt du badge : signaler qu'il y a quelque chose à regarder.
  const [alertesPiecesCount, setAlertesPiecesCount] = useState(0);
  const [alertesPiecesTick, setAlertesPiecesTick] = useState(0);
  // Suivi atelier : total des files ACTIVES (Réceptionné + Contrôle qualité +
  // À facturer). « Facturé » en est exclu — un dossier facturé n'appelle plus de
  // geste, le compter donnerait un badge qui ne redescend jamais.
  const [suiviAtelierCount, setSuiviAtelierCount] = useState(0);
  const [rdvOnlineList, setRdvOnlineList] = useState([]); // RDV en ligne « en attente » (panneau latéral Charge)
  // Demande de RDV en ligne à confirmer via la modale d'intervention (mode confirmation), ou null
  const [confirmOnlineRdv, setConfirmOnlineRdv] = useState(null);

  const [atelierView, setAtelierView] = useState('jour'); // 'jour' ou 'semaine'
  const [selectedMecanicien, setSelectedMecanicien] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [modalIntervention, setModalIntervention] = useState(null); // intervention à éditer (null = création)
  // Navigation « dossier précédent / suivant » dans la modale : liste ordonnée
  // des dossiers voisins (colonne Kanban d'origine). Vide = pas de navigation.
  const [modalFreres, setModalFreres] = useState([]);
  const [summaryModal, setSummaryModal] = useState(null);
  const [statutsAtelier, setStatutsAtelier] = useState([]);
  // « Déjà reçu » : un dossier sort de la réception (file ET grille) dès qu'il
  // porte un statut atelier ≥ « Réceptionné ». Il n'y reste que sans statut ou
  // en « Dossier préparé » (pré-arrivée). Sert la file de droite ET la grille
  // Réception plein écran, pour qu'un dossier reçu (surtout après déplacement de
  // l'intervention) ne réapparaisse plus comme « à recevoir » (2026-09-12).
  const estDejaRecu = useMemo(() => {
    const r = statutsAtelier.find(s => /^r[eé]ceptionn/i.test((s.nom || '').trim()));
    const ordreRecep = r ? r.ordre : null;
    return (intv) => {
      if (!intv || !intv.statut_atelier_id) return false;
      const s = statutsAtelier.find(x => x.id === intv.statut_atelier_id);
      if (!s) return false;
      return ordreRecep == null ? !/pr[eé]par/i.test(s.nom || '') : s.ordre >= ordreRecep;
    };
  }, [statutsAtelier]);
  // Tags d'intervention de la concession (actifs seulement) + filtre de la grille.
  // Le filtre est un Set d'identifiants : plusieurs tags cochés = OU.
  const [tags, setTags] = useState([]);
  const [tagsFiltre, setTagsFiltre] = useState(() => new Set());
  const [travauxTypes, setTravauxTypes] = useState([]);
  const [hoveredReception, setHoveredReception] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragType, setDragType] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  // Bandeau « Annuler » après un déplacement appliqué directement (drag & drop).
  const [undoMove, setUndoMove] = useState(null);
  const undoTimer = useRef(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  // Sélecteur de date : le calendrier natif ne sait ni griser un jour fermé, ni
  // montrer la charge. On lui substitue le calendrier de charge déjà écrit
  // (`renderChargeCalendar`), en fenêtre ancrée sous l'onglet.
  // ⚠️ La fenêtre est en `position: fixed` et non `absolute` : ses deux
  // ancêtres la ROGNAIENT — `.atelier-tabs-date` porte `overflow: hidden`
  // (pour arrondir ses bords) et `.planning-block` un `overflow-x: auto`.
  // Elle était bien dans le DOM, simplement invisible. On mémorise donc la
  // position du bouton au moment du clic.
  const [datePop, setDatePop] = useState(null); // { top, left } | null

  const [dropIndicatorY, setDropIndicatorY] = useState(null);
  const [dropPreviewHeure, setDropPreviewHeure] = useState(null);
  const [dropPreviewFin, setDropPreviewFin] = useState(null); // heure de FIN prévisualisée pendant le glisser

  // Vidage de la journée affichée (super-admin) : { date, compte } une fois le
  // décompte reçu, null sinon. On n'ouvre jamais la modale à vide — savoir ce
  // qu'on s'apprête à détruire fait partie du geste.
  // Prise en charge d'un client de la file : on prévient le client par SMS que
  // c'est son tour, en même temps qu'on le passe « en cours ».
  const [recevoirModal, setRecevoirModal] = useState(null); // { entree, telephone, message, envoi }
  // Proposition de SMS au client — après déplacement d'une réception, ou au
  // passage d'un dossier en « Facturé » (véhicule prêt). Jamais d'envoi seul.
  const [smsProposition, setSmsProposition] = useState(null);
  // Proposition de SMS préparée, offerte depuis le récapitulatif de création.
  const [smsConfirmPret, setSmsConfirmPret] = useState(null); // { interventionId, titre, sousTitre, telephone, message, envoi }
  const [smsPropositionBusy, setSmsPropositionBusy] = useState(false);
  // Popover post-lasso (nouveau RDV / absence) + mini-formulaire d'absence
  const [creneauChoix, setCreneauChoix] = useState(null); // { x, y, staff, heure }
  const [absenceRapide, setAbsenceRapide] = useState(null); // { staff, date, motif, commentaire, busy, erreur }
  // [11] Suppression d'une absence depuis la grille : { absence, staff }
  const [suppressionAbsence, setSuppressionAbsence] = useState(null);
  const [recevoirBusy, setRecevoirBusy] = useState(false);

  // { portee, compte, chargement, saisie }  — `saisie` = confirmation tapée,
  // exigée dès que la portée dépasse la journée.
  const [videJourModal, setVideJourModal] = useState(null);
  const [videJourBusy, setVideJourBusy] = useState(false);
  // { portee, jours[], avance } — import Mécaplanning, jour par jour.
  const [mecaModal, setMecaModal] = useState(null);
  const mecaAnnule = useRef(false);
  const [dropForbidden, setDropForbidden] = useState(false);        // survol au-delà de la borne réception↔intervention
  const [dragBoundaryHeure, setDragBoundaryHeure] = useState(null); // heure de la carte liée (plafond réception / plancher intervention)
  // ⚠️ Jour auquel cette borne s'applique. Sans lui, on comparait des heures
  // NUES : un client reçu la VEILLE à 09:00 interdisait de planifier son
  // intervention du lendemain avant 09:00 — alors que la réception est passée
  // depuis longtemps (constat utilisateur). La borne ne vaut que le même jour.
  const [dragBoundaryDate, setDragBoundaryDate] = useState(null);
  const [moveWarning, setMoveWarning] = useState(null);             // message transitoire quand un déplacement est refusé
  const dragOffsetY = useRef(0);
  const moveWarnTimer = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mecaSyncing, setMecaSyncing] = useState(false); // import Mécaplanning en cours (bouton super-admin)
  // Sélecteur de concession : visible uniquement pour le super-admin (les autres ont une concession_id fixe)
  const canSelectConcession = user?.role === 'super-admin';
  // Concession = choix du MENU (user = utilisateur effectif, 2026-10-26).
  // Le Planning n'a plus de sélecteur à lui : avant, il écrivait directement
  // 'crm-global-concession', ce qui filtrait d'autres pages à l'insu du menu.
  // Pas de setter : la page est remontée (key du contenu) quand le menu change.
  const [selectedConcessionId] = useState(() => user?.concession_id || null);

  // ─── Droits sur la page Planning ───────────────────────────────────────────
  // Le verrou réel est côté serveur (routeur planning : méthode HTTP → droit).
  // Ici on évite d'offrir des gestes qui finiraient en 403 : un LECTEUR consulte,
  // point. Tant que les droits ne sont pas chargés on reste permissif, l'écran
  // ne pouvant de toute façon rien écrire sans l'accord du serveur.
  const [droits, setDroits] = useState(null);
  useEffect(() => {
    permissionsApi.checkAccess('planning')
      .then(r => setDroits(r.data?.permissions || r.data || null))
      .catch(() => setDroits(null));
  }, []);
  // Rôle Planning (Gestion des accès) : pilote l'expérience Technicien —
  // grille jour seule, gestes coupés, menu contextuel réduit à 3 actions.
  // Le verrou réel reste le middleware serveur ; l'UI se contente de ne pas
  // proposer ce qui finirait en 403.
  // Accès JDMA : conditionne le SEUL lien du panneau qualité, pas la pastille
  // elle-même. 9 des 44 comptes du Planning n'ont pas la page JDMA ; leur
  // proposer un lien qui finirait en 403 serait pire que ne rien proposer.
  const [peutVoirJdma, setPeutVoirJdma] = useState(false);
  useEffect(() => {
    permissionsApi.checkAccess('jdma')
      .then(r => setPeutVoirJdma(!!(r.data?.permissions || r.data || {}).peut_voir))
      .catch(() => setPeutVoirJdma(false));
  }, []);
  // Meme logique pour le lien « Avis Google » du panneau de la pastille.
  const [peutVoirGmb, setPeutVoirGmb] = useState(false);
  useEffect(() => {
    permissionsApi.checkAccess('gmb_reviews')
      .then(r => setPeutVoirGmb(!!(r.data?.permissions || r.data || {}).peut_voir))
      .catch(() => setPeutVoirGmb(false));
  }, []);
  // ─── Jours fériés / fermetures exceptionnelles ────────────────────────────
  // Réemploi de la table `jours_ouvres` (Budget → Jours ouvrés), déjà peuplée et
  // par territoire. Chargé sur une fenêtre large et non au jour le jour : la
  // navigation doit pouvoir SAUTER un férié sans aller-retour serveur.
  const [joursFermes, setJoursFermes] = useState(() => new Set());
  useEffect(() => {
    let annule = false;
    if (!selectedConcessionId) { setJoursFermes(new Set()); return; }
    const an = selectedDate.getFullYear();
    api.get('/planning/jours-fermes', { params: {
      concession_id: selectedConcessionId, from: `${an - 1}-01-01`, to: `${an + 1}-12-31` } })
      .then(r => { if (!annule) setJoursFermes(new Set(r.data?.jours || [])); })
      .catch(() => { if (!annule) setJoursFermes(new Set()); });
    return () => { annule = true; };
  }, [selectedConcessionId, selectedDate.getFullYear()]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Note JDMA de l'atelier (30 j glissants, APV) ──────────────────────────
  // Chargée par un effet PROPRE, indexé sur la seule concession, et
  // délibérément TENUE HORS de `loadData()` : celui-ci est rejoué à chaque
  // événement SSE (37 postes connectés, ~16 requêtes par mutation). Un avis
  // JDMA arrive une fois par jour — l'y ajouter aurait multiplié un appel
  // parfaitement statique par le volume de la tempête de rechargements.
  const [qualite, setQualite] = useState(null);

  useEffect(() => {
    let annule = false;
    if (!selectedConcessionId) { setQualite(null); return; }
    api.get('/planning/qualite-service', { params: { concession_id: selectedConcessionId } })
      .then(r => { if (!annule) setQualite(r.data?.actif ? r.data : null); })
      .catch(() => { if (!annule) setQualite(null); });   // muet : un indicateur ne casse pas un planning
    return () => { annule = true; };
  }, [selectedConcessionId]);

  const [planningRole, setPlanningRole] = useState(null); // { role, superAdmin } | null tant que non chargé
  useEffect(() => {
    api.get('/planning/mon-role')
      .then(r => setPlanningRole(r.data || { role: null }))
      .catch(() => setPlanningRole({ role: null }));
  }, []);
  const estAdminPlanning = planningRole?.superAdmin === true || planningRole?.role === 'administrateur';
  // Chaque décision d'affichage se lit sur une capacité NOMMÉE, plus sur un rôle
  // deviné : à cinq rôles, « si technicien » ne veut plus rien dire. Tant que le
  // rôle n'est pas chargé l'ensemble est vide — on masque puis on révèle, jamais
  // l'inverse : un bouton interdit ne doit pas clignoter à l'écran.
  const capacites = useMemo(() => new Set(planningRole?.capacites || []), [planningRole]);
  const peut = useCallback((c) => capacites.has(c), [capacites]);
  // Le menu contextuel reste accessible tant qu'il porte AU MOINS une action.
  const peutMenuContextuel = useMemo(
    () => CTX_ACTIONS.some((a) => a.cap && capacites.has(a.cap)), [capacites]);
  const lectureSeuleDroits = !!droits && droits.peut_voir && !droits.peut_creer && !droits.peut_modifier && !droits.peut_supprimer;
  // Lecture seule pour TOUS les gestes de la grille (drag, lasso, resize) : ils
  // reviennent tous à replanifier. Le menu contextuel, lui, est rouvert plus bas
  // dès qu'une seule de ses actions est autorisée.
  const lectureSeule = lectureSeuleDroits || !peut('dossier.replanifier');

  // Bouton d'action + segmented control dans le header
  useEffect(() => {
    if (!setHeaderAction) return;
    // Sans création NI accès aux tableaux, l'en-tête n'a plus rien à offrir.
    if (!peut('dossier.creer') && !peut('voir.tableaux')) { setHeaderAction(null); return; }
    const hasScope = !canSelectConcession || !!selectedConcessionId;
    setHeaderAction({
      ...(hasScope ? {
        segmented: {
          value: view,
          options: [
            { value: 'atelier', label: 'Atelier', onClick: () => setView('atelier') },
            { value: 'reception', label: 'Réception', onClick: () => setView('reception') },
            // Nouveautés pièces, sorties du menu Tableaux (2026-10-29) : en 1 clic
            // et avec leur compteur, pour qu'on y aille sans avoir à les chercher.
            // Un point, pas un nombre : le compteur ne redescend que si chacun
            // archive ce qu'il a vu, il aurait fini par afficher 500 en continu.
            { value: 'pieces-alertes', label: 'Notifications', point: alertesPiecesCount > 0, onClick: () => setView('pieces-alertes') },
          ]
        },
        dropdown: {
          // Le badge du menu ne compte QUE ce qui est nouveau et à traiter (RDV à
          // confirmer, nouveautés pièces). Le Suivi atelier en est exclu : c'est
          // une file permanente, jamais vide, qui allumerait le badge en continu
          // et lui ferait perdre tout sens. Son compteur reste sur sa ligne.
          label: 'Tableaux',
          value: ['assistance', 'participation', 'rdv-online', 'accord-immo', 'suivi-atelier', 'non-honores', 'receptions', 'derniers-contacts', 'sans-contact', 'kanban', 'rappels-rdv', 'remplissage'].includes(view) ? view : '',
          options: [
            { value: 'suivi-atelier', label: `📊 Suivi atelier${suiviAtelierCount > 0 ? ` (${suiviAtelierCount})` : ''}`, onClick: () => setView('suivi-atelier') },
            // Kanban : SUPER-ADMIN seulement pour l'instant. Le glisser-déposer
            // écrit le statut en base depuis l'atelier — on ouvre le périmètre
            // quand l'écran aura été éprouvé, pas avant. Garde ici ET au rendu.
            ...(user?.role === 'super-admin' ? [{ value: 'kanban', label: '🗂️ Kanban atelier', onClick: () => setView('kanban') }] : []),
            // Rappels J-1 : super-admin, comme le réglage qu'ils servent à
            // contrôler. La liste est une simulation ; l'envoi se fait ligne par
            // ligne, sur un clic explicite.
            ...(user?.role === 'super-admin' ? [{ value: 'rappels-rdv', label: '📱 Rappels de RDV', onClick: () => setView('rappels-rdv') }] : []),
            // Remplissage atelier V1 : super-admin, écran de validation des
            // paramètres (effectif, amplitude, absences) avant tout taux publié.
            ...(user?.role === 'super-admin' ? [{ value: 'remplissage', label: '📊 Remplissage atelier', onClick: () => setView('remplissage') }] : []),
            // Échanges clients : fermés au technicien, ici comme au serveur.
            ...(peut('client.echanges_voir') ? [{ value: 'derniers-contacts', label: '💬 Derniers contacts', onClick: () => setView('derniers-contacts') }] : []),
            ...(peut('client.echanges_voir') ? [{ value: 'sans-contact', label: '🔕 Clients sans contact', onClick: () => setView('sans-contact') }] : []),
            // Outil de management (comptages par réceptionnaire) : administrateurs
            // du Planning uniquement — le serveur refuse de toute façon (403).
            ...(estAdminPlanning ? [{ value: 'receptions', label: '🧾 Réceptions', onClick: () => setView('receptions') }] : []),
            { value: 'rdv-online', label: `📥 RDV à confirmer${rdvOnlineCount > 0 ? ` (${rdvOnlineCount})` : ''}`, onClick: () => setView('rdv-online') },
            { value: 'non-honores', label: `🚫 RDV non honorés${nonHonores.length > 0 ? ` (${nonHonores.length})` : ''}`, onClick: () => setView('non-honores') },
            { value: 'assistance', label: '🛠️ Assistance technique', onClick: () => setView('assistance') },
            { value: 'participation', label: '🤝 Demande participation', onClick: () => setView('participation') },
            { value: 'accord-immo', label: '📋 Accord Client Immo', onClick: () => setView('accord-immo') },
          ]
        },
      } : {}),
      // Gestes rares rangés sous « ⋯ » : un picto seul ne dit pas ce qu'il fait,
      // un libellé dans un menu, si. Le bouton est toujours rendu (le plein écran
      // est pour tous) : la largeur de l'en-tête reste constante, sinon la barre
      // Atelier/Réception se décale sous le curseur (mesuré le 2026-08-09).
      // Import et vidage restent grisés hors de la vue Atelier, jamais retirés.
      menu: {
        label: '⋯',
        title: "Plus d'actions",
        items: [
          ...(hasScope && user?.role === 'super-admin' ? [{
            label: mecaSyncing ? '⏳ Import Mécaplanning en cours…' : '🔄 Importer depuis Mécaplanning',
            onClick: () => { if (!mecaSyncing) handleMecaSyncRef.current?.(); },
            disabled: view !== 'atelier' || mecaSyncing,
            title: view === 'atelier' ? 'Jour, semaine ou mois' : 'Disponible depuis la vue Atelier',
          }] : []),
          ...(hasScope && user?.role === 'super-admin' ? [{
            label: '🧹 Vider le planning',
            onClick: () => ouvrirVideJourRef.current?.(),
            disabled: view !== 'atelier',
            title: view === 'atelier' ? 'Jour, semaine ou mois (super-admin)' : 'Disponible depuis la vue Atelier',
          }] : []),
          { label: '⊞ Plein écran', onClick: () => toggleFullscreen() },
        ],
      },
      secondary: [
        ...(user?.role === 'super-admin' || estAdminPlanning ? [{ label: '⚙️', onClick: () => setView('config'), title: 'Configuration' }] : []),
      ],
      primary: (!lectureSeule && hasScope
        && ((view === 'atelier' && peut('dossier.creer')) || (view === 'reception' && peut('reception.recevoir'))))
        ? { label: '+ Nouveau RDV', onClick: () => handleNewRdv() } : null,
      // Rendu par Dashboard entre le titre et le sélecteur Atelier/Réception.
      // Le composant possède son propre état d'ouverture : le survol ne rejoue
      // donc pas cet effet, qui reconstruirait tous les boutons de l'en-tête.
      indicator: qualite ? <NoteJdmaPill qualite={qualite} peutVoirJdma={peutVoirJdma} peutVoirGmb={peutVoirGmb} /> : null,
    });
  }, [setHeaderAction, view, isFullscreen, rdvOnlineCount, alertesPiecesCount, suiviAtelierCount, nonHonores.length, mecaSyncing, selectedConcessionId, lectureSeule, capacites, estAdminPlanning, qualite, peutVoirJdma, peutVoirGmb]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vue ramenée d'office dans le périmètre autorisé, si un état antérieur
  // (sessionStorage, navigation) pointait ailleurs.
  useEffect(() => {
    if (!planningRole) return;                       // rôle pas encore chargé
    if (!peut('voir.onglet_reception')) setView('atelier');
    if (!peut('voir.vue_semaine')) setAtelierView('jour');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacites, planningRole]);
  const planningRef = useRef(null);
  const datePickerRef = useRef(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [selectionMeca, setSelectionMeca] = useState(null);
  // Panneau latéral droit de la vue Atelier : onglet courant + état replié (persisté)
  // sideTab : 'reception' | 'calendrier' | 'demandes' | 'notifications' | 'or'
  const [sideTab, setSideTab] = useState('reception');
  // Un onglet masqué ne peut pas rester l'onglet actif : sans ce repli, un rôle
  // sans Réception ouvrirait le panneau sur un contenu vide.
  const ONGLETS_LATERAUX = useMemo(() => [
    ['reception', 'voir.onglet_reception'], ['calendrier', 'voir.onglet_calendrier'],
    ['demandes', 'voir.onglet_demandes'], ['notifications', 'voir.onglet_notifications'],
    ['or', 'voir.onglet_or'],
  ], []);
  useEffect(() => {
    if (!planningRole) return;
    const autorises = ONGLETS_LATERAUX.filter(([, c]) => capacites.has(c)).map(([t]) => t);
    if (autorises.length && !autorises.includes(sideTab)) setSideTab(autorises[0]);
  }, [capacites, planningRole, sideTab, ONGLETS_LATERAUX]);
  // ── Onglet 💬 Demandes : les demandes clients EN COURS du réceptionnaire,
  // sans quitter le planning. Données chargées paresseusement à l'ouverture de
  // l'onglet, puis toutes les 2 min tant qu'il est affiché. La modale est CELLE
  // de la page Demandes clients (DemandeModal), rendue ici.
  // ── Onglet 🔧 OR : les ordres de reparation ouverts, sans quitter le planning.
  // « Mes OR » s'appuie sur le NOM du compte connecte : cote Datacar le
  // receptionnaire est un alias (« 011-kelismar »), que la table de
  // correspondance ramene a un nom canonique (« ELISMAR ») — c'est ce nom que
  // l'API sait filtrer. Un compte dont le nom ne correspond a aucun alias voit
  // donc une liste vide, ce que le message d'etat dit explicitement.
  const [orPanel, setOrPanel] = useState({ liste: [], total: 0, charge: false, erreur: null });
  const [orVue, setOrVue] = useState('miens');   // 'miens' | 'tous'
  const [orFiltre, setOrFiltre] = useState('');

  const rafraichirOr = useCallback(async (vue) => {
    setOrPanel(p => ({ ...p, charge: true, erreur: null }));
    try {
      const params = { limit: 200 };
      if (vue === 'miens') params.receptionnaire = user?.nom || '—';
      const { data } = await api.get('/or-en-cours', { params });
      setOrPanel({ liste: data?.ors || [], total: data?.total || 0, charge: false, erreur: null });
    } catch (e) {
      console.error('[panneau OR]', e);
      setOrPanel(p => ({ ...p, charge: false, erreur: e.response?.data?.error || 'Chargement impossible' }));
    }
  }, [user]);

  useEffect(() => {
    if (sideTab !== 'or') return;
    rafraichirOr(orVue);
  }, [sideTab, orVue, selectedConcessionId, rafraichirOr]);

  // ── Onglet 🔔 Notifications : les evolutions qui touchent un dossier.
  // Premiere source branchee : les Nouveautes pieces (livraisons et anomalies),
  // qui existaient deja en tableau dans le menu « Tableaux » — meme file, meme
  // regle de « vu » INDIVIDUELLE (2026-09-07) : chacun archive pour lui seul,
  // la carte reste visible pour les collegues qui ne l'ont pas encore vue.
  // Le bloc est concu pour accueillir d'autres sources : chaque carte porte son
  // origine, et la liste est triee par date, toutes sources confondues.
  const [notifsPanel, setNotifsPanel] = useState({ liste: [], charge: false, erreur: null });
  const [notifBusy, setNotifBusy] = useState(null);   // id de la carte en cours de validation
  const [demandesPanel, setDemandesPanel] = useState({ liste: [], charge: false, erreur: null });
  const [demandesVue, setDemandesVue] = useState('miennes'); // 'miennes' | 'toutes'
  const [demandesCount, setDemandesCount] = useState(0);
  const [demandeOuverte, setDemandeOuverte] = useState(null); // objet demande → <DemandeModal>
  // Archivage depuis le panneau : même modale de justification que la page
  // Demandes clients (<ArchiveDemandeModal>), donc mêmes motifs types.
  const [demandeAArchiver, setDemandeAArchiver] = useState(null);
  const [archivageEnCours, setArchivageEnCours] = useState(false);
  const [demandesRefs, setDemandesRefs] = useState(null); // { responsables, marques, modeles, sujets } (lazy)
  const monResponsableId = useMemo(
    () => (demandesRefs?.responsables || []).find(r => r.user_id === user?.id)?.id ?? null,
    [demandesRefs, user?.id]
  );

  const chargerDemandesRefs = async () => {
    if (demandesRefs) return demandesRefs;
    const [resp, marq, mod, suj] = await Promise.all([
      referentielsApi.getResponsables(),
      referentielsApi.getMarques(),
      referentielsApi.getModeles(),
      sujetsApi.getAll(),
    ]);
    const refs = {
      responsables: resp.data.responsables || [],
      marques: marq.data.marques || [],
      modeles: mod.data.modeles || [],
      sujets: suj.data.sujets || [],
    };
    setDemandesRefs(refs);
    return refs;
  };

  const rafraichirDemandes = useCallback(async (vue = demandesVue) => {
    setDemandesPanel(p => ({ ...p, charge: true, erreur: null }));
    try {
      // Le panneau du Planning Atelier ne montre QUE l'atelier : les demandes
      // Pieces / VN / VO sont ecartees des la requete, dans les deux vues (la
      // vue « Mes demandes » ne filtrait rien et remontait tous les services).
      // Exclusion plutot que `service=ATELIER` : les collaborateurs dont le
      // service n'est pas renseigne perdraient sinon toutes leurs demandes.
      // « Mes demandes » ajoute ensuite un filtre client sur mon responsable
      // (le serveur borne deja un operateur « own » a ses propres demandes).
      const params = { service_exclu: 'PIECES,VN,VO' };
      const { data } = await demandesApi.getAll(params);
      setDemandesPanel({ liste: Array.isArray(data) ? data : (data.demandes || []), charge: false, erreur: null });
    } catch (e) {
      console.error('[panneau demandes]', e);
      setDemandesPanel(p => ({ ...p, charge: false, erreur: e.response?.data?.error || 'Chargement impossible' }));
    }
    demandesApi.getMyCount().then(r => setDemandesCount(r.data?.count ?? 0)).catch(() => {});
  }, [demandesVue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compteur du badge dès le montage du panneau, liste au premier affichage de
  // l'onglet, rafraîchissement léger tant qu'il reste ouvert.
  useEffect(() => {
    demandesApi.getMyCount().then(r => setDemandesCount(r.data?.count ?? 0)).catch(() => {});
  }, []);
  useEffect(() => {
    if (sideTab !== 'demandes') return;
    chargerDemandesRefs().catch(e => console.error('[panneau demandes/refs]', e));
    rafraichirDemandes();
    const t = setInterval(() => rafraichirDemandes(), 120000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideTab, demandesVue]);

  // Chargement des notifications : a l'ouverture de l'onglet, puis a chaque
  // evenement SSE `pieces-alertes` (meme signal que le compteur du menu
  // Tableaux) — pas de sondage periodique, le flux nous reveille.
  const rafraichirNotifications = useCallback(async () => {
    setNotifsPanel(p => ({ ...p, charge: true, erreur: null }));
    try {
      const { data } = await api.get('/pieces-alertes');
      const liste = (Array.isArray(data) ? data : []).map(a => ({ ...a, source: 'pieces' }));
      setNotifsPanel({ liste, charge: false, erreur: null });
    } catch (e) {
      console.error('[panneau notifications]', e);
      setNotifsPanel(p => ({ ...p, charge: false, erreur: e.response?.data?.error || 'Chargement impossible' }));
    }
  }, []);

  useEffect(() => {
    if (sideTab !== 'notifications') return;
    rafraichirNotifications();
  }, [sideTab, alertesPiecesTick, selectedConcessionId, rafraichirNotifications]);

  // « J'ai vu » : retire la carte tout de suite (l'attente d'un aller-retour sur
  // un geste aussi anodin se remarque), et la remet si le serveur refuse.
  const marquerNotifVue = async (notif) => {
    if (notifBusy) return;
    setNotifBusy(notif.id);
    const avant = notifsPanel.liste;
    setNotifsPanel(p => ({ ...p, liste: p.liste.filter(n => n.id !== notif.id) }));
    try {
      await api.post('/pieces-alertes/traiter', { ids: [notif.id] });
      setAlertesPiecesCount(c => Math.max(0, c - 1));
    } catch (e) {
      console.error('[notifications/vue]', e);
      setNotifsPanel(p => ({ ...p, liste: avant, erreur: "Impossible d'enregistrer" }));
    } finally {
      setNotifBusy(null);
    }
  };

  // Clic sur le picto 💬 d'une carte : retrouver la demande EN COURS du
  // véhicule (par immat, sinon par VIN — même route) et l'ouvrir.
  const ouvrirDemandeDuVehicule = async (intervention) => {
    try {
      await chargerDemandesRefs();
      const cles = [intervention.immat || intervention.immatriculation, intervention.vin]
        .map(v => String(v || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase())
        .filter(v => v.length >= 4);
      for (const cle of cles) {
        const { data } = await api.get(`/demandes/by-immat/${encodeURIComponent(cle)}`);
        const enCours = data?.demandes || [];
        if (enCours.length) {
          const fraiche = await demandesApi.getById(enCours[0].id).catch(() => null);
          setDemandeOuverte(fraiche?.data?.demande || enCours[0]);
          return;
        }
      }
    } catch (e) {
      console.error('[picto demande]', e);
    }
  };

  const ouvrirDemande = async (d) => {
    try {
      await chargerDemandesRefs();
      const { data } = await demandesApi.getById(d.id);   // fraîcheur (commentaires, synthèse)
      setDemandeOuverte(data.demande || data);
    } catch (e) {
      console.error('[panneau demandes/ouvrir]', e);
      setDemandeOuverte(d);   // repli : la version de la liste
    }
  };
  // Mois affiché dans le mini-calendrier de charge mensuelle (onglet 'calendrier')
  const [chargeMonth, setChargeMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  // Charge du mois, calculée par le serveur (`GET /planning/charge-mensuelle`).
  // ⚠️ Elle NE PEUT PAS être dérivée de `interventions` : cet état ne couvre que
  // ±14 jours autour de la date affichée, alors que le calendrier en dessine
  // jusqu'à 42 — plus de la moitié du mois comptait donc zéro heure et sortait
  // en vert. C'était la première cause du « tout reste vert » après un import.
  const [chargeMois, setChargeMois] = useState(null);
  const [sideCollapsed, setSideCollapsed] = useState(
    () => localStorage.getItem('planning-side-collapsed') === 'true'
  );
  useEffect(() => {
    localStorage.setItem('planning-side-collapsed', String(sideCollapsed));
  }, [sideCollapsed]);
  // Largeur du panneau latéral, redimensionnable via la barre de séparation (persistée).
  const [sideWidth, setSideWidth] = useState(() => {
    const v = parseInt(localStorage.getItem('planning-side-width'), 10);
    return v && v >= 240 && v <= 640 ? v : 320;
  });
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = sideWidth;
    let current = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      current = Math.min(640, Math.max(240, startWidth - (ev.clientX - startX)));
      setSideWidth(current);
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      localStorage.setItem('planning-side-width', String(current));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  // focusedReceptionnaire persisté en sessionStorage — pilote le filtre par avatar du panneau latéral
  const [focusedReceptionnaire, setFocusedReceptionnaireState] = useState(() => {
    const saved = sessionStorage.getItem('planning-focused-receptionnaire');
    return saved ? parseInt(saved, 10) || null : null;
  });
  const setFocusedReceptionnaire = (val) => {
    setFocusedReceptionnaireState(val);
    if (val) sessionStorage.setItem('planning-focused-receptionnaire', String(val));
    else sessionStorage.removeItem('planning-focused-receptionnaire');
  };
  const [showTermines, setShowTermines] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Recherche globale (loupe) : elle a remplacé la barre qui n'estompait que
  // les cartes de la journée affichée. `ouvertureEnAttente` sert au retour d'un
  // résultat — voir `ouvrirDossierDepuisRecherche`.
  const [rechercheOuverte, setRechercheOuverte] = useState(false);
  const ouvertureEnAttente = useRef(null);
  const [hoveredInterventionId, setHoveredInterventionId] = useState(null);
  // Mise en évidence d'une intervention dans la grille planning (recherche, alertes).
  // Le survol du panneau Réception ne déclenche plus rien : le focus + auto-scroll
  // à chaque passage de souris était vécu comme un sursaut de la grille (retiré 2026-08-10).
  const [highlightedInterventionId, setHighlightedInterventionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const initialLoadDone = useRef(false);
  // Jour réellement affiché à l'instant T. Sert à JETER les réponses périmées :
  // deux récupérateurs visent la file d'attente (loadData et le rafraîchissement
  // 5 s), et une requête partie avant un changement de jour ne s'annule pas —
  // sa réponse arrivait après et réaffichait le jour précédent, que le tour
  // suivant corrigeait 5 s plus tard. D'où le « ça charge, ça repart, ça
  // recharge » signalé en revenant sur aujourd'hui.
  const jourAfficheRef = useRef(null);
  // Compteur (pas booléen) : chaque mutation incrémente, chaque event SSE reçu décrémente.
  // Gère correctement les mutations concurrentes (2 POST en parallèle = 2 SSE à skipper).
  const skipNextSSE = useRef(0);
  const handleMecaSyncRef = useRef(null);
  // Même motif que l'import : le bouton du header est posé avant que le handler
  // n'existe, on passe donc par une ref.
  const ouvrirVideJourRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(new Date());



  const [fileAttente, setFileAttente] = useState([]);
  const [aPlanifier, setAPlanifier] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());

  // Staff dynamique
  const [staffList, setStaffList] = useState([]);
  const [ateliersList, setAteliersList] = useState([]);
  const [absencesList, setAbsencesList] = useState([]);
  // Filtre textuel du bloc « À planifier » — partagé avec le choix de dossier
  // depuis un créneau : même saisie (nom, immat, OR), même résultat.
  const [filtreAPlanifier, setFiltreAPlanifier] = useState('');
  const [configConcession, setConfigConcession] = useState('');
  const [concessions, setConcessions] = useState([]);
  // ⚠️ La vue « Tous » a été supprimée (2026-09-05, demande utilisateur) : elle
  // affichait jusqu'à 80 colonnes, illisibles quel que soit le plancher de
  // largeur. Le Planning s'ouvre donc TOUJOURS sur un atelier.
  //
  // Lequel : le DERNIER consulté, mémorisé sur le poste. Mesuré avant de
  // choisir : se rabattre sur le premier atelier configuré masquerait les deux
  // tiers de la journée (CENTRAUTODIS 43 dossiers sur 66, SOGUAVASOREC 31/47,
  // BLUE 27/42) — et chez CENTRAUTODIS le premier configuré est « Service
  // Rapide », le PETIT atelier, pas « Atelier Mécanique ». Chacun retrouve donc
  // le sien sans le rechercher ; le repli sur le premier ne sert qu'au premier
  // usage.
  //
  // Un compteur « n dossiers dans d'autres ateliers » avait été ajouté puis
  // RETIRÉ le jour même à la demande de l'utilisateur : les réceptionnaires
  // travaillent atelier par atelier, ce qui se passe à côté ne les concerne pas.
  // Ne pas le réintroduire au motif que « deux tiers de la journée sont
  // masqués » — c'est voulu.
  const CLE_ATELIER = 'planning-dernier-atelier';
  const [selectedAtelierId, setSelectedAtelierId] = useState(
    () => localStorage.getItem(CLE_ATELIER) || ''
  );
  useEffect(() => {
    if (selectedAtelierId) localStorage.setItem(CLE_ATELIER, String(selectedAtelierId));
  }, [selectedAtelierId]);
  const [pausesList, setPausesList] = useState([]);
  // Référentiel des marques (logo + couleur). Chargé UNE FOIS au montage et non
  // dans loadData() : ce référentiel ne bouge jamais en cours de journée, et
  // loadData() est rejoué à chaque événement SSE — une dixième requête y serait
  // rejouée par tous les postes à chaque écriture d'un collègue.
  const [marquesRef, setMarquesRef] = useState([]);
  const [filtreMarque, setFiltreMarque] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    api.get('/referentiels/marques')
      .then(r => setMarquesRef(r.data?.marques || []))
      .catch(() => setMarquesRef([]));   // sans référentiel : pastilles à initiale
  }, []);

  // Marque du véhicule dans la file de réception : option de concession
  // (Administration ▸ Concessions). Réservée aux multimarques — chez une
  // mono-marque la pastille serait la même partout. Portée au niveau du
  // composant : la file l'utilise, mais aussi la modale « Recevoir ».
  // `selectedConcessionId` et non `user.concession_id` : un super-admin change de
  // concession depuis l'en-tête du Planning, et l'option doit suivre ce choix —
  // sinon il verrait la file de BLUE avec les réglages de Soguava.
  const marqueFileActive = useMemo(
    () => !!concessions.find(c => c.id === selectedConcessionId)?.marque_file_enabled,
    [concessions, selectedConcessionId]
  );

  // Marques proposées au comptoir : celles de la concession, « Autres » exclu
  // (la modale offre déjà son propre « À préciser »).
  const marquesConcession = useMemo(
    () => marquesRef.filter(m =>
      !/^autres?( marque)?$/i.test(String(m.nom).trim()) &&
      (!selectedConcessionId || (m.concessions || []).some(c => c.id === selectedConcessionId))
    ),
    [marquesRef, selectedConcessionId]
  );

  // IA-3 Conflits + suggestions de réaffectation
  const [conflictPopover, setConflictPopover] = useState(null); // { meca, conflicts[] }
  const [suggestions, setSuggestions] = useState(null);         // { meca, items[], intervention }
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // ═══ Menu contextuel (clic droit) — panneau à colonnes ═══════════════════
  // Trois colonnes plutôt qu'un accordéon : colonne 1 = les actions, colonne 2 =
  // le contenu de l'action SURVOLÉE (sans clic), colonne 3 = « Et ensuite ? »,
  // affichée APRÈS une écriture pour enchaîner les gestes qui la suivent
  // naturellement. Un seul chemin d'état remplace les six booléens exclusifs
  // d'avant, qu'il fallait penser à remettre à false dans cinq handlers.
  const [contextMenu, setContextMenu] = useState(null); // { x, y, intervention, dir: 'right'|'left' }
  const [ctxCol2, setCtxCol2] = useState(null);         // clé de l'action survolée, cf. CTX_ACTIONS
  const [ctxSuites, setCtxSuites] = useState(null);     // { action, label, fait: { parking, commentaire } }
  const [ctxFiltre, setCtxFiltre] = useState('');       // filtre au clavier de la colonne 2
  const [ctxCommentaire, setCtxCommentaire] = useState('');
  const [ctxBusy, setCtxBusy] = useState(null);         // clé de l'écriture en cours (anti double-clic)

  // Caches préchargés à l'OUVERTURE du menu (et non au clic sur le sous-menu) :
  // sans ça, le survol n'aurait rien d'instantané à afficher.
  const [ctxParking, setCtxParking] = useState(null);   // { position, zones }
  const [ctxMessages, setCtxMessages] = useState(null); // modèles de messages types
  const [smsModal, setSmsModal] = useState(null);       // { intervention, texte, envoi }

  // Suppression : le menu contextuel ne fait plus qu'ouvrir une modale de
  // confirmation (deux clics au même endroit suffisaient à supprimer un dossier).
  const [deleteModal, setDeleteModal] = useState(null); // { intervention }
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [ctxPlanifForm, setCtxPlanifForm] = useState({ date: '', heure: '', duree: '', mecanicien_id: '' });

  // Mécanique du survol : refs et non états, pour ne pas re-rendre le menu à
  // chaque mouvement de souris (le pointeur émet des dizaines d'événements par
  // seconde ; un setState par mouvement rendrait le panneau saccadé).
  const ctxPanelRef = useRef(null);     // panneau entier (test « clic à l'extérieur »)
  const ctxCol2Ref = useRef(null);      // élément DOM de la colonne 2 ouverte
  const ctxCommutTimer = useRef(null);  // minuteur de commutation en attente
  const ctxAttente = useRef(null);      // { cle, depuis } action en attente d'affichage
  const ctxTrace = useRef([]);          // derniers points du pointeur : [{ x, y, t }]

  // Modale de report avec vue disponibilités
  // Motif facultatif du panneau de programmation, versé au commentaire du
  // dossier. Il n'existait que dans l'ancienne modale de report — donc jamais
  // disponible au moment de déplacer, là où l'on aimerait le plus savoir pourquoi.
  const [ctxMotif, setCtxMotif] = useState('');
  // Disponibilité du mécanicien visé, recalculée à chaque changement du
  // formulaire : c'est la seule chose qu'apportait le tableau de charge.
  const [ctxDispo, setCtxDispo] = useState(null); // { etat: 'load'|'ok'|'conflit'|'erreur', texte }

  const [formData, setFormData] = useState({
    client_nom: '',
    client_prenom: '',
    client_tel: '',
    client_email: '',
    immatriculation: '',
    vin: '',
    poste_reception: null,
    heure_reception: '08:00',
    date_reception: toYmd(new Date()),
    mecanicien: '',
    mecanicien_id: null,
    heure_debut: '09:00',
    duree: 2,
    type_intervention: '',   // vide par défaut (22/10), comme la modale
    commentaire: '',
    date_intervention: toYmd(new Date()),
    pieces: [],
    statut_pieces: '',
    statut_avancement: 'planifie',
    numero_or: '',
    marque: '',
    modele: '',
    statut_atelier_id: null,
    travaux: []
  });

  // Le collaborateur est-il en poste CE JOUR-LÀ ? Répond au cas de l'intérimaire :
  // passé sa date de sortie, sa colonne disparaît de la grille — sans qu'on ait à
  // le supprimer, ce qui orphelinerait ses interventions passées.
  // ⚠️ Comparaison de chaînes 'AAAA-MM-JJ' via toYmd, jamais de `new Date()` sur
  // une date pure : elle serait lue en UTC et reculerait d'un jour aux Antilles.
  const estEnPoste = useCallback((s, date) => {
    const j = toYmd(date);
    if (s.date_entree && toYmd(s.date_entree) > j) return false;
    if (s.date_sortie && toYmd(s.date_sortie) < j) return false;
    return true;
  }, []);

  const staffReceptionnaires = staffList.filter(s => s.role === 'receptionnaire' && s.actif !== false && estEnPoste(s, selectedDate));
  // Filtrer les mécaniciens par atelier sélectionné
  const staffMecas = staffList.filter(s => s.role === 'mecanicien' && (!selectedAtelierId || String(s.atelier_id) === String(selectedAtelierId)) && estEnPoste(s, selectedDate));
  // Nom affiché = "Prénom NOM" si prénom renseigné, sinon juste "NOM"
  // Nom AVANT prénom (demande métier du 2026-08-09) : on cherche un collaborateur
  // par son nom, et c'est lui qui doit tomber sous l'œil en premier dans une liste
  // ou un en-tête de colonne. Les noms sont déjà stockés en capitales dans
  // `planning_staff`, la distinction se fait donc d'elle-même.
  // ⚠️ Le même ordre est appliqué CÔTÉ SERVEUR (planning.js) : le nom affiché sur
  // les cartes n'est pas construit ici mais composé en SQL.
  const getStaffLabel = (s) => [s.nom, s.prenom].filter(Boolean).join(' ') || s.nom || `Staff #${s.id}`;
  // Nom compact pour les cellules étroites = "NOM P." (nom complet + initiale du prénom)
  const getStaffShortLabel = (s) => {
    if (!s) return '';
    const p = (s.prenom || '').trim();
    const n = (s.nom || '').trim();
    if (p && n) return `${n} ${p[0].toUpperCase()}.`;
    return n || p || `Staff #${s.id}`;
  };
  // En-tête de colonne : les noms sont saisis en capitales (« CERIAC M. ») ;
  // en casse de titre ils se lisent d'un coup d'œil et crient moins.
  const casseTitre = (t) => (t || '').toLowerCase().replace(/(^|[\s'’-])(\p{L})/gu, (m, sep, l) => sep + l.toUpperCase());
  // Heures à la française dans l'en-tête : « 6,5 h » et non « 6.5h ».
  const heuresFr = (h) => `${String(Math.round(h * 100) / 100).replace('.', ',')} h`;
  // Initiales = 1ʳᵉ lettre du prénom + 1ʳᵉ lettre du nom (fallback sur les 2 premières du nom)
  // Helper : statut d'arrivée du client pour une intervention donnée (jour courant uniquement)
  const getClientStatusForIntervention = (intv) => {
    if (!intv || !intv.id) return null;
    const today = formatDate(new Date());
    if (intv.date !== today) return null;
    const parentId = intv.is_complement ? intv.parent_intervention_id : intv.id;
    const entry = fileAttente.find(f => f.intervention_id === parentId);
    return entry || null;
  };
  // « Confirmé par le client » : visible tant que le client n'est ni arrivé à
  // la borne (passage en file d'attente) ni reçu (statut ≥ « Réceptionné »).
  const confirmeVisible = (intv) => !!(intv && intv.client_confirme_at && !intv.client_annule_at
    && !estDejaRecu(intv) && !getClientStatusForIntervention(intv));
  // Zone d'action contextuelle d'une carte intervention.
  // Retourne au plus UN élément à afficher sous la ligne du véhicule :
  //   - badge "Restitué HH:MM" si déjà restituée
  //   - badge Arrivé / En réception (plus de « Reçu » : la confirmation pose
  //     désormais le statut atelier « Réceptionné », qui porte sa propre couleur)
  //   - null sinon (carte épurée)
  //
  // ⚠️ Le BOUTON « Restituer » a été retiré le 2026-09-04 (demande utilisateur),
  // avec sa fenêtre de confirmation et sa route serveur. Mesuré avant de le
  // supprimer : 22 restitutions en production depuis le 18/08, dont 20 chez une
  // seule concession et 17 par une seule personne — et le commentaire, la
  // friction même du geste, JAMAIS rempli. Le réceptionnaire change désormais
  // le statut lui-même.
  //
  // La LECTURE reste : `restituee_at` est encore posé sur 22 dossiers réels et
  // lu à une trentaine d'endroits (badge ci-dessous, filtres, véhicules de
  // courtoisie). Retirer le geste n'est pas retirer l'historique.
  const getCardActionSlot = (intervention) => {
    if (!intervention) return null;
    if (intervention.is_complement) return null;
    // 1. Restituée → badge lecture seule
    if (intervention.restituee_at) {
      const d = new Date(intervention.restituee_at);
      return { kind: 'restituee', heure: d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) };
    }
    // 3. Flux d'accueil : deux badges seulement, avant la confirmation
    const fa = getClientStatusForIntervention(intervention);
    if (!fa) return null;
    if (fa.statut === 'en_attente') {
      const t = fa.heure_arrivee ? new Date(fa.heure_arrivee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;
      // Le temps d'attente sur le badge lui-même : « Arrivé » seul obligeait à
      // survoler pour savoir depuis quand le client patiente.
      const mins = fa.heure_arrivee ? Math.max(0, Math.floor((currentTime - new Date(fa.heure_arrivee)) / 60000)) : null;
      const depuis = mins == null ? ''
        : mins >= 60 ? ` depuis ${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, '0')}`
        : ` depuis ${mins} min`;
      return { kind: 'flux', statut: 'en_attente', entryId: fa.id, next: 'en_cours',
               label: `🙋 Arrivé${depuis}`, tooltip: `${t ? `Arrivé à ${t}` : 'Client arrivé'} — cliquer : commencer la réception` };
    }
    if (fa.statut === 'en_cours') {
      const t = fa.heure_prise_en_charge ? new Date(fa.heure_prise_en_charge).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;
      return { kind: 'flux', statut: 'en_cours', entryId: fa.id, next: 'termine',
               label: '🔧 En réception', tooltip: `${t ? `Pris en charge à ${t}` : 'En réception'} — cliquer : réception terminée` };
    }
    // Plus de badge « Reçu » sur la carte (2026-08-14, demande utilisateur) :
    // confirmer la réception pose désormais le statut atelier « Réceptionné »
    // (serveur, `PUT /planning/file-attente/:id`), et ce statut porte déjà sa
    // couleur sur la carte — le badge faisait doublon.
    // Ne concerne QUE la carte atelier : la pastille « Reçu » de la colonne
    // Réception, qui suit l'accueil du client, reste affichée.
    return null;
  };
  // Clic sur le badge de flux d'une carte : un cran d'avancement (Arrivé →
  // En réception → Reçu), le MÊME parcours que le panneau Réception — passer en
  // réception ouvre donc la modale « Recevoir » (aperçu SMS « c'est à vous »,
  // affectation au compte connecté), jamais un passage direct silencieux.
  const fluxEnCoursRef = useRef(new Set());
  const avancerFluxAccueil = async (slot) => {
    if (!slot?.entryId || !slot.next || fluxEnCoursRef.current.has(slot.entryId)) return;
    if (slot.next === 'en_cours') {
      const entree = fileAttente.find(f => f.id === slot.entryId);
      if (entree) ouvrirRecevoir(entree);
      return;
    }
    fluxEnCoursRef.current.add(slot.entryId);
    try {
      await api.put(`/planning/file-attente/${slot.entryId}`, { statut: slot.next });
      await refreshFileAttente();
    } catch (err) {
      console.error('[flux-accueil carte]', err);
    } finally {
      fluxEnCoursRef.current.delete(slot.entryId);
    }
  };
  // Ligne d'heure courante — partagée entre la grille Atelier (créneau de 20 px)
  // et la grille Réception (38 px). Le conteneur doit être position:relative.
  const renderCurrentTimeLine = (slotPx) => {
    if (toYmd(new Date()) !== toYmd(selectedDate)) return null;
    const minutesSinceOpen = currentTime.getHours() * 60 + currentTime.getMinutes() - GRID_START_MIN;
    if (minutesSinceOpen < 0 || minutesSinceOpen > GRID_SLOTS * 15) return null;
    const top = 40 + (minutesSinceOpen / 15) * slotPx;
    const hh = currentTime.getHours().toString().padStart(2, '0');
    const mm = currentTime.getMinutes().toString().padStart(2, '0');
    return (
      // ⚠️ `right: 0` s'évalue sur la largeur VISIBLE du conteneur, pas sur sa
      // largeur défilée : au-delà de quelques mécaniciens, le trait s'arrêtait
      // au bord de l'écran au lieu de traverser toute la grille. Aucune règle
      // CSS ne donne accès à la largeur de défilement — on la lit donc au
      // montage, et à chaque rendu (la minute qui change en provoque un, les
      // changements de colonnes aussi). La pastille de l'heure, elle, reste
      // collée à gauche : c'est là qu'on la cherche.
      <div
        className="current-time-line"
        style={{ top }}
        ref={(el) => {
          if (!el || !el.parentElement) return;
          // ⚠️ SE REMETTRE À ZÉRO AVANT DE MESURER. Le trait est un enfant
          // absolu : il compte dans la largeur de défilement du conteneur. Se
          // mesurer sans se neutraliser, c'est se mesurer soi-même — la largeur
          // ne pouvait alors que croître, jamais décroître. Symptôme signalé en
          // production : après être passé d'un atelier de 15 colonnes à un de 7,
          // la grille gardait 2 738 px de défilement pour 1 546 px de contenu,
          // avec une vaste zone vide à droite.
          el.style.width = '0px';
          el.style.width = `${el.parentElement.scrollWidth}px`;
        }}
      >
        <div className="current-time-badge">{hh}:{mm}</div>
      </div>
    );
  };
  // mecaniciens = tableau d'objets staff { id, nom, prenom, ... }
  // Aucun atelier retenu (premier usage), ou atelier mémorisé qui n'appartient
  // pas à la concession courante : on se pose sur le premier de la liste.
  useEffect(() => {
    if (!ateliersList.length) return;
    const connu = ateliersList.some(a => String(a.id) === String(selectedAtelierId));
    if (!connu) setSelectedAtelierId(String(ateliersList[0].id));
  }, [ateliersList, selectedAtelierId]);

  const mecaniciens = staffMecas;
  // ─── Largeur des colonnes : plancher adaptatif ─────────────────────────────
  // Demande utilisateur : « voir toutes les colonnes sans naviguer à
  // l'horizontale ». `flex: 1` répartit déjà la place ; ce qui imposait le
  // défilement, c'était le plancher fixe de 180 px.
  //
  // On le calcule donc : la place disponible divisée par le nombre de colonnes,
  // bornée entre 72 px (en dessous, une carte ne se lit plus — mieux vaut
  // défiler que ne rien pouvoir lire) et 180 px (au-delà, `flex` étire tout
  // seul, inutile d'imposer davantage).
  const grilleRef = useRef(null);
  const [colMin, setColMin] = useState(180);
  useLayoutEffect(() => {
    const el = grilleRef.current;
    const n = mecaniciens.length;
    if (!el || !n) return;
    const calculer = () => {
      const dispo = el.clientWidth - 38; // la colonne des heures ne se comprime pas
      // ⚠️ Arrondi INFÉRIEUR, jamais au plus proche : `Math.round` remontait la
      // largeur d'une fraction de pixel par colonne, et 15 colonnes de 101 px
      // dépassaient de 7 px — assez pour faire apparaître une barre de
      // défilement horizontale sur une grille qui tenait à l'écran.
      const valeur = Math.floor(Math.min(180, Math.max(72, dispo / n)));
      setColMin(prev => (prev === valeur ? prev : valeur));
    };
    calculer();
    const ro = new ResizeObserver(calculer);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mecaniciens.length]);
  // Helper : trouver un staff par son id
  // TOUS les mécaniciens actifs de la concession, ordonnés par atelier : les
  // contextes d'ÉDITION (replanifier, travaux de la modale) doivent permettre de
  // changer d'atelier — l'atelier d'une carte n'est pas un champ, il découle du
  // mécanicien choisi. Les contextes de NAVIGATION (chips, grille) restent, eux,
  // filtrés par l'atelier sélectionné.
  const mecaniciensTous = useMemo(() => {
    const ordreAtelier = new Map(ateliersList.map((a, i) => [a.id, i]));
    return staffList
      .filter(st => st.role === 'mecanicien' && st.actif !== false)
      .sort((a, b) => (ordreAtelier.get(a.atelier_id) ?? 99) - (ordreAtelier.get(b.atelier_id) ?? 99)
        || (a.ordre ?? 0) - (b.ordre ?? 0) || String(a.nom).localeCompare(String(b.nom), 'fr'));
  }, [staffList, ateliersList]);

  // Groupes pour un <select> : un <optgroup> par atelier, « Sans atelier » en queue.
  const groupesMecaniciens = useMemo(() => {
    const parAtelier = new Map();
    for (const m of mecaniciensTous) {
      const cle = m.atelier_id ?? 'aucun';
      if (!parAtelier.has(cle)) parAtelier.set(cle, []);
      parAtelier.get(cle).push(m);
    }
    return [...parAtelier.entries()].map(([cle, mecas]) => ({
      atelier: cle === 'aucun' ? 'Sans atelier' : (ateliersList.find(a => a.id === cle)?.nom || `Atelier ${cle}`),
      mecas,
    }));
  }, [mecaniciensTous, ateliersList]);

  // Résolution sur la liste COMPLÈTE : un mécanicien choisi hors de l'atelier
  // affiché doit se résoudre quand même, sinon son nom se perdait à l'enregistrement.
  const getMecaById = (id) => mecaniciensTous.find(m => m.id === id) || mecaniciens.find(m => m.id === id);

  const [rdvReception, setRdvReception] = useState([]);
  const [interventions, setInterventions] = useState([]);

  // Fermer les modales avec Échap
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // Échap = annuler : sur la confirmation de suppression, c'est le geste attendu.
        if (deleteModal && !deleteBusy) { setDeleteModal(null); return; }
        if (conflictPopover) { setConflictPopover(null); setSuggestions(null); return; }
        if (pendingMove) cancelMove();
        // Escape ne ferme plus la modale pour éviter les erreurs
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });


  const handleCycleAvancement = async (e, interventionId) => {
    e.stopPropagation();
    const cycle = { planifie: 'en_cours', en_cours: 'termine', termine: 'planifie' };
    const intervention = interventions.find(i => i.id === interventionId);
    if (!intervention) return;
    const newStatut = cycle[intervention.statut_avancement || 'planifie'];
    // Mise à jour optimiste
    setInterventions(prev => prev.map(i =>
      i.id === interventionId ? { ...i, statut_avancement: newStatut } : i
    ));
    try {
      await api.patch(`/planning/interventions/${interventionId}/statut`, { statut_avancement: newStatut });
    } catch (err) {
      console.error('[handleCycleAvancement] Erreur:', err);
      // Annuler la mise à jour optimiste
      setInterventions(prev => prev.map(i =>
        i.id === interventionId ? { ...i, statut_avancement: intervention.statut_avancement } : i
      ));
    }
  };

  const calcEndTime = (heureDebut, duree) => {
    const [h, m] = heureDebut.split(':').map(Number);
    const endMins = h * 60 + m + duree * 60;
    const endH = Math.floor(endMins / 60);
    const endM = endMins % 60;
    return `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`;
  };

  // Les six jours ouvrés (lundi → samedi) de la semaine affichée.
  //
  // ⚠️ La version d'origine appelait `curr.setDate(first + i)` sur le MÊME objet
  // à chaque tour : `setDate` mutant la date, le mois glissait sous les pieds du
  // calcul dès que la semaine franchissait une fin de mois. La semaine du lundi
  // 31 août 2026 rendait ainsi 31/08, 01/09, 03/10, 03/11, 05/12 et 05/01/2027 —
  // une « semaine » de cinq mois, qui atteignait la grille de la vue Semaine, la
  // géométrie de la plage horaire et le calcul de charge. Une semaine sur quatre
  // environ est concernée. Chaque jour est désormais construit depuis la même
  // origine, le constructeur `Date` normalisant lui-même les débordements.
  //
  // Dimanche est volontairement rattaché à la semaine SUIVANTE (comportement
  // d'origine conservé) : l'atelier est fermé ce jour-là, c'est la semaine qui
  // s'ouvre que l'on veut voir.
  // ── Jours d'ouverture (configures par atelier) ───────────────────────────
  // Numerotation ISO en base (1 = lundi ... 7 = dimanche) ; `Date.getDay()`
  // rend 0 pour dimanche, d'ou la conversion `getDay() === 0 ? 7 : getDay()`.
  // Un jour est OUVERT des qu'AU MOINS UN atelier retenu l'ouvre : le panneau
  // et la grille montrent plusieurs ateliers a la fois, en fermer un ne doit
  // pas rendre la journee inaccessible aux autres. Quand un atelier precis est
  // selectionne, c'est le sien qui decide.
  const ateliersRetenus = useMemo(() => {
    const actifs = (ateliersList || []).filter(a => a.actif !== false);
    if (!selectedAtelierId) return actifs;
    const seul = actifs.filter(a => String(a.id) === String(selectedAtelierId));
    return seul.length > 0 ? seul : actifs;
  }, [ateliersList, selectedAtelierId]);
  const joursOuvertsSet = useMemo(() => {
    const set = new Set();
    for (const a of ateliersRetenus) {
      const jours = (a.jours_ouverture && a.jours_ouverture.length) ? a.jours_ouverture : [1, 2, 3, 4, 5, 6];
      for (const j of jours) set.add(Number(j));
    }
    // Aucun atelier configure (concession vierge) : on ne ferme rien, sans quoi
    // le planning deviendrait inutilisable avant meme d'avoir ete parametre.
    return set.size > 0 ? set : new Set([1, 2, 3, 4, 5, 6, 7]);
  }, [ateliersRetenus]);
  // Un jour est ouvert s'il fait partie des jours d'ouverture de l'atelier ET
  // qu'il n'est pas férié. Les deux notions vivaient séparément : la navigation
  // sautait le dimanche mais s'arrêtait volontiers un 1er mai.
  const estJourOuvert = useCallback(
    (date) => joursOuvertsSet.has(date.getDay() === 0 ? 7 : date.getDay())
              && !joursFermes.has(toYmd(date)),
    [joursOuvertsSet, joursFermes]
  );
  // Avance de `pas` jours en sautant les jours fermes. Bornee a 14 essais :
  // une concession fermee toute la semaine ferait sinon boucler l'interface.
  const prochainJourOuvert = useCallback((depuis, pas) => {
    const d = new Date(depuis);
    for (let i = 0; i < 14; i++) {
      d.setDate(d.getDate() + pas);
      if (estJourOuvert(d)) return d;
    }
    return null;
  }, [estJourOuvert]);

  const getWeekDays = (date) => {
    const decalage = date.getDay() === 0 ? 1 : 1 - date.getDay();
    const a = date.getFullYear(), m = date.getMonth(), j = date.getDate();
    return Array.from({ length: 6 }, (_, i) => new Date(a, m, j + decalage + i));
  };

  // Semaine CALENDAIRE de la date affichee : lundi -> samedi, toujours 6 entrees,
  // quels que soient les jours d'ouverture. C'est elle qui donne les bornes des
  // portees (import Mecaplanning, vidage) : les bornes d'une periode ne doivent
  // pas retrecir parce qu'un atelier ferme un jour.
  const semaineCalendaire = getWeekDays(selectedDate);
  // La vue Semaine, elle, ne montre que les jours ouverts : une colonne « Samedi »
  // vide sur un atelier ferme le samedi coute de la largeur a chaque autre jour.
  // ⚠️ Ce tableau est FILTRE — ne jamais y acceder par index fixe (weekDays[5]).
  const weekDays = semaineCalendaire.filter(estJourOuvert);
  // Vue Semaine : une colonne = un JOUR du mecanicien selectionne. Les heures
  // d'ouverture a hachurer sont donc celles de SON atelier.
  const mecanicienSemaine = (staffList || []).find(st => st.id === selectedMecanicien) || null;

  // ── Géométrie de la grille (plage horaire) dérivée de l'atelier sélectionné ──
  // En vue Atelier avec un atelier précis → sa plage d'ouverture. Sinon (Tous, ou vue
  // Réception concession-large) → union des plages des ateliers chargés. Défaut 07:00–17:00
  // (= grille historique). Tout est calculé en minutes depuis minuit, pas de « base 7h » codée.
  const _toMin = (t, def) => {
    if (!t) return def;
    const [h, m] = String(t).slice(0, 5).split(':').map(Number);
    return Number.isFinite(h) ? h * 60 + (Number.isFinite(m) ? m : 0) : def;
  };
  const gridAtelier = (view === 'atelier' && selectedAtelierId)
    ? ateliersList.find(a => String(a.id) === String(selectedAtelierId))
    : null;
  let _startMin = 7 * 60, _endMin = 17 * 60;
  if (gridAtelier) {
    _startMin = _toMin(gridAtelier.heure_ouverture, 7 * 60);
    _endMin = _toMin(gridAtelier.heure_fermeture, 17 * 60);
  } else if (ateliersList.length > 0) {
    _startMin = Math.min(...ateliersList.map(a => _toMin(a.heure_ouverture, 7 * 60)));
    _endMin = Math.max(...ateliersList.map(a => _toMin(a.heure_fermeture, 17 * 60)));
  }
  if (!(_endMin > _startMin)) { _startMin = 7 * 60; _endMin = 17 * 60; } // garde-fou

  // ── Une heure de marge de part et d'autre (demande 2026-08-12) ────────────
  // La grille montre une heure AVANT l'ouverture et une heure APRES la
  // fermeture : un client depose son vehicule en avance, un depassement mord
  // sur la fermeture, et ces creneaux doivent rester visibles — hachures a
  // l'appui, pour qu'on voie tout de suite qu'on sort des heures d'ouverture.
  const OUVERTURE_MIN = _startMin;   // heures d'ouverture « nues », avant marge
  const FERMETURE_MIN = _endMin;
  _startMin -= 60;
  _endMin += 60;

  // ── Élargissement défensif : la grille couvre TOUJOURS ce qui est planifié ──
  // Une carte est positionnée en `créneau × hauteur`. Un créneau situé avant
  // l'ouverture donne un `top` NÉGATIF : la carte sort du conteneur et devient
  // invisible alors que le dossier existe bien en base — sans le moindre signal.
  // Après la fermeture, elle sort par le bas. Ça se produit dès qu'on modifie les
  // horaires d'un atelier a posteriori, qu'on bascule de filtre atelier (un dossier
  // de l'atelier A vu depuis la plage de l'atelier B), ou qu'un import Mécaplanning
  // pose une heure hors plage. On étire donc la plage affichée jusqu'à englober
  // tout ce qui est planifié sur le ou les jours visibles : les horaires
  // d'ouverture restent la plage « normale », mais rien ne disparaît de l'écran.
  // Jours réellement à l'écran uniquement : un dossier isolé à 05:00 dans dix jours
  // ne doit pas étirer la grille d'aujourd'hui (les données chargées couvrent ±2 semaines).
  // `toYmd` (utils/planningTime) reconstruit la date en heure LOCALE — surtout pas
  // `toISOString()`, qui bascule d'un jour à l'ouest de Greenwich (UTC−4 en Guadeloupe).
  const _visibleDays = (view === 'atelier' && atelierView === 'semaine')
    ? weekDays.map(toYmd)
    : [toYmd(selectedDate)];
  {
    let planMin = null, planMax = null;
    const cover = (heure, dureeH) => {
      const m = _toMin(heure, null);
      if (m === null) return;
      const end = m + Math.round((parseFloat(dureeH) || 0) * 60);
      planMin = planMin === null ? m : Math.min(planMin, m);
      planMax = planMax === null ? end : Math.max(planMax, end);
    };
    if (view === 'reception') {
      // Les cartes de réception occupent un créneau de 15 min.
      (rdvReception || []).forEach(r => {
        if (_visibleDays.includes(String(r.date).slice(0, 10))) cover(r.heure, 0.25);
      });
    } else {
      (interventions || []).forEach(i => {
        if (_visibleDays.includes(String(i.date).slice(0, 10))) cover(i.heure_debut, i.duree);
      });
    }
    if (planMin !== null && planMin < _startMin) _startMin = Math.floor(planMin / 15) * 15;
    if (planMax !== null && planMax > _endMin) _endMin = Math.ceil(planMax / 15) * 15;
  }
  // Bornes dures : la grille reste dans la journée civile.
  _startMin = Math.max(0, _startMin);
  _endMin = Math.min(24 * 60, _endMin);

  const GRID_START_MIN = _startMin;
  const GRID_SLOTS = Math.round((_endMin - _startMin) / 15);

  // Plage d'ouverture PAR ATELIER : chaque colonne hachure ce qui precede
  // l'ouverture et ce qui suit la fermeture de SON atelier — deux ateliers aux
  // horaires differents ne peuvent pas partager la meme zone grisee. Sans
  // atelier rattache, on retombe sur la plage globale.
  const bornesParAtelier = useMemo(() => {
    const m = new Map();
    for (const a of (ateliersList || [])) {
      m.set(String(a.id), {
        debut: _toMin(a.heure_ouverture, OUVERTURE_MIN),
        fin: _toMin(a.heure_fermeture, FERMETURE_MIN),
      });
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ateliersList, OUVERTURE_MIN, FERMETURE_MIN]);

  // `idx` est l'index du creneau de 15 min depuis le haut de la grille.
  const horsOuverture = useCallback((idx, atelierId) => {
    const b = bornesParAtelier.get(String(atelierId)) || { debut: OUVERTURE_MIN, fin: FERMETURE_MIN };
    const minutes = GRID_START_MIN + idx * 15;
    return minutes < b.debut || minutes >= b.fin;
  }, [bornesParAtelier, GRID_START_MIN, OUVERTURE_MIN, FERMETURE_MIN]);
  // Conversions créneau ↔ heure, valables pour n'importe quelle plage.
  const slotToHeure = (slot) => {
    const min = GRID_START_MIN + slot * 15;
    return `${Math.floor(min / 60).toString().padStart(2, '0')}:${(min % 60).toString().padStart(2, '0')}`;
  };
  const heureToSlot = (heure) => {
    const [h, m] = String(heure).slice(0, 5).split(':').map(Number);
    return (h * 60 + m - GRID_START_MIN) / 15;
  };
  const heures = Array.from({ length: GRID_SLOTS }, (_, i) => slotToHeure(i));

  // Synchroniser le mécanicien sélectionné (vue semaine) quand la liste change
  useEffect(() => {
    if (mecaniciens.length > 0 && (!selectedMecanicien || !mecaniciens.find(m => m.id === selectedMecanicien))) {
      setSelectedMecanicien(mecaniciens[0].id);
    }
  }, [mecaniciens, selectedMecanicien]);

  // Pauses applicables à la VUE COURANTE. Une pause peut désormais viser un
  // atelier précis (`atelier_id`) ou toute la concession (`atelier_id` NULL).
  // - filtre atelier actif  → pauses de cet atelier + celles de la concession ;
  // - vue « Tous »          → uniquement les pauses de concession : une pause
  //   propre à un atelier ne concerne pas les mécaniciens des autres, la griser
  //   partout serait faux.
  // ⚠️ `GET /planning/pauses` ne filtre par concession QUE si une concession est
  // effective. Un super-admin en vue « toutes concessions » reçoit donc les pauses
  // de TOUTES les concessions — et celles sans atelier (`atelier_id` NULL)
  // s'appliqueraient alors à n'importe quel mécanicien. Vu en base : SOGUAVASOREC
  // porte un « Début de journée » 00:00→07:30 qui grisait le début de matinée des
  // mécaniciens de BLUE. On se limite donc à la concession affichée.
  const pausesConcession = useMemo(() => (pausesList || []).filter(
    p => !selectedConcessionId || String(p.concession_id) === String(selectedConcessionId)
  ), [pausesList, selectedConcessionId]);

  const pausesApplicables = useMemo(() => (pausesConcession || []).filter(p => {
    if (!p.atelier_id) return true;
    return selectedAtelierId && String(p.atelier_id) === String(selectedAtelierId);
  }), [pausesConcession, selectedAtelierId]);

  // Déterminer les créneaux de pause pour une date donnée
  // Retourne un Set d'index de créneaux (0-39) qui sont en pause
  const calcPauseSlots = useCallback((date, pauses) => {
    const slots = new Set();
    // JS: 0=Dimanche, 1=Lundi, ...6=Samedi
    const jsDay = date.getDay();
    const dayMap = { 1: 'lundi', 2: 'mardi', 3: 'mercredi', 4: 'jeudi', 5: 'vendredi', 6: 'samedi', 0: 'dimanche' };
    const dayKey = dayMap[jsDay];
    if (!dayKey || dayKey === 'dimanche') return slots;

    (pauses || []).forEach(p => {
      if (!p[dayKey]) return;
      // heure_debut / heure_fin sont au format "HH:MM:SS" depuis PostgreSQL
      const [dh, dm] = p.heure_debut.split(':').map(Number);
      const [fh, fm] = p.heure_fin.split(':').map(Number);
      const startMin = dh * 60 + dm;
      const endMin = fh * 60 + fm;
      // Convertir en index de slots (0 = ouverture de la grille, chaque slot = 15 min)
      for (let min = startMin; min < endMin; min += 15) {
        const idx = Math.floor((min - GRID_START_MIN) / 15);
        if (idx >= 0 && idx < GRID_SLOTS) slots.add(idx);
      }
    });
    return slots;
  }, [GRID_START_MIN, GRID_SLOTS]);

  // Pauses de la VUE (colonne des heures, calculs globaux) : en vue « Tous », une
  // pause propre à un atelier ne concerne pas tous les mécaniciens.
  const getPauseSlots = useCallback(
    (date) => calcPauseSlots(date, pausesApplicables),
    [calcPauseSlots, pausesApplicables]
  );

  // Pauses d'UN mécanicien : celles de son atelier, plus celles de la concession.
  // ⚠️ Indépendantes de la vue affichée, et c'est tout l'enjeu : la géométrie d'une
  // carte est un fait métier, pas une question de filtre. En vue « Tous », se
  // rabattre sur `pausesApplicables` revenait à ignorer TOUTE pause d'atelier —
  // une intervention de 2 h 30 démarrée à 07:00 se terminait alors à 09:30 au lieu
  // de 09:45, sans enjamber la pause de 09:00, alors que Mécaplanning, lui, la compte.
  // Absence couvrant ce collaborateur ce jour-là (bornes incluses), ou null.
  // Le modèle est à la JOURNÉE (date_debut/date_fin, pas d'heures) : une absence
  // neutralise donc la colonne entière.
  const absenceDe = useCallback((staffId, date) => {
    if (!staffId || !absencesList.length) return null;
    const jour = toYmd(date);
    return absencesList.find(a =>
      a.staff_id === staffId &&
      String(a.date_debut).slice(0, 10) <= jour &&
      jour <= String(a.date_fin).slice(0, 10)
    ) || null;
  }, [absencesList]);

  // Une absence de journée ferme la colonne ; une absence partielle n'en ferme
  // qu'une tranche. Confondre les deux figeait toute la journée d'un mécanicien
  // parti une heure chez le médecin — plus personne ne pouvait lui poser un RDV
  // le matin.
  const estJourneeEntiere = (a) => !!a && !a.heure_debut;
  // ⚠️ [10] La différence entre une ABSENCE et une INDISPONIBILITÉ n'est pas de
  // stockage — c'est la même table — mais de COMPORTEMENT : une absence bloque
  // le créneau, une indisponibilité accepte qu'on planifie par-dessus. C'est
  // même sa raison d'être : réserver un mécanicien pour les imprévus, quitte à
  // l'utiliser quand même. Tous les refus de planification passent donc par ce
  // filtre, jamais par `absenceDe` directement.
  const estIndispo = (a) => !!a && a.type === 'indisponibilite';
  const absenceBloquante = (staffId, date) => {
    const a = absenceDe(staffId, date);
    return estIndispo(a) ? null : a;
  };
  const libelleAbsence = (a) => (a && a.heure_debut)
    ? `${String(a.heure_debut).slice(0, 5)}–${String(a.heure_fin || '').slice(0, 5)}`
    : 'Absent';

  // Géométrie du voile d'absence, en pixels, selon la hauteur d'un créneau.
  // Absence de JOURNÉE (heures nulles) → toute la colonne. Absence PARTIELLE →
  // du créneau de début à celui de fin, comme une carte. Renvoie `null` si le
  // créneau tombe hors de la grille affichée (une visite à 18 h sur une grille
  // qui s'arrête à 17 h ne doit pas dessiner un voile à cheval sur le bord).
  // Volontairement NON memoisee. En `useCallback([])` elle gardait la fermeture
  // du premier rendu, donc le GRID_SLOTS du premier jour affiche : en passant a
  // un jour dont la grille est plus courte, le voile d'une absence de journee
  // gardait sa hauteur d'origine et debordait sous la grille.
  const geometrieVoile = (absence, hauteurCreneau) => {
    if (!absence) return null;
    // Journee entiere : on se cale sur les bords du conteneur (qui porte sa
    // hauteur en ligne) plutot que de recalculer une hauteur. C'est la seule
    // forme qui ne peut structurellement pas depasser la journee affichee.
    if (!absence.heure_debut) return { top: 0, bottom: 0 };
    const iDeb = getHeureIndex(String(absence.heure_debut).slice(0, 5));
    const iFin = getHeureIndex(String(absence.heure_fin).slice(0, 5));
    if (iDeb < 0 && iFin < 0) return null;
    // Bornage : une absence 06:00-19:00 sur une grille 07:00-17:00 doit se
    // couper aux bords, pas deborder des deux cotes.
    const borne = (v) => Math.max(0, Math.min(GRID_SLOTS, v));
    const d = borne(iDeb < 0 ? 0 : iDeb);
    const f = borne(iFin < 0 ? GRID_SLOTS : iFin);
    if (f <= d) return null;
    return { top: d * hauteurCreneau, height: (f - d) * hauteurCreneau };
  };

  // Un dossier « à planifier » correspond-il à la recherche ? Nom du client,
  // immatriculation (séparateurs ignorés) ou numéro d'OR.
  const filtrerAPlanifier = useCallback((liste, terme) => {
    const q = String(terme || '').trim().toLowerCase();
    if (!q) return liste;
    const nu = q.replace(/[-\s.]/g, '');
    return liste.filter(i => {
      const plaque = String(i.immatriculation || i.immat || '').toLowerCase().replace(/[-\s]/g, '');
      return [i.client_nom, i.client, i.numero_or, i.type_intervention, i.commentaire]
        .some(v => String(v || '').toLowerCase().includes(q)) || (nu && plaque.includes(nu));
    });
  }, []);

  const libelleMotifAbsence = (motif) =>
    (MOTIFS_ABSENCE.find(m => m.value === motif)?.label) || motif || 'Absent';

  const getPauseSlotsMecanicien = useCallback(
    (date, atelierId) => calcPauseSlots(date, (pausesConcession || []).filter(
      p => !p.atelier_id || String(p.atelier_id) === String(atelierId)
    )),
    [calcPauseSlots, pausesConcession]
  );

  const goToPreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  // ── Plein écran ──
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      planningRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Les fleches sautent les jours fermes : cliquer « jour suivant » un vendredi
  // soir doit mener a lundi, pas a un samedi vide qu'il faut re-franchir.
  const goToPreviousDay = () => {
    const d = prochainJourOuvert(selectedDate, -1);
    if (d) setSelectedDate(d);
  };

  const goToNextDay = () => {
    const d = prochainJourOuvert(selectedDate, 1);
    if (d) setSelectedDate(d);
  };

  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // Calcul de la plage de dates à charger (±2 semaines autour de la date sélectionnée)
  const getDateRange = useCallback(() => {
    const from = new Date(selectedDate);
    from.setDate(from.getDate() - 14);
    const to = new Date(selectedDate);
    to.setDate(to.getDate() + 14);
    return {
      date_from: toYmd(from),
      date_to: toYmd(to)
    };
  }, [selectedDate]);

  const loadData = useCallback(async () => {
    if (!initialLoadDone.current) setLoading(true);
    try {
      const { date_from, date_to } = getDateRange();
      // allSettled : une requête qui échoue ne bloque pas les 7 autres
      // (ex. /planning/pauses down → l'atelier reste utilisable)
      const results = await Promise.allSettled([
        api.get(`/planning/interventions?date_from=${date_from}&date_to=${date_to}`),
        api.get(`/planning/rdv?date_from=${date_from}&date_to=${date_to}`),
        api.get('/referentiels/statuts-atelier'),
        api.get('/referentiels/travaux-types'),
        api.get('/planning/staff'),
        api.get('/planning/ateliers'),
        api.get('/concessions'),
        api.get('/planning/pauses'),
        api.get('/planning/tags'),
      ]);
      const labels = ['interventions', 'rdv', 'statuts-atelier', 'travaux-types', 'staff', 'ateliers', 'concessions', 'pauses', 'tags'];
      const getData = (idx, fallback) => {
        const r = results[idx];
        if (r.status === 'fulfilled') return r.value.data;
        console.error(`[PlanningAtelier] ${labels[idx]} KO:`, r.reason);
        return fallback;
      };
      setInterventions(getData(0, { interventions: [] }).interventions || []);
      setRdvReception(getData(1, { rdv: [] }).rdv || []);
      setStatutsAtelier(getData(2, { statuts_atelier: [] }).statuts_atelier || []);
      setTravauxTypes(getData(3, { travaux_types: [] }).travaux_types || []);
      setStaffList(getData(4, []) || []);
      setAteliersList(getData(5, []) || []);
      const concData = getData(6, { concessions: [] });
      setConcessions(concData?.concessions || concData || []);
      setPausesList(getData(7, []) || []);
      // Seuls les tags ACTIFS : le référentiel complet (désactivés compris) ne
      // sert qu'à l'écran de Configuration.
      setTags((getData(8, { tags: [] }).tags || []).filter(t => t.actif));
      // Charger file d'attente
      try {
        // La file d'attente du JOUR AFFICHÉ uniquement : un enregistrement ne
        // s'affiche que le jour où il a été fait. L'ancienne plage ±14 jours
        // faisait apparaître les arrivées d'un autre jour sur tous les écrans —
        // que le rafraîchissement 5 s (borné, lui, à un seul jour) effaçait
        // ensuite : le nom clignotait puis disparaissait.
        const jourDemande = formatDate(selectedDate);
        if (!peut('voir.onglet_reception')) {
          setFileAttente([]);
        } else {
          const faRes = await api.get(`/planning/file-attente?date=${jourDemande}`);
          if (!jourAfficheRef.current || jourDemande === jourAfficheRef.current) setFileAttente(faRes.data || []);
        }
      } catch (e) { console.error('[planning/file-attente]', e); setFileAttente([]); }
      // Absences des collaborateurs : la grille doit les MONTRER (et refuser
      // qu'on planifie dessus). L'état existait mais n'était jamais alimenté —
      // les congés restaient invisibles côté planning (constat utilisateur).
      try {
        const absRes = await api.get(`/planning/absences?date_from=${date_from}&date_to=${date_to}`);
        setAbsencesList(Array.isArray(absRes.data) ? absRes.data : []);
      } catch (e) { console.error('[planning/absences]', e); setAbsencesList([]); }
      // Charger les véhicules à planifier (sans RDV / remorquage), tous tant qu'ils ne sont pas affectés
      try {
        if (!peut('voir.bloc_lateral')) { setAPlanifier([]); setNonHonores([]); }
        else {
          const apRes = await api.get('/planning/a-planifier');
          setAPlanifier(apRes.data?.a_planifier || []);
          const nhRes = await api.get('/planning/non-honores');
          setNonHonores(nhRes.data?.non_honores || []);
        }
      } catch (e) { console.error('[planning/a-planifier]', e); setAPlanifier([]); }
      // Charger les RDV en ligne « en attente » (liste pour le panneau latéral + compteur badge)
      try {
        if (!peut('reception.rdv_online')) {
          setRdvOnlineList([]); setRdvOnlineCount(0);
        } else {
          const rdvRes = await api.get('/planning/rdv-online?statut=en_attente');
          const list = rdvRes.data || [];
          setRdvOnlineList(list);
          setRdvOnlineCount(list.length);
        }
      } catch (e) {
        console.error('[planning/rdv-online en_attente]', e);
        setRdvOnlineList([]);
      }
    } catch (err) {
      console.error('[PlanningAtelier] Erreur chargement données:', err);
    } finally {
      initialLoadDone.current = true;
      setLoading(false);
    }
    // `selectedDate` : lue directement pour la file d'attente du jour affiché.
    // `capacites` : le chargeur ne demande plus ce que le rôle ne voit pas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDateRange, selectedDate, capacites]);

  useEffect(() => { jourAfficheRef.current = formatDate(selectedDate); }, [selectedDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Re-fetch quand la concession globale change (sélecteur menu utilisateur
  // super-admin). ⚠️ Au MONTAGE, cet effet tirait un second chargement complet
  // par-dessus celui de l'effet ci-dessus — 24 requêtes au lieu de 12, et
  // l'écran se remplissait deux fois. On ne réagit donc qu'aux changements
  // RÉELS de concession.
  const concessionChargee = useRef(user?.concession_id);
  useEffect(() => {
    if (concessionChargee.current === user?.concession_id) return;
    concessionChargee.current = user?.concession_id;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.concession_id]);


  // SSE : synchronisation temps réel entre les postes
  const sseReloadRef = useRef(null);
  // `loadData` est recréé à chaque changement de date ou de vue (useCallback sur
  // getDateRange). Le brancher en dépendance de l'effet SSE fermait et rouvrait
  // l'EventSource à CHAQUE navigation jour/semaine : reconnexion permanente côté
  // navigateur et va-et-vient dans la Map des clients côté serveur. On passe donc
  // par une ref — la connexion est établie une seule fois au montage et appelle
  // toujours la dernière version de loadData.
  const loadDataRef = useRef(loadData);
  useEffect(() => { loadDataRef.current = loadData; }, [loadData]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const evtSource = new EventSource(`/api/planning/events?token=${token}`);
    const scheduleReload = () => {
      clearTimeout(sseReloadRef.current);
      sseReloadRef.current = setTimeout(() => loadDataRef.current?.(), 300);
    };
    evtSource.addEventListener('planning-updated', () => {
      if (skipNextSSE.current > 0) { skipNextSSE.current--; return; }
      scheduleReload();
    });
    // Nouveau RDV en ligne reçu → rafraîchir (met à jour le badge « RDV à confirmer »)
    evtSource.addEventListener('rdv-online-new', scheduleReload);
    // Une synchro pièces vient d'émettre des nouveautés → recharger le compteur.
    evtSource.addEventListener('pieces-alertes', () => setAlertesPiecesTick(t => t + 1));
    return () => {
      evtSource.close();
      clearTimeout(sseReloadRef.current);
    };
  }, []);

  // Ouverture en attente d'un dossier venu de la recherche : dès que la journée
  // visée est chargée, on ouvre avec l'objet de la grille — celui qui porte la
  // réception liée.
  useEffect(() => {
    const attente = ouvertureEnAttente.current;
    if (!attente) return;
    const trouve = interventions.find(i => i.id === attente.id);
    if (!trouve) return;
    clearTimeout(attente.repli);
    ouvertureEnAttente.current = null;
    handleEditIntervention(trouve);
    setHighlightedInterventionId(trouve.id);
    // La carte se signale au retour dans la grille, une fois la modale refermée.
    setTimeout(() => {
      const el = document.querySelector(`[data-intervention-id="${trouve.id}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 200);
  }, [interventions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compteur des nouveautés Pièces — chargé même quand la vue est fermée :
  // c'est tout l'intérêt du badge, signaler qu'il y a quelque chose à regarder.
  useEffect(() => {
    let annule = false;
    api.get('/pieces-alertes/count')
      .then(r => { if (!annule) setAlertesPiecesCount(r.data?.count || 0); })
      .catch(() => { if (!annule) setAlertesPiecesCount(0); });
    return () => { annule = true; };
  }, [alertesPiecesTick, selectedConcessionId]);

  // Charge du mois affiché dans le mini-calendrier. Rechargée quand le mois
  // change, quand on bascule de concession, et à chaque rechargement des
  // interventions — c'est ce dernier point qui la remet à jour APRÈS un import
  // Mécaplanning, sans lequel le calendrier resterait figé sur l'avant-import.
  useEffect(() => {
    let annule = false;
    if (!peut('voir.onglet_calendrier')) { setChargeMois(null); return; }
    const mois = `${chargeMonth.getFullYear()}-${String(chargeMonth.getMonth() + 1).padStart(2, '0')}`;
    api.get('/planning/charge-mensuelle', { params: { mois } })
      .then(r => { if (!annule) setChargeMois(r.data || null); })
      .catch(() => { if (!annule) setChargeMois(null); });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeMonth, selectedConcessionId, interventions, capacites]);

  // Taux de charge du jour par atelier (onglets d'atelier) : calcul de
  // Remplissage atelier, servi par GET /planning/charge. Rechargé quand le jour
  // ou la concession change et à chaque rechargement des interventions (carte
  // posée, déplacée, supprimée), avec un léger délai pour ne pas appeler le
  // serveur à chaque glisser.
  const [chargeAteliers, setChargeAteliers] = useState(null);
  useEffect(() => {
    let annule = false;
    const date = formatDate(selectedDate);
    const t = setTimeout(() => {
      api.get('/planning/charge', { params: { date } })
        .then(r => { if (!annule) setChargeAteliers(r.data || null); })
        .catch(() => { if (!annule) setChargeAteliers(null); });
    }, 400);
    return () => { annule = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedConcessionId, interventions]);

  // Compteur du Suivi atelier — même raison, et même parti pris que le composant :
  // « Facturé » n'est pas compté, un dossier facturé n'appelle plus de geste.
  useEffect(() => {
    let annule = false;
    if (!peut('voir.tableaux')) { setSuiviAtelierCount(0); return; }
    api.get('/planning/suivi-atelier', { params: { count: 1 } })
      .then(r => {
        if (annule) return;
        const s = r.data?.statuts || [];
        setSuiviAtelierCount(s.filter(x => x.nom !== 'Facturé').reduce((n, x) => n + (x.count || 0), 0));
      })
      .catch(() => { if (!annule) setSuiviAtelierCount(0); });
    return () => { annule = true; };
  }, [selectedConcessionId, interventions]);

  // Ctrl/⌘ + K : le raccourci de recherche que tout le monde connaît déjà.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setRechercheOuverte(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Tick toutes les 30s pour mettre à jour les compteurs d'attente
  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const minutesSince = (timestamp) => {
    if (!timestamp) return null;
    const diff = nowTick - new Date(timestamp).getTime();
    return Math.max(0, Math.floor(diff / 60000));
  };

  // Rafraîchir la file d'attente
  // ─── Prise en charge d'un client de la file ──────────────────────────────
  // Le SMS et le passage « en cours » vont ensemble : c'est le même geste métier
  // (« je vous prends maintenant »). On demande confirmation parce qu'un SMS part
  // chez un vrai client, et on montre le numéro ET le texte avant d'envoyer.
  const ouvrirRecevoir = async (entree) => {
    try {
      const { data } = await api.get(`/planning/file-attente/${entree.id}/sms`);
      setRecevoirModal({ entree, telephone: data.telephone, message: data.message, envoi: null });
    } catch (err) {
      // Sans message pré-calculé on n'empêche pas de recevoir le client : le SMS
      // est un plus, la prise en charge est l'essentiel.
      setRecevoirModal({ entree, telephone: entree.telephone || null, message: '', envoi: null });
    }
  };

  // `statutCible` : 'en_cours' pour une réception qui commence, 'termine' pour
  // « Déjà reçu ». Le SMS n'a de sens que dans le premier cas — prévenir un
  // client déjà au comptoir n'aurait aucun sens.
  const confirmerRecevoir = async (avecSms, statutCible = 'en_cours') => {
    if (!recevoirModal || recevoirBusy) return;
    const { entree, message } = recevoirModal;
    setRecevoirBusy(true);
    try {
      // Le changement de statut d'abord : si l'envoi échoue, le client est
      // quand même pris en charge — l'inverse laisserait la file mentir.
      await api.put(`/planning/file-attente/${entree.id}`, { statut: statutCible });
      if (avecSms && message.trim()) {
        try {
          await api.post(`/planning/file-attente/${entree.id}/sms`, { message: message.trim() });
        } catch (err) {
          alert('Client pris en charge, mais SMS non envoyé : ' + (err.response?.data?.error || 'erreur inconnue'));
        }
      }
      setRecevoirModal(null);
      await refreshFileAttente();
    } finally {
      setRecevoirBusy(false);
    }
  };

  // Toujours le JOUR AFFICHÉ — la même date que loadData, sinon les deux
  // récupérateurs se contredisent et les entrées clignotent.
  const refreshFileAttente = async () => {
    // Le rafraîchissement périodique doit suivre la même règle que le chargement
    // initial : sans la capacité, il tirerait un 403 toutes les 5 secondes.
    if (!peut('voir.onglet_reception')) return;
    const jour = formatDate(selectedDate);
    try {
      const r = await api.get(`/planning/file-attente?date=${jour}`);
      // Réponse périmée (l'utilisateur a changé de jour entre-temps) : on la jette.
      if (jourAfficheRef.current && jour !== jourAfficheRef.current) return;
      setFileAttente(r.data || []);
    } catch (e) { console.error('[planning/file-attente refresh]', e); }
  };

  // Rafraîchir la liste des véhicules à planifier
  const refreshAPlanifier = async () => {
    if (!peut('voir.bloc_lateral')) return;
    try {
      const r = await api.get('/planning/a-planifier');
      setAPlanifier(r.data?.a_planifier || []);
    } catch (e) { console.error('[planning/a-planifier refresh]', e); }
  };

  const refreshNonHonores = async () => {
    if (!peut('voir.bloc_lateral')) return;
    try {
      const r = await api.get('/planning/non-honores');
      setNonHonores(r.data?.non_honores || []);
    } catch (e) { console.error('[non-honores refresh]', e); }
  };

  // Polling file d'attente toutes les 5s en vue Atelier / Réception
  // (Atelier inclus pour que le badge « Client arrivé » sur les cartes d'intervention se rafraîchisse)
  useEffect(() => {
    if (!['atelier', 'reception'].includes(view)) return;
    refreshFileAttente();
    const interval = setInterval(refreshFileAttente, 5000);
    return () => clearInterval(interval);
    // `selectedDate` dans les dépendances : sans elle, l'intervalle garderait la
    // fermeture du PREMIER jour affiché et rechargerait toujours cette date-là.
  }, [view, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetFormData = () => {
    setFormData({
      client_nom: '',
      client_prenom: '',
      client_tel: '',
      client_email: '',
      immatriculation: '',
      vin: '',
      // Réceptionnaire laissé vide volontairement à la création (doit être un choix explicite)
      poste_reception: null,
      heure_reception: '08:00',
      date_reception: formatDate(selectedDate),
      mecanicien: mecaniciens[0] ? getStaffLabel(mecaniciens[0]) : '',
      mecanicien_id: mecaniciens[0]?.id || null,
      heure_debut: '09:00',
      duree: 2,
      type_intervention: '',   // vide par défaut (22/10), comme la modale
      commentaire: '',
      date_intervention: formatDate(selectedDate),
      pieces: [],
      statut_pieces: '',
      statut_avancement: 'planifie',
      numero_or: '',
      marque: '',
      modele: '',
      // Aucun statut à la création : le dossier reste « — Non défini — » (carte
      // blanche) tant que personne ne l'a pris en charge à l'atelier.
      statut_atelier_id: null,
      travaux: []
    });
  };


  // ─── Menu contextuel (clic droit) ────────────────────────────────────────
  const annulerCommutation = () => {
    if (ctxCommutTimer.current) { clearTimeout(ctxCommutTimer.current); ctxCommutTimer.current = null; }
    ctxAttente.current = null;
  };

  // L'action a abouti : au lieu de fermer, le menu bascule sur la colonne
  // « Et ensuite ? ». La colonne 2 se referme pour que l'œil suive.
  const apresAction = (action, label) => {
    annulerCommutation();
    setCtxCol2(null);
    setCtxFiltre('');
    setCtxCommentaire('');
    setCtxBusy(null);
    setCtxSuites({ action, label, fait: {} });
  };

  const closeContextMenu = () => {
    annulerCommutation();
    setContextMenu(null);
    setCtxCol2(null);
    setCtxSuites(null);
    setCtxFiltre('');
    setCtxCommentaire('');
    setCtxBusy(null);
    setCtxParking(null);
    setCtxMessages(null);
  };

  const handleContextMenu = (e, intervention) => {
    e.preventDefault();
    // En consultation, aucun menu : tout ce qu'il propose écrit quelque chose.
    // Exception : le TECHNICIEN travaille précisément par ce menu (statut,
    // commentaire, position parking) — c'est son seul outil d'écriture.
    if (lectureSeule && !peutMenuContextuel) return;
    e.stopPropagation();
    annulerCommutation();

    // Géométrie calculée UNE FOIS ici, dans l'hypothèse pire cas (trois
    // colonnes ouvertes). Rien ne la recalcule ensuite : c'est ce qui garantit
    // que la colonne 1 ne bouge pas d'un pixel quand les suivantes s'ouvrent.
    const M = 8;
    const vw = window.innerWidth, vh = window.innerHeight;
    const largeurMax = CTX_LARGEURS.col1 + CTX_LARGEURS.col2 + CTX_LARGEURS.col3 + 2 * 6;
    const etroit = vw < largeurMax + 2 * M;
    // Sens d'ouverture : un panneau qui se retournerait en cours de route
    // serait déroutant, on tranche maintenant et on n'y revient plus.
    const dir = (!etroit && e.clientX + largeurMax + M > vw) ? 'left' : 'right';
    // Ancrage par le côté d'ouverture : à gauche c'est `right` qui est fixé,
    // sinon il faudrait recalculer `left` à chaque colonne — le bug à éviter.
    const top = Math.max(M, Math.min(e.clientY, vh - M - 320));
    const geo = { top, maxH: vh - top - M, etroit };
    if (etroit) geo.left = M;
    else if (dir === 'right') geo.left = Math.max(M, Math.min(e.clientX, vw - M - largeurMax));
    else geo.right = Math.max(M, Math.min(vw - e.clientX, vw - M - largeurMax));

    setContextMenu({ x: e.clientX, y: e.clientY, dir, intervention, ...geo });
    setCtxCol2(null);
    setCtxSuites(null);
    setCtxFiltre('');
    setCtxCommentaire('');
    setCtxBusy(null);
    setCtxPlanifForm({
      date: intervention.date_intervention || '',
      heure: intervention.heure_debut?.substring(0, 5) || '09:00',
      duree: intervention.duree || 2,
      mecanicien_id: intervention.mecanicien_id || ''
    });

    // Préchargement : le survol doit avoir quelque chose à afficher tout de
    // suite. Les deux appels partent en parallèle, sans bloquer l'affichage.
    const parentId = intervention.is_complement ? intervention.parent_intervention_id : intervention.id;
    setCtxParking(null);
    setCtxMessages(null);
    api.get(`/planning/interventions/${parentId}/parking`)
      .then(r => setCtxParking(r.data))
      .catch(() => setCtxParking({ zones: [], position: null }));
    api.get(`/planning/interventions/${parentId}/messages`)
      .then(r => setCtxMessages(r.data.messages || []))
      .catch(() => setCtxMessages([]));
  };

  // On garde une petite trace du pointeur plutôt que le seul dernier événement :
  // à 120 Hz, deux points consécutifs sont distants de 1 ou 2 px et la direction
  // qu'on en tire n'est que du bruit. L'origine du geste est le point le plus
  // récent qui ait au moins CTX_AGE_ORIGINE ms.
  const suivrePointeur = (e) => {
    const t = ctxTrace.current;
    t.push({ x: e.clientX, y: e.clientY, t: performance.now() });
    if (t.length > 8) t.shift();
  };

  // Le pointeur vise-t-il la colonne déjà ouverte ? On prolonge son geste jusqu'au
  // bord de cette colonne : si le point d'arrivée tombe dans sa hauteur, la
  // trajectoire est intentionnelle et on ne commute pas. C'est ce qui permet de
  // descendre EN DIAGONALE vers un statut du bas sans que la colonne bascule en
  // traversant les entrées voisines de la colonne 1.
  const viseColonneOuverte = () => {
    const el = ctxCol2Ref.current;
    const trace = ctxTrace.current;
    const p = trace[trace.length - 1];
    if (!el || !contextMenu || !p) return false;
    const maintenant = performance.now();
    if (maintenant - p.t > CTX_POINTEUR_FRAIS) return false; // pointeur à l'arrêt = intention d'ouvrir ici

    let origine = trace[0];
    for (let i = trace.length - 2; i >= 0; i--) {
      if (maintenant - trace[i].t >= CTX_AGE_ORIGINE) { origine = trace[i]; break; }
    }
    const dx = p.x - origine.x;
    const dy = p.y - origine.y;
    if (Math.hypot(dx, dy) < 4) return false;              // sur place : pas une trajectoire

    const sens = contextMenu.dir === 'left' ? -1 : 1;
    if (sens * dx <= 0) return false;                      // ne va pas vers la colonne
    const r = el.getBoundingClientRect();
    const bordX = sens === 1 ? r.left : r.right;
    const pas = (bordX - p.x) / dx;
    if (pas <= 0) return false;                            // le bord est déjà derrière
    const yProjete = p.y + dy * pas;
    const marge = 32; // tolérance : viser le bord d'une ligne reste viser la colonne
    return yProjete >= r.top - marge && yProjete <= r.bottom + marge;
  };

  // Survol d'une action de la colonne 1. Ouvrir est immédiat ; CHANGER de
  // colonne est retenu le temps de laisser passer une visée en diagonale.
  const survolerAction = (cle) => {
    if (cle === ctxCol2) { annulerCommutation(); return; }
    if (!ctxCol2) { annulerCommutation(); setCtxCol2(cle); setCtxFiltre(''); return; }

    if (ctxAttente.current?.cle === cle) return; // commutation déjà armée
    annulerCommutation();
    ctxAttente.current = { cle, depuis: performance.now() };

    const tenter = () => {
      const attente = ctxAttente.current;
      if (!attente) return;
      const tropLongtemps = performance.now() - attente.depuis > CTX_VISEE_MAX;
      if (!tropLongtemps && viseColonneOuverte()) {
        // Toujours en route vers la colonne ouverte : on redonne du sursis.
        ctxCommutTimer.current = setTimeout(tenter, CTX_SURSIS_VISEE);
        return;
      }
      ctxAttente.current = null;
      ctxCommutTimer.current = null;
      setCtxCol2(cle);
      setCtxFiltre('');
    };
    ctxCommutTimer.current = setTimeout(tenter, CTX_DELAI_COMMUT);
  };

  // Le clic fait la même chose que le survol : c'est le repli tactile, où il
  // n'y a pas de survol du tout. À la souris il reste cohérent (et referme la
  // colonne si on reclique sur l'action déjà ouverte).
  const choisirAction = (a) => {
    annulerCommutation();
    if (!contextMenu) return;
    const intv = contextMenu.intervention;
    if (a.cle === 'aplanifier') { handleCtxAPlanifier(); return; }   // action directe
    if (a.cle === 'prepicking') { handleCtxPrepicking(); return; }   // action directe
    if (a.cle === 'supprimer') { closeContextMenu(); setDeleteModal({ intervention: intv }); return; }
    // Les deux modes partagent le formulaire mais pas le point de départ :
    // déplacer part de là où l'intervention est, ajouter part du lendemain
    // ouvré — le cas de très loin le plus courant pour une reprise.
    if (a.cle === 'planif' && ctxCol2 !== 'planif') {
      setCtxMotif('');
      setCtxPlanifForm({
        date: intv.date_intervention || '',
        heure: intv.heure_debut?.substring(0, 5) || '09:00',
        duree: intv.duree || 2,
        mecanicien_id: intv.mecanicien_id || '',
      });
    }
    if (a.cle === 'ajouter' && ctxCol2 !== 'ajouter') {
      setCtxMotif('');
      // Même point de départ que « Reporter au lendemain » : prochain jour
      // ouvert, à l'ouverture de l'atelier.
      const suite = lendemainOuvertDe(intv);
      setCtxPlanifForm({
        date: toYmd(suite.jour),
        heure: suite.heure,
        duree: intv.duree || 2,
        mecanicien_id: intv.mecanicien_id || '',
      });
    }
    setCtxCol2(c => (c === a.cle ? null : a.cle));
    setCtxFiltre('');
  };

  // Le menu ne se ferme plus après une action : il faut donc un test de sortie
  // fiable. `pointerdown` + `contains` plutôt qu'un `click` global neutralisé
  // par des stopPropagation partout — le panneau contient désormais des champs
  // de saisie, où un clic ne doit évidemment pas tout refermer. Même patron que
  // InlineStatusSelect.js.
  useEffect(() => {
    if (!contextMenu) return;
    const surSortie = (e) => { if (!ctxPanelRef.current?.contains(e.target)) closeContextMenu(); };
    const surEchap = (e) => { if (e.key === 'Escape') closeContextMenu(); };
    // Le panneau est en position fixe sur des coordonnées figées : si la grille
    // défile dessous, il pointerait une carte qui n'est plus là.
    const surDefilement = (e) => { if (!ctxPanelRef.current?.contains(e.target)) closeContextMenu(); };
    document.addEventListener('pointerdown', surSortie);
    document.addEventListener('keydown', surEchap);
    window.addEventListener('scroll', surDefilement, true); // capture : attrape aussi les zones défilantes internes
    window.addEventListener('resize', closeContextMenu);
    return () => {
      document.removeEventListener('pointerdown', surSortie);
      document.removeEventListener('keydown', surEchap);
      window.removeEventListener('scroll', surDefilement, true);
      window.removeEventListener('resize', closeContextMenu);
    };
  }, [contextMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  // Le menu garde une PHOTO du dossier prise au clic droit. Tant qu'il se
  // fermait après chaque action, c'était sans conséquence ; maintenant qu'il
  // reste ouvert, la coche du statut courant et le libellé du parking
  // resteraient sur l'ancienne valeur. On resynchronise sur la liste vivante.
  useEffect(() => {
    setContextMenu(cm => {
      if (!cm) return cm;
      const frais = interventions.find(i => i.id === cm.intervention.id);
      return frais && frais !== cm.intervention ? { ...cm, intervention: frais } : cm;
    });
  }, [interventions]);

  // Filtre au clavier de la colonne 2 : on tape, la liste se réduit. Pas de
  // champ de saisie à cliquer — il faudrait le focaliser, ce qui casserait le
  // flux du survol. AUTODISCOUNT a 28 parkings : les faire défiler est pénible.
  useEffect(() => {
    const filtrables = ['statuts', 'parking', 'contact', 'tags'];
    const colonne = CTX_ACTIONS.find(a => a.cle === ctxCol2)?.colonne;
    if (!contextMenu || !filtrables.includes(colonne)) return;
    const surTouche = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Une frappe destinée à un champ du panneau (commentaire, date…) n'est
      // évidemment pas un filtre.
      if (e.target?.matches?.('input, textarea, select')) return;
      if (e.key === 'Backspace') { setCtxFiltre(f => f.slice(0, -1)); e.preventDefault(); return; }
      if (e.key.length === 1 && e.key !== ' ') setCtxFiltre(f => (f + e.key).slice(0, 24));
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [contextMenu, ctxCol2]);

  // Nouveau statut sur TOUTES les cartes d'un dossier : la carte principale ET
  // chaque jour complémentaire (`comp-…`, qui porte l'id du dossier dans
  // `parent_intervention_id`). Ne recolorer que la carte cliquée laissait les
  // autres jours à l'ancienne couleur jusqu'au rechargement.
  const recolorerDossier = (dossierId, statutId) => {
    const statut = statutsAtelier.find(s => s.id === statutId);
    setInterventions(prev => prev.map(i => {
      const duDossier = i.is_complement ? i.parent_intervention_id === dossierId : i.id === dossierId;
      if (!duDossier) return i;
      return {
        ...i,
        statut_atelier_id: statutId || null,
        statut_atelier_couleur: statut?.couleur || null,
        statut_atelier_text_color: statut?.text_color || null,
      };
    }));
  };

  const handleCtxStatutAtelier = async (statutId) => {
    if (!contextMenu || ctxBusy) return;
    const intv = contextMenu.intervention;
    const apiId = intv.is_complement ? intv.parent_intervention_id : intv.id;
    const statut = statutsAtelier.find(s => s.id === statutId);
    setCtxBusy(`statut-${statutId || 'none'}`);
    try {
      await api.patch(`/planning/interventions/${apiId}/statut-atelier`, { statut_atelier_id: statutId || null });
      recolorerDossier(apiId, statutId);
    } catch (err) {
      console.error('[ctxStatutAtelier]', err);
      setCtxBusy(null);
      alert('Erreur lors du changement de statut.');
      return;
    }
    apresAction('statut', statut ? `Statut : ${statut.nom}` : 'Statut effacé');
    // Dossier facturé = véhicule prêt : proposer d'en informer le client.
    if (estStatutFacture(statutId)) proposerSmsVehiculePret(apiId);
    // Un statut posé sur un dossier sans N° d'OR : le réclamer tant que le
    // geste est frais. Seulement à la POSE d'un statut — l'effacer n'appelle
    // pas de numéro.
    if (statutId && !intv.numero_or) {
      setDemandeOr({ ...intv, id: apiId });
    }
  };

  // Identifiant du DOSSIER porté par une carte. Une carte complémentaire a un id
  // synthétique (`comp-12`) et renvoie à son parent : c'est lui qui porte les
  // tags, le statut et tout le reste.
  const idDossier = (i) => (i.is_complement ? i.parent_intervention_id : i.id);

  // Bascule d'un tag. À la différence du statut, cette action NE FERME PAS la
  // colonne (pas d'`apresAction`) : les tags sont cumulables, et il faut pouvoir
  // en poser trois d'affilée sans rouvrir le menu à chaque fois.
  //
  // Fluidité (2026-08-10) : plus de verrou `ctxBusy` — il JETAIT en silence les
  // clics émis pendant le PATCH précédent (poser 3 tags exigeait d'attendre
  // chaque aller-retour). L'endpoint est idempotent et l'optimiste fait foi ;
  // chaque tag vit son propre appel. Et chaque PATCH s'auto-exclut du SSE
  // (`skipNextSSE`) : sans ça, l'auteur recevait son propre broadcast et
  // `loadData()` re-rendait toute la grille ~300 ms après chaque coche — le
  // « ça disparaît puis réapparaît ».
  const handleCtxToggleTag = async (tag) => {
    if (!contextMenu) return;
    const apiId = idDossier(contextMenu.intervention);
    const dejaPose = (contextMenu.intervention.tags || []).some(t => t.id === tag.id);
    const pose = !dejaPose;

    // Mise à jour optimiste de TOUTES les cartes du même dossier — la carte
    // complémentaire affiche les tags de son parent, elle doit suivre.
    const appliquer = (poser) => setInterventions(prev => prev.map(i => {
      if (idDossier(i) !== apiId) return i;
      const actuels = i.tags || [];
      return {
        ...i,
        tags: poser
          ? (actuels.some(t => t.id === tag.id) ? actuels : [...actuels, { id: tag.id, nom: tag.nom, couleur: tag.couleur }])
          : actuels.filter(t => t.id !== tag.id),
      };
    }));

    appliquer(pose);
    skipNextSSE.current++;
    try {
      await api.patch(`/planning/interventions/${apiId}/tags/${tag.id}`, { pose });
    } catch (err) {
      console.error('[ctxToggleTag]', err);
      appliquer(!pose);   // retour arrière : l'écran ne doit pas montrer un tag qui n'existe pas en base
    }
  };

  // ── Filtre par tag ────────────────────────────────────────────────────────
  // Plusieurs tags cochés = OU : on cherche « tout ce qui attend des pièces OU
  // qu'il faut rappeler », jamais l'intersection des deux.
  const carteRetenue = (i) => tagsFiltre.size === 0 || (i.tags || []).some(t => tagsFiltre.has(t.id));

  const basculerFiltreTag = (id) => setTagsFiltre(prev => {
    const suivant = new Set(prev);
    if (suivant.has(id)) suivant.delete(id); else suivant.add(id);
    return suivant;
  });


  // Déclarer un RDV non honoré depuis le menu contextuel : motif obligatoire,
  // commentaire optionnel — le dossier quitte la grille et rejoint la file de
  // rappel (Tableaux ▸ RDV non honorés + section du panneau).
  const declarerNonHonore = async () => {
    if (!contextMenu || ctxBusy || !ctxNonHonore.motif) return;
    const intv = contextMenu.intervention;
    const apiId = idDossier(intv);
    setCtxBusy('non_honore');
    try {
      await api.patch(`/planning/interventions/${apiId}/non-honore`, {
        motif: ctxNonHonore.motif,
        commentaire: ctxNonHonore.commentaire.trim() || undefined,
      });
      skipNextSSE.current++;
      await Promise.all([loadData(), refreshNonHonores()]);
    } catch (err) {
      console.error('[ctxNonHonore]', err);
      setCtxBusy(null);
      alert(err.response?.data?.error || 'Déclaration impossible.');
      return;
    }
    setCtxNonHonore({ motif: null, commentaire: '' });
    apresAction('non_honore', 'RDV non honoré — le dossier a quitté la grille');
  };

  const handleCtxPlanifSubmit = async () => {
    if (!contextMenu) return;
    const intv = contextMenu.intervention;
    const meca = getMecaById(Number(ctxPlanifForm.mecanicien_id));
    const payload = {
      date_intervention: ctxPlanifForm.date,
      heure_debut: ctxPlanifForm.heure,
      duree: Number(ctxPlanifForm.duree),
      mecanicien_id: Number(ctxPlanifForm.mecanicien_id),
      mecanicien: meca ? getStaffLabel(meca) : undefined
    };
    try {
      if (intv.is_complement && intv.complement_id) {
        await api.put(`/planning/interventions/complements/${intv.complement_id}`, payload);
      } else {
        await api.patch(`/planning/interventions/${intv.id}/move`, payload);
      }
      // Le motif est facultatif, et sa perte ne doit pas faire échouer un
      // déplacement déjà écrit : on le pose après coup, sans bloquer.
      if (ctxMotif.trim()) {
        const cibleId = intv.is_complement ? intv.parent_intervention_id : intv.id;
        api.post(`/planning/interventions/${cibleId}/commentaires`, {
          texte: `Déplacé au ${formatDateFr(ctxPlanifForm.date)} ${ctxPlanifForm.heure} — ${ctxMotif.trim()}`,
        }).catch(e => console.error('[ctxPlanifSubmit motif]', e));
      }
      skipNextSSE.current++;
      await loadData();
    } catch (err) {
      console.error('[ctxPlanifSubmit]', err);
      setCtxBusy(null);
      alert('Erreur lors de la modification.');
      return;
    }
    apresAction('planif', `Déplacée au ${formatDateFr(ctxPlanifForm.date)} à ${ctxPlanifForm.heure}`);
  };

  // Horaires de l'atelier d'un mécanicien (ouverture, fermeture, jours). Sans
  // atelier connu : l'ouverture la plus tôt des ateliers de la concession.
  const horairesDuMeca = (mecaId) => {
    const st = mecaId ? staffList.find(s => s.id === mecaId) : null;
    const at = st?.atelier_id ? ateliersList.find(a => a.id === st.atelier_id) : null;
    return horairesAtelier(at, ateliersList);
  };

  // Nom de l'atelier d'un dossier, déduit de son mécanicien (un dossier ne porte
  // pas d'atelier propre). Null si inconnu : dossier sans mécanicien, ou client
  // enregistré à l'accueil sans dossier rattaché.
  const atelierNomDe = (intv) => {
    if (!intv || !(intv.mecanicien_id > 0)) return null;
    const st = staffList.find(s => s.id === intv.mecanicien_id);
    return (st?.atelier_id && ateliersList.find(a => a.id === st.atelier_id)?.nom) || null;
  };

  // Bouton « Répartir » de la modale : découpe la durée d'un dossier en
  // journées sur le même mécanicien (utils/repartitionPlanning). Jours sautés :
  // fermeture de l'atelier, fériés, absence d'une JOURNÉE ENTIÈRE du mécanicien
  // (une indisponibilité ou une absence de quelques heures ne ferme pas le
  // jour). Pauses de l'atelier et de la concession déduites de chaque journée.
  const calculerRepartition = async ({ date, heure, duree, mecanicienId, concessionId }) => {
    const horaires = horairesDuMeca(mecanicienId);
    // Les absences chargées par l'écran ne couvrent que la période affichée :
    // on relit la fenêtre utile (90 jours couvrent largement 60 journées).
    let absences = [];
    try {
      const fin = toYmd(dateLocale(date) ? new Date(dateLocale(date).getTime() + 90 * 864e5) : new Date());
      const { data } = await api.get(`/planning/absences?staff_id=${mecanicienId}&date_from=${toYmd(date)}&date_to=${fin}`);
      absences = (Array.isArray(data) ? data : (data?.absences || []))
        .filter(a => a.staff_id === mecanicienId && !a.heure_debut && a.type !== 'indisponibilite');
    } catch (e) { console.error('[repartition/absences]', e); }
    const absent = (ymd) => absences.some(a =>
      String(a.date_debut).slice(0, 10) <= ymd && ymd <= String(a.date_fin).slice(0, 10));
    const jourKey = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const pauses = (pausesList || []).filter(p => p.actif !== false
      && (!concessionId || String(p.concession_id) === String(concessionId))
      && (!p.atelier_id || String(p.atelier_id) === String(horaires.atelierId)));
    const pausesDuJour = (ymd, deMin, aMin) => {
      const cle = jourKey[dateLocale(ymd).getDay()];
      return pauses.filter(p => p[cle]).reduce((m, p) => {
        const a = hhmmEnMin(p.heure_debut), b = hhmmEnMin(p.heure_fin);
        return m + Math.max(0, Math.min(aMin, b) - Math.max(deMin, a));
      }, 0);
    };
    const res = repartirDuree({
      date, heureDebut: heure, duree, horaires,
      estFerme: (ymd) => joursFermes.has(ymd) || absent(ymd),
      pausesDuJour,
    });
    return { ...res, horaires };
  };

  // Le prochain jour ouvert de l'atelier du mécanicien, fériés sautés.
  const lendemainOuvertDe = (intv) => {
    const h = horairesDuMeca(intv.mecanicien_id > 0 ? intv.mecanicien_id : null);
    const jour = prochainJourOuvertAtelier(intv.date_intervention || intv.date,
      { jours: h.jours, estFerme: (ymd) => joursFermes.has(ymd) })
      || jourSuivantOuvre(intv.date_intervention || intv.date);
    return { jour, heure: h.ouverture };
  };

  // Report au lendemain : crée une planification complémentaire de la durée
  // choisie, le prochain jour ouvert, sur le même mécanicien, DÈS L'OUVERTURE
  // de son atelier (décision du 30/09 : le plus tôt possible, quitte à
  // chevaucher un dossier déjà posé — reprendre l'heure du jour d'origine
  // repoussait la suite des travaux sans raison). (La logique vivait en
  // ligne dans le JSX, à côté de deux handlers nommés qui n'étaient plus
  // appelés nulle part : ils sont supprimés, celui-ci est le seul.)
  const handleCtxLendemain = async (duree) => {
    if (!contextMenu || ctxBusy) return;
    const intv = contextMenu.intervention;
    const parentId = intv.is_complement ? intv.parent_intervention_id : intv.id;
    // Dates en heure LOCALE (helpers partagés) : `new Date('2026-08-10')` vaut
    // minuit UTC, soit la veille aux Antilles — le report retombait sur le
    // jour même.
    const { jour: demain, heure } = lendemainOuvertDe(intv);
    setCtxBusy(`lendemain-${duree}`);
    try {
      await api.post(`/planning/interventions/${parentId}/complements`, {
        date_intervention: toYmd(demain),
        heure_debut: heure,
        duree,
        mecanicien: intv.mecanicien,
        mecanicien_id: intv.mecanicien_id > 0 ? intv.mecanicien_id : null,
        commentaire: `Report au lendemain (${formatDuree(duree)})`,
      });
      skipNextSSE.current++;
      await loadData();
    } catch (err) {
      console.error('[ctxLendemain]', err);
      setCtxBusy(null);
      alert('Erreur lors du report.');
      return;
    }
    apresAction('lendemain', `Reporté au ${formatDateFr(toYmd(demain))} à ${heure} · ${formatDuree(duree)}`);
  };

  // ── Modale de report : fonctions ──────────────────────────────────────────

  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const computeFreeSlots = (mecaId, dateStr, intvs) => {
    // Tableau de GRID_SLOTS créneaux de 15 min (plage d'ouverture), marquer les occupés
    const slots = new Array(GRID_SLOTS).fill(false);
    const dayIntvs = intvs.filter(i => (i.mecanicien_id === mecaId) && (i.date_intervention || i.date) === dateStr);
    for (const intv of dayIntvs) {
      const [h, m] = (intv.heure_debut || '09:00').split(':').map(Number);
      const startSlot = Math.floor((h * 60 + m - GRID_START_MIN) / 15);
      const dureeSlots = Math.ceil((parseFloat(intv.duree) || 1) * 4);
      for (let s = startSlot; s < Math.min(startSlot + dureeSlots, GRID_SLOTS); s++) {
        if (s >= 0) slots[s] = true;
      }
    }
    // Marquer les pauses
    const dayOfWeek = new Date(dateStr).getDay(); // 0=dim, 1=lun...
    const jourKeys = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    for (const pause of pausesApplicables) {
      if (!pause[jourKeys[dayOfWeek]]) continue;
      const [ph, pm] = (pause.heure_debut || '12:00').split(':').map(Number);
      const [fh, fm] = (pause.heure_fin || '13:00').split(':').map(Number);
      const pStart = Math.floor((ph * 60 + pm - GRID_START_MIN) / 15);
      const pEnd = Math.floor((fh * 60 + fm - GRID_START_MIN) / 15);
      for (let s = pStart; s < Math.min(pEnd, GRID_SLOTS); s++) {
        if (s >= 0) slots[s] = true;
      }
    }
    // Extraire les plages libres
    const freeSlots = [];
    let i = 0;
    while (i < GRID_SLOTS) {
      if (!slots[i]) {
        const start = i;
        while (i < GRID_SLOTS && !slots[i]) i++;
        const durationSlots = i - start;
        freeSlots.push({
          heure: slotToHeure(start),
          duree: durationSlots * 0.25,
        });
      } else {
        i++;
      }
    }
    return freeSlots;
  };

  // ── Disponibilité du mécanicien visé ────────────────────────────────────
  // Répond à « est-ce qu'il y a la place ? » par une phrase, là où l'ancienne
  // modale affichait un total d'heures et laissait faire la conversion.
  // `exclureId` : en mode déplacement, l'intervention ne doit pas se compter
  // elle-même comme un obstacle si elle reste sur sa propre journée.
  const analyserDispo = (mecaId, dateStr, heure, duree, intvs, exclureId) => {
    const autres = intvs.filter(i => String(i.id) !== String(exclureId));
    const libres = computeFreeSlots(mecaId, dateStr, autres);
    const enMinutes = (h) => { const [a, b] = h.split(':').map(Number); return a * 60 + b; };
    const debut = enMinutes(heure);
    const fin = debut + Math.round(duree * 60);
    // `computeFreeSlots` ne connaît que les interventions et les pauses : ses
    // plages courent sur TOUTE la grille (05:30–19:30 chez BLUE). Annoncer
    // « libre de 05:30 à 19:30 » serait faux — on borne aux horaires de
    // l'atelier du mécanicien visé.
    const meca = getMecaById(mecaId);
    const bornes = bornesParAtelier.get(String(meca?.atelier_id)) || { debut: OUVERTURE_MIN, fin: FERMETURE_MIN };
    const contenant = libres.find(l => {
      const ld = enMinutes(l.heure);
      return debut >= ld && fin <= ld + Math.round(l.duree * 60);
    });
    const fmtH = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    if (debut < bornes.debut || fin > bornes.fin) {
      const dedans = contenant ? '' : ' Et le créneau est déjà pris.';
      return { etat: 'conflit', texte: `Hors des heures d'ouverture (${fmtH(bornes.debut)}–${fmtH(bornes.fin)}).${dedans}` };
    }
    if (contenant) {
      const ld = Math.max(enMinutes(contenant.heure), bornes.debut);
      const lf = Math.min(enMinutes(contenant.heure) + Math.round(contenant.duree * 60), bornes.fin);
      return { etat: 'ok', texte: `Libre de ${fmtH(ld)} à ${fmtH(lf)} — les ${formatDuree(duree)} tiennent.` };
    }
    // Ce qui bloque : la première intervention qui chevauche la plage demandée.
    const gene = autres
      .filter(i => i.mecanicien_id === mecaId && (i.date_intervention || i.date) === dateStr)
      .map(i => {
        const d = enMinutes((i.heure_debut || '09:00').substring(0, 5));
        return { ...i, d, f: d + Math.round((parseFloat(i.duree) || 1) * 60) };
      })
      .filter(i => i.d < fin && i.f > debut)
      .sort((a, b) => a.d - b.d)[0];
    // La sortie proposée doit elle aussi tenir dans les heures d'ouverture.
    const suivant = libres.find(l => {
      const ld = Math.max(enMinutes(l.heure), bornes.debut);
      return ld >= debut && ld + Math.round(duree * 60) <= Math.min(enMinutes(l.heure) + Math.round(l.duree * 60), bornes.fin);
    });
    const sortie = suivant
      ? ` Libre à partir de ${fmtH(Math.max(enMinutes(suivant.heure), bornes.debut))}.`
      : ' Plus de place ce jour-là.';
    if (!gene) return { etat: 'conflit', texte: `Pas de place suffisante à cette heure.${sortie}` };
    const quoi = gene.type_intervention || gene.description || gene.client_nom || 'une autre intervention';
    return { etat: 'conflit', texte: `Chevauche ${quoi} ${fmtH(gene.d)}–${fmtH(gene.f)}.${sortie}` };
  };

  // Recalcul à chaque changement du formulaire. La journée visée sort souvent
  // de la plage déjà chargée : on la demande au serveur, en annulant la réponse
  // d'une saisie précédente (course fréquente quand on tape une date).
  const dispoJetonRef = useRef(0);
  useEffect(() => {
    if (!contextMenu || !['planif', 'ajouter'].includes(ctxCol2)) { setCtxDispo(null); return; }
    const { date, heure, duree, mecanicien_id } = ctxPlanifForm;
    if (!date || !heure || !duree || !mecanicien_id) { setCtxDispo(null); return; }
    const jeton = ++dispoJetonRef.current;
    setCtxDispo({ etat: 'load', texte: 'Vérification…' });
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/planning/interventions?date_from=${date}&date_to=${date}`);
        if (jeton !== dispoJetonRef.current) return;
        const intvs = r.data.interventions || [];
        // En ajout, l'intervention d'origine reste : elle compte comme obstacle.
        const exclure = ctxCol2 === 'planif' ? contextMenu.intervention.id : null;
        setCtxDispo(analyserDispo(Number(mecanicien_id), date, heure, Number(duree), intvs, exclure));
      } catch (e) {
        if (jeton !== dispoJetonRef.current) return;
        setCtxDispo({ etat: 'erreur', texte: 'Disponibilité indisponible (réseau).' });
      }
    }, 280);
    return () => clearTimeout(t);
  }, [contextMenu, ctxCol2, ctxPlanifForm.date, ctxPlanifForm.heure, ctxPlanifForm.duree, ctxPlanifForm.mecanicien_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // « Ajouter une intervention » : même formulaire que le déplacement, mais on
  // écrit un COMPLÉMENT — l'intervention d'origine ne bouge pas.
  const handleCtxAjoutSubmit = async () => {
    if (!contextMenu || ctxBusy) return;
    const intv = contextMenu.intervention;
    const parentId = intv.is_complement ? intv.parent_intervention_id : intv.id;
    const meca = getMecaById(Number(ctxPlanifForm.mecanicien_id));
    setCtxBusy('ajouter');
    try {
      await api.post(`/planning/interventions/${parentId}/complements`, {
        date_intervention: ctxPlanifForm.date,
        heure_debut: ctxPlanifForm.heure,
        duree: Number(ctxPlanifForm.duree),
        mecanicien: meca ? getStaffLabel(meca) : '',
        mecanicien_id: Number(ctxPlanifForm.mecanicien_id) > 0 ? Number(ctxPlanifForm.mecanicien_id) : null,
        commentaire: ctxMotif || null,
      });
      skipNextSSE.current++;
      await loadData();
    } catch (err) {
      console.error('[ctxAjoutSubmit]', err);
      setCtxBusy(null);
      alert("Erreur lors de l'ajout de l'intervention.");
      return;
    }
    apresAction('ajouter', `Intervention ajoutée le ${formatDateFr(ctxPlanifForm.date)} à ${ctxPlanifForm.heure}`);
  };

  // « Mettre à planifier » : le dossier quitte la grille et rejoint le tiroir
  // 🪝 À planifier du panneau latéral, d'où il se glisse ensuite sur un créneau.
  // Le créneau d'origine est libéré — c'est le sens du geste.
  const handleCtxAPlanifier = async () => {
    if (!contextMenu || ctxBusy) return;
    const intv = contextMenu.intervention;
    if (intv.is_complement) { alert("Une intervention complémentaire ne peut pas être mise à planifier : supprimez-la ou déplacez-la."); return; }
    setCtxBusy('aplanifier');
    try {
      await api.patch(`/planning/interventions/${intv.id}/move`, { a_planifier: true });
      skipNextSSE.current++;
      await Promise.all([loadData(), refreshAPlanifier()]);
    } catch (err) {
      console.error('[ctxAPlanifier]', err);
      setCtxBusy(null);
      alert(err.response?.data?.error || 'Mise à planifier impossible.');
      return;
    }
    // Le tiroir « À planifier » vit dans l'onglet Calendrier du panneau latéral :
    // sans cette bascule, la carte disparaît de la grille et rien ne montre où
    // elle est partie.
    setSideTab('calendrier');
    apresAction('aplanifier', 'Dossier mis à planifier — il est dans le tiroir 🪝');
  };

  // Prépicking : bascule. Un second clic annule — c'est une déclaration, pas un
  // engagement, et une erreur de ligne doit se corriger aussi vite qu'elle se
  // commet.
  // ⚠️ Bascule à la manière des TAGS, et surtout PAS d'`apresAction` : le menu
  // reste ouvert et c'est le libellé lui-même qui change (« Prépicking » →
  // « ✓ Pièces préparées »). Signalé le 2026-09-05 : avec une fermeture après
  // action, on cliquait sans rien voir se passer et on doutait que le geste ait
  // été pris. Ici l'état se lit AVANT le clic, et le changement se voit après.
  // Mise à jour optimiste, comme les tags — l'aller-retour ne doit pas retarder
  // le retour visuel.
  const handleCtxPrepicking = async () => {
    if (!contextMenu) return;
    const apiId = idDossier(contextMenu.intervention);
    const actuel = (interventions.find(i => idDossier(i) === apiId) || {}).prepicking_at;
    const pose = !actuel;
    const appliquer = (valeur) => setInterventions(prev => prev.map(i => (
      idDossier(i) === apiId ? { ...i, prepicking_at: valeur } : i
    )));
    appliquer(pose ? new Date().toISOString() : null);
    skipNextSSE.current++;
    try {
      await api.patch(`/planning/interventions/${apiId}/prepicking`, { pose });
    } catch (err) {
      console.error('[ctxPrepicking]', err);
      appliquer(actuel || null);   // retour arrière : l'écran ne doit pas mentir
    }
  };

  // Déclaration de la position. Appelable depuis la colonne 2 (l'action a été
  // choisie pour elle-même) comme depuis la colonne 3 (elle est proposée à la
  // suite d'une autre action) : dans le second cas, on reste sur la colonne des
  // suites et on y coche simplement ce qui vient d'être fait.
  const declarerParking = async (intervention, zone, { depuisSuites = false } = {}) => {
    if (ctxBusy) return;
    const parentId = intervention.is_complement ? intervention.parent_intervention_id : intervention.id;
    setCtxBusy(`parking-${zone}`);
    try {
      const r = await api.patch(`/planning/interventions/${parentId}/parking`, { zone });
      setCtxParking(p => ({ ...(p || {}), position: r.data.position }));
      skipNextSSE.current++;
      await loadData();
    } catch (err) {
      setCtxBusy(null);
      alert(err.response?.data?.error || 'Déclaration impossible');
      return;
    }
    if (depuisSuites) {
      setCtxBusy(null);
      setCtxSuites(s => (s ? { ...s, fait: { ...s.fait, parking: zone } } : s));
    } else {
      apresAction('parking', `Placé en ${zone}`);
    }
  };

  // Commentaire interne, posté sur le fil du dossier — le même que celui de la
  // modale complète, mais sans avoir à l'ouvrir.
  const enregistrerCommentaire = async (intervention, texte, { depuisSuites = false } = {}) => {
    const contenu = (texte || '').trim();
    if (!contenu || ctxBusy) return;
    const parentId = intervention.is_complement ? intervention.parent_intervention_id : intervention.id;
    setCtxBusy('commentaire');
    try {
      await api.post(`/planning/interventions/${parentId}/commentaires`, { texte: contenu });
    } catch (err) {
      setCtxBusy(null);
      alert(err.response?.data?.error || 'Enregistrement impossible');
      return;
    }
    setCtxCommentaire('');
    setCtxBusy(null);
    if (depuisSuites) {
      setCtxSuites(s => (s ? { ...s, fait: { ...s.fait, commentaire: contenu } } : s));
    } else {
      apresAction('commentaire', 'Commentaire enregistré');
    }
  };

  // Ouvre la modale d'envoi SMS, pré-remplie par le modèle choisi (ou vide).
  const ouvrirSms = (intervention, modele) => {
    const intv = intervention;
    closeContextMenu();
    setSmsModal({ intervention: intv, texte: modele ? modele.contenu : '', envoi: null });
  };

  const envoyerSms = async () => {
    if (!smsModal || !smsModal.texte.trim() || smsModal.envoi === 'encours') return;
    const intv = smsModal.intervention;
    const parentId = intv.is_complement ? intv.parent_intervention_id : intv.id;
    setSmsModal(m => ({ ...m, envoi: 'encours', erreur: null }));
    try {
      await api.post(`/planning/interventions/${parentId}/sms`, { message: smsModal.texte.trim() });
      setSmsModal(m => ({ ...m, envoi: 'ok' }));
      setTimeout(() => setSmsModal(null), 1600);
    } catch (err) {
      setSmsModal(m => ({ ...m, envoi: 'erreur', erreur: err.response?.data?.error || 'Envoi impossible' }));
    }
  };

  // Suppression effective — n'est appelée que depuis la modale de confirmation,
  // jamais directement depuis le menu contextuel.
  const handleDeleteConfirmed = async () => {
    const intv = deleteModal?.intervention;
    if (!intv || deleteBusy) return;
    setDeleteBusy(true);
    try {
      if (intv.is_complement && intv.complement_id) {
        await api.delete(`/planning/interventions/complements/${intv.complement_id}`);
      } else {
        // Le serveur supprime lui-meme le RDV de reception lie : inutile (et
        // fragile) de l'appeler d'abord, on ne connait pas toujours son id.
        await api.delete(`/planning/interventions/${intv.id}`);
      }
      skipNextSSE.current++;
      setDeleteModal(null);
      await loadData();
    } catch (err) {
      console.error('[ctxDelete]', err);
      alert('Erreur lors de la suppression.');
    } finally {
      setDeleteBusy(false);
    }
  };

  // Drag-to-resize durée (haut = ajuste heure_debut, bas = ajuste durée)
  const resizeRef = useRef(null);

  const handleResizeStart = (e, intervention, edge) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      interventionId: intervention.id,
      originalDuree: intervention.duree,
      originalHeure: intervention.heure_debut,
      currentDuree: intervention.duree,
      currentHeure: intervention.heure_debut,
      isComplement: intervention.is_complement,
      complementId: intervention.complement_id,
      startY: e.clientY,
      edge, // 'top' | 'bottom'
    };
    const handleResizeMove = (moveEvent) => {
      if (!resizeRef.current) return;
      const ref = resizeRef.current;
      const deltaY = moveEvent.clientY - ref.startY;
      const deltaSlots = Math.round(deltaY / 20);
      if (ref.edge === 'bottom') {
        const newDuree = Math.max(0.25, ref.originalDuree + deltaSlots * 0.25);
        ref.currentDuree = newDuree;
        ref.currentHeure = ref.originalHeure;
        setInterventions(prev => prev.map(i =>
          i.id === ref.interventionId ? { ...i, duree: newDuree } : i
        ));
      } else {
        // top : déplacer heure_debut, la fin reste fixe
        const origStart = getHeureIndex(ref.originalHeure);
        const newStart = Math.max(0, Math.min(GRID_SLOTS - 1, origStart + deltaSlots));
        const origEnd = origStart + Math.ceil(ref.originalDuree * 4);
        const newDuree = Math.max(0.25, (origEnd - newStart) * 0.25);
        const newHeure = slotToHeure(newStart);
        ref.currentDuree = newDuree;
        ref.currentHeure = newHeure;
        setInterventions(prev => prev.map(i =>
          i.id === ref.interventionId ? { ...i, heure_debut: newHeure, duree: newDuree } : i
        ));
      }
    };
    const handleResizeEnd = async () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      if (!resizeRef.current) return;
      const ref = resizeRef.current;
      resizeRef.current = null;
      const finalDuree = ref.currentDuree ?? ref.originalDuree;
      const finalHeure = ref.currentHeure ?? ref.originalHeure;
      const changed = finalDuree !== ref.originalDuree || finalHeure !== ref.originalHeure;
      if (!changed) return;
      // Trouver l'intervention pour le nom client
      const intv = interventions.find(i => i.id === ref.interventionId);
      const formatDuree = (d) => { const h = Math.floor(d); const m = Math.round((d - h) * 60); return m ? `${h}h${String(m).padStart(2,'0')}` : `${h}h`; };
      // Le resize est un geste, pas une décision : application directe + « Annuler »
      // (la modale ne réapparaît que si la nouvelle durée crée un chevauchement).
      requestMove({
        type: 'resize',
        item: {
          id: ref.interventionId,
          client: intv ? `${intv.client_nom || ''} ${intv.client_prenom || ''}`.trim() : '',
          immat: intv?.immatriculation || '',
          type: intv?.type_intervention || '',
          is_complement: ref.isComplement,
          complement_id: ref.complementId,
        },
        from: {
          heure: ref.originalHeure,
          duree: ref.originalDuree,
          dureeLabel: formatDuree(ref.originalDuree),
        },
        to: {
          heure: finalHeure,
          duree: finalDuree,
          dureeLabel: formatDuree(finalDuree),
        },
      }, `${intv?.immatriculation || 'Intervention'} → ${formatDuree(finalDuree)}`);
    };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const getHeureIndex = (heure) => heureToSlot(heure);

  // Filet de sécurité au rendu. L'élargissement défensif de la grille (voir plus
  // haut) doit rendre ce clamp inopérant ; il ne sert qu'au cas résiduel — une
  // carte dont la date ne correspond à aucun jour visible, par exemple. Objectif :
  // une carte mal placée reste préférable à une carte invisible, car elle affiche
  // sa vraie heure de début et l'utilisateur peut la rouvrir pour la corriger.
  const clampSlot = (slot) => Math.max(0, Math.min(GRID_SLOTS - 1, Number.isFinite(slot) ? slot : 0));

  // Calcule le slot de fin réelle d'une intervention en sautant les pauses.
  // ⚠️ La borne est `GRID_SLOTS`, PAS une constante : la refonte du 2026-07-28 a
  // rendu la grille adaptative (plage d'ouverture par atelier) mais ce plafond
  // était resté à 40, l'ancienne base 07:00–17:00. Dès qu'un atelier ouvrait plus
  // large — en vue « Tous », la grille prend l'UNION des plages, ex. 06:00–20:00
  // = 56 créneaux — toute intervention démarrant au-delà du 40ᵉ créneau voyait sa
  // durée écrasée et s'affichait en carte de 15 min.
  const calcRealEndSlot = (heureDebut, duree, pSlots) => {
    const startSlot = getHeureIndex(heureDebut);
    const workSlotsNeeded = Math.ceil(duree * 4);
    let currentSlot = startSlot;
    let workDone = 0;
    while (workDone < workSlotsNeeded && currentSlot < GRID_SLOTS) {
      if (pSlots.has(currentSlot)) {
        currentSlot++;
      } else {
        workDone++;
        currentSlot++;
      }
    }
    return currentSlot;
  };

  const formatClientShort = (prenom, nom) => {
    if (!nom) return prenom || '';
    if (!prenom) return nom;
    const initials = prenom.split(/[\s-]+/).map(p => p.charAt(0).toUpperCase()).join('');
    return `${initials}.${nom}`;
  };

  const formatDuree = (duree) => {
    const d = parseFloat(duree) || 0;
    if (d < 1) return `${Math.round(d * 60)}min`;
    const h = Math.floor(d);
    const m = Math.round((d - h) * 60);
    if (m === 0) return `${h}h`;
    return `${h}h${m}`;
  };


  const getInterventionForReception = (receptionId) => {
    const reception = rdvReception.find(r => r.id === receptionId);
    if (!reception) return null;
    return interventions.find(i => i.id === reception.intervention_id);
  };

  // Mémoïsé : filter O(n) sur potentiellement 300+ interventions, appelé une fois par mécanicien
  // dans .map() sans useMemo = 30× O(n) par render. Ici : 1× O(n) par render.
  const todayInterventions = useMemo(() => {
    const today = formatDate(selectedDate);
    return interventions.filter(i => i.date === today);
  }, [interventions, selectedDate]);
  const getTodayInterventions = useCallback(() => todayInterventions, [todayInterventions]);

  // ─── Détection des conflits (tient compte des pauses) ─────────────
  // Mémoïsé : getPauseSlots fait un parsing string HH:MM à chaque appel, 3-4× par render
  const pauseSlots = useMemo(() => getPauseSlots(selectedDate), [getPauseSlots, selectedDate]);

  const conflicts = useMemo(() => {
    const today = formatDate(selectedDate);
    const pSlots = getPauseSlots(selectedDate);
    const result = {};
    mecaniciens.forEach(staff => {
      const sorted = interventions
        .filter(i => i.mecanicien_id === staff.id && i.date === today)
        .sort((a, b) => (a.heure_debut || '').localeCompare(b.heure_debut || ''));
      const mecaConflicts = [];
      for (let j = 0; j < sorted.length - 1; j++) {
        // Fin réelle = après les pauses
        const realEndSlot = calcRealEndSlot(sorted[j].heure_debut, sorted[j].duree, pSlots);
        const nextStartSlot = getHeureIndex(sorted[j + 1].heure_debut);
        if (realEndSlot > nextStartSlot) {
          mecaConflicts.push({ a: sorted[j], b: sorted[j + 1] });
        }
      }
      if (mecaConflicts.length > 0) result[staff.id] = mecaConflicts;
    });
    return result;
  }, [interventions, selectedDate, mecaniciens, pausesList]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Conflit à la validation d'un déplacement / resize ────────────
  // Renvoie les interventions du même mécanicien+jour que le déplacement chevaucherait.
  // Avertissement non bloquant : même logique d'intervalles (pauses incluses) que `conflicts`.
  const getMoveConflicts = (pm) => {
    if (!pm || (pm.type !== 'intervention' && pm.type !== 'resize' && pm.type !== 'a_planifier')) return [];
    const base = interventions.find(i => i.id === pm.item.id) || pm.item;
    const targetMeca = pm.to.mecanicien_id ?? base.mecanicien_id;
    const targetDate = pm.to.date ?? base.date;
    const targetHeure = pm.to.heure ?? base.heure_debut;
    const targetDuree = pm.to.duree ?? base.duree;
    if (!targetMeca || !targetDate || !targetHeure || !targetDuree) return [];
    // Identité du dossier déplacé (intervention + ses compléments) pour ne pas se compter soi-même
    const baseDossier = base.is_complement ? base.parent_intervention_id : base.id;
    const pSlots = getPauseSlots(new Date(targetDate + 'T00:00:00'));
    const startSlot = getHeureIndex(targetHeure);
    const endSlot = calcRealEndSlot(targetHeure, targetDuree, pSlots);
    return interventions.filter(other => {
      if (other.id === pm.item.id) return false;
      const otherDossier = other.is_complement ? other.parent_intervention_id : other.id;
      if (otherDossier === baseDossier) return false;
      if (other.mecanicien_id !== targetMeca || other.date !== targetDate) return false;
      if (!other.heure_debut) return false;
      const oStart = getHeureIndex(other.heure_debut);
      const oEnd = calcRealEndSlot(other.heure_debut, other.duree, pSlots);
      return startSlot < oEnd && oStart < endSlot;
    });
  };

  // Layout d'overlap : les compléments en conflit sont affichés en overlay à droite,
  // l'intervention existante est rétrécie à gauche pour éviter le chevauchement visuel
  // Disposition en sous-colonnes (lanes) des interventions qui se chevauchent dans le temps
  // pour un même mécanicien et un même jour → rendu « côte à côte » façon agenda (comme
  // Mécaplanning), au lieu d'empiler les cartes pleine largeur. Algorithme : on isole les
  // clusters de chevauchement (composantes connexes par recouvrement temporel), puis on
  // affecte à chaque carte la 1re lane libre (first-fit) ; toutes les cartes d'un cluster
  // partagent le même nombre total de lanes (largeur identique). Couvre aussi les compléments
  // (qui chevauchent leur parent). Renvoie { [id]: { lane, lanes } } pour les cartes en chevauchement.
  const overlapLayout = useMemo(() => {
    const layout = {};
    const groups = {};
    interventions.forEach(i => {
      if (i.mecanicien_id == null) return;
      const key = `${i.mecanicien_id}_${i.date}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(i);
    });
    Object.values(groups).forEach(list => {
      if (list.length < 2) return;
      // dateLocale et non new Date(...) : la date est PURE, et getPauseSlots en tire
      // un getDay(). Lu en UTC, le lundi devient dimanche et toutes les pauses sont
      // ignorees -> geometrie des cartes fausse un jour sur sept.
      const pSlots = getPauseSlots(dateLocale(list[0].date));
      const items = list.map(i => ({
        id: i.id,
        start: getHeureIndex(i.heure_debut),
        end: calcRealEndSlot(i.heure_debut, i.duree, pSlots),
      })).sort((a, b) => a.start - b.start || a.end - b.end);

      let cluster = [];
      let clusterEnd = -Infinity;
      const flushCluster = () => {
        if (cluster.length > 1) {
          const laneEnds = []; // fin (slot) de la dernière carte de chaque lane
          cluster.forEach(it => {
            let lane = laneEnds.findIndex(end => end <= it.start);
            if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.end); }
            else laneEnds[lane] = it.end;
            it.lane = lane;
          });
          const lanes = laneEnds.length;
          if (lanes > 1) cluster.forEach(it => { layout[it.id] = { lane: it.lane, lanes }; });
        }
        cluster = [];
        clusterEnd = -Infinity;
      };
      items.forEach(it => {
        if (cluster.length && it.start >= clusterEnd) flushCluster(); // plus de chevauchement → cluster suivant
        cluster.push(it);
        clusterEnd = Math.max(clusterEnd, it.end);
      });
      flushCluster();
    });
    return layout;
  }, [interventions, pausesList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calcule les créneaux libres d'un mécanicien pour le jour sélectionné
  const computeCreneauxLibres = (staffId) => {
    const today = toYmd(selectedDate);
    const mecaInts = interventions
      .filter(i => i.mecanicien_id === staffId && i.date === today)
      .sort((a, b) => (a.heure_debut || '').localeCompare(b.heure_debut || ''));

    const DEBUT = '07:00';
    const FIN = '17:00';
    let cursor = DEBUT;
    const creneaux = [];

    mecaInts.forEach(i => {
      if (cursor < i.heure_debut) {
        const [ch, cm] = cursor.split(':').map(Number);
        const [ih, im] = (i.heure_debut || '00:00').split(':').map(Number);
        const dureeH = ((ih * 60 + im) - (ch * 60 + cm)) / 60;
        if (dureeH >= 0.5) {
          creneaux.push({ debut: cursor, fin: i.heure_debut, duree_h: Math.round(dureeH * 10) / 10 });
        }
      }
      const [h, m] = (i.heure_debut || '00:00').split(':').map(Number);
      const endMins = h * 60 + m + i.duree * 60;
      const fin = `${Math.floor(endMins / 60).toString().padStart(2, '0')}:${(endMins % 60).toString().padStart(2, '0')}`;
      if (fin > cursor) cursor = fin;
    });

    if (cursor < FIN) {
      const [ch, cm] = cursor.split(':').map(Number);
      const [fh, fm] = FIN.split(':').map(Number);
      const dureeH = ((fh * 60 + fm) - (ch * 60 + cm)) / 60;
      if (dureeH >= 0.5) {
        creneaux.push({ debut: cursor, fin: FIN, duree_h: Math.round(dureeH * 10) / 10 });
      }
    }
    return creneaux;
  };

  // ─── IA-3 : Suggestions de réaffectation ─────────────────────────────────────
  const handleSuggestReassignment = async (staffId, conflictIntervention) => {
    setSuggestionsLoading(true);
    setSuggestions(null);
    try {
      const mecaniciens_libres = mecaniciens
        .filter(m => m.id !== staffId)
        .map(m => ({ nom: getStaffLabel(m), id: m.id, creneaux_libres: computeCreneauxLibres(m.id) }))
        .filter(m => m.creneaux_libres.length > 0);
      const { data } = await api.post('/ai/planning/suggest-reassignment', {
        intervention: conflictIntervention,
        mecaniciens_libres
      });
      setSuggestions({ staffId, items: data.suggestions || [], intervention: conflictIntervention });
    } catch (err) {
      console.error('[handleSuggestReassignment] erreur:', err);
      setSuggestions({ staffId, items: [], intervention: conflictIntervention });
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const applySuggestion = (suggestion) => {
    const intervention = suggestions?.intervention;
    if (!intervention) return;
    setPendingMove({
      type: 'intervention',
      item: intervention,
      from: { heure: intervention.heure_debut, mecanicien: intervention.mecanicien, mecanicien_id: intervention.mecanicien_id },
      to: { heure: suggestion.heure, mecanicien: suggestion.mecanicien, mecanicien_id: suggestion.id || suggestion.mecanicien_id }
    });
    setConflictPopover(null);
    setSuggestions(null);
  };


  const computeSlotFromY = (mouseY, containerTop, slotH = 20) => {
    const slotIndex = Math.floor((mouseY - containerTop) / slotH);
    const clamped = Math.max(0, Math.min(GRID_SLOTS - 1, slotIndex));
    return slotToHeure(clamped);
  };

  // Normalise une heure 'HH:MM[:SS]' en 'HH:MM' (comparaisons lexicographiques sûres, zéros conservés).
  const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

  // Affiche un message transitoire quand un déplacement est refusé (contrainte réception ≤ intervention).
  const showMoveWarning = (msg) => {
    setMoveWarning(msg);
    clearTimeout(moveWarnTimer.current);
    moveWarnTimer.current = setTimeout(() => setMoveWarning(null), 3500);
  };

  const handleDragStart = (e, item, type) => {
    setDraggedItem(item);
    setDragType(type);
    // Borne réception↔intervention : plafond (réception) = heure de l'intervention liée ;
    // plancher (intervention) = heure de sa réception.
    let boundary = null;
    let boundaryDate = null;
    if (type === 'reception') {
      const intv = interventions.find(i => i.id === item.intervention_id);
      boundary = hhmm(intv?.heure_debut);
      boundaryDate = intv?.date_intervention || intv?.date || null;   // jour de l'intervention liée
    } else if (type === 'intervention') {
      boundary = hhmm(item.heure_reception);
      boundaryDate = item.date_reception || null;                     // jour de la réception
    }
    setDragBoundaryHeure(boundary || null);
    setDragBoundaryDate(boundaryDate ? String(boundaryDate).slice(0, 10) : null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id.toString());
    e.target.classList.add('dragging');
    const cardRect = e.target.getBoundingClientRect();
    dragOffsetY.current = e.clientY - cardRect.top;
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDragOverColumn(null);
    setDropIndicatorY(null);
    setDropPreviewHeure(null);
    setDropPreviewFin(null);
    setDropForbidden(false);
    setDragBoundaryHeure(null);
    setDragBoundaryDate(null);
  };

  const handleDragOver = (e, columnKey, slotH = 20) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnKey);
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const relativeY = e.clientY - rect.top - dragOffsetY.current;
    const slotIndex = Math.max(0, Math.min(GRID_SLOTS - 1, Math.floor(relativeY / slotH)));
    setDropIndicatorY(slotIndex * slotH);
    const slotHeure = slotToHeure(slotIndex);
    setDropPreviewHeure(slotHeure);

    // Heure de FIN prévisualisée. Même règle que la carte posée : la durée est un
    // temps de TRAVAIL, la fin enjambe donc les pauses — et celles du mécanicien
    // VISÉ, pas celles d'origine : déplacer une intervention vers un atelier dont
    // la pause tombe ailleurs change sa fin. Un glisser de réception n'a pas de
    // durée : on n'affiche alors que l'heure de début.
    let finHeure = null;
    const dureeGlissee = parseFloat(draggedItem?.duree) || 0;
    if (dragType === 'intervention' && dureeGlissee > 0) {
      const enVueSemaine = view === 'atelier' && atelierView === 'semaine';
      const mecaId = enVueSemaine ? draggedItem?.mecanicien_id : columnKey;
      const meca = (staffList || []).find(x => String(x.id) === String(mecaId));
      const jour = (enVueSemaine && typeof columnKey === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(columnKey))
        ? new Date(`${columnKey}T00:00:00`)
        : selectedDate;
      const pSlots = getPauseSlotsMecanicien(jour, meca?.atelier_id);
      finHeure = slotToHeure(Math.min(GRID_SLOTS, calcRealEndSlot(slotHeure, dureeGlissee, pSlots)));
    }
    setDropPreviewFin(finHeure);
    // Zone interdite (comparaison heure seule, valable en contexte même-jour : vue Réception
    // et vue Atelier « jour »). En vue « semaine » (changement de jour possible), on laisse
    // la garde backend datetime-aware trancher pour éviter les faux positifs.
    const crossDay = view === 'atelier' && atelierView === 'semaine';
    // La borne ne vaut que si la carte liée est le MÊME JOUR que le créneau visé.
    const memeJour = !dragBoundaryDate || dragBoundaryDate === formatDate(selectedDate);
    let forbidden = false;
    if (!crossDay && dragBoundaryHeure && memeJour) {
      if (dragType === 'reception') forbidden = slotHeure > dragBoundaryHeure;
      else if (dragType === 'intervention') forbidden = slotHeure < dragBoundaryHeure;
    }
    setDropForbidden(forbidden);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
      setDropIndicatorY(null);
      setDropPreviewHeure(null);
      setDropPreviewFin(null);
      setDropForbidden(false);
    }
  };

  const handleDropAtelierJour = (e, targetStaff) => {
    e.preventDefault();
    setDragOverColumn(null);
    setDropIndicatorY(null);
    setDropPreviewHeure(null);
    setDropPreviewFin(null);
    setDropForbidden(false);
    const boundary = dragBoundaryHeure;
    const boundaryDate = dragBoundaryDate;
    setDragBoundaryHeure(null);
    setDragBoundaryDate(null);
    if (!draggedItem || (dragType !== 'intervention' && dragType !== 'a_planifier' && dragType !== 'non_honore')) return;

    // Collaborateur absent ce jour-là : on ne planifie pas dessus. Le voile le
    // dit visuellement, ce refus le garantit (drag venant d'une autre colonne).
    const absence = absenceBloquante(targetStaff?.id, selectedDate);
    if (absence) {
      showMoveWarning(`${getStaffLabel(targetStaff)} est absent ce jour (${libelleMotifAbsence(absence.motif)}) — impossible de planifier.`);
      return;
    }

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const newHeure = computeSlotFromY(e.clientY - rect.top - dragOffsetY.current, 0);

    // Contrainte : une intervention ne peut pas commencer avant sa réception (refus).
    const borneMemeJour = !boundaryDate || boundaryDate === formatDate(selectedDate);
    if (dragType === 'intervention' && boundary && borneMemeJour && hhmm(newHeure) < boundary) {
      showMoveWarning(`Impossible : l'intervention ne peut pas commencer avant la réception (${boundary}).`);
      return;
    }

    // Planification d'un véhicule « à planifier » : on l'affecte à ce mécanicien à cette heure.
    if (dragType === 'a_planifier') {
      setPendingMove({
        type: 'a_planifier',
        item: draggedItem,
        from: { heure: '—', mecanicien: 'Non planifié' },
        to: { heure: newHeure, mecanicien: getStaffLabel(targetStaff), mecanicien_id: targetStaff.id, date: formatDate(selectedDate) }
      });
      return;
    }
    if (dragType === 'non_honore') {
      setPendingMove({
        type: 'non_honore',
        item: draggedItem,
        from: { heure: '—', mecanicien: 'Non planifié' },
        to: { heure: newHeure, mecanicien: getStaffLabel(targetStaff), mecanicien_id: targetStaff.id, date: formatDate(selectedDate) }
      });
      return;
    }

    if (newHeure === draggedItem.heure_debut && targetStaff.id === draggedItem.mecanicien_id) return;

    requestMove({
      type: 'intervention',
      item: draggedItem,
      from: { heure: draggedItem.heure_debut, mecanicien: draggedItem.mecanicien, mecanicien_id: draggedItem.mecanicien_id },
      to: { heure: newHeure, mecanicien: getStaffLabel(targetStaff), mecanicien_id: targetStaff.id }
    }, `${draggedItem.client || draggedItem.immat || 'Intervention'} → ${newHeure} · ${getStaffLabel(targetStaff)}`);
  };

  const handleDropAtelierSemaine = (e, targetDate) => {
    e.preventDefault();
    setDragOverColumn(null);
    setDropIndicatorY(null);
    setDropPreviewHeure(null);
    setDropPreviewFin(null);
    setDropForbidden(false);
    setDragBoundaryHeure(null);
    setDragBoundaryDate(null);
    // Jour d'absence du mécanicien sélectionné : planification refusée.
    const absenceSem = absenceBloquante(selectedMecanicien, targetDate);
    if (absenceSem) {
      showMoveWarning(`Absence ce jour (${libelleMotifAbsence(absenceSem.motif)}) — impossible de planifier.`);
      return;
    }

    if (!draggedItem || dragType !== 'intervention') return;

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const newHeure = computeSlotFromY(e.clientY - rect.top - dragOffsetY.current, 0);

    // Contrainte réception ≤ intervention : en vue semaine le jour peut changer, donc la
    // comparaison heure seule n'est pas fiable → on délègue à la garde backend datetime-aware
    // (409 capté dans confirmMove, qui affiche le message et recharge).

    if (newHeure === draggedItem.heure_debut && targetDate === draggedItem.date) return;

    requestMove({
      type: 'intervention',
      item: draggedItem,
      from: { heure: draggedItem.heure_debut, date: draggedItem.date },
      to: { heure: newHeure, date: targetDate }
    }, `${draggedItem.client || draggedItem.immat || 'Intervention'} → ${formatDateFr(targetDate, { day: '2-digit', month: '2-digit' })} ${newHeure}`);
  };

  const handleDropReception = (e, targetPoste) => {
    e.preventDefault();
    setDragOverColumn(null);
    setDropIndicatorY(null);
    setDropPreviewHeure(null);
    setDropPreviewFin(null);
    setDropForbidden(false);
    const boundary = dragBoundaryHeure;
    const boundaryDate = dragBoundaryDate;
    setDragBoundaryHeure(null);
    setDragBoundaryDate(null);
    if (!draggedItem || dragType !== 'reception') return;
    // Le dépôt reste branché même sur une colonne absente (l'indicateur de créneau
    // en dépend) : le refus se fait ici, pas en débranchant le gestionnaire.
    const absentCible = absenceBloquante(targetPoste, selectedDate);
    if (estJourneeEntiere(absentCible)) {
      setDraggedItem(null);
      alert(`Ce réceptionnaire est absent (${libelleMotifAbsence(absentCible.motif)}). Choisissez un autre poste.`);
      return;
    }

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    // Grille Réception = 38 px / créneau (≠ 20 px de l'Atelier).
    const newHeure = computeSlotFromY(e.clientY - rect.top - dragOffsetY.current, 0, 38);

    // Absence partielle : seule la tranche concernée refuse le dépôt. Le reste
    // de la journée du réceptionnaire est ouvert comme d'habitude.
    if (absentCible && newHeure >= String(absentCible.heure_debut).slice(0, 5)
                    && newHeure < String(absentCible.heure_fin).slice(0, 5)) {
      setDraggedItem(null);
      alert(`Ce réceptionnaire est absent de ${String(absentCible.heure_debut).slice(0, 5)} à ${String(absentCible.heure_fin).slice(0, 5)} (${libelleMotifAbsence(absentCible.motif)}).`);
      return;
    }

    // Contrainte : une réception ne peut pas être après l'intervention liée (refus).
    const borneRecepMemeJour = !boundaryDate || boundaryDate === formatDate(selectedDate);
    if (boundary && borneRecepMemeJour && hhmm(newHeure) > boundary) {
      showMoveWarning(`Impossible : la réception ne peut pas être après l'intervention (${boundary}).`);
      return;
    }

    if (newHeure === draggedItem.heure && targetPoste === draggedItem.poste) return;

    const posteLabel = staffReceptionnaires.find(r => r.id === targetPoste);
    requestMove({
      type: 'reception',
      item: draggedItem,
      from: { heure: draggedItem.heure, poste: draggedItem.poste },
      to: { heure: newHeure, poste: targetPoste }
    }, `${draggedItem.client || draggedItem.immat || 'Réception'} → ${newHeure}${posteLabel ? ' · ' + getStaffLabel(posteLabel) : ''}`);
  };

  // Exécute réellement un déplacement (mutation optimiste + API). Partagé par
  // l'application directe (drag & drop) et par la confirmation (cas à conflit).
  const performMove = async (move) => {
    if (!move) return;

    if (move.type === 'a_planifier') {
      skipNextSSE.current++;
      try {
        await api.patch(`/planning/interventions/${move.item.id}/move`, {
          heure_debut: move.to.heure,
          mecanicien: move.to.mecanicien,
          mecanicien_id: move.to.mecanicien_id,
          date_intervention: move.to.date,
          a_planifier: false
        });
      } catch (e) {
        // Échec SILENCIEUX jusqu'ici : le dossier revenait dans la liste sans
        // un mot, alors que le serveur explique pourquoi (typiquement « la
        // réception ne peut pas être après l'intervention »).
        console.error('[performMove a_planifier]', e);
        showMoveWarning(e?.response?.data?.error || 'Planification impossible.');
        await Promise.all([loadData(), refreshAPlanifier()]);
        return false;
      }
      await Promise.all([loadData(), refreshAPlanifier()]);
      return true;
    }

    if (move.type === 'non_honore') {
      // Replanifier un RDV non honoré : le déplacement SOLDE la déclaration
      // (non_honore_traite) — la trace reste pour les KPIs no-show.
      skipNextSSE.current++;
      try {
        await api.patch(`/planning/interventions/${move.item.id}/move`, {
          heure_debut: move.to.heure,
          mecanicien: move.to.mecanicien,
          mecanicien_id: move.to.mecanicien_id,
          date_intervention: move.to.date,
          non_honore_traite: true
        });
      } catch (e) { console.error('[performMove non_honore]', e); }
      await Promise.all([loadData(), refreshNonHonores()]);
      return;
    }

    if (move.type === 'resize') {
      // Mise à jour optimiste (l'annulation d'un resize repasse par performMove)
      setInterventions(prev => prev.map(i =>
        i.id === move.item.id ? { ...i, duree: move.to.duree, heure_debut: move.to.heure ?? i.heure_debut } : i
      ));
      const patch = { duree: move.to.duree };
      if (move.from.heure !== move.to.heure) patch.heure_debut = move.to.heure;
      skipNextSSE.current++;
      try {
        if (move.item.is_complement && move.item.complement_id) {
          await api.put(`/planning/interventions/complements/${move.item.complement_id}`, patch);
        } else {
          await api.patch(`/planning/interventions/${move.item.id}/move`, patch);
        }
      } catch (err) {
        console.error('[performMove resize]', err);
        await loadData();
      }
      return;
    }

    if (move.type === 'intervention') {
      // Mise à jour optimiste
      setInterventions(prev => prev.map(i => {
        if (i.id !== move.item.id) return i;
        const updated = { ...i, heure_debut: move.to.heure };
        if (move.to.mecanicien !== undefined) updated.mecanicien = move.to.mecanicien;
        if (move.to.mecanicien_id !== undefined) updated.mecanicien_id = move.to.mecanicien_id;
        if (move.to.date !== undefined) updated.date = move.to.date;
        return updated;
      }));
      skipNextSSE.current++;
      try {
        if (move.item.is_complement && move.item.complement_id) {
          await api.put(`/planning/interventions/complements/${move.item.complement_id}`, {
            heure_debut: move.to.heure,
            mecanicien: move.to.mecanicien,
            mecanicien_id: move.to.mecanicien_id,
            date_intervention: move.to.date,
          });
        } else {
          await api.patch(`/planning/interventions/${move.item.id}/move`, {
            heure_debut: move.to.heure,
            mecanicien: move.to.mecanicien,
            mecanicien_id: move.to.mecanicien_id,
            date_intervention: move.to.date,
          });
        }
      } catch (err) {
        console.error('[performMove intervention]', err);
        if (err?.response?.status === 409) showMoveWarning(err.response.data?.error || 'Déplacement refusé.');
        await loadData();
      }
    } else if (move.type === 'reception') {
      skipNextSSE.current++;
      try {
        if (move.item._fromIntervention) {
          // Carte-shim (dossier sans rdv explicite, ex. import Mécaplanning) :
          // le créneau de réception vit sur le dossier lui-même.
          setInterventions(prev => prev.map(i => (
            i.id === move.item.intervention_id ? { ...i, heure_reception: move.to.heure, poste_reception: move.to.poste } : i
          )));
          // Route dédiée : écrit le créneau et le trace dans l'historique.
          await api.patch(`/planning/interventions/${move.item.intervention_id}/reception`, {
            heure: move.to.heure,
            poste: move.to.poste,
          });
        } else {
          setRdvReception(prev => prev.map(r => (
            r.id === move.item.id ? { ...r, heure: move.to.heure, poste: move.to.poste } : r
          )));
          // Le dossier lié suit côté serveur : on le reflète tout de suite, sinon
          // la modale ouverte juste après afficherait l'ancienne heure.
          if (move.item.intervention_id) {
            setInterventions(prev => prev.map(i => (
              i.id === move.item.intervention_id ? { ...i, heure_reception: move.to.heure, poste_reception: move.to.poste } : i
            )));
          }
          await api.patch(`/planning/rdv/${move.item.id}/move`, {
            heure: move.to.heure,
            poste: move.to.poste,
          });
        }
        proposerSmsDeplacement(move);
      } catch (err) {
        console.error('[performMove reception]', err);
        if (err?.response?.status === 409) showMoveWarning(err.response.data?.error || 'Déplacement refusé.');
        await loadData();
      }
    }
  };

  // ── SMS après déplacement d'une réception ────────────────────────────────
  // Proposition, jamais d'envoi automatique : le déplacement vient de réussir,
  // on offre de prévenir le client du nouveau créneau (s'il a un téléphone).
  const proposerSmsDeplacement = (move) => {
    if (move._estAnnulation) return;   // l'annulation d'un déplacement ne re-propose pas
    // Changement de RÉCEPTIONNAIRE à heure égale : rien à annoncer au client,
    // son rendez-vous n'a pas bougé. Ne proposer un SMS que si l'heure change.
    if (move.from?.heure && move.to?.heure && move.from.heure === move.to.heure) return;
    const intv = interventions.find(i => i.id === move.item.intervention_id);
    if (!intv || !intv.client_tel) return;
    const concession = intv.concession_nom ? ` ${intv.concession_nom}` : '';
    const client = [intv.client_prenom, intv.client_nom].filter(Boolean).join(' ') || move.item.client || 'le client';
    setSmsProposition({
      interventionId: intv.id,
      titre: `✉️ Prévenir ${client} ?`,
      sousTitre: <>Rendez-vous déplacé à <strong>{move.to.heure}</strong> — SMS à</>,
      telephone: intv.client_tel,
      message: `Bonjour, votre rendez-vous${concession} est déplacé à ${move.to.heure}. Merci de votre comprehension.`,
      envoi: null,
    });
  };

  // Statuts autorisés au rôle Technicien (même liste que le verrou serveur)
  const estStatutTechnicien = (nom) => {
    const cle = String(nom || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    return cle === 'en intervention' || cle === 'diag en cours' || cle === 'diag termine';
  };

  // Passage en « Facturé » : proposer le SMS « véhicule prêt », pré-rempli avec
  // le modèle de la concession (Messages types, variables déjà substituées).
  const estStatutFacture = (statutId) => {
    const nom = statutsAtelier.find(x => x.id === statutId)?.nom;
    return !!nom && String(nom).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim() === 'facture';
  };
  // Dossier dont le N° d'OR manque, à réclamer après un changement de statut.
  const [demandeOr, setDemandeOr] = useState(null);

  const proposerSmsVehiculePret = async (interventionId) => {
    try {
      const { data } = await api.get(`/planning/interventions/${interventionId}/messages`);
      // Même règle que la confirmation de RDV : ne rien proposer que le serveur
      // refuserait. Ce contrôle ne regardait que la présence du numéro.
      if (!estMobileEnvoyable(data?.client_tel)) return;
      const modeles = (data.messages || []).filter(m => m.canal !== 'email');
      const modele = modeles.find(m => /pr[eê]t/i.test(m.libelle || '')) || modeles[0] || null;
      const intv = interventions.find(i => i.id === interventionId);
      const client = intv ? ([intv.client_prenom, intv.client_nom].filter(Boolean).join(' ') || 'le client') : 'le client';
      setSmsProposition({
        interventionId,
        titre: `✉️ Prévenir ${client} ?`,
        sousTitre: <>Dossier <strong>Facturé</strong> — véhicule prêt. SMS à</>,
        telephone: data.client_tel,
        message: modele ? modele.contenu : `Bonjour, votre vehicule est pret. ${intv?.concession_nom || ''}`.trim(),
        envoi: null,
      });
    } catch (e) { console.error('[sms-vehicule-pret]', e); }
  };

  // Confirmation de RDV (2026-08-14, demande utilisateur) : à la création d'un
  // rendez-vous, on propose de prévenir le client. Même mécanique que le SMS
  // « véhicule prêt » ci-dessus — l'envoi, les modèles et la substitution des
  // variables existent déjà côté serveur, il n'y avait qu'à raccorder.
  const proposerSmsConfirmationRdv = async (interventionId, clientNom, avecRecap) => {
    try {
      const { data } = await api.get(`/planning/interventions/${interventionId}/messages`);
      // Rien à proposer si le numéro n'est pas un mobile joignable : le serveur
      // refuserait l'envoi (`normaliserNumeroMobile`), la modale n'aurait servi
      // qu'à faire échouer le geste. Le test couvre le fixe DOM-TOM, mais AUSSI
      // le numéro tronqué ou vide — que l'ancien contrôle laissait passer.
      if (!estMobileEnvoyable(data?.client_tel)) return;
      const modeles = (data.messages || []).filter(m => m.canal !== 'email');
      const modele = modeles.find(m => /confirm/i.test(m.libelle || ''))
        || modeles.find(m => /planifi|rendez-vous|rdv/i.test(m.libelle || ''))
        || null;
      const proposition = {
        interventionId,
        titre: `✉️ Confirmer le RDV à ${clientNom || 'le client'} ?`,
        sousTitre: <>Rendez-vous <strong>créé</strong> — SMS de confirmation à</>,
        telephone: data.client_tel,
        // Repli quand la concession n'a pas encore de modèle « Confirmation RDV ».
        // Il vient du serveur (`defaut_confirmation`), déjà rendu avec la date,
        // l'heure, l'immatriculation et la concession du dossier — le
        // réceptionnaire reste libre de le retoucher avant envoi.
        message: modele ? modele.contenu : (data.defaut_confirmation || ''),
        envoi: null,
      };
      // Deux modales empilées seraient illisibles : quand le récapitulatif
      // « Rendez-vous créé » s'affiche, la proposition devient un bouton DANS
      // ce récapitulatif. Sans récapitulatif (chemin Datacar), elle s'ouvre.
      if (avecRecap) setSmsConfirmPret(proposition);
      else setSmsProposition(proposition);
    } catch (e) { console.error('[sms-confirmation-rdv]', e); }
  };

  // ── Annulation d'un déplacement déjà appliqué ────────────────────────────
  // Le geste inverse est le même déplacement avec `from` et `to` permutés.
  const undoLastMove = async () => {
    const move = undoMove;
    setUndoMove(null);
    clearTimeout(undoTimer.current);
    if (!move) return;
    await performMove({ ...move, from: move.to, to: move.from, _estAnnulation: true });
  };

  const showUndoToast = (move, label) => {
    setUndoMove({ ...move, label });
    clearTimeout(undoTimer.current);
    // 8 s : le temps de réaliser l'erreur sans laisser le bandeau s'incruster.
    undoTimer.current = setTimeout(() => setUndoMove(null), 8000);
  };

  // ── Point d'entrée des déplacements par glisser-déposer ──────────────────
  // Application DIRECTE (le geste est déjà l'intention) + bandeau « Annuler ».
  // La modale de confirmation est réservée aux cas où le déplacement crée un
  // CHEVAUCHEMENT : là, une validation explicite se justifie.
  const requestMove = async (move, label) => {
    if (getMoveConflicts(move).length > 0) {
      setPendingMove(move);
      return;
    }
    setDraggedItem(null);
    setDragType(null);
    // `performMove` renvoie false quand le serveur a refusé : proposer
    // « Annuler » sur une opération qui n'a pas eu lieu induirait en erreur.
    const ok = await performMove(move);
    if (ok !== false) showUndoToast(move, label);
  };

  const confirmMove = async () => {
    if (!pendingMove) return;
    const move = pendingMove;
    setPendingMove(null);
    setDraggedItem(null);
    setDragType(null);
    await performMove(move);
  };

  const cancelMove = () => {
    if (pendingMove?.type === 'resize') {
      setInterventions(prev => prev.map(i =>
        i.id === pendingMove.item.id ? { ...i, duree: pendingMove.from.duree, heure_debut: pendingMove.from.heure } : i
      ));
    }
    setPendingMove(null);
    setDraggedItem(null);
    setDragType(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setModalIntervention(null);
    setModalFreres([]);
    resetFormData();
    setPendingMove(null);
  };

  // Navigation dossier précédent/suivant dans la modale (flèches). L'index se
  // calcule à la volée : la liste `modalFreres` est figée à l'ouverture.
  const naviguerDossier = (sens) => {
    if (!modalIntervention || modalFreres.length < 2) return;
    const i = modalFreres.findIndex(d => String(d.id) === String(modalIntervention.id));
    if (i === -1) return;
    const cible = modalFreres[i + sens];
    if (cible) ouvrirDossierSurPlace(cible, modalFreres);
  };
  const idxFrere = modalIntervention
    ? modalFreres.findIndex(d => String(d.id) === String(modalIntervention.id))
    : -1;

  // ── Déplacements « rapides » depuis les mini-aperçus embarqués dans la modale
  // d'intervention (InterventionModal) : mêmes endpoints que confirmMove, mais
  // application immédiate (pas d'étape de confirmation — aperçu compact).
  const handleQuickMoveIntervention = async (interventionId, changes) => {
    setInterventions(prev => prev.map(i => (i.id === interventionId ? { ...i, ...changes } : i)));
    skipNextSSE.current++;
    try {
      await api.patch(`/planning/interventions/${interventionId}/move`, {
        heure_debut: changes.heure_debut,
        mecanicien: changes.mecanicien,
        mecanicien_id: changes.mecanicien_id,
        date_intervention: changes.date_intervention,
      });
    } catch (err) {
      console.error('[handleQuickMoveIntervention] Erreur:', err);
      await loadData();
    }
  };

  const handleQuickMoveReception = async (rdvId, changes) => {
    setRdvReception(prev => prev.map(r => (r.id === rdvId ? { ...r, ...changes } : r)));
    skipNextSSE.current++;
    try {
      await api.patch(`/planning/rdv/${rdvId}/move`, {
        heure: changes.heure,
        poste: changes.poste,
      });
    } catch (err) {
      console.error('[handleQuickMoveReception] Erreur:', err);
      await loadData();
    }
  };

  // ─── Vider la journée affichée (super-admin) ─────────────────────────────
  // Deux temps : on demande d'abord au serveur CE QUI SERAIT DÉTRUIT, puis on
  // l'annonce. Une suppression en masse ne se déclenche pas sur un simple « OK ».
  // ── Portées : bornes et libellés ──────────────────────────────────────────
  // Tout est construit en heure LOCALE. `new Date(a, m + 1, 0)` donne le dernier
  // jour du mois `m` — le constructeur normalise, il n'y a pas de table des
  // longueurs de mois à tenir, ni de cas février bissextile à traiter.
  const plagePortee = (cle) => {
    // Semaine calendaire (lun->sam), PAS `weekDays` : ce dernier est filtre sur
    // les jours d'ouverture, donc `weekDays[5]` vaut undefined des qu'un atelier
    // ferme un jour — et `formatDate(undefined)` faisait ecran blanc a l'ouverture
    // de la modale d'import Mecaplanning.
    if (cle === 'semaine') {
      return { from: formatDate(semaineCalendaire[0]), to: formatDate(semaineCalendaire[semaineCalendaire.length - 1]) };
    }
    if (cle === 'mois') {
      const a = selectedDate.getFullYear(), m = selectedDate.getMonth();
      return { from: formatDate(new Date(a, m, 1)), to: formatDate(new Date(a, m + 1, 0)) };
    }
    const j = formatDate(selectedDate);
    return { from: j, to: j };
  };

  const libellePortee = (cle) => {
    const { from, to } = plagePortee(cle);
    if (cle === 'jour') return formatDateFr(from);
    if (cle === 'mois') {
      return selectedDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
    return `du ${formatDateFr(from)} au ${formatDateFr(to)}`;
  };

  // Jours à interroger pour un import. Le dimanche est écarté : l'atelier est
  // fermé, Mécaplanning n'y a rien, et 4 appels SOAP inutiles par mois allongent
  // l'attente sans rien rapporter.
  const joursOuvresDePlage = (cle) => {
    const { from, to } = plagePortee(cle);
    const [af, mf, jf] = from.split('-').map(Number);
    const [at, mt, jt] = to.split('-').map(Number);
    const fin = new Date(at, mt - 1, jt);
    const jours = [];
    for (let d = new Date(af, mf - 1, jf); d <= fin; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      if (d.getDay() !== 0) jours.push(formatDate(d));
    }
    return jours;
  };

  // ── Vidage du planning ────────────────────────────────────────────────────
  const chargerCompteVidage = async (portee) => {
    setVideJourModal(m => ({ ...(m || {}), portee, chargement: true, saisie: '' }));
    const { from, to } = plagePortee(portee);
    try {
      const { data } = await api.get('/planning/interventions/jour/compte', { params: { date_from: from, date_to: to } });
      setVideJourModal(m => (m ? { ...m, portee, compte: data, chargement: false } : m));
    } catch (err) {
      setVideJourModal(m => (m ? { ...m, portee, compte: null, chargement: false } : m));
      alert(err.response?.data?.error || 'Impossible de compter les interventions.');
    }
  };

  const ouvrirVideJour = () => {
    setVideJourModal({ portee: 'jour', compte: null, chargement: true, saisie: '' });
    chargerCompteVidage('jour');
  };
  useEffect(() => { ouvrirVideJourRef.current = ouvrirVideJour; });

  const confirmerVideJour = async () => {
    if (!videJourModal || videJourBusy) return;
    const { portee } = videJourModal;
    const { from, to } = plagePortee(portee);
    setVideJourBusy(true);
    try {
      const { data } = await api.delete('/planning/interventions/jour', { params: { date_from: from, date_to: to } });
      setVideJourModal(null);
      skipNextSSE.current++;
      await loadData();
      alert(`🧹 ${data.supprimees} intervention(s) supprimée(s) — ${libellePortee(portee)}.`);
    } catch (err) {
      alert(err.response?.data?.error || 'Suppression impossible.');
    } finally {
      setVideJourBusy(false);
    }
  };

  // ── Import du planning Mécaplanning (API SOAP) ────────────────────────────
  // Super-admin uniquement. Le backend résout le site Mécaplanning de la
  // concession et n'importe qu'UNE journée par appel.
  //
  // La boucle est donc côté client, jour par jour, et non une plage passée au
  // serveur. Trois raisons : un import prend 2 à 3 s par jour, soit ~65 s pour un
  // mois — une requête unique laisserait le bouton en ⏳ sans rien dire pendant
  // une minute ; un jour en échec (API Mécaplanning indisponible) n'emporte pas
  // les autres ; et l'opération reste interruptible.
  const ouvrirMecaModal = () => {
    if (mecaSyncing) return;
    mecaAnnule.current = false;
    setMecaModal({ mode: 'periode', portee: 'jour', avance: null, q: '', resultats: null, rechercheEnCours: false, importEnCours: null });
  };
  handleMecaSyncRef.current = ouvrirMecaModal;

  // ── Import individuel : chercher un dossier précis dans la journée Mécaplanning
  // affichée, puis n'importer que lui — sans purge du jour, donc sans toucher aux
  // statuts et commentaires déjà saisis sur les autres dossiers.
  const rechercherDossierMeca = async () => {
    const q = (mecaModal?.q || '').trim();
    if (q.length < 2 || mecaModal?.rechercheEnCours) return;
    const concessionId = user?.concession_id || Number(localStorage.getItem('crm-global-concession')) || null;
    setMecaModal(m => (m ? { ...m, rechercheEnCours: true, resultats: null, erreurRecherche: null } : m));
    try {
      const { data } = await api.get('/mecaplanning-api/recherche', {
        params: { date: formatDate(selectedDate), concessionId, q },
      });
      setMecaModal(m => (m ? { ...m, rechercheEnCours: false, resultats: data.correspondances, totalJour: data.total_jour } : m));
    } catch (err) {
      setMecaModal(m => (m ? { ...m, rechercheEnCours: false, resultats: [], erreurRecherche: err.response?.data?.error || err.message } : m));
    }
  };

  // ── Import des indicateurs Mécaplanning → tags, jour par jour ─────────────
  // Ne touche à AUCUN dossier : pose uniquement des tags sur les dossiers déjà
  // importés (rapprochés par leur marqueur, y compris après correction manuelle
  // des dates). Pose seulement, jamais de retrait — décision utilisateur.
  const lancerSyncIndicateurs = async (portee) => {
    const concessionId = user?.concession_id || Number(localStorage.getItem('crm-global-concession')) || null;
    const jours = joursOuvresDePlage(portee);
    mecaAnnule.current = false;
    setMecaSyncing(true);
    setMecaModal(m => ({ ...m, avanceTags: { fait: 0, total: jours.length, poses: [], dejaPoses: 0, sansTag: {}, echecs: [], jour: null } }));

    for (const jour of jours) {
      if (mecaAnnule.current) break;
      setMecaModal(m => (m ? { ...m, avanceTags: { ...m.avanceTags, jour } } : m));
      try {
        const { data } = await api.post('/mecaplanning-api/sync-indicateurs', { date: jour, concessionId });
        setMecaModal(m => {
          if (!m) return m;
          const a = m.avanceTags;
          const sansTag = { ...a.sansTag };
          for (const [lib, n] of Object.entries(data.indicateurs_sans_tag || {})) sansTag[lib] = (sansTag[lib] || 0) + n;
          return { ...m, avanceTags: {
            ...a,
            fait: a.fait + 1,
            poses: [...a.poses, ...(data.poses || [])],
            dejaPoses: a.dejaPoses + (data.deja_poses || 0),
            sansTag,
            aucunTag: data.aucun_tag || a.aucunTag,
          } };
        });
        if (data.aucun_tag) break; // aucun tag actif : inutile de balayer 26 jours
      } catch (err) {
        const motif = err.response?.data?.error || err.message;
        setMecaModal(m => (m ? { ...m, avanceTags: { ...m.avanceTags, fait: m.avanceTags.fait + 1, echecs: [...m.avanceTags.echecs, { jour, motif }] } } : m));
        if (/Aucun site Mécaplanning/i.test(motif)) break;
      }
    }

    setMecaSyncing(false);
    setMecaModal(m => (m ? { ...m, avanceTags: { ...m.avanceTags, jour: null, termine: true, interrompu: mecaAnnule.current } } : m));
    skipNextSSE.current++;
    await loadData();
  };

  const importerUnDossierMeca = async (r) => {
    if (mecaModal?.importEnCours) return;
    const concessionId = user?.concession_id || Number(localStorage.getItem('crm-global-concession')) || null;
    setMecaModal(m => (m ? { ...m, importEnCours: r.rdvId } : m));
    try {
      const { data } = await api.post('/mecaplanning-api/import-un', {
        date: formatDate(selectedDate),
        concessionId,
        rdvId: r.rdvId,
        // L'atelier affiché : c'est là que le dossier atterrit si son mécanicien
        // Mécaplanning est inconnu du CRM (affectation de secours, à corriger).
        atelierId: selectedAtelierId ? Number(selectedAtelierId) : null,
      });
      const dossier = data.dossiers?.[0] || null;
      setMecaModal(m => (m ? {
        ...m,
        importEnCours: null,
        resultats: (m.resultats || []).map(x => x.rdvId === r.rdvId ? { ...x, importe: dossier || { echec: true } } : x),
      } : m));
      skipNextSSE.current++;
      await loadData();
    } catch (err) {
      setMecaModal(m => (m ? { ...m, importEnCours: null, erreurRecherche: err.response?.data?.error || err.message } : m));
    }
  };

  const lancerMecaSync = async (portee) => {
    const concessionId = user?.concession_id || Number(localStorage.getItem('crm-global-concession')) || null;
    const jours = joursOuvresDePlage(portee);
    mecaAnnule.current = false;
    setMecaSyncing(true);
    setMecaModal({ portee, avance: { fait: 0, total: jours.length, importes: 0, maj: 0, echecs: [], jour: null } });

    for (const jour of jours) {
      if (mecaAnnule.current) break;
      setMecaModal(m => (m ? { ...m, avance: { ...m.avance, jour } } : m));
      try {
        const res = await api.post('/mecaplanning-api/sync', { date: jour, concessionId });
        setMecaModal(m => (m ? { ...m, avance: {
          ...m.avance,
          fait: m.avance.fait + 1,
          importes: m.avance.importes + (res.data.imported || 0),
          maj: m.avance.maj + (res.data.updated || 0),
        } } : m));
      } catch (err) {
        const motif = err.response?.data?.error || err.message;
        setMecaModal(m => (m ? { ...m, avance: {
          ...m.avance,
          fait: m.avance.fait + 1,
          echecs: [...m.avance.echecs, { jour, motif }],
        } } : m));
        // Une concession sans site Mécaplanning configuré échouera à l'identique
        // sur les 26 jours : inutile de la faire attendre pour 26 fois le même
        // refus.
        if (/Aucun site Mécaplanning/i.test(motif)) break;
      }
    }

    setMecaSyncing(false);
    setMecaModal(m => (m ? { ...m, avance: { ...m.avance, jour: null, termine: true, interrompu: mecaAnnule.current } } : m));
    skipNextSSE.current++;
    await loadData();
  };

  const handleNewRdv = () => {
    setEditingId(null);
    resetFormData();
    setModalIntervention(null);
    setShowModal(true);
  };

  const handleEditIntervention = (intervention) => {
    // Ouvrir n'est pas modifier : un rôle en lecture voit le dossier, la modale
    // se charge de n'y rien laisser écrire (prop `lectureSeule`).
    if (!peut('dossier.ouvrir')) return;
    const parentId = intervention.is_complement ? intervention.parent_intervention_id : intervention.id;
    const linkedReception = rdvReception.find(r => r.intervention_id === parentId);
    setEditingId({
      interventionId: parentId,
      receptionId: linkedReception?.id || null
    });
    setModalIntervention(intervention);
    setShowModal(true);
  };

  // ─── Ouvrir un dossier trouvé par la recherche globale ────────────────────
  // On ne se contente pas d'ouvrir la modale : le planning se positionne sur la
  // journée du dossier, pour qu'en refermant on soit au bon endroit dans la
  // grille plutôt que sur une journée sans rapport.
  const ouvrirDossierDepuisRecherche = (intervention) => {
    if (!intervention) return;
    setRechercheOuverte(false);

    // Un véhicule « à planifier » n'a pas de place dans la grille : rien à
    // rejoindre, on ouvre directement.
    if (intervention.a_planifier || !intervention.date) {
      handleEditIntervention(intervention);
      return;
    }

    setView('atelier');
    setAtelierView('jour');
    // Date reconstruite en heure LOCALE : `new Date('YYYY-MM-DD')` donne minuit
    // UTC, soit la veille aux Antilles — piège documenté dans planningTime.js.
    const [an, mois, jour] = toYmd(intervention.date).split('-').map(Number);
    if (an && mois && jour) setSelectedDate(new Date(an, mois - 1, jour));

    // Sans bascule d'atelier, la carte serait dans une grille qu'on n'affiche pas.
    const meca = staffList.find(m => m.id === intervention.mecanicien_id);
    if (meca?.atelier_id) setSelectedAtelierId(String(meca.atelier_id));

    // ⚠️ Ouverture DIFFÉRÉE. `handleEditIntervention` résout la réception liée
    // dans `rdvReception`, qui n'est chargé que pour la journée affichée.
    // L'appeler avant le rechargement donnerait `receptionId: null`, et à
    // l'enregistrement la modale CRÉERAIT une seconde réception au lieu de
    // mettre à jour l'existante. On attend donc que la journée soit chargée.
    const repli = setTimeout(() => {
      if (ouvertureEnAttente.current?.id === intervention.id) {
        ouvertureEnAttente.current = null;
        handleEditIntervention(intervention);   // repli : mieux vaut ouvrir que rien
      }
    }, 3000);
    ouvertureEnAttente.current = { id: intervention.id, repli };
  };

  // ─── Lien profond depuis Gestion Parking ──────────────────────────────────
  // La colonne Planification du parking dépose l'id d'intervention à ouvrir
  // dans sessionStorage puis navigue ici. On attend que le référentiel staff
  // soit chargé (le positionnement d'atelier de `ouvrirDossierDepuisRecherche`
  // en dépend), puis on récupère le dossier — il n'appartient presque jamais à
  // la journée affichée — et on le rejoint comme un résultat de recherche.
  const lienParkingTraite = useRef(false);
  useEffect(() => {
    if (lienParkingTraite.current || staffList.length === 0) return;
    let id = null;
    try {
      id = sessionStorage.getItem('planning-open-intervention');
      if (id) sessionStorage.removeItem('planning-open-intervention');
    } catch (e) { /* stockage indisponible */ }
    if (!id) { lienParkingTraite.current = true; return; }
    lienParkingTraite.current = true;
    api.get(`/planning/interventions/${id}`)
      .then(r => { if (r.data?.intervention) ouvrirDossierDepuisRecherche(r.data.intervention); })
      .catch(e => console.error('[planning/lien-parking]', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList]);

  // Une alerte ne porte que l'identifiant du dossier, et celui-ci n'appartient
  // presque jamais à la journée affichée : on va donc le chercher avant
  // d'ouvrir la modale, au lieu de le supposer déjà chargé.
  // ─── Ouvrir un dossier SUR PLACE (sans quitter la vue courante) ────────────
  // Pour les vues-tableaux (Suivi Atelier…) : la modale s'ouvre par-dessus la
  // vue, et on y revient en la fermant — pas de bascule vers la grille.
  // ⚠️ La réception liée est résolue CÔTÉ SERVEUR : `rdvReception` ne couvre
  // que la journée affichée, et un `receptionId` manquant ferait CRÉER une
  // seconde réception à l'enregistrement (piège documenté plus haut).
  const ouvrirDossierSurPlace = async (intervention, freres) => {
    if (!intervention || !peut('dossier.ouvrir')) return;
    // Contexte de navigation (flèches précédent/suivant). Absent → pas de flèches.
    setModalFreres(Array.isArray(freres) && freres.length > 1 ? freres : []);
    const parentId = intervention.is_complement ? intervention.parent_intervention_id : intervention.id;
    let receptionId = rdvReception.find(r => r.intervention_id === parentId)?.id || null;
    if (!receptionId) {
      try {
        const r = await api.get('/planning/rdv', { params: { intervention_id: parentId } });
        receptionId = r.data?.rdv?.[0]?.id || null;
      } catch (e) { console.error('[ouvrir-sur-place/rdv]', e); }
    }
    setEditingId({ interventionId: parentId, receptionId });
    setModalIntervention(intervention);
    setShowModal(true);
  };

  // Ouvre un dossier par son id SANS changer de vue. Le dossier n'est pas
  // forcément dans `interventions` (fenêtre ±14 jours) : on le récupère au
  // besoin. Passe par `ouvrirDossierSurPlace`, qui résout la réception —
  // l'oublier ferait CRÉER une réception en double à l'enregistrement.
  const ouvrirDossierParId = async (interventionId) => {
    if (!interventionId || !peut('dossier.ouvrir')) return;
    const connue = interventions.find(i => i.id === interventionId);
    if (connue) { ouvrirDossierSurPlace(connue); return; }
    try {
      const r = await api.get(`/planning/interventions/${interventionId}`);
      if (r.data?.intervention) ouvrirDossierSurPlace(r.data.intervention);
    } catch (e) {
      console.error('[derniers-contacts/ouvrir]', e);
      window.alert("Impossible d'ouvrir ce dossier.");
    }
  };

  const ouvrirDossierDepuisAlerte = async (interventionId) => {
    try {
      const r = await api.get(`/planning/interventions/${interventionId}`);
      const intervention = r.data?.intervention;
      if (intervention) handleEditIntervention(intervention);
    } catch (e) {
      console.error('[pieces-alertes/ouvrir]', e);
    }
  };

  const handleEditReception = (rdv) => {
    const intervention = interventions.find(i => i.id === rdv.intervention_id);
    if (intervention) {
      handleEditIntervention(intervention);
    }
  };

  const handleMouseDown = (e, staff) => {
    if (e.button !== 0 || !peut('dossier.replanifier')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const slotIndex = Math.floor((e.clientY - rect.top) / 20);
    const clamped = Math.max(0, Math.min(GRID_SLOTS - 1, slotIndex));
    setIsSelecting(true);
    setSelectionStart(clamped);
    setSelectionEnd(clamped);
    setSelectionMeca(staff);
    e.preventDefault();
  };

  const handleMouseMove = (e, staff) => {
    if (!isSelecting || staff?.id !== selectionMeca?.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const slotIndex = Math.floor((e.clientY - rect.top) / 20);
    const clamped = Math.max(0, Math.min(GRID_SLOTS - 1, slotIndex));
    setSelectionEnd(clamped);
  };

  const handleMouseUp = (e, staff) => {
    if (!isSelecting || staff?.id !== selectionMeca?.id) return;
    setIsSelecting(false);

    const startSlot = Math.min(selectionStart, selectionEnd);
    const endSlot = Math.max(selectionStart, selectionEnd);
    const slots = endSlot - startSlot + 1;

    const heureDebut = slotToHeure(startSlot);
    const duree = slots * 0.25;

    const dureeOptions = [0.5, 1, 1.5, 2, 3, 4, 6, 8];
    const closestDuree = dureeOptions.reduce((prev, curr) =>
      Math.abs(curr - duree) < Math.abs(prev - duree) ? curr : prev
    );

    // Création via drag : pré-remplir les champs déduits du drag
    resetFormData();
    setEditingId(null);
    setFormData(prev => ({
      ...prev,
      mecanicien: getStaffLabel(staff),
      mecanicien_id: staff.id,
      heure_debut: heureDebut,
      duree: closestDuree,
      date_intervention: formatDate(selectedDate)
    }));
    setModalIntervention(null);
    // Étape intermédiaire : le geste peut vouloir dire deux choses — un RDV, ou
    // poser une absence du collaborateur sur ce créneau. Petit popover au point
    // de relâchement plutôt que d'ouvrir la modale directement. Sans le droit
    // de créer, comportement historique (modale directe, le serveur tranche).
    if (!lectureSeule && (droits ? droits.peut_creer !== false : true)) {
      setCreneauChoix({
        x: Math.min(e.clientX, window.innerWidth - 250),
        y: Math.min(e.clientY, window.innerHeight - 140),
        staff,
        heure: heureDebut,
        // Borne haute EXCLUE : tracer jusqu'au créneau de 15:45 veut dire
        // « jusqu'à 16:00 », sinon le dernier quart d'heure serait perdu.
        heureFin: slotToHeure(endSlot + 1),
        slots,
      });
    } else {
      setShowModal(true);
    }
    setSelectionStart(null);
    setSelectionEnd(null);
    setSelectionMeca(null);
  };

  // Choix post-lasso : nouveau RDV (comportement historique) ou absence.
  // Chemin INVERSE du glisser-déposer : on a choisi le quand et le qui (lasso),
  // on choisit ensuite le dossier parmi ceux « à planifier ». Même mutation que
  // le drop (`performMove` type a_planifier) — un seul chemin d'écriture.
  const [choixDossier, setChoixDossier] = useState(null);   // { staff, heure } | null

  const ouvrirChoixDossierDepuisCreneau = () => {
    setCreneauChoix(c => {
      if (c) setChoixDossier({ staff: c.staff, heure: c.heure });
      return null;
    });
    setFiltreAPlanifier('');
  };

  const fermerChoixDossier = () => { setChoixDossier(null); setFiltreAPlanifier(''); };

  const planifierDossierSurCreneau = async (item) => {
    if (!choixDossier) return;
    const { staff, heure } = choixDossier;
    fermerChoixDossier();
    // `requestMove` applique directement et affiche le bandeau « Annuler » —
    // sauf chevauchement, où il ouvre la confirmation. Exactement le drop.
    await requestMove({
      type: 'a_planifier',
      item,
      from: { heure: '—', mecanicien: 'Non planifié' },
      to: { heure, mecanicien: getStaffLabel(staff), mecanicien_id: staff.id, date: formatDate(selectedDate) },
    }, `${item.client_nom || item.immatriculation || 'Dossier'} → ${heure} · ${getStaffLabel(staff)}`);
  };

  const ouvrirRdvDepuisCreneau = () => {
    setCreneauChoix(null);
    setShowModal(true);
  };
  const ouvrirAbsenceDepuisCreneau = () => {
    setCreneauChoix(c => {
      if (c) setAbsenceRapide({
        staff: c.staff, date: formatDate(selectedDate),
        motif: 'conges', commentaire: '', busy: false, erreur: null,
        type: 'absence',            // ou 'indisponibilite' — cf. la bascule du formulaire
        repete: false, jours: [], jusqua: '',
        dateFin: '',                // plage CONTINUE (« congés jusqu'au 30/10 »)
        // La plage tracée EST l'intention : un geste sur deux heures veut dire
        // deux heures d'absence, pas la journée. Le basculement reste offert.
        heure_debut: c.heureFin ? c.heure : '',
        heure_fin: c.heureFin || '',
        journee: !c.heureFin,
      });
      return null;
    });
  };
  const enregistrerAbsenceRapide = async () => {
    if (!absenceRapide || absenceRapide.busy) return;
    // ⚠️ Le serveur REFUSE une récurrence sans jour coché ou sans date de fin :
    // ce sont des saisies incomplètes, pas des absences simples, et les
    // dégrader en silence ferait perdre le « jusqu'au ». On le dit ici plutôt
    // que d'attendre le refus.
    if (absenceRapide.dateFin && absenceRapide.dateFin < absenceRapide.date) {
      setAbsenceRapide(a => ({ ...a, erreur: 'La date de fin ne peut pas précéder le premier jour.' })); return;
    }
    if (absenceRapide.repete) {
      if (!(absenceRapide.jours || []).length) {
        setAbsenceRapide(a => ({ ...a, erreur: 'Choisissez au moins un jour de la semaine.' })); return;
      }
      if (!absenceRapide.jusqua) {
        setAbsenceRapide(a => ({ ...a, erreur: 'Indiquez jusqu\'à quelle date la répétition va.' })); return;
      }
    }
    setAbsenceRapide(a => ({ ...a, busy: true, erreur: null }));
    try {
      await api.post('/planning/absences', {
        staff_id: absenceRapide.staff.id,
        date_debut: absenceRapide.date,
        // Plage continue : une seule ligne couvrant tous les jours, du premier
        // au dernier. À ne pas confondre avec la RÉCURRENCE hebdomadaire, qui
        // crée une occurrence par jour coché — deux gestes, deux résultats.
        date_fin: absenceRapide.dateFin || absenceRapide.date,
        motif: absenceRapide.motif,
        type: absenceRapide.type || 'absence',
        commentaire: absenceRapide.commentaire || null,
        heure_debut: absenceRapide.journee ? null : (absenceRapide.heure_debut || null),
        heure_fin: absenceRapide.journee ? null : (absenceRapide.heure_fin || null),
        recurrence: absenceRapide.repete
          ? { jours: absenceRapide.jours, jusqua: absenceRapide.jusqua }
          : undefined,
      });
      setAbsenceRapide(null);
      skipNextSSE.current++;
      await loadData();
    } catch (err) {
      console.error('[absence-rapide]', err);
      setAbsenceRapide(a => a ? { ...a, busy: false, erreur: err.response?.data?.error || 'Enregistrement impossible.' } : a);
    }
  };

  // Rendu compact de la liste de réception pour le panneau latéral de la vue Atelier
  // (mêmes 4 sections que l'ancienne vue jour : à recevoir, en cours, à venir, déjà reçus)
  // Rendu de l'onglet 💬 Demandes (panneau latéral) — même densité de cartes
  // que la liste Réception : 2 lignes, téléphone cliquable, clic = modale.
  const renderDemandesList = () => {
    // Du plus récent au plus ancien (l'API sert par nb d'appels — ici c'est la
    // fraîcheur qui ordonne le travail, demande utilisateur).
    const liste = (demandesVue === 'miennes' && monResponsableId != null
      ? demandesPanel.liste.filter(d => d.responsable_id === monResponsableId)
      : demandesPanel.liste
    ).slice().sort((a, b) => new Date(b.date_demande || 0) - new Date(a.date_demande || 0));
    const fmtDateRel = (v) => {
      if (!v) return '';
      const d = new Date(v);
      const jours = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (jours <= 0) return "Aujourd'hui";
      if (jours === 1) return 'Hier';
      return `Il y a ${jours} j`;
    };
    const fmtTel = (t) => {
      const c = String(t || '').replace(/\D/g, '');
      return c.length === 10 ? c.replace(/(\d{2})(?=\d)/g, '$1 ') : (t || '');
    };
    return (
      <div className="rtl">
        <div className="pd-vues">
          <button type="button" className={`pd-vue-btn${demandesVue === 'miennes' ? ' is-active' : ''}`}
            onClick={() => setDemandesVue('miennes')}>👤 Mes demandes</button>
          <button type="button" className={`pd-vue-btn${demandesVue === 'toutes' ? ' is-active' : ''}`}
            onClick={() => setDemandesVue('toutes')}>👥 Toutes</button>
        </div>
        {demandesPanel.erreur && <div className="rtl-empty">⚠️ {demandesPanel.erreur}</div>}
        {demandesPanel.charge && liste.length === 0 && <div className="rtl-empty">Chargement…</div>}
        {!demandesPanel.charge && !demandesPanel.erreur && liste.length === 0 && (
          <div className="rtl-empty">
            {demandesVue === 'miennes' ? 'Aucune demande en cours vous concernant' : 'Aucune demande atelier en cours'}
          </div>
        )}
        {liste.map(d => {
          const nomClient = [d.prenom, d.nom].filter(Boolean).join(' ') || d.client || 'Client';
          return (
            <div key={d.id} className="rtl-card pd-card" onClick={() => ouvrirDemande(d)}>
              <div className="rtl-row rtl-row--id">
                {/* Pas de picto de sujet : la table n'en couvre que 8 et
                    « Rendez-vous APV » (le sujet le plus frequent apres les
                    trois principaux) tombait sur le fallback 💬, illisible.
                    La synthese IA en dessous dit la demande en clair. */}
                <span className="rtl-card-name">{nomClient}</span>
                {d.immatriculation && <span className="rtl-immat">{d.immatriculation}</span>}
              </div>
              {d.synthese && (
                /* Synthese IA de la page Demandes clients : elle dit en une
                   ligne ce que veut le client, ce que le sujet seul ne dit pas.
                   Bornee a une ligne (ellipsis) — le panneau est etroit et la
                   carte tient en 2 lignes ; le texte entier reste en infobulle. */
                <div className="rtl-row pd-synthese" title={d.synthese}>{d.synthese}</div>
              )}
              <div className="rtl-row rtl-row--foot">
                <span className="rtl-timing">
                  {d.telephone ? (
                    <a className="rtl-tel pd-tel" href={`tel:${String(d.telephone).replace(/\D/g, '')}`}
                       onClick={(ev) => ev.stopPropagation()}>{fmtTel(d.telephone)}</a>
                  ) : null}
                  {d.telephone ? ' · ' : ''}{fmtDateRel(d.date_demande)}
                  {Number(d.nb_appels) > 1 ? ` · ${d.nb_appels} appels` : ''}
                </span>
                {demandesVue === 'toutes' && !d.responsable_id && <span className="pd-libre">À attribuer</span>}
                {/* Archiver sans ouvrir la demande : c'est le geste de sortie le
                    plus fréquent une fois le client rappelé. En pied de carte,
                    à droite, hors du flux du texte. */}
                <button
                  type="button"
                  className="pd-archiver"
                  title="Archiver cette demande"
                  onClick={(ev) => { ev.stopPropagation(); setDemandeAArchiver(d); }}
                >
                  ✅ Archiver
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Rendu de l'onglet 🔧 OR (panneau lateral). Meme densite que les autres
  // listes : identite en haut, l'essentiel en pied. Un OR planifie porte sa
  // date aux couleurs du statut atelier et ouvre le dossier au clic.
  const renderOrList = () => {
    const terme = orFiltre.trim().toLowerCase();
    const sansSep = (v) => String(v || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    const liste = !terme ? orPanel.liste : orPanel.liste.filter(o =>
      String(o.nom_client || '').toLowerCase().includes(terme)
      || String(o.num_or || '').toLowerCase().includes(terme)
      || sansSep(o.immat).includes(sansSep(terme))
    );
    const montant = (v) => (v == null ? null : Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €');
    // Date d'ouverture : « aujourd'hui » / « hier » pour les OR frais, sinon la
    // date courte — c'est ce qui rend l'ordre de la liste lisible.
    const ouvertureOr = (v) => {
      if (!v) return 'Date inconnue';
      const s = String(v).slice(0, 10);
      const jours = Math.round((Date.parse(`${formatDate(new Date())}T12:00:00`) - Date.parse(`${s}T12:00:00`)) / 86400000);
      if (jours <= 0) return "Ouvert aujourd'hui";
      if (jours === 1) return 'Ouvert hier';
      return `Ouvert le ${s.slice(8, 10)}/${s.slice(5, 7)}`;
    };
    return (
      <div className="rtl">
        <div className="pd-vues">
          <button type="button" className={`pd-vue-btn${orVue === 'miens' ? ' is-active' : ''}`}
            onClick={() => setOrVue('miens')}>👤 Mes OR ouverts</button>
          <button type="button" className={`pd-vue-btn${orVue === 'tous' ? ' is-active' : ''}`}
            onClick={() => setOrVue('tous')}>👥 Tous les OR</button>
        </div>
        {orPanel.liste.length >= 4 && (
          <input
            className="pa-filtre"
            type="search"
            value={orFiltre}
            onChange={(e) => setOrFiltre(e.target.value)}
            placeholder="Filtrer : client, immat, n° OR…"
          />
        )}
        {orPanel.erreur && <div className="rtl-empty">⚠️ {orPanel.erreur}</div>}
        {orPanel.charge && liste.length === 0 && <div className="rtl-empty">Chargement…</div>}
        {!orPanel.charge && !orPanel.erreur && liste.length === 0 && (
          <div className="rtl-empty">
            {orVue === 'miens'
              ? `Aucun OR ouvert au nom de ${user?.nom || 'ce compte'}`
              : 'Aucun OR ouvert'}
          </div>
        )}
        {/* L'API est bornee a 200 lignes : le dire plutot que de laisser croire
            que la liste est complete. */}
        {!orPanel.charge && orPanel.total > liste.length && !terme && (
          <div className="pa-or-total">{liste.length} affichés sur {orPanel.total}</div>
        )}
        {liste.map(o => (
          <div key={o.id} className="rtl-card pd-card" onClick={() => o.planning_intervention_id && ouvrirDossierDepuisAlerte(o.planning_intervention_id)}>
            <div className="rtl-row rtl-row--id">
              <span className="rtl-card-name">{o.nom_client || 'Client'}</span>
              {o.immat && <span className="rtl-immat">{o.immat}</span>}
            </div>
            <div className="rtl-row rtl-row--foot">
              <span className="rtl-timing">
                {/* Date d'OUVERTURE de l'OR : la liste est servie du plus recent
                    au plus ancien, mais sans cette date l'ordre est invisible
                    et on ne peut pas le verifier d'un coup d'oeil. */}
                {ouvertureOr(o.date_creation)}
                {o.num_or ? ` · OR ${o.num_or}` : ' · Sans OR'}
                {o.montant_ht != null && Number(o.montant_ht) > 0 ? ` · ${montant(o.montant_ht)}` : ''}
                {orVue === 'tous' && o.receptionnaire ? ` · ${o.receptionnaire}` : ''}
              </span>
              {o.date_planification && (
                <PlanificationCell dossier={o} onOuvrir={() => ouvrirDossierDepuisAlerte(o.planning_intervention_id)} />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Rendu de l'onglet 🔔 Notifications (panneau lateral).
  // La carte reprend la densite des deux autres listes : identite du dossier en
  // haut, l'evolution en clair au milieu, l'age et l'origine en pied. Le clic
  // ouvre le dossier concerne — c'est le geste attendu quand on apprend qu'une
  // piece est arrivee.
  const renderNotificationsList = () => {
    // Statuts stockes en cle technique : « recu_partiellement » n'est pas du
    // francais (meme table de correspondance que la vue Tableaux).
    // La nuance porte sur le statut ATTEINT, pas sur le type d'evenement :
    // une livraison complete et un retour en arriere ne se valent pas.
    const tonDe = (n) => {
      if (n.type === 'livraison') {
        if (n.nouveau_statut === 'livre') return 'pn-card--livre';
        if (n.nouveau_statut === 'recu_partiellement') return 'pn-card--partiel';
        return 'pn-card--retour';
      }
      if (n.type === 'anomalie_ouverte') return 'pn-card--anomalie';
      if (n.type === 'anomalie_resolue') return 'pn-card--resolue';
      return '';
    };
    // Date de planification du dossier : c'est elle qui declenche la decision
    // « les pieces sont la, mais il est prevu dans trois semaines — on avance ».
    // Comparaison en AAAA-MM-JJ, jamais via `new Date(iso)` : la chaine nue
    // serait lue en UTC et ferait basculer d'un jour en Guadeloupe.
    const aujourdhui = formatDate(new Date());
    const joursEntre = (a, b) => Math.round(
      (Date.parse(`${b}T12:00:00`) - Date.parse(`${a}T12:00:00`)) / 86400000
    );
    // Statuts sur lesquels il n'y a plus rien à reprogrammer : l'intervention
    // est faite, le dossier part en facturation. Sans ça, la ligne « prévu le …
    // il y a X j » s'allume en rouge sur un dossier terminé — mesuré en
    // production le 2026-08-14 : 185 cartes sur 1 042 dans ce cas. Afficher le
    // statut À CÔTÉ d'une alerte fausse ne ferait que déplacer la question.
    // « restitué » y figure par précaution : le serveur retire déjà ces
    // alertes, mais la règle ne doit pas dépendre de ce détour.
    const STATUTS_TERMINES = ['intervention terminée', 'à facturer', 'facturé', 'restitué'];
    const estTermine = (n) => STATUTS_TERMINES.includes((n.statut_nom || '').trim().toLowerCase());

    const prevuDe = (n) => {
      if (!n.date_intervention) return null;
      const ecart = joursEntre(aujourdhui, n.date_intervention);
      const jour = n.date_intervention.slice(8, 10) + '/' + n.date_intervention.slice(5, 7);
      const quand = ecart === 0 ? "aujourd'hui"
        : ecart === 1 ? 'demain'
        : ecart === -1 ? 'hier'
        : ecart > 0 ? `dans ${ecart} j`
        : `il y a ${-ecart} j`;
      // Trois situations, trois lectures : une date depassee veut dire que le
      // dossier a pris du retard ; une date lointaine ALORS QUE toutes les
      // pieces sont arrivees est l'occasion d'avancer le rendez-vous (avec des
      // pieces seulement partielles, avancer n'aurait aucun sens).
      const piecesCompletes = n.type === 'livraison' && n.nouveau_statut === 'livre';
      // Dossier terminé : la date reste affichée (elle situe le passage), mais
      // en neutre — ni retard à rattraper, ni rendez-vous à avancer.
      const termine = estTermine(n);
      const ton = termine ? ''
        : ecart < 0 ? 'pn-prevu--passe'
        : (ecart > 7 && piecesCompletes) ? 'pn-prevu--avancer'
        : '';
      const titre = termine ? `Dossier ${(n.statut_nom || '').toLowerCase()} — rien à reprogrammer`
        : ecart < 0 ? 'Le dossier est planifié à une date déjà passée'
        : (ecart > 7 && piecesCompletes) ? 'Toutes les pièces sont là et le dossier est loin — il peut être avancé'
        : 'Date de planification du dossier';
      return { texte: `Prévu le ${jour} · ${quand}`, ton, titre };
    };

    // « (STATUT) depuis le (DATE) » — l'état courant du dossier, à la couleur du
    // statut. La pastille, et non la carte entière : la couleur de la carte est
    // déjà prise par l'événement pièces (livré / partiel / retour / anomalie),
    // et les couleurs de statut ne tiennent pas en aplat — quatre statuts
    // d'attente partagent le même gris #9CA3AF, deux diags sont quasi noirs.
    // Le couple couleur + text_color, lui, est calibré pour un badge.
    const statutDe = (n) => {
      if (!n.statut_nom) return null;
      let depuis = '';
      if (n.statut_depuis) {
        const d = new Date(n.statut_depuis);
        if (!Number.isNaN(d.getTime())) {
          const jour = formatDate(d);
          // « ~ » = durée approchée, reprise de `updated_at` à la migration et
          // jamais mesurée. Même convention que la vue Tableaux.
          const tilde = n.statut_depuis_estime ? '~' : '';
          depuis = jour === aujourdhui
            ? ` depuis ${tilde}aujourd'hui`
            : ` depuis le ${tilde}${jour.slice(8, 10)}/${jour.slice(5, 7)}`
              + (jour.slice(0, 4) !== aujourdhui.slice(0, 4) ? `/${jour.slice(2, 4)}` : '');
        }
      }
      return {
        texte: `${n.statut_nom}${depuis}`,
        fond: n.statut_couleur || 'var(--bg-muted)',
        encre: n.statut_text_color || '#000000',
        titre: n.statut_depuis_estime
          ? "Statut du dossier. La date est approchée : elle vient de la dernière modification connue, avant l'horodatage des statuts."
          : 'Statut du dossier, et date à laquelle il y est passé',
      };
    };
    const age = (iso) => {
      if (!iso) return '';
      const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
      if (min < 1) return "à l'instant";
      if (min < 60) return `il y a ${min} min`;
      if (min < 60 * 24) return `il y a ${Math.floor(min / 60)} h`;
      return `il y a ${Math.floor(min / 1440)} j`;
    };
    const liste = notifsPanel.liste;
    return (
      <div className="rtl">
        {notifsPanel.erreur && <div className="rtl-empty">⚠️ {notifsPanel.erreur}</div>}
        {notifsPanel.charge && liste.length === 0 && <div className="rtl-empty">Chargement…</div>}
        {!notifsPanel.charge && !notifsPanel.erreur && liste.length === 0 && (
          <div className="rtl-empty">Rien de neuf sur les dossiers. Tout est à jour.</div>
        )}
        {liste.map(n => (
          <div
            key={`${n.source}-${n.id}`}
            className={`rtl-card pn-card ${tonDe(n)}`}
            onClick={() => n.intervention_id && ouvrirDossierDepuisAlerte(n.intervention_id)}
            title={n.intervention_id ? 'Ouvrir le dossier' : "Le dossier n'est plus au planning"}
          >
            {/* L'OR remplace l'immatriculation (2026-09-01) : c'est par lui
                qu'on retrouve un dossier partout ailleurs — carnet, BL,
                facturation, Datacar. Le détail de l'évolution a été retiré :
                le libellé le dit déjà. */}
            <div className="rtl-row rtl-row--id">
              <span className="rtl-card-name">{n.client_nom || 'Client'}</span>
              <span className="rtl-immat">{n.num_or ? `OR ${n.num_or}` : 'Sans OR'}</span>
            </div>
            <div className="rtl-row pn-evolution">
              {n.libelle}
              {n.reference_piece && <span className="pn-ref">{n.reference_piece}</span>}
            </div>
            {(() => {
              const prevu = prevuDe(n);
              if (!prevu) {
                return n.intervention_id ? null : (
                  <div className="rtl-row pn-prevu pn-prevu--absent">Dossier absent du planning</div>
                );
              }
              return <div className={`rtl-row pn-prevu ${prevu.ton}`} title={prevu.titre}>{prevu.texte}</div>;
            })()}
            {(() => {
              const st = statutDe(n);
              if (!st) return null;
              // Juste sous « Prévu le … » : les deux se lisent d'un coup, la
              // date de passage et l'état où en est le dossier.
              return (
                <div className="rtl-row pn-statut-row">
                  <span className="pn-statut" style={{ background: st.fond, color: st.encre }} title={st.titre}>
                    {st.texte}
                  </span>
                </div>
              );
            })()}
            <div className="rtl-row rtl-row--foot">
              {/* Le réceptionnaire du dossier : celui qui devra faire quelque
                  chose de cette nouveauté. Sans lui, chacun ouvrait les cartes
                  pour savoir si elles le concernaient. */}
              <span className="pn-recep">
                {n.receptionnaire_nom || <span className="pn-recep--vide">Sans réceptionnaire</span>}
              </span>
              {/* Meme geste que « Deja recu ✓ » du panneau Reception. Le libelle
                  dit ce que fait le bouton : la carte part pour TOUTE l'equipe. */}
              <button
                type="button"
                className="pn-vu"
                disabled={notifBusy === n.id}
                title="J'ai vu — retire la carte pour vous seulement"
                onClick={(ev) => { ev.stopPropagation(); marquerNotifVue(n); }}
              >
                {notifBusy === n.id ? '…' : 'Vu ✓'}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderReceptionList = () => {
    // « prise_rdv » est le motif borne « Je n'ai pas rendez-vous » : afficher
    // « Sans RDV » — le client n'a pas forcément un rendez-vous à prendre.
    const motifLabels = { rdv: '📅 RDV', prise_rdv: '🙋 Sans RDV', info: 'ℹ️ Info', recuperation: '🚗 Récupération' };
    const todayStr = formatDate(selectedDate);

    // ── Marque du véhicule (concessions multimarques, opt-in) ──────────────
    // Chez Soguava les réceptionnaires se répartissent le comptoir par marque ;
    // rien dans la file ne la donnait. Chez une mono-marque au contraire, la
    // pastille serait identique partout (mesuré : 99,1 % de Peugeot chez BLUE)
    // et volerait de la place au nom du client — d'où le drapeau par concession.
    const marqueInfo = (nom) => {
      const n = String(nom || '').trim();
      if (!n) return null;
      const ref = marquesRef.find(m => String(m.nom).trim().toLowerCase() === n.toLowerCase());
      return { nom: n, logo_url: ref?.logo_url || null, couleur: ref?.couleur || null };
    };
    // Une entrée de file porte `marque_effective` (dossier pour les RDV, saisie
    // borne sinon) ; une carte « pas encore arrivé » n'existe que par son
    // dossier, donc la marque vient de l'intervention.
    const marqueDeFile = (e) => marqueInfo(e.marque_effective);
    const marqueDeRdv = (r) => marqueInfo(r._intervention?.marque);
    const passeFiltreMarque = (nom) => {
      if (!filtreMarque) return true;
      if (filtreMarque === '__vide__') return !nom;
      return String(nom || '').toLowerCase() === filtreMarque.toLowerCase();
    };
    // ── File de réception = PARTAGÉE (2026-09-10) ────────────────────────────
    // La file des sans-RDV n'est filtrée NI par atelier NI par réceptionnaire :
    // les réceptionnaires travaillent tous ateliers confondus, et un client sans
    // RDV est « à recevoir par n'importe qui ». Le filtre atelier (retiré ici)
    // masquait à tort les arrivées rattachées à un autre atelier via
    // intervention → mécanicien ; le filtre par avatar cachait les sans-RDV pris
    // en charge par un collègue. Désormais le SEUL filtre du bloc de droite est
    // l'avatar, et il ne s'applique qu'aux CARTES DE RDV (colonnes par
    // réceptionnaire) — pas à la file, qui reste intégralement visible.
    // Sans `reception.file_attente`, la file reste vide : le rôle voit le
    // planning de réception sans avoir à traiter les arrivées.
    const fileSource = peut('reception.file_attente') ? fileAttente : [];
    const fileHorsMarque = fileSource;
    const filteredFile = fileHorsMarque
      .filter(f => !marqueFileActive || passeFiltreMarque(f.marque_effective));
    // Heure de RDV d'une entrée de file, via son dossier (les shims d'allRdvs
    // sont bâtis sur heure_reception : même source, disponible plus tôt).
    // ⚠️ `heure_reception` SEULE, jamais `heure_debut` en repli (04/09) : sur un
    // écran de réception, l'heure de l'atelier n'est pas une heure de
    // rendez-vous client. Un client reçu à 08:00 s'affichait à 11:15, l'heure
    // du mécanicien. Sans heure de réception, la ligne se classe à l'arrivée
    // réelle — ce qui est bien ce qu'on sait d'elle.
    const heureRdvDe = (f) => {
      if (!f.intervention_id) return null;
      const i = interventions.find(x => x.id === f.intervention_id);
      return i?.heure_reception || null;
    };
    // Ordre de TRAVAIL du réceptionnaire : par heure de RDV croissante, les
    // sans-RDV ensuite par ordre d'arrivée. C'est un choix distinct de l'ordre
    // d'APPEL de l'écran de salle (serveur, /affichage), qui reste inchangé.
    const enAttente = filteredFile.filter(f => f.statut === 'en_attente').sort((a, b) => {
      const ha = heureRdvDe(a), hb = heureRdvDe(b);
      if (ha && hb) return ha.localeCompare(hb) || (new Date(a.heure_arrivee) - new Date(b.heure_arrivee));
      if (ha || hb) return ha ? -1 : 1;
      return new Date(a.heure_arrivee) - new Date(b.heure_arrivee);
    });
    const enCours = filteredFile.filter(f => f.statut === 'en_cours').sort((a, b) => new Date(a.heure_prise_en_charge) - new Date(b.heure_prise_en_charge));
    const termines = filteredFile.filter(f => f.statut === 'termine').sort((a, b) => new Date(b.heure_prise_en_charge || b.heure_arrivee) - new Date(a.heure_prise_en_charge || a.heure_arrivee));

    // ── « Déjà reçu » : sort de la file « à recevoir » ───────────────────────
    // Un dossier reste dans la file de réception TANT qu'il n'est pas reçu :
    // statut atelier absent, ou « Dossier préparé » (pré-arrivée). Dès
    // « Réceptionné » — et tout statut suivant (En intervention, … À facturer,
    // Restitué) — le client est déjà passé au comptoir. Le laisser en « pas
    // encore arrivé » était faux, surtout après un DÉPLACEMENT de l'intervention
    // à un autre jour : la réception réelle a déjà eu lieu (demande 2026-09-12,
    // dossier 006019456). Les sections de présence réelle au comptoir (file
    // d'attente : en attente / en cours / terminé) ne sont PAS filtrées : elles
    // reflètent qui est physiquement là aujourd'hui.
    const dejaRecu = estDejaRecu; // helper partagé (défini au niveau du composant)

    const allRecep = staffReceptionnaires;
    const displayedRecep = focusedReceptionnaire ? allRecep.filter(r => r.id === focusedReceptionnaire) : allRecep;
    const allRdvs = [];
    displayedRecep.forEach(recep => {
      const rdvExplicites = rdvReception.filter(r => r.poste === recep.id && r.date === todayStr);
      const rdvIntIds = new Set(rdvExplicites.map(r => r.intervention_id).filter(Boolean));
      const intSansRdv = interventions
        .filter(i => i.poste_reception === recep.id && i.date === todayStr && !rdvIntIds.has(i.id) && i.heure_reception && !i.is_complement)
        .map(i => ({ id: `int-${i.id}`, heure: i.heure_reception, client: i.client, client_nom: i.client_nom, client_prenom: i.client_prenom, immat: i.immat || i.immatriculation, intervention_id: i.id, _fromIntervention: true }));
      [...rdvExplicites, ...intSansRdv].forEach(r => {
        const intv = r._fromIntervention ? interventions.find(i => i.id === r.intervention_id) : interventions.find(i => i.id === r.intervention_id);
        // Déjà reçu → il n'a plus rien à faire dans la file « à recevoir ».
        if (dejaRecu(intv)) return;
        allRdvs.push({ ...r, _intervention: intv, _recep: recep });
      });
    });

    // Dossiers du jour SANS réceptionnaire : la boucle ci-dessus parcourt les
    // fiches, donc elle ne pouvait pas les voir — ils n'apparaissaient dans
    // AUCUNE colonne ni carte, alors qu'ils pèsent 20 à 50 % des dossiers
    // (import Mécaplanning : noms non appariés). Ils rejoignent la liste avec
    // un marqueur, et restent visibles même en vue filtrée par avatar : mieux
    // vaut les voir deux fois que les perdre (même parti pris que la file).
    interventions
      .filter(i => !i.poste_reception && i.date === todayStr && i.heure_reception && !i.is_complement && !dejaRecu(i))
      .forEach(i => {
        allRdvs.push({
          id: `na-${i.id}`, heure: i.heure_reception, client: i.client,
          client_nom: i.client_nom, client_prenom: i.client_prenom,
          immat: i.immat || i.immatriculation, intervention_id: i.id,
          _fromIntervention: true, _intervention: i, _recep: null, _nonAffecte: true,
        });
      });

    const fileIntIds = new Set(fileAttente.map(f => f.intervention_id).filter(Boolean));
    const now = new Date();
    const aVenir = allRdvs.filter(r => {
      if (fileIntIds.has(r.intervention_id)) return false;
      const [h] = (r.heure || '').split(':').map(Number);
      if (isNaN(h)) return false;
      if (marqueFileActive && !passeFiltreMarque(r._intervention?.marque)) return false;
      // Plus de filtre atelier (2026-09-10) : le bloc de réception est partagé,
      // seul l'avatar (déjà appliqué via displayedRecep) filtre les RDV.
      return true;
    }).sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));

    const enrichFile = (entry) => {
      const intv = entry.intervention_id ? interventions.find(i => i.id === entry.intervention_id) : null;
      const rdv = entry.intervention_id ? allRdvs.find(r => r.intervention_id === entry.intervention_id) : null;
      return { ...entry, _intervention: intv, _rdvHeure: rdv?.heure || heureRdvDe(entry) };
    };

    const fmtHM = (ts) => ts ? new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
    // Numéro par paires (06 90 12 34 56) — le réceptionnaire doit pouvoir
    // rappeler un client enregistré qui ne se présente pas au comptoir.
    const fmtTel = (t) => {
      const c = String(t || '').replace(/\D/g, '');
      return c.length === 10 ? c.replace(/(\d{2})(?=\d)/g, '$1 ') : (t || '');
    };

    // ── Clients reçus qui PATIENTENT SUR PLACE ────────────────────────────
    // Le client a laissé sa voiture mais reste au comptoir : c'est le dossier
    // qu'il ne faut surtout pas oublier, et rien ne le signalait côté Réception
    // (seul un picto de 16 px sur la carte du planning le portait).
    // « Réceptionné » est vrai dès que le client est arrivé, par l'un ou l'autre
    // des signaux disponibles — toutes les concessions n'utilisent pas la file
    // d'attente, et le statut atelier n'est pas toujours renseigné :
    //   passage en file d'attente pris en charge ou terminé,
    //   OU statut atelier posé (le véhicule est pris en charge),

    // ── Barre de filtres par marque ────────────────────────────────────────
    // Ne liste QUE les marques réellement présentes dans la file du jour : un
    // filtre qui ne renvoie rien est une fausse piste, et le référentiel de
    // Soguava compte 14 marques pour 6 vraiment vues au comptoir.
    const comptesMarque = new Map();
    let sansMarque = 0;
    if (marqueFileActive) {
      fileHorsMarque.forEach(f => {
        const n = String(f.marque_effective || '').trim();
        if (!n) { sansMarque += 1; return; }
        comptesMarque.set(n, (comptesMarque.get(n) || 0) + 1);
      });
    }
    const marquesPresentes = [...comptesMarque.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

    return (
      <div className="rtl">
        {marqueFileActive && (marquesPresentes.length > 1 || (marquesPresentes.length === 1 && sansMarque > 0)) && (
          <div className="rtl-marques-bar">
            <span className="rtl-marques-lab">MARQUE</span>
            {marquesPresentes.map(([nom, n]) => {
              const info = marqueInfo(nom);
              const actif = filtreMarque.toLowerCase() === nom.toLowerCase();
              return (
                <button
                  key={nom}
                  className={`rtl-marque-chip${actif ? ' rtl-marque-chip--on' : ''}`}
                  title={nom}
                  onClick={() => setFiltreMarque(actif ? '' : nom)}
                >
                  <BadgeMarque nom={info.nom} logoUrl={info.logo_url} couleur={info.couleur} taille={18} />
                  {n}
                </button>
              );
            })}
            {sansMarque > 0 && (
              <button
                className={`rtl-marque-chip${filtreMarque === '__vide__' ? ' rtl-marque-chip--on' : ''}`}
                title="Marque non renseignée"
                onClick={() => setFiltreMarque(filtreMarque === '__vide__' ? '' : '__vide__')}
              >
                <BadgeMarque placeholder taille={18} />
                {sansMarque}
              </button>
            )}
            {filtreMarque && (
              <button className="rtl-marque-clear" onClick={() => setFiltreMarque('')}>Tout voir</button>
            )}
          </div>
        )}
        {/* BLOC « patiente sur place » retiré le 2026-08-10 à la demande du
            métier — priorité à la densité sur les petits écrans des
            réceptionnaires. Les aides `patientent`/`heureRendu`/`estArrive`
            partent avec lui ; à récupérer dans l'historique si on le remet. */}
        {/* BLOC 1 : EN ATTENTE */}
        <div className="rtl-section-header rtl-section-header--attente">CLIENT ARRIVÉ, À RECEVOIR {enAttente.length > 0 && <span>· {enAttente.length}</span>}</div>
        {enAttente.length === 0 && <div className="rtl-empty">Aucun client en attente</div>}
        {enAttente.map(f => {
          const e = enrichFile(f);
          const mins = minutesSince(e.heure_arrivee);
          const isUrgent = mins >= 15;
          const waitClass = isUrgent ? 'rtl-urgent' : mins >= 5 ? 'rtl-warning' : '';
          return (
            <div
              key={e.id}
              /* Cliquable pour ouvrir le dossier, comme les cartes « pas encore
                 arrivé » — mais seulement quand une intervention est rattachée :
                 un client venu sans rendez-vous n'a pas de dossier à ouvrir. */
              className={`rtl-card rtl-card--attente ${waitClass}${e._intervention ? ' rtl-card--cliquable' : ''}`}
              title={e._intervention ? 'Ouvrir le dossier' : undefined}
              onClick={e._intervention ? () => handleEditIntervention(e._intervention) : undefined}
            >
              <div className="rtl-row rtl-row--id">
                {marqueFileActive && (() => {
                  const m = marqueDeFile(e);
                  return <BadgeMarque nom={m?.nom} logoUrl={m?.logo_url} couleur={m?.couleur} placeholder />;
                })()}
                {/* Gabarit 2026-09-07 (demande utilisateur) : la plaque en haut
                    à droite sur la ligne du nom, le téléphone seul dessous,
                    calé à gauche — le numéro ne tronque plus le nom (le badge
                    plaque, lui, est court et à largeur quasi fixe). */}
                <span className="rtl-card-name">{e.nom || 'Client'}</span>
                {e.immatriculation && <span className="rtl-immat">{e.immatriculation}</span>}
              </div>
              {(e.telephone || atelierNomDe(e._intervention)) && (
                <div className="rtl-row rtl-row--refs">
                  {e.telephone && (
                    <a className="rtl-tel" href={`tel:${String(e.telephone).replace(/\D/g, '')}`}
                       onClick={(ev) => ev.stopPropagation()}>{fmtTel(e.telephone)}</a>
                  )}
                  {atelierNomDe(e._intervention) && <span className="rtl-atelier">{atelierNomDe(e._intervention)}</span>}
                </div>
              )}
              <div className="rtl-row rtl-row--foot">
                <span className={`rtl-timing ${waitClass}`}>{e._rdvHeure ? `📅 RDV à ${e._rdvHeure}` : (motifLabels[e.motif] || e.motif)} · Arrivé {fmtHM(e.heure_arrivee)} · ⏱ {mins} min</span>
                {/* Plus de variante rouge : elle se déclenchait très vite et
                    saturait la colonne. L'urgence se lit déjà dans le compteur
                    de minutes, juste à gauche. */}
                <button
                  className="rtl-btn-recevoir"
                  onClick={(ev) => { ev.stopPropagation(); ouvrirRecevoir(e); }}
                >Recevoir →</button>
              </div>
            </div>
          );
        })}

        {/* BLOC 2 : EN COURS */}
        <div className="rtl-section-header rtl-section-header--encours">RÉCEPTION DU CLIENT EN COURS {enCours.length > 0 && <span>· {enCours.length}</span>}</div>
        {enCours.length === 0 && <div className="rtl-empty">Aucun client en cours</div>}
        {enCours.map(f => {
          const e = enrichFile(f);
          const mins = minutesSince(e.heure_prise_en_charge);
          const recepName = e.receptionnaire_prenom ? `${e.receptionnaire_prenom} ${e.receptionnaire_nom}` : e.receptionnaire_nom;
          return (
            <div
              key={e.id}
              className={`rtl-card rtl-card--encours${e._intervention ? ' rtl-card--cliquable' : ''}`}
              title={e._intervention ? 'Ouvrir le dossier' : undefined}
              onClick={e._intervention ? () => handleEditIntervention(e._intervention) : undefined}
            >
              <div className="rtl-row rtl-row--id">
                {marqueFileActive && (() => {
                  const m = marqueDeFile(e);
                  return <BadgeMarque nom={m?.nom} logoUrl={m?.logo_url} couleur={m?.couleur} placeholder />;
                })()}
                <span className="rtl-card-name">{e.nom || 'Client'}</span>
                {e.immatriculation && <span className="rtl-immat">{e.immatriculation}</span>}
              </div>
              {(e.telephone || atelierNomDe(e._intervention)) && (
                <div className="rtl-row rtl-row--refs">
                  {e.telephone && (
                    <a className="rtl-tel" href={`tel:${String(e.telephone).replace(/\D/g, '')}`}
                       onClick={(ev) => ev.stopPropagation()}>{fmtTel(e.telephone)}</a>
                  )}
                  {atelierNomDe(e._intervention) && <span className="rtl-atelier">{atelierNomDe(e._intervention)}</span>}
                </div>
              )}
              <div className="rtl-row rtl-row--foot">
                <span className="rtl-timing">{motifLabels[e.motif] || e.motif} · Reçu {fmtHM(e.heure_prise_en_charge)} · ⏱ {mins} min{recepName ? ` · ${recepName}` : ''}</span>
                <button
                  className="rtl-btn-done"
                  onClick={async (ev) => {
                    ev.stopPropagation();
                    await api.put(`/planning/file-attente/${e.id}`, { statut: 'termine' });
                    refreshFileAttente();
                  }}
                >Terminé ✓</button>
              </div>
            </div>
          );
        })}

        {/* BLOC 3 : À VENIR */}
        <div className="rtl-section-header rtl-section-header--avenir">CLIENT PAS ENCORE ARRIVÉ</div>
        {aVenir.length === 0 ? (
          <div className="rtl-empty">Aucun RDV à venir</div>
        ) : aVenir.map(r => {
          const intv = r._intervention;
          const [hh, mm] = (r.heure || '').split(':').map(Number);
          const rdvTime = new Date(selectedDate); rdvTime.setHours(hh, mm || 0, 0, 0);
          const diffMin = Math.round((rdvTime - now) / 60000);
          const isLate = diffMin < 0;
          const absDiff = Math.abs(diffMin);
          const dansLabel = isLate
            ? (absDiff >= 60 ? `en retard de ${Math.floor(absDiff / 60)}h${absDiff % 60 > 0 ? String(absDiff % 60).padStart(2, '0') : ''}` : `en retard de ${absDiff} min`)
            : (absDiff >= 60 ? `dans ${Math.floor(absDiff / 60)}h${absDiff % 60 > 0 ? String(absDiff % 60).padStart(2, '0') : ''}` : `dans ${absDiff} min`);
          const clientLabel = r.client || [r.client_prenom, r.client_nom].filter(Boolean).join(' ') || '—';
          // Pas encore d'entrée de file : le numéro vient du RDV, sinon du dossier.
          const telAvenir = r.client_tel || intv?.client_tel || null;
          const forceRecu = async (ev) => {
            ev.stopPropagation();
            if (!window.confirm(`Marquer ce RDV (${clientLabel}${r.heure ? ' · ' + r.heure : ''}) comme déjà reçu ?\nIl sera retiré du flux de réception et ajouté aux clients déjà reçus.`)) return;
            try {
              await api.post('/planning/file-attente', {
                nom: clientLabel,
                immatriculation: r.immat || r.immatriculation || null,
                motif: 'rdv',
                intervention_id: r.intervention_id || null,
                statut: 'termine',
              });
              refreshFileAttente();
            } catch (err) {
              console.error('[file-attente force termine]', err);
              window.alert('Impossible de marquer ce RDV comme reçu.');
            }
          };
          // Affectation en un geste : le nom du réceptionnaire est souvent dans
          // le commentaire du dossier (« Réceptionnaire: … ») sans fiche en face.
          const affecter = async (ev) => {
            const staffId = Number(ev.target.value);
            ev.stopPropagation();
            if (!staffId) return;
            try {
              await api.put(`/planning/interventions/${r.intervention_id}`, { poste_reception: staffId });
              await loadData();
            } catch (err) {
              console.error('[affectation receptionnaire]', err);
              window.alert("Impossible d'affecter ce dossier.");
            }
          };
          const nomMeca = (intv?.commentaire || '').match(/Réceptionnaire\s*:\s*([^\n|]+)/);
          return (
            <div
              key={r.id}
              className={`rtl-card rtl-card--avenir${isLate ? ' rtl-card--retard' : ''}${r._nonAffecte ? ' rtl-card--nonaffecte' : ''}`}
              onClick={() => handleEditReception(r)}
            >
              <div className="rtl-row rtl-row--id">
                {marqueFileActive && (() => {
                  const m = marqueDeRdv(r);
                  return <BadgeMarque nom={m?.nom} logoUrl={m?.logo_url} couleur={m?.couleur} placeholder />;
                })()}
                <span className="rtl-card-name">{clientLabel}</span>
                {(r.immat || r.immatriculation) && <span className="rtl-immat">{r.immat || r.immatriculation}</span>}
              </div>
              {(telAvenir || atelierNomDe(intv)) && (
                <div className="rtl-row rtl-row--refs">
                  {telAvenir && (
                    <a className="rtl-tel" href={`tel:${String(telAvenir).replace(/\D/g, '')}`}
                       onClick={(ev) => ev.stopPropagation()}>{fmtTel(telAvenir)}</a>
                  )}
                  {atelierNomDe(intv) && <span className="rtl-atelier">{atelierNomDe(intv)}</span>}
                </div>
              )}
              <div className="rtl-row rtl-row--foot">
                <span className="rtl-timing">
                  RDV à {r.heure || '—'} · {isLate ? <span className="rtl-duree-retard">{dansLabel}</span> : dansLabel}
                  {r._nonAffecte && nomMeca && (
                    <span className="rtl-nonaffecte-src" title={`Réceptionnaire indiqué par Mécaplanning : ${nomMeca[1].trim()}`}> · {nomMeca[1].trim()}</span>
                  )}
                </span>
                {r._nonAffecte ? (
                  <select
                    className="rtl-affecter"
                    defaultValue=""
                    onClick={(ev) => ev.stopPropagation()}
                    onChange={affecter}
                    title="Ce dossier n'a pas de réceptionnaire — l'affecter"
                  >
                    <option value="" disabled>À affecter…</option>
                    {staffReceptionnaires.map(sr => (
                      <option key={sr.id} value={sr.id}>{getStaffLabel(sr)}</option>
                    ))}
                  </select>
                ) : (
                  <button
                    className="rtl-btn-force-recu"
                    onClick={forceRecu}
                    title="Marquer ce RDV comme déjà reçu (client venu en avance, hors flux normal)"
                  >Déjà reçu ✓</button>
                )}
              </div>
            </div>
          );
        })}

        {/* BLOC 4 : DÉJÀ REÇUS */}
        {termines.length > 0 && (
          <>
            <div className="rtl-section-header rtl-section-header--termines">
              DÉJÀ REÇUS · {termines.length}
              <button className="rtl-toggle-termines" onClick={() => setShowTermines(v => !v)}>{showTermines ? 'Masquer ▲' : 'Afficher ▼'}</button>
            </div>
            {showTermines && termines.map(f => {
              const e = enrichFile(f);
              const dureeRecep = (e.heure_arrivee && e.heure_prise_en_charge)
                ? Math.max(0, Math.round((new Date(e.heure_prise_en_charge) - new Date(e.heure_arrivee)) / 60000))
                : null;
              return (
                <div
                  key={e.id}
                  className="rtl-card rtl-card--termine"
                >
                  <div className="rtl-row rtl-row--heure" style={{ textDecoration: 'line-through' }}>
                    {e._rdvHeure || '—'} <span className="rtl-termine-check">✓</span>
                  </div>
                  <div className="rtl-row rtl-row--id">
                    {marqueFileActive && (() => {
                      const m = marqueDeFile(e);
                      return <BadgeMarque nom={m?.nom} logoUrl={m?.logo_url} couleur={m?.couleur} placeholder />;
                    })()}
                    <span className="rtl-card-name">{e.nom || 'Client'}</span>
                    {e.immatriculation && <span className="rtl-immat">{e.immatriculation}</span>}
                  </div>
                      <div className="rtl-row rtl-row--foot">
                    <span className="rtl-timing">Arrivé {fmtHM(e.heure_arrivee)} · Reçu {fmtHM(e.heure_prise_en_charge)}{dureeRecep !== null ? ` · ⏱ ${dureeRecep} min` : ''}</span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  // Mini-calendrier mensuel de charge atelier (onglet 'calendrier' du side-block).
  // Métrique reprise de la vue mécaniciens : somme des durées d'interventions confirmées
  // du jour ÷ (8h × nombre de mécaniciens présents — actifs et non absents).
  // Couleurs alignées sur les barres de charge existantes (seuils 75 % / 100 %).
  // Bloc « À planifier » : véhicules sans RDV (dépannage/remorquage) à glisser sur la grille.
  const renderAPlanifier = () => {
    const listeFiltree = filtrerAPlanifier(aPlanifier, filtreAPlanifier);
    return (
    <div className="pa-section">
      <div className="pa-section-title">
        🪝 À planifier{aPlanifier.length > 0 && <span className="pa-count">{aPlanifier.length}</span>}
      </div>
      {/* Recherche : au-delà de 3-4 véhicules, retrouver le bon dossier à l'œil
          devient le geste lent. Filtre local, aucun appel serveur. */}
      {aPlanifier.length > 3 && (
        <div className="pa-filtre">
          <input
            type="text"
            className="pa-filtre-input"
            placeholder="🔍 Nom, immat ou OR…"
            value={filtreAPlanifier}
            onChange={(e) => setFiltreAPlanifier(e.target.value)}
          />
          {filtreAPlanifier && (
            <button type="button" className="pa-filtre-clear" onClick={() => setFiltreAPlanifier('')} title="Effacer">✕</button>
          )}
        </div>
      )}
      {aPlanifier.length === 0 ? (
        <div className="pa-empty">Aucun véhicule à planifier</div>
      ) : listeFiltree.length === 0 ? (
        <div className="pa-empty">Aucun dossier ne correspond à « {filtreAPlanifier} ».</div>
      ) : (
        <div className="pa-list">
          {listeFiltree.map(item => (
            <div
              key={item.id}
              className="pa-card"
              draggable={!lectureSeule}
              onDragStart={(e) => handleDragStart(e, item, 'a_planifier')}
              onDragEnd={handleDragEnd}
              onClick={() => handleEditIntervention(item)}
              title="Cliquer pour ouvrir le dossier · glisser sur un mécanicien pour planifier"
            >
              <div className="pa-card-head">
                <span className="pa-card-icon">🪝</span>
                <span className="pa-card-immat">{item.immatriculation || '—'}</span>
                <span className="pa-card-grip">⠿</span>
              </div>
              <div className="pa-card-meta">
                {item.type_intervention || 'Sans RDV'}
                {item.commentaire ? ` · ${item.commentaire.replace('Dépanneur : ', '')}` : ''}
                {item.client_nom ? ` · ${item.client_nom}` : ''}
              </div>
              {/* Auteur du dossier : savoir a qui s'adresser avant de programmer
                  un vehicule qu'on n'a pas recu soi-meme. Repli sur l'identifiant
                  de connexion quand le nom n'est pas renseigne. */}
              {(item.created_by_nom || item.created_by_username) && (
                <div className="pa-card-auteur">Dossier ouvert par {item.created_by_nom || item.created_by_username}</div>
              )}
              {item.heure_reception && <div className="pa-card-time">Arrivé {item.heure_reception}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  // ── Confirmation d'un RDV en ligne : ouvre la modale d'intervention en mode confirmation
  //    (pré-remplie, avec aperçus de disponibilité mécano + réception). ──
  const openConfirmRdv = (rdv) => setConfirmOnlineRdv(rdv);

  // Bloc « RDV à confirmer » : demandes de RDV en ligne en attente de validation.
  // Un clic ouvre la modale de confirmation (affecter réceptionnaire + mécanicien) sans quitter le planning.
  const renderRdvAConfirmer = () => (
    <div className="pa-section">
      <div className="pa-section-title">
        📥 RDV à confirmer{rdvOnlineList.length > 0 && <span className="pa-count">{rdvOnlineList.length}</span>}
      </div>
      {rdvOnlineList.length === 0 ? (
        <div className="pa-empty">Aucun RDV à confirmer</div>
      ) : (
        <div className="pa-list">
          {rdvOnlineList.map(rdv => {
            const dateLabel = rdv.date_souhaitee
              ? formatDateFr(rdv.date_souhaitee, { weekday: 'short', day: '2-digit', month: '2-digit' })
              : '—';
            const heureLabel = (rdv.heure_souhaitee || '').substring(0, 5);
            const vehicule = [rdv.marque, rdv.modele].filter(Boolean).join(' ');
            const client = [rdv.client_nom, rdv.client_prenom].filter(Boolean).join(' ');
            return (
              <div
                key={rdv.id}
                className="pa-card"
                onClick={() => openConfirmRdv(rdv)}
                title="Cliquer pour confirmer ce RDV (affecter un réceptionnaire et un mécanicien)"
                style={{ cursor: 'pointer' }}
              >
                <div className="pa-card-head">
                  <span className="pa-card-icon" title={rdvOnlineSource(rdv.source).label}>{rdvOnlineSource(rdv.source).icon}</span>
                  <span className="pa-card-immat">{rdv.immatriculation || vehicule || '—'}</span>
                  <span className="pa-card-grip">✅</span>
                </div>
                <div className="pa-card-meta">
                  {dateLabel}{heureLabel ? ` · ${heureLabel}` : ''}
                  {client ? ` · ${client}` : ''}
                </div>
                {rdv.source === 'remorqueur' && rdv.remorqueur_entreprise
                  ? <div className="pa-card-time">🚛 {rdv.remorqueur_entreprise}{rdv.remorqueur_assureur ? ` · ${rdv.remorqueur_assureur}` : ''}</div>
                  : (vehicule && rdv.immatriculation && <div className="pa-card-time">{vehicule}</div>)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderChargeCalendar = () => {
    const monthLabel = chargeMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    const year = chargeMonth.getFullYear();
    const month = chargeMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    // Lundi = 0, Dimanche = 6 (semaine ISO)
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayStr = formatDate(new Date());
    const selectedStr = formatDate(selectedDate);

    // Construit la grille 6 semaines × 7 jours, en partant du lundi de la 1ère semaine
    const cells = [];
    const gridStart = new Date(year, month, 1 - firstWeekday);
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    // Tronque les semaines vides en fin de mois (5 semaines suffisent souvent)
    const lastNeededIndex = firstWeekday + daysInMonth - 1;
    const weekCount = Math.ceil((lastNeededIndex + 1) / 7);
    const visibleCells = cells.slice(0, weekCount * 7);

    // Colonnes du calendrier : on ne garde que les jours ouverts par au moins
    // un atelier retenu. Un samedi et un dimanche fermes font passer la grille
    // de 7 a 5 colonnes — il ne reste pas de colonne vide a parcourir des yeux.
    const colonnes = [1, 2, 3, 4, 5, 6, 7].filter(j => joursOuvertsSet.has(j));
    const LIB_COLONNE = { 1: 'L', 2: 'M', 3: 'M', 4: 'J', 5: 'V', 6: 'S', 7: 'D' };
    const cellulesVisibles = visibleCells.filter(d => joursOuvertsSet.has(d.getDay() === 0 ? 7 : d.getDay()));

    // Taux du jour, lu dans la charge calculée par le serveur.
    //
    // Deux changements de fond par rapport au calcul précédent, qui expliquaient
    // à eux deux le « tout reste vert » :
    //  1. les heures viennent du MOIS entier et non des ±14 jours chargés à
    //     l'écran — au-delà de cette fenêtre, tout comptait zéro ;
    //  2. la capacité vient des VRAIES amplitudes d'atelier moins les pauses
    //     configurées, et non d'un forfait de 8 h par mécanicien. Chez BLUE,
    //     Service Rapide Soir ouvre 11:00–18:30 : 7,25 h utiles, pas 8.
    //
    // Sans filtre d'atelier, la couleur suit l'atelier LE PLUS CHARGÉ et non la
    // moyenne : le 10/08, Service Rapide Soir est à 89 % quand l'ensemble est à
    // 63 %. Moyenner faisait disparaître l'atelier saturé — c'est-à-dire la
    // seule information qu'on cherche dans ce calendrier.
    const computeDayLoad = (date) => {
      const dayStr = formatDate(date);
      const j = chargeMois?.jours?.[dayStr];
      if (!j) return { pct: null, hours: 0, capacity: 0, closed: !estJourOuvert(date), detail: [] };
      if (j.ferme) return { pct: null, hours: 0, capacity: 0, closed: true, detail: [] };

      if (selectedAtelierId) {
        const a = (j.detail || []).find(x => String(x.atelier_id) === String(selectedAtelierId));
        if (!a) return { pct: null, hours: 0, capacity: 0, closed: false, detail: [] };
        return { pct: a.pct, hours: a.heures, capacity: a.capacite, closed: false, detail: [a] };
      }
      return {
        pct: j.pct_max, atelierMax: j.atelier_max,
        hours: j.heures, capacity: j.capacite, pctGlobal: j.pct,
        closed: false, detail: j.detail || [],
      };
    };

    const goPrevMonth = () => setChargeMonth(new Date(year, month - 1, 1));
    const goNextMonth = () => setChargeMonth(new Date(year, month + 1, 1));
    const goToday = () => setChargeMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

    return (
      <div className="psc-calendar">
        <div className="psc-nav">
          <button type="button" className="psc-nav-btn" onClick={goPrevMonth} title="Mois précédent">◀</button>
          <button type="button" className="psc-nav-label" onClick={goToday} title="Revenir au mois courant">
            {monthLabel}
          </button>
          <button type="button" className="psc-nav-btn" onClick={goNextMonth} title="Mois suivant">▶</button>
        </div>
        <div className="psc-weekday-header" style={{ gridTemplateColumns: `repeat(${colonnes.length}, 1fr)` }}>
          {colonnes.map(j => (
            <div key={j} className="psc-weekday">{LIB_COLONNE[j]}</div>
          ))}
        </div>
        <div className="psc-grid" style={{ gridTemplateColumns: `repeat(${colonnes.length}, 1fr)` }}>
          {cellulesVisibles.map((d, idx) => {
            const dayStr = formatDate(d);
            const inMonth = d.getMonth() === month;
            const { pct, hours, capacity, closed, detail, atelierMax, pctGlobal } = computeDayLoad(d);
            let colorClass = 'psc-day--neutral';
            if (pct !== null) {
              if (pct > 1) colorClass = 'psc-day--red';
              else if (pct > 0.75) colorClass = 'psc-day--orange';
              else colorClass = 'psc-day--green';
            }
            const classes = [
              'psc-day',
              colorClass,
              !inMonth && 'psc-day--outside',
              dayStr === todayStr && 'psc-day--today',
              dayStr === selectedStr && 'psc-day--selected',
              closed && 'psc-day--closed',
            ].filter(Boolean).join(' ');
            // L'infobulle détaille atelier par atelier : la pastille ne peut
            // porter qu'une couleur, mais on ne veut pas cacher pourquoi elle
            // est orange. C'est là qu'on lit « Service Rapide Soir 89 % ».
            const tooltip = closed
              ? `${dayStr} · Fermé`
              : capacity === 0
                ? `${dayStr} · Aucun mécanicien`
                : [
                    `${dayStr}`,
                    atelierMax
                      ? `Atelier le plus chargé : ${atelierMax} — ${Math.round((pct || 0) * 100)}%`
                      : `${hours.toFixed(1)}h / ${capacity}h (${Math.round((pct || 0) * 100)}%)`,
                    ...(detail || []).map(a => `  · ${a.nom} : ${a.heures}h / ${a.capacite}h`
                      + (a.pct !== null ? ` (${Math.round(a.pct * 100)}%)` : ' (aucun mécanicien)')),
                    atelierMax ? `Ensemble : ${hours.toFixed(1)}h / ${capacity}h (${Math.round((pctGlobal || 0) * 100)}%)` : null,
                  ].filter(Boolean).join('\n');
            return (
              <button
                type="button"
                key={idx}
                className={classes}
                disabled={closed}
                onClick={() => { if (!closed) setSelectedDate(new Date(d.getFullYear(), d.getMonth(), d.getDate())); }}
                title={tooltip}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
        {/* Dire ce que la couleur mesure : sans filtre d'atelier elle suit
            l'atelier le plus chargé, pas la moyenne. Sans cette mention, un jour
            orange à côté d'un « ensemble » à 63 % passerait pour une erreur. */}
        <div className="psc-legend-note" title="Sans filtre d'atelier, la couleur du jour suit l'atelier le plus chargé — survolez un jour pour le détail par atelier.">
          {selectedAtelierId ? 'Charge de l’atelier filtré' : 'Charge de l’atelier le plus chargé'}
        </div>
        <div className="psc-legend">
          <span className="psc-legend-item"><span className="psc-legend-dot psc-day--green" />≤ 75 %</span>
          <span className="psc-legend-item"><span className="psc-legend-dot psc-day--orange" />75–100 %</span>
          <span className="psc-legend-item"><span className="psc-legend-dot psc-day--red" />&gt; 100 %</span>
        </div>
      </div>
    );
  };

  // Section « Rendez-vous non honorés » du panneau (onglet Charge) : la file de
  // rappel vue du réceptionnaire — cartes DRAGGABLES vers la grille pour
  // replanifier (le drop solde la déclaration).
  const renderNonHonores = () => (
    <div className="pa-section">
      <div className="pa-section-title">🚫 Rendez-vous non honorés{nonHonores.length > 0 && <span className="pa-count">{nonHonores.length}</span>}</div>
      {nonHonores.length === 0 ? (
        <div className="pa-empty">Aucun rendez-vous non honoré</div>
      ) : (
        <div className="pa-list">
          {nonHonores.map(item => (
            <div
              key={item.id}
              className="pa-card"
              draggable={!lectureSeule}
              onDragStart={(e) => handleDragStart(e, item, 'non_honore')}
              onDragEnd={handleDragEnd}
              title="Glisser sur la grille pour replanifier (solde la déclaration)"
            >
              <div className="pa-card-head">
                <span className="pa-card-icon">🚫</span>
                <span className="pa-card-immat">{item.client_nom || item.client || 'Client'}{item.immat ? ` · ${item.immat}` : ''}</span>
                <span className="pa-card-grip">⠿</span>
              </div>
              <div className="pa-card-meta">
                {item.non_honore_motif === 'annule' ? 'A annulé' : item.non_honore_motif === 'non_presente' ? "Ne s'est pas présenté" : 'Autre'}
                {item.date ? ` · RDV du ${item.date.split('-').reverse().slice(0, 2).join('/')}` : ''}
              </div>
              {item.client_tel && (
                <div className="pa-card-time">
                  <a className="rtl-tel" href={`tel:${String(item.client_tel).replace(/\D/g, '')}`}
                     onClick={(ev) => ev.stopPropagation()}>{String(item.client_tel).replace(/(\d{2})(?=\d)/g, '$1 ')}</a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // `voir.planning` garde l'entrée du module. Le refus n'est affiché QU'UNE FOIS
  // le rôle chargé : sans cette précaution, tout le monde verrait le message
  // clignoter le temps d'un aller-retour réseau.
  if (planningRole && !peut('voir.planning')) {
    return (
      <div className="page-container page-full">
        <div className="planning-acces-refuse">
          <h2>Planning Atelier</h2>
          <p>
            Votre rôle Planning (<strong>{planningRole.role || 'aucun'}</strong>) ne donne pas accès à ce module.
          </p>
          <p className="planning-acces-refuse-aide">
            Un administrateur peut le modifier depuis <strong>Planning ▸ Configuration ▸ Gestion des accès</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container page-full">
      {/* ── ONGLETS PRINCIPAUX ── */}

    <div className={`planning-atelier${isFullscreen ? ' planning-fullscreen' : ''}`} ref={planningRef}>

      {/* ── Bouton sortie plein écran (uniquement en mode plein écran) ── */}
      {isFullscreen && (
        <button className="planning-fullscreen-btn planning-fs-exit" onClick={toggleFullscreen} title="Quitter le plein écran">
          ⊠ Quitter le plein écran
        </button>
      )}

      {/* ── Message si aucune concession sélectionnée (super-admin) ── */}
      {canSelectConcession && !selectedConcessionId && (
        <div className="planning-no-concession">
          <div className="planning-no-concession-icon">🏢</div>
          <p>Le planning se consulte concession par concession. Choisissez-en une : elle devient la concession du menu, pour toutes les pages.</p>
          {onChoisirConcession && (concessionsChoix || concessions).length > 0 && (
            <div className="planning-no-concession-choix">
              {(concessionsChoix || concessions).map(c => (
                <button key={c.id} type="button" className="btn-secondary" onClick={() => onChoisirConcession(String(c.id))}>{c.nom}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Contenu principal : affiché seulement si une concession est active ── */}
      {(!canSelectConcession || !!selectedConcessionId) && (<>

      {view === 'assistance' && (
        <SupportRequests user={user} editMode={editMode} />
      )}

      {view === 'accord-immo' && (
        <AccordClientImmoModal
          inline
          statutsAtelier={statutsAtelier}
          onOpenIntervention={(intervention) => handleEditIntervention(intervention)}
        />
      )}

      {view === 'participation' && (
        <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🤝</div>
          <p style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-secondary)' }}>Demande de participation</p>
          <p style={{ fontSize: '13px', marginTop: '6px' }}>Cette section est en cours de développement.</p>
        </div>
      )}

      {view === 'rdv-online' && (
        <RdvOnlineManager user={user} staffList={staffList} onReload={loadData} />
      )}

      <RechercheDossier
        show={rechercheOuverte}
        onClose={() => setRechercheOuverte(false)}
        onOuvrir={ouvrirDossierDepuisRecherche}
      />

      {view === 'derniers-contacts' && peut('client.echanges_voir') && (
        <DerniersContacts onOuvrirDossier={ouvrirDossierParId} />
      )}

      {view === 'sans-contact' && peut('client.echanges_voir') && (
        <ClientsSansContact onOuvrirDossier={ouvrirDossierParId} />
      )}

      {view === 'non-honores' && (
        <RdvNonHonores
          /* Ouverture SUR PLACE : la modale s'ouvre par-dessus la liste des RDV
             non honorés (demande utilisateur). L'ancien chemin
             (ouvrirDossierDepuisRecherche) basculait vers la grille atelier du
             jour du dossier, on quittait donc la page. ouvrirDossierParId ouvre
             la modale par id sans changer de vue. */
          onOpenIntervention={(d) => ouvrirDossierParId(d.id)}
          onCountChange={() => refreshNonHonores()}
        />
      )}

      {/* Ouverture SUR PLACE (2026-08-15, demande utilisateur) : la modale
          s'ouvre par-dessus le Suivi Atelier et on y revient en la fermant —
          l'ancien chemin basculait vers la grille du jour du dossier. */}
      {view === 'suivi-atelier' && (
        <SuiviAtelier
          onCountChange={setSuiviAtelierCount}
          onOpenIntervention={ouvrirDossierSurPlace}
        />
      )}

      {/* Double garde : un `view` résiduel ne doit pas exposer l'écran si le rôle
          change (retour d'une impersonation, par exemple). */}
      {view === 'kanban' && user?.role === 'super-admin' && (
        <KanbanAtelier
          staffList={staffList}
          ateliersList={ateliersList}
          onOpenIntervention={ouvrirDossierSurPlace}
        />
      )}

      {demandeOr && (
        <DemandeNumeroOr
          dossier={demandeOr}
          onFerme={() => setDemandeOr(null)}
          onEnregistre={(num) => {
            // Toutes les cartes du même dossier suivent : la complémentaire
            // affiche l'OR de son parent.
            setInterventions(prev => prev.map(i => (idDossier(i) === demandeOr.id ? { ...i, numero_or: num } : i)));
            setDemandeOr(null);
          }}
        />
      )}

      {view === 'rappels-rdv' && user?.role === 'super-admin' && <RappelsRdvSimulation onOuvrirDossier={ouvrirDossierParId} />}
      {view === 'remplissage' && user?.role === 'super-admin' && <RemplissageAtelier />}

      {view === 'receptions' && <ReceptionsStats />}

      {view === 'pieces-alertes' && (
        <PiecesAlertes
          rafraichir={alertesPiecesTick}
          onCountChange={setAlertesPiecesCount}
          onOpenIntervention={ouvrirDossierDepuisAlerte}
        />
      )}

      {datePop && (
        <>
          {/* Voile transparent : ferme au clic à côté, sans écouteur global. */}
          <div className="atelier-date-voile" onClick={() => setDatePop(null)} />
          <div className="atelier-date-pop" style={{ top: datePop.top, left: datePop.left }}
            onClick={(e) => { if (e.target.closest('.psc-day')) setDatePop(null); }}>
            {renderChargeCalendar()}
          </div>
        </>
      )}

      {view === 'config' && (
        <PlanningConfig
          concessions={concessions}
          staffList={staffList}
          ateliersList={ateliersList}
          absencesList={absencesList}
          pausesList={pausesList}
          onReload={loadData}
          peut={peut}
        />
      )}


        {/* ── Bloc Atelier ── */}
        {view === 'atelier' && (
        <div className="planning-atelier-layout">
        <div className="planning-block planning-block--main">
        {ateliersList.length > 1 && (
          <div className={`atelier-tabs${atelierView === 'jour' ? ' atelier-tabs--centre' : ''}`}>
            {(() => {
              const isJour = atelierView === 'jour';
              return (
                <div className="atelier-tabs-date">
                  <button className="atelier-tab" onClick={isJour ? goToPreviousDay : goToPreviousWeek}>◀</button>
                  <div className="atelier-date-anchor">
                    <button className="atelier-tab atelier-tab-date"
                      onClick={(e) => {
                        if (datePop) { setDatePop(null); return; }
                        const r = e.currentTarget.getBoundingClientRect();
                        setDatePop({ top: r.bottom + 6, left: r.left + r.width / 2 });
                      }}
                      title="Choisir une date — les carrés montrent la charge de chaque jour">
                      {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} · S.{getWeekNumber(selectedDate)}
                    </button>
                  </div>
                  <input
                    ref={datePickerRef}
                    type="date"
                    className="planning-date-picker-hidden"
                    value={formatDate(selectedDate)}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const choisi = new Date(e.target.value + 'T00:00:00');
                      // Le calendrier natif ne sait pas griser des jours : si le
                      // choix tombe sur un jour ferme, on glisse au suivant ouvert.
                      setSelectedDate(estJourOuvert(choisi) ? choisi : (prochainJourOuvert(choisi, 1) || choisi));
                    }}
                  />
                  <button className="atelier-tab" onClick={isJour ? goToNextDay : goToNextWeek}>▶</button>
                  {loading && <span className="planning-loading-indicator">⏳</span>}
                </div>
              );
            })()}
            <div className="atelier-tabs-ateliers">
              {atelierView === 'jour' ? (
                <div className="header-segmented">
                  {ateliersList.map(a => {
                    // Taux de charge = calcul de Remplissage atelier (serveur) :
                    // heures planifiées / heures disponibles. Objectif 100 %.
                    const ch = (chargeAteliers?.ateliers || []).find(x => String(x.atelier_id) === String(a.id));
                    const isActive = String(selectedAtelierId) === String(a.id);

                    return (
                      <button
                        key={a.id}
                        className={`header-segmented-btn${isActive ? ' active' : ''}`}
                        onClick={() => setSelectedAtelierId(String(a.id))}
                      >
                        <span className="header-segmented-libelle" data-libelle={a.nom}>{a.nom}</span> <TauxCharge charge={ch} />
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="header-segmented">
                  {mecaniciens.map(m => (
                    <button
                      key={m.id}
                      className={`header-segmented-btn staff-chip${selectedMecanicien === m.id ? ' active' : ''}`}
                      onClick={() => setSelectedMecanicien(m.id)}
                      title={[getStaffLabel(m), m.specialite].filter(Boolean).join(' — ')}
                    >
                      <span className="staff-chip-text">
                        <span className="staff-chip-name">{getStaffLabel(m)}</span>
                        {m.specialite && <span className="staff-chip-specialite">{m.specialite}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Colonne de droite : la loupe ne pousse plus le sélecteur d'ateliers,
                qui reste centré sous celui de l'en-tête. */}
            <div className="atelier-tabs-droite">
              {/* Loupe descendue de l'en-tête dans la barre de l'atelier : en plein
                  écran l'en-tête de page n'est plus affiché, la recherche y était
                  donc hors d'atteinte — alors que c'est précisément là qu'on en a
                  besoin. Le raccourci Ctrl/⌘ + K reste actif partout. */}
              <button
                type="button"
                className="atelier-tabs-recherche"
                onClick={() => setRechercheOuverte(true)}
                title="Rechercher un dossier dans tout le planning (Ctrl+K)"
                aria-label="Rechercher un dossier"
              >🔍</button>
            </div>
          </div>
        )}

        {/* Barre de filtre par tag MASQUÉE le 2026-08-10 (demande métier :
            priorité à la place verticale). Les tags restent posables au clic
            droit et visibles dans Suivi atelier ; le JSX de la barre est dans
            l'historique git si on la réactive. */}
        {atelierView === 'jour' && mecaniciens.length === 0 && (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>👷</div>
            <p style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-secondary)' }}>Aucun mécanicien affecté à cet atelier</p>
            <p style={{ fontSize: '13px', marginTop: '6px' }}>Allez dans <strong>⚙️ Configuration → Équipe</strong> pour affecter des mécaniciens à cet atelier.</p>
          </div>
        )}
        {atelierView === 'jour' && mecaniciens.length > 0 && (
          <div ref={grilleRef} className="planning-grid planning-grid--staff-headers"
            style={{ position: 'relative', '--pa-col-min': `${colMin}px` }}>
            {renderCurrentTimeLine(20)}
            <div className="time-column time-column-narrow">
              <div className="header-cell header-cell-empty">
                <button className="view-toggle-btn" style={peut('voir.vue_semaine') ? undefined : { display: 'none' }} onClick={() => setAtelierView(atelierView === 'jour' ? 'semaine' : 'jour')} title={atelierView === 'jour' ? 'Passer en vue Semaine' : 'Passer en vue Jour'}>
                  <span className="vt-picto">{atelierView === 'jour' ? '📆' : '📅'}</span>{atelierView === 'jour' ? 'Jour' : 'Sem.'}
                </button>
              </div>
              {(() => {
                const pauseSlots = getPauseSlots(selectedDate);
                return heures.map((h, idx) => (
                  <div key={idx} className={`time-slot${pauseSlots.has(idx) ? ' pause-slot' : ''}${h.endsWith(':00') ? ' full-hour' : ''}`}>{h.endsWith(':00') ? h : h.split(':')[1]}</div>
                ));
              })()}
            </div>

            {mecaniciens.map((staff) => {
              const mecaInterventions = getTodayInterventions().filter(i => i.mecanicien_id === staff.id);
              const totalH = mecaInterventions.reduce((sum, i) => sum + i.duree, 0);
              const chargePct = Math.min(100, (totalH / 8) * 100);
              const chargeColor = chargePct > 100 ? 'var(--color-error)' : chargePct > 75 ? 'var(--color-warning)' : 'var(--color-success)';
              const absenceDuJour = absenceDe(staff.id, selectedDate);

              return (
                <div key={staff.id} className={`resource-column${dragOverColumn === staff.id ? ' drag-over' : ''}${estJourneeEntiere(absenceDuJour) && !estIndispo(absenceDuJour) ? ' resource-column--absent' : ''}`}>
                  <div className="header-cell header-cell--staff">
                    <div className="staff-top">
                      <div className="staff-identity">
                        <strong className="staff-name">{casseTitre(getStaffShortLabel(staff))}</strong>
                        {/* ⚠️ L'étiquette d'absence a quitté `staff-top` le
                            2026-09-05 (demande utilisateur) : le motif est
                            désormais l'ENTÊTE du bloc, sur la grille, là où
                            l'absence se situe réellement dans la journée. */}
                        {false
                          ? null
                          : staff.specialite && <span className="staff-specialite">{staff.specialite}</span>}
                      </div>
                    </div>
                    {/* Bandeau « ⚠️ N conflits » retiré le 2026-08-09 : deux cartes
                        qui se chevauchent se voient à l'œil nu dans la colonne, le
                        bandeau ne faisait que répéter l'évidence en poussant la
                        barre de charge d'une ligne. La détection elle-même
                        (`conflicts`) reste en place — voir le panneau de conflits,
                        auquel il faut désormais un autre point d'entrée. */}
                    <div className="charge-row">
                      <div className="charge-bar-wrapper" title={`${totalH}h planifiées / 8h disponibles`}>
                        <div className="charge-bar-fill" style={{ width: `${chargePct}%`, background: chargeColor }} />
                      </div>
                      <span className="resource-count">{estJourneeEntiere(absenceDuJour) && !estIndispo(absenceDuJour) ? '—' : heuresFr(totalH)}</span>
                    </div>
                  </div>

                  <div
                    className={`slots-container${estJourneeEntiere(absenceDuJour) ? (estIndispo(absenceDuJour) ? ' is-indispo' : ' is-absent') : ''}`}
                    style={{ height: GRID_SLOTS * 20 }}
                    onDragOver={estJourneeEntiere(absenceDuJour) ? undefined : (e) => handleDragOver(e, staff.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDropAtelierJour(e, staff)}
                    onMouseDown={estJourneeEntiere(absenceDuJour) ? undefined : (e) => handleMouseDown(e, staff)}
                    onMouseMove={estJourneeEntiere(absenceDuJour) ? undefined : (e) => handleMouseMove(e, staff)}
                    onMouseUp={estJourneeEntiere(absenceDuJour) ? undefined : (e) => handleMouseUp(e, staff)}
                  >
                    {(() => {
                      const g = geometrieVoile(absenceDuJour, 20);
                      if (!g) return null;
                      const indispo = estIndispo(absenceDuJour);
                      // [10][11] Le voile porte désormais un ENTÊTE — le motif de
                      // l'absence, ou « Indisponible ». Il vivait jusqu'ici dans
                      // `staff-top`, en tête de colonne : il y disait « absent »
                      // sans dire ni quand ni pourquoi, et il fallait survoler
                      // pour le savoir. Clic droit = suppression.
                      return (
                        <div className={`absence-bloc${indispo ? ' absence-bloc--indispo' : ''}`} style={g}
                          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation();
                            setSuppressionAbsence({ absence: absenceDuJour, staff }); }}
                          title={`${indispo ? 'Indisponible' : libelleMotifAbsence(absenceDuJour.motif)}`
                            + `${absenceDuJour.heure_debut ? ` · ${libelleAbsence(absenceDuJour)}` : ' · journée entière'}`
                            + `${absenceDuJour.commentaire ? `\n${absenceDuJour.commentaire}` : ''}`
                            + '\nClic droit pour supprimer'}>
                          <span className="absence-bloc-entete">
                            {indispo ? 'Indisponible' : libelleMotifAbsence(absenceDuJour.motif)}
                          </span>
                          {absenceDuJour.heure_debut && (
                            <span className="absence-bloc-heures">{libelleAbsence(absenceDuJour)}</span>
                          )}
                        </div>
                      );
                    })()}
                    {(() => {
                      // Les pauses DE CE MÉCANICIEN : sa colonne les grise même en
                      // vue « Tous », où elles ne s'appliquent pas aux autres ateliers.
                      const pauseSlots = getPauseSlotsMecanicien(selectedDate, staff.atelier_id);
                      return heures.map((h, idx) => (
                        <div key={idx} className={`empty-slot${h.endsWith(':00') ? ' full-hour' : ''}${pauseSlots.has(idx) ? ' pause-slot' : ''}${horsOuverture(idx, staff?.atelier_id) ? ' hors-ouverture' : ''}`} />
                      ));
                    })()}

                    {dragOverColumn === staff.id && dropIndicatorY !== null && (
                      <div className={`drop-indicator${dropForbidden ? ' is-forbidden' : ''}`} style={{ top: `${dropIndicatorY}px` }}>
                        {dropPreviewHeure && (
                          <span className="drop-indicator-label">
                            {dropForbidden
                              ? `⛔ avant ${dragBoundaryHeure}`
                              : (dropPreviewFin ? `${dropPreviewHeure} → ${dropPreviewFin}` : dropPreviewHeure)}
                          </span>
                        )}
                      </div>
                    )}

                    {isSelecting && selectionMeca === staff && selectionStart !== null && selectionEnd !== null && (
                      <div
                        className="selection-highlight"
                        style={{
                          top: `${Math.min(selectionStart, selectionEnd) * 20}px`,
                          height: `${(Math.abs(selectionEnd - selectionStart) + 1) * 20}px`
                        }}
                      />
                    )}

                    {mecaInterventions.map(intervention => {
                      const statutCouleur = intervention.statut_atelier_couleur;
                      // Fond de carte = STATUT atelier uniquement (plus de couleur par type d'intervention).
                      // Blanc neutre si aucun statut ; texte contrasté automatiquement sur fond coloré.
                      const cardBg = statutCouleur || 'var(--bg-planning-card)';
                      const cardTextColor = statutCouleur ? (intervention.statut_atelier_text_color || getContrastColor(statutCouleur)) : undefined;
                      const lane = overlapLayout[intervention.id]; // { lane, lanes } si chevauchement, sinon undefined

                      // Carte unique qui couvre toute la plage (travail + pauses traversées).
                      // Les pauses traversées reçoivent un overlay semi-transparent pour
                      // matérialiser le fait que le mécanicien ne travaille pas, tout en
                      // gardant une carte continue et lisible.
                      const startSlot = clampSlot(getHeureIndex(intervention.heure_debut));
                      // La carte enjambe les pauses DE SON mécanicien : la durée
                      // stockée est un temps de TRAVAIL, pas une plage horaire.
                      const pausesMeca = getPauseSlotsMecanicien(selectedDate, staff.atelier_id);
                      const endSlot = calcRealEndSlot(intervention.heure_debut, intervention.duree, pausesMeca);
                      const totalSlots = Math.max(1, endSlot - startSlot);
                      // Regroupe les slots de pause consécutifs en ranges relatifs à startSlot
                      const pauseRanges = [];
                      let runStart = -1;
                      for (let s = startSlot; s < endSlot; s++) {
                        if (pausesMeca.has(s)) {
                          if (runStart === -1) runStart = s;
                        } else if (runStart !== -1) {
                          pauseRanges.push({ offset: runStart - startSlot, length: s - runStart });
                          runStart = -1;
                        }
                      }
                      if (runStart !== -1) pauseRanges.push({ offset: runStart - startSlot, length: endSlot - runStart });

                      return (
                        <div
                          key={intervention.id}
                          data-intervention-id={intervention.id}
                          className={`intervention-card${lane ? ' intervention-laned' : ''}${highlightedInterventionId === intervention.id ? ' is-highlighted' : ''}${intervention.client_annule_at ? ' is-annule-client' : ''}${confirmeVisible(intervention) ? ' is-confirme-client' : ''}`}
                          draggable={!lectureSeule}
                          onMouseDown={(e) => e.stopPropagation()}
                          onDragStart={(e) => handleDragStart(e, intervention, 'intervention')}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleEditIntervention(intervention)}
                          onContextMenu={(e) => handleContextMenu(e, intervention)}
                          onMouseEnter={() => setHoveredInterventionId(intervention.id)}
                          onMouseLeave={() => setHoveredInterventionId(null)}
                          style={{
                            top: `${startSlot * 20}px`,
                            height: `${totalSlots * 20 - 4}px`,
                            background: cardBg,
                            color: cardTextColor,
                            // Filtre par tag : on estompe au lieu de masquer, pour
                            // que la charge de la journée reste lisible — une grille
                            // vidée de ses cartes donnerait l'illusion d'un atelier libre.
                            opacity: carteRetenue(intervention) ? 1 : 0.15,
                            ...(lane ? {
                              left: `calc(${(lane.lane / lane.lanes) * 100}% + 1px)`,
                              right: 'auto',
                              width: `calc(${100 / lane.lanes}% - 2px)`,
                              zIndex: 5 + lane.lane,
                            } : {})
                          }}
                        >
                          {/* Menu d'actions — même contenu que le clic droit, qui reste
                              en raccourci. Sans ce bouton, tout le menu (statut, replanifier,
                              reporter, contacter, supprimer) était indécouvrable. */}
                          {/* [9] Carré de couleur du réceptionnaire — opt-in par
                              concession (`carre_receptionnaire_enabled`). Les
                              réceptionnaires repèrent ainsi leurs dossiers d'un
                              coup d'œil. Le nom est en infobulle : une couleur
                              seule n'est pas lisible pour tout le monde. */}
                          {intervention.carre_receptionnaire_enabled && (() => {
                            const r = staffList.find(x => x.id === intervention.poste_reception && x.role === 'receptionnaire');
                            if (!r || !r.couleur) return null;
                            return (
                              <span className="card-receptionnaire"
                                style={{ background: r.couleur }}
                                title={`Réceptionnaire : ${[r.prenom, r.nom].filter(Boolean).join(' ')}`} />
                            );
                          })()}
                          <button
                            type="button"
                            className={`card-menu-btn${lectureSeule ? ' is-hidden' : ''}`}
                            title="Actions"
                            aria-label="Actions sur cette intervention"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); handleContextMenu(e, intervention); }}
                          >⋯</button>
                          {(() => {
                            const slot = getCardActionSlot(intervention);
                            if (!slot) return null;
                            if (slot.kind === 'restituee') {
                              return <div className="card-restituee-badge" title="Véhicule restitué au client">✓ Restitué · {slot.heure}</div>;
                            }
                            if (slot.next && !lectureSeule) {
                              return (
                                <button
                                  type="button"
                                  className={`card-client-status card-client-status--${slot.statut} card-client-status--btn`}
                                  title={slot.tooltip}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => { e.stopPropagation(); avancerFluxAccueil(slot); }}
                                >{slot.label}</button>
                              );
                            }
                            return <div className={`card-client-status card-client-status--${slot.statut}`} title={slot.tooltip}>{slot.label}</div>;
                          })()}
                          <MarqueReponseClient intv={intervention} confirmeVisible={confirmeVisible(intervention)} />
                          <div className="card-top-row">
                            <div className="card-client">{intervention.client_nom || intervention.client}</div>
                            <div className="card-heure" title={intervention.heure_reception ? `Réception à ${intervention.heure_reception.substring(0, 5)} · intervention à ${intervention.heure_debut?.substring(0, 5) || '—'}` : `Début d'intervention (pas d'heure de réception)`}>{intervention.heure_reception ? `RDV à ${intervention.heure_reception.substring(0, 5)}` : intervention.heure_debut?.substring(0, 5)} · {formatDuree(intervention.duree)}</div>
                          </div>
                          {/* Ligne d'identification (2026-08-14) : immat et n° d'OR
                              côte à côte — ce sont les deux repères qu'on cherche sur
                              une carte — puis la cravate collée à droite. Le libellé
                              véhicule descend d'une ligne : il est long et n'identifie
                              rien à lui seul. L'immat ne rétrécit JAMAIS. */}
                          <div className="card-ident">
                            {intervention.immat && <span className="card-immat-badge">{intervention.immat}</span>}
                            {intervention.numero_or ? (
                              <span className="card-or-inline">OR {intervention.numero_or}</span>
                            ) : (
                              <button
                                type="button"
                                className="card-or-line-btn"
                                title="Saisir le numéro d'OR"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditIntervention(intervention);
                                }}
                              >
                                OR à préparer
                              </button>
                            )}
                          </div>
                          {(intervention.marque || intervention.modele) && (
                            <div className="card-vehicule"><span>{[intervention.marque, intervention.modele].filter(Boolean).join(' ')}</span></div>
                          )}
                          <div className="card-type">{intervention.description}</div>
                          {/* Tous les pictos informatifs sont regroupés en bas de carte,
                              en carrés arrondis : ils y prennent moins de place qu'éparpillés
                              sur trois angles, et le haut reste au contenu (nom, heure). */}
                          {(intervention.is_complement || statutPiecesCarte(intervention) || !intervention.client_tel
                            || intervention.client_attend || intervention.a_courtoisie || intervention.est_roole || intervention.a_demande
                            || intervention.au_parking || intervention.numero_cravate
                            || intervention.a_photos || intervention.prepicking_at) && (
                            <div className={`card-flags${totalSlots >= 5 ? ' card-flags--large' : ''}`}>
                              {intervention.is_complement && (
                                <span className="card-flag card-flag--complement" title="Intervention complémentaire"><CardFlagIcon type="complement" /></span>
                              )}
                              {statutPiecesCarte(intervention) && (
                                <span
                                  className={`card-flag card-pieces-dot dot-${statutPiecesCarte(intervention)}`}
                                  title={
                                    statutPiecesCarte(intervention) === 'disponible' ? 'Pièces disponibles' :
                                    statutPiecesCarte(intervention) === 'en_commande' ? 'Pièces en commande' :
                                    'Pièces indisponibles'
                                  }
                                ><CardFlagIcon type="pieces" /></span>
                              )}
                              {/* [16] Prépicking : placé JUSTE APRÈS le picto Pièces,
                                  c'est le même sujet vu par le magasin. Forme
                                  distincte (chariot vs carton) — voir CardFlagIcon. */}
                              {intervention.prepicking_at && (
                                <span className="card-flag card-flag--prepicking"
                                  title={`Pièces préparées${intervention.prepicking_par_nom ? ` par ${intervention.prepicking_par_nom}` : ''} le ${new Date(intervention.prepicking_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`}
                                ><CardFlagIcon type="prepicking" /></span>
                              )}
                              {/* [6] Le dossier porte des photos (atelier ou tour du véhicule). */}
                              {intervention.a_photos && (
                                <span className="card-flag card-flag--photos" title="Photos ou vidéos sur ce dossier"><CardFlagIcon type="photos" /></span>
                              )}
                              {!intervention.client_tel && (
                                <span className="card-flag card-flag--tel" title="Téléphone client manquant"><CardFlagIcon type="tel" /></span>
                              )}
                              {intervention.client_attend && (
                                <span className="card-flag card-flag--attend" title="Le client attend sur place"><CardFlagIcon type="attend" /></span>
                              )}
                              {intervention.a_courtoisie && (
                                <span className="card-flag card-flag--courtoisie" title="Véhicule de courtoisie prêté sur cet OR"><CardFlagIcon type="courtoisie" /></span>
                              )}
                              {intervention.est_roole && (
                                <span className="card-flag card-flag--roole" title="Client Roole"><CardFlagIcon type="roole" /></span>
                              )}
                              {intervention.au_parking && (
                                <span className="card-flag card-flag--parking" title="Véhicule présent au parking (dernier inventaire)"><CardFlagIcon type="parking" /></span>
                              )}
                              {intervention.a_demande && (
                                <span
                                  className="card-flag card-flag--demande"
                                  title="Demande client en cours — cliquer pour l'ouvrir"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => { e.stopPropagation(); ouvrirDemandeDuVehicule(intervention); }}
                                ><CardFlagIcon type="demande" /></span>
                              )}
                              {/* La cravate ferme la rangée, collée à droite : c'est un
                                  repère qu'on cherche du regard, pas un drapeau parmi
                                  les autres — et sa place ne dépend pas du nombre de
                                  pictos qui la précèdent. */}
                              {intervention.numero_cravate && (() => {
                                const t = teinteCravate(intervention.cravate_couleur);
                                return (
                                  <span className="card-cravate" style={{ background: t.fond, color: t.texte }}
                                    title={`Cravate ${intervention.numero_cravate} (${t.nom.toLowerCase()})`}>
                                    <span className="card-cravate-hole" />
                                    {intervention.numero_cravate}
                                  </span>
                                );
                              })()}
                            </div>
                          )}

                          {/* Overlays transparents sur les plages de pause traversées par la carte */}
                          {pauseRanges.map((r, i) => (
                            <div
                              key={`pause-${i}`}
                              className="intervention-pause-overlay"
                              style={{ top: `${r.offset * 20}px`, height: `${r.length * 20}px` }}
                              aria-hidden="true"
                            />
                          ))}

                          {hoveredInterventionId === intervention.id && (
                            <>
                              <div className="intervention-tooltip">
                                {intervention.numero_or && (
                                  <div className="tooltip-or">#{intervention.numero_or}</div>
                                )}
                                <div className="tooltip-title">{intervention.client}</div>
                                <div className="tooltip-time">
                                  {intervention.heure_debut} → {calcEndTime(intervention.heure_debut, intervention.duree)} · {formatDuree(intervention.duree)}
                                </div>
                                <div className="tooltip-meca">{intervention.mecanicien} · {intervention.type}</div>
                                <div className="tooltip-desc">{intervention.description}</div>
                                {intervention.immat && (
                                  <div style={{ fontFamily: 'monospace', marginTop: '5px', opacity: 0.85, fontSize: '11px' }}>{intervention.immat}</div>
                                )}
                                {statutPiecesCarte(intervention) && (
                                  <div style={{ marginTop: '5px', fontSize: '10px' }}>
                                    {statutPiecesCarte(intervention) === 'disponible' ? '🟢 Pièces disponibles' :
                                     statutPiecesCarte(intervention) === 'en_commande' ? '🟠 Pièces en commande' :
                                     '🔴 Pièces indisponibles'}
                                  </div>
                                )}
                              </div>
                              <div
                                className={`card-resize-grip card-resize-grip-top${lectureSeule ? ' is-hidden' : ''}`}
                                onMouseDown={(e) => handleResizeStart(e, intervention, 'top')}
                              >
                                <span className="card-resize-dots">⋯</span>
                              </div>
                              <div
                                className={`card-resize-grip card-resize-grip-bottom${lectureSeule ? ' is-hidden' : ''}`}
                                onMouseDown={(e) => handleResizeStart(e, intervention, 'bottom')}
                              >
                                <span className="card-resize-dots">⋯</span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {atelierView === 'semaine' && mecaniciens.length === 0 && (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>👷</div>
            <p style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-secondary)' }}>Aucun mécanicien affecté à cet atelier</p>
            <p style={{ fontSize: '13px', marginTop: '6px' }}>Allez dans <strong>⚙️ Configuration → Équipe</strong> pour affecter des mécaniciens à cet atelier.</p>
          </div>
        )}
        {atelierView === 'semaine' && mecaniciens.length > 0 && (
          <div className="planning-grid week-view">
            <div className="time-column">
              <div className="header-cell">
                <button className="view-toggle-btn" onClick={() => setAtelierView('jour')} title="Passer en vue Jour"><span className="vt-picto">📅</span>Sem.</button>
              </div>
              {heures.map((h, idx) => (
                <div key={idx} className={`time-slot${h.endsWith(':00') ? ' full-hour' : ''}`}>{h.endsWith(':00') ? h : h.split(':')[1]}</div>
              ))}
            </div>

            {weekDays.map((day) => {
              const dayStr = formatDate(day);
              const dayInterventions = interventions.filter(
                i => i.mecanicien_id === selectedMecanicien && i.date === dayStr
              );
              const totalH = dayInterventions.reduce((sum, i) => sum + i.duree, 0);
              const chargePct = Math.min(100, (totalH / 8) * 100);
              const chargeColor = chargePct > 100 ? 'var(--color-error)' : chargePct > 75 ? 'var(--color-warning)' : 'var(--color-success)';
              // Vue Semaine : une colonne = un JOUR du mécanicien sélectionné —
              // l'absence s'y lit donc jour par jour.
              const absenceDuJour = absenceDe(selectedMecanicien, day);

              return (
                <div key={dayStr} className={`resource-column${dragOverColumn === dayStr ? ' drag-over' : ''}${estJourneeEntiere(absenceDuJour) ? ' resource-column--absent' : ''}`}>
                  <div className="header-cell">
                    <strong>{formatDateDisplay(day)}</strong>
                    {absenceDuJour && <span className="staff-absent-tag" title={libelleMotifAbsence(absenceDuJour.motif)}>{libelleAbsence(absenceDuJour)}</span>}
                    <div className="charge-row">
                      <div className="charge-bar-wrapper" title={`${totalH}h planifiées / 8h disponibles`}>
                        <div className="charge-bar-fill" style={{ width: `${chargePct}%`, background: chargeColor }} />
                      </div>
                      <span className="resource-count">{estJourneeEntiere(absenceDuJour) ? '—' : heuresFr(totalH)}</span>
                    </div>
                  </div>

                  <div
                    className={`slots-container${estJourneeEntiere(absenceDuJour) ? ' is-absent' : ''}`}
                    style={{ height: GRID_SLOTS * 20 }}
                    onDragOver={estJourneeEntiere(absenceDuJour) ? undefined : (e) => handleDragOver(e, dayStr)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDropAtelierSemaine(e, dayStr)}
                  >
                    {(() => {
                      const g = geometrieVoile(absenceDuJour, 20);
                      if (!g) return null;
                      const indispo = estIndispo(absenceDuJour);
                      // [10][11] Le voile porte désormais un ENTÊTE — le motif de
                      // l'absence, ou « Indisponible ». Il vivait jusqu'ici dans
                      // `staff-top`, en tête de colonne : il y disait « absent »
                      // sans dire ni quand ni pourquoi, et il fallait survoler
                      // pour le savoir. Clic droit = suppression.
                      return (
                        <div className={`absence-bloc${indispo ? ' absence-bloc--indispo' : ''}`} style={g}
                          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation();
                            setSuppressionAbsence({ absence: absenceDuJour, staff: getMecaById(selectedMecanicien) }); }}
                          title={`${indispo ? 'Indisponible' : libelleMotifAbsence(absenceDuJour.motif)}`
                            + `${absenceDuJour.heure_debut ? ` · ${libelleAbsence(absenceDuJour)}` : ' · journée entière'}`
                            + `${absenceDuJour.commentaire ? `\n${absenceDuJour.commentaire}` : ''}`
                            + '\nClic droit pour supprimer'}>
                          <span className="absence-bloc-entete">
                            {indispo ? 'Indisponible' : libelleMotifAbsence(absenceDuJour.motif)}
                          </span>
                          {absenceDuJour.heure_debut && (
                            <span className="absence-bloc-heures">{libelleAbsence(absenceDuJour)}</span>
                          )}
                        </div>
                      );
                    })()}
                    {(() => {
                      const pauseSlots = getPauseSlots(day);
                      return heures.map((h, idx) => (
                        <div key={idx} className={`empty-slot${h.endsWith(':00') ? ' full-hour' : ''}${pauseSlots.has(idx) ? ' pause-slot' : ''}${horsOuverture(idx, mecanicienSemaine?.atelier_id) ? ' hors-ouverture' : ''}`} />
                      ));
                    })()}

                    {dragOverColumn === dayStr && dropIndicatorY !== null && (
                      <div className={`drop-indicator${dropForbidden ? ' is-forbidden' : ''}`} style={{ top: `${dropIndicatorY}px` }}>
                        {dropPreviewHeure && (
                          <span className="drop-indicator-label">
                            {dropForbidden
                              ? `⛔ avant ${dragBoundaryHeure}`
                              : (dropPreviewFin ? `${dropPreviewHeure} → ${dropPreviewFin}` : dropPreviewHeure)}
                          </span>
                        )}
                      </div>
                    )}

                    {dayInterventions.map(intervention => {
                      const startIdx = clampSlot(getHeureIndex(intervention.heure_debut));
                      const height = intervention.duree * 4;
                      const statutCouleur = intervention.statut_atelier_couleur;
                      // Fond de carte = STATUT atelier uniquement (plus de couleur par type d'intervention).
                      // Blanc neutre si aucun statut ; texte contrasté automatiquement sur fond coloré.
                      const cardBg = statutCouleur || 'var(--bg-planning-card)';
                      const cardTextColor = statutCouleur ? (intervention.statut_atelier_text_color || getContrastColor(statutCouleur)) : undefined;
                      const lane = overlapLayout[intervention.id];

                      return (
                        <div
                          key={intervention.id}
                          data-intervention-id={intervention.id}
                          className={`intervention-card${lane ? ' intervention-laned' : ''}${highlightedInterventionId === intervention.id ? ' is-highlighted' : ''}${intervention.client_annule_at ? ' is-annule-client' : ''}${confirmeVisible(intervention) ? ' is-confirme-client' : ''}`}
                          draggable={!lectureSeule}
                          onMouseDown={(e) => e.stopPropagation()}
                          onDragStart={(e) => handleDragStart(e, intervention, 'intervention')}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleEditIntervention(intervention)}
                          onContextMenu={(e) => handleContextMenu(e, intervention)}
                          onMouseEnter={() => setHoveredInterventionId(intervention.id)}
                          onMouseLeave={() => setHoveredInterventionId(null)}
                          style={{
                            top: `${startIdx * 20}px`,
                            height: `${height * 20 - 4}px`,
                            background: cardBg,
                            color: cardTextColor,
                            opacity: carteRetenue(intervention) ? 1 : 0.15,
                            ...(lane ? {
                              left: `calc(${(lane.lane / lane.lanes) * 100}% + 1px)`,
                              right: 'auto',
                              width: `calc(${100 / lane.lanes}% - 2px)`,
                              zIndex: 5 + lane.lane,
                            } : {})
                          }}
                        >
                          {/* Menu d'actions — même contenu que le clic droit, qui reste
                              en raccourci. Sans ce bouton, tout le menu (statut, replanifier,
                              reporter, contacter, supprimer) était indécouvrable. */}
                          {/* [9] Carré de couleur du réceptionnaire — opt-in par
                              concession (`carre_receptionnaire_enabled`). Les
                              réceptionnaires repèrent ainsi leurs dossiers d'un
                              coup d'œil. Le nom est en infobulle : une couleur
                              seule n'est pas lisible pour tout le monde. */}
                          {intervention.carre_receptionnaire_enabled && (() => {
                            const r = staffList.find(x => x.id === intervention.poste_reception && x.role === 'receptionnaire');
                            if (!r || !r.couleur) return null;
                            return (
                              <span className="card-receptionnaire"
                                style={{ background: r.couleur }}
                                title={`Réceptionnaire : ${[r.prenom, r.nom].filter(Boolean).join(' ')}`} />
                            );
                          })()}
                          <button
                            type="button"
                            className={`card-menu-btn${lectureSeule ? ' is-hidden' : ''}`}
                            title="Actions"
                            aria-label="Actions sur cette intervention"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); handleContextMenu(e, intervention); }}
                          >⋯</button>
                          {(() => {
                            const slot = getCardActionSlot(intervention);
                            if (!slot) return null;
                            if (slot.kind === 'restituee') {
                              return <div className="card-restituee-badge" title="Véhicule restitué au client">✓ Restitué · {slot.heure}</div>;
                            }
                            if (slot.next && !lectureSeule) {
                              return (
                                <button
                                  type="button"
                                  className={`card-client-status card-client-status--${slot.statut} card-client-status--btn`}
                                  title={slot.tooltip}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => { e.stopPropagation(); avancerFluxAccueil(slot); }}
                                >{slot.label}</button>
                              );
                            }
                            return <div className={`card-client-status card-client-status--${slot.statut}`} title={slot.tooltip}>{slot.label}</div>;
                          })()}
                          <MarqueReponseClient intv={intervention} confirmeVisible={confirmeVisible(intervention)} />
                          <div className="card-top-row">
                            <div className="card-client">{intervention.client_nom || intervention.client}</div>
                            <div className="card-heure" title={intervention.heure_reception ? `Réception à ${intervention.heure_reception.substring(0, 5)} · intervention à ${intervention.heure_debut?.substring(0, 5) || '—'}` : `Début d'intervention (pas d'heure de réception)`}>{intervention.heure_reception ? `RDV à ${intervention.heure_reception.substring(0, 5)}` : intervention.heure_debut?.substring(0, 5)} · {formatDuree(intervention.duree)}</div>
                          </div>
                          {/* Ligne d'identification (2026-08-14) : immat et n° d'OR
                              côte à côte — ce sont les deux repères qu'on cherche sur
                              une carte — puis la cravate collée à droite. Le libellé
                              véhicule descend d'une ligne : il est long et n'identifie
                              rien à lui seul. L'immat ne rétrécit JAMAIS. */}
                          <div className="card-ident">
                            {intervention.immat && <span className="card-immat-badge">{intervention.immat}</span>}
                            {intervention.numero_or ? (
                              <span className="card-or-inline">OR {intervention.numero_or}</span>
                            ) : (
                              <button
                                type="button"
                                className="card-or-line-btn"
                                title="Saisir le numéro d'OR"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditIntervention(intervention);
                                }}
                              >
                                OR à préparer
                              </button>
                            )}
                          </div>
                          {(intervention.marque || intervention.modele) && (
                            <div className="card-vehicule"><span>{[intervention.marque, intervention.modele].filter(Boolean).join(' ')}</span></div>
                          )}
                          <div className="card-type">{intervention.description}</div>
                          {/* Tous les pictos informatifs sont regroupés en bas de carte,
                              en carrés arrondis : ils y prennent moins de place qu'éparpillés
                              sur trois angles, et le haut reste au contenu (nom, heure). */}
                          {(intervention.is_complement || statutPiecesCarte(intervention) || !intervention.client_tel
                            || intervention.client_attend || intervention.a_courtoisie || intervention.est_roole || intervention.a_demande
                            || intervention.au_parking || intervention.numero_cravate) && (
                            <div className={`card-flags${height >= 5 ? ' card-flags--large' : ''}`}>
                              {intervention.is_complement && (
                                <span className="card-flag card-flag--complement" title="Intervention complémentaire"><CardFlagIcon type="complement" /></span>
                              )}
                              {statutPiecesCarte(intervention) && (
                                <span
                                  className={`card-flag card-pieces-dot dot-${statutPiecesCarte(intervention)}`}
                                  title={
                                    statutPiecesCarte(intervention) === 'disponible' ? 'Pièces disponibles' :
                                    statutPiecesCarte(intervention) === 'en_commande' ? 'Pièces en commande' :
                                    'Pièces indisponibles'
                                  }
                                ><CardFlagIcon type="pieces" /></span>
                              )}
                              {!intervention.client_tel && (
                                <span className="card-flag card-flag--tel" title="Téléphone client manquant"><CardFlagIcon type="tel" /></span>
                              )}
                              {intervention.client_attend && (
                                <span className="card-flag card-flag--attend" title="Le client attend sur place"><CardFlagIcon type="attend" /></span>
                              )}
                              {intervention.a_courtoisie && (
                                <span className="card-flag card-flag--courtoisie" title="Véhicule de courtoisie prêté sur cet OR"><CardFlagIcon type="courtoisie" /></span>
                              )}
                              {intervention.est_roole && (
                                <span className="card-flag card-flag--roole" title="Client Roole"><CardFlagIcon type="roole" /></span>
                              )}
                              {intervention.au_parking && (
                                <span className="card-flag card-flag--parking" title="Véhicule présent au parking (dernier inventaire)"><CardFlagIcon type="parking" /></span>
                              )}
                              {intervention.a_demande && (
                                <span
                                  className="card-flag card-flag--demande"
                                  title="Demande client en cours — cliquer pour l'ouvrir"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => { e.stopPropagation(); ouvrirDemandeDuVehicule(intervention); }}
                                ><CardFlagIcon type="demande" /></span>
                              )}
                              {/* La cravate ferme la rangée, collée à droite : c'est un
                                  repère qu'on cherche du regard, pas un drapeau parmi
                                  les autres — et sa place ne dépend pas du nombre de
                                  pictos qui la précèdent. */}
                              {intervention.numero_cravate && (() => {
                                const t = teinteCravate(intervention.cravate_couleur);
                                return (
                                  <span className="card-cravate" style={{ background: t.fond, color: t.texte }}
                                    title={`Cravate ${intervention.numero_cravate} (${t.nom.toLowerCase()})`}>
                                    <span className="card-cravate-hole" />
                                    {intervention.numero_cravate}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                          {hoveredInterventionId === intervention.id && (
                            <div className="intervention-tooltip">
                              {intervention.numero_or && (
                                <div className="tooltip-or">#{intervention.numero_or}</div>
                              )}
                              <div className="tooltip-title">{intervention.client}</div>
                              <div className="tooltip-time">
                                {intervention.heure_debut} → {calcEndTime(intervention.heure_debut, intervention.duree)} · {formatDuree(intervention.duree)}
                              </div>
                              <div className="tooltip-meca">{intervention.mecanicien} · {intervention.type}</div>
                              <div className="tooltip-desc">{intervention.description}</div>
                              {intervention.immat && (
                                <div style={{ fontFamily: 'monospace', marginTop: '5px', opacity: 0.85, fontSize: '11px' }}>{intervention.immat}</div>
                              )}
                              {statutPiecesCarte(intervention) && (
                                <div style={{ marginTop: '5px', fontSize: '10px' }}>
                                  {statutPiecesCarte(intervention) === 'disponible' ? '🟢 Pièces disponibles' :
                                   statutPiecesCarte(intervention) === 'en_commande' ? '🟠 Pièces en commande' :
                                   '🔴 Pièces indisponibles'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        </div>
        {mecaniciens.length > 0 && peut('voir.bloc_lateral') && !sideCollapsed && (
          <div
            className="planning-resizer"
            onMouseDown={startResize}
            title="Glisser pour redimensionner les colonnes"
          />
        )}
        {mecaniciens.length > 0 && peut('voir.bloc_lateral') && <aside
          className={`planning-side-block${sideCollapsed ? ' is-collapsed' : ''}`}
          style={!sideCollapsed ? { width: sideWidth } : undefined}
        >
          <div className="planning-side-header">
            {!sideCollapsed && (
              <>
                <div className="header-segmented">
                  {/* L'onglet ACTIF garde son libellé ; les autres se replient en
                      picto seul (le survol donne le nom complet). */}
                  {peut('voir.onglet_reception') && (sideTab === 'reception' && staffReceptionnaires.length > 1 ? (
                    <select
                      className="header-segmented-btn header-segmented-btn--select active"
                      value={focusedReceptionnaire || ''}
                      onChange={(e) => setFocusedReceptionnaire(e.target.value ? parseInt(e.target.value, 10) : null)}
                      title="Réception — filtrer par réceptionnaire"
                    >
                      <option value="">📋 Réception · Tous</option>
                      {staffReceptionnaires.map(r => (
                        <option value={r.id} key={r.id}>📋 Réception · {r.nom || getStaffLabel(r)}</option>
                      ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      className={`header-segmented-btn${sideTab === 'reception' ? ' active' : ' is-picto'}`}
                      onClick={() => setSideTab('reception')}
                      title="Réception"
                    >
                      {sideTab === 'reception' ? '📋 Réception' : '📋'}
                    </button>
                  ))}
                  {peut('voir.onglet_calendrier') && <button
                    type="button"
                    className={`header-segmented-btn${sideTab === 'calendrier' ? ' active' : ' is-picto'}`}
                    onClick={() => setSideTab('calendrier')}
                    title="Charge mensuelle"
                  >
                    {sideTab === 'calendrier' ? '📅 Charge' : '📅'}
                  </button>}
                  {peut('voir.onglet_demandes') && <button
                    type="button"
                    className={`header-segmented-btn${sideTab === 'demandes' ? ' active' : ' is-picto'}`}
                    onClick={() => setSideTab('demandes')}
                    title="Demandes clients"
                  >
                    {sideTab === 'demandes' ? '💬 Demandes clients' : '💬'}
                    {/* Le compte de mes demandes à traiter n'a de valeur que
                        REPLIÉ : l'onglet ouvert a la liste elle-même sous les
                        yeux, la pastille n'y répète qu'une évidence. */}
                    {sideTab !== 'demandes' && demandesCount > 0 && (
                      <span className="side-tab-badge">{demandesCount}</span>
                    )}
                  </button>}
                  {peut('voir.onglet_notifications') && <button
                    type="button"
                    className={`header-segmented-btn${sideTab === 'notifications' ? ' active' : ' is-picto'}`}
                    onClick={() => setSideTab('notifications')}
                    title="Notifications des dossiers"
                  >
                    {sideTab === 'notifications' ? '🔔 Notifications' : '🔔'}
                    {/* Meme regle que la pastille des demandes : elle ne sert
                        que REPLIE, l'onglet ouvert a la liste sous les yeux. */}
                    {sideTab !== 'notifications' && alertesPiecesCount > 0 && (
                      <span className="side-tab-badge">{alertesPiecesCount}</span>
                    )}
                  </button>}
                  {peut('voir.onglet_or') && <button
                    type="button"
                    className={`header-segmented-btn${sideTab === 'or' ? ' active' : ' is-picto'}`}
                    onClick={() => setSideTab('or')}
                    title="Ordres de réparation ouverts"
                  >
                    {sideTab === 'or' ? '🔧 OR' : '🔧'}
                  </button>}
                </div>
              </>
            )}
            <button
              type="button"
              className="planning-side-collapse-btn"
              onClick={() => setSideCollapsed(v => !v)}
              title={sideCollapsed ? 'Déplier le panneau' : 'Replier le panneau'}
              aria-label={sideCollapsed ? 'Déplier' : 'Replier'}
            >
              {sideCollapsed ? '‹' : '›'}
            </button>
          </div>
          {!sideCollapsed && (
            <div className="planning-side-content">
              {/* 🪝 À planifier visible sous les DEUX onglets : c'est depuis
                  Réception qu'on coche « À planifier » dans la modale, le dossier
                  doit rester sous les yeux sans changer d'onglet. */}
              {/* « À planifier » ne vit plus que sous l'onglet Charge (demande
                  utilisateur 2026-08-11) : l'onglet Réception suit les clients
                  du jour, un véhicule sans créneau n'y a pas sa place et
                  allongeait la colonne pour rien. */}
              {sideTab === 'reception' && renderReceptionList()}
              {sideTab === 'calendrier' && (<>{renderChargeCalendar()}{renderAPlanifier()}{renderRdvAConfirmer()}{renderNonHonores()}</>)}
              {sideTab === 'demandes' && renderDemandesList()}
              {sideTab === 'notifications' && renderNotificationsList()}
              {sideTab === 'or' && renderOrList()}
            </div>
          )}
        </aside>}
        </div>
        )}

        {view === 'reception' && peut('voir.onglet_reception') && (
        <div className="planning-block">
        {staffReceptionnaires.length > 0 && <div className="planning-reception-bar">
          <div className="week-navigation">
            <button onClick={goToPreviousDay}>◀</button>
            <button className="planning-current-date" onClick={() => datePickerRef.current?.showPicker?.() || datePickerRef.current?.click()}>
              {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </button>
            <input ref={datePickerRef} type="date" className="planning-date-picker-hidden" value={formatDate(selectedDate)} onChange={(e) => { if (e.target.value) setSelectedDate(new Date(e.target.value + 'T00:00:00')); }} />
            <button onClick={goToNextDay}>▶</button>
            {loading && <span className="planning-loading-indicator">⏳</span>}
          </div>
          {/* Même loupe que dans la barre de l'atelier : la vue Réception n'a pas
              cette barre-là, et sans ce bouton la recherche y disparaîtrait avec
              son retrait de l'en-tête. */}
          <button
            type="button"
            className="atelier-tabs-recherche"
            onClick={() => setRechercheOuverte(true)}
            title="Rechercher un dossier dans tout le planning (Ctrl+K)"
            aria-label="Rechercher un dossier"
          >🔍</button>
        </div>}
        {/* Vue Liste : déplacée dans renderReceptionList() (panneau latéral de la vue Atelier) */}

        {(() => {
          const dayStr = formatDate(selectedDate);
          const todayStr = formatDate(new Date());
          const isCurrentDay = dayStr === todayStr;

          // Tous les RDV du jour (explicites + interventions sans rdv explicite)
          const rdvExpDay = rdvReception.filter(r => r.date === dayStr);
          const rdvIntIds = new Set(rdvExpDay.map(r => r.intervention_id).filter(Boolean));
          const intSansRdvDay = interventions
            .filter(i => i.poste_reception && i.date === dayStr && !rdvIntIds.has(i.id) && i.heure_reception && !i.is_complement)
            .map(i => ({
              id: `int-${i.id}`, poste: i.poste_reception, heure: i.heure_reception, date: i.date,
              client: i.client, client_nom: i.client_nom, client_prenom: i.client_prenom,
              immat: i.immat, immatriculation: i.immatriculation,
              mecanicien: i.mecanicien, intervention_id: i.id, _fromIntervention: true,
            }));
          // Un dossier déjà reçu (statut ≥ « Réceptionné ») n'occupe plus de
          // créneau à recevoir — même règle que la file de droite, notamment
          // après un déplacement d'intervention à un autre jour.
          const allDayRdvs = [...rdvExpDay, ...intSansRdvDay]
            .filter(r => !estDejaRecu(interventions.find(i => i.id === r.intervention_id)));
          // Dossiers du jour sans réceptionnaire : aucune colonne ne peut les
          // accueillir (la grille est bâtie par fiche). Ils forment leur propre
          // colonne, à droite, d'où on les glisse vers le bon réceptionnaire.
          const rdvNonAffectes = interventions
            .filter(i => !i.poste_reception && i.date === dayStr && i.heure_reception && !i.is_complement && !estDejaRecu(i))
            .map(i => ({
              id: `na-${i.id}`, poste: null, heure: i.heure_reception, date: i.date,
              client: i.client, client_nom: i.client_nom, client_prenom: i.client_prenom,
              immat: i.immat, immatriculation: i.immatriculation,
              mecanicien: i.mecanicien, intervention_id: i.id, _fromIntervention: true,
              _nomMeca: (i.commentaire || '').match(/Réceptionnaire\s*:\s*([^\n|]+)/)?.[1]?.trim() || null,
            }))
            .sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));
          // Pour le scaling de la barre de charge : max RDV sur un réceptionnaire (évite /0)
          const maxPerRecep = Math.max(1, ...staffReceptionnaires.map(r => allDayRdvs.filter(x => x.poste === r.id).length));

          if (staffReceptionnaires.length === 0) {
            return (
              <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>👷</div>
                <p style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-secondary)' }}>Aucun réceptionnaire affecté à cet atelier</p>
                <p style={{ fontSize: '13px', marginTop: '6px' }}>Allez dans <strong>⚙️ Configuration → Équipe</strong> pour affecter des réceptionnaires à cet atelier.</p>
              </div>
            );
          }

          return (
            <div className="planning-grid planning-grid--staff-headers week-view week-view--reception" style={{ position: 'relative' }}>
              {renderCurrentTimeLine(38)}
              <div className="time-column time-column-narrow">
                <div className="header-cell header-cell-empty" />
                {heures.map((h, idx) => (
                  <div key={idx} className={`time-slot${h.endsWith(':00') ? ' full-hour' : ''}`}>{h.endsWith(':00') ? h : h.split(':')[1]}</div>
                ))}
              </div>

              {staffReceptionnaires.map((recep) => {
                const recepLabel = getStaffLabel(recep);
                // Un réceptionnaire absent ne reçoit pas. La vue Réception ignorait
                // totalement les absences : elles n'étaient consultées que pour les
                // colonnes mécaniciens de la vue Atelier (constat utilisateur 2026-08-27).
                const absenceRecep = absenceDe(recep.id, selectedDate);
                const recepRdvs = allDayRdvs.filter(r => r.poste === recep.id);
                const chargePct = (recepRdvs.length / maxPerRecep) * 100;
                const chargeColor = recepRdvs.length >= maxPerRecep ? 'var(--color-error)' : recepRdvs.length >= maxPerRecep * 0.75 ? 'var(--color-warning)' : 'var(--color-success)';

                // Grouper par heure pour empiler les RDV simultanés
                const rdvsByHeure = {};
                recepRdvs.forEach(r => {
                  const h = r.heure || '';
                  if (!rdvsByHeure[h]) rdvsByHeure[h] = [];
                  rdvsByHeure[h].push(r);
                });

                return (
                  <div key={recep.id} className={`resource-column${dragOverColumn === recep.id ? ' drag-over' : ''}${estJourneeEntiere(absenceRecep) ? ' resource-column--absent' : ''}`}>
                    <div className="header-cell header-cell--staff">
                      <div className="staff-top">
                        <div className="staff-identity">
                          <strong className="staff-name">{getStaffShortLabel(recep)}</strong>
                          {absenceRecep
                            ? <span className="staff-absent-tag" title={libelleMotifAbsence(absenceRecep.motif)}>{libelleAbsence(absenceRecep)}</span>
                            : recep.specialite && <span className="staff-specialite">{recep.specialite}</span>}
                        </div>
                      </div>
                      <div className="charge-row">
                        <div className="charge-bar-wrapper" title={`${recepRdvs.length} RDV sur ${maxPerRecep} max`}>
                          <div className="charge-bar-fill" style={{ width: `${chargePct}%`, background: chargeColor }} />
                        </div>
                        <span className="resource-count">{recepRdvs.length}</span>
                      </div>
                    </div>

                    <div
                      className={`slots-container${absenceRecep ? ' is-absent' : ''}`}
                      style={{ height: GRID_SLOTS * 38 }}
                      onDragOver={absenceRecep ? undefined : (e) => handleDragOver(e, recep.id, 38)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDropReception(e, recep.id)}
                    >
                      {(() => { const g = geometrieVoile(absenceRecep, 38); return g && (
                        <div className="absence-voile" aria-hidden="true" style={g} />
                      ); })()}
                      {heures.map((h, idx) => (
                        <div key={idx} className={`empty-slot${h.endsWith(':00') ? ' full-hour' : ''}`} />
                      ))}

                      {/* Aperçu de l'heure de l'intervention liée (plafond) pendant un drag réception */}
                      {dragType === 'reception' && dragBoundaryHeure && getHeureIndex(dragBoundaryHeure) >= 0 && (
                        <div className="intervention-ref-marker" style={{ top: `${getHeureIndex(dragBoundaryHeure) * 38}px` }}>
                          <span className="intervention-ref-label">🔧 Intervention {dragBoundaryHeure}</span>
                        </div>
                      )}

                      {/* Indicateur du créneau visé (parité avec la vue Atelier) */}
                      {dragOverColumn === recep.id && dropIndicatorY !== null && (
                        <div className={`drop-indicator${dropForbidden ? ' is-forbidden' : ''}`} style={{ top: `${dropIndicatorY}px` }}>
                          {dropPreviewHeure && (
                            <span className="drop-indicator-label">
                              {dropForbidden ? `⛔ après ${dragBoundaryHeure}` : dropPreviewHeure}
                            </span>
                          )}
                        </div>
                      )}

                      {Object.entries(rdvsByHeure).map(([heure, rdvs]) => {
                        const startIdx = clampSlot(getHeureIndex(heure));
                        if (startIdx < 0) return null;
                        return (
                          <div
                            key={heure}
                            className="reception-semaine-stack"
                            style={{ top: `${startIdx * 38}px` }}
                          >
                            {rdvs.map(r => {
                              const fileEntry = r.intervention_id
                                ? fileAttente.find(f => f.intervention_id === r.intervention_id)
                                : null;
                              // Fond de carte = STATUT ATELIER (2026-08-15, demande
                              // utilisateur) — même règle que les cartes de la grille
                              // Atelier, pour qu'une couleur veuille dire la même
                              // chose dans les deux vues. La couleur vient du DOSSIER,
                              // pas du RDV : un rendez-vous explicite ne la porte pas,
                              // on la retrouve donc par `intervention_id`.
                              const dossierLie = r.intervention_id
                                ? interventions.find(i => i.id === r.intervention_id)
                                : null;
                              const statutCouleur = dossierLie?.statut_atelier_couleur || null;
                              const rscStyle = statutCouleur
                                ? { background: statutCouleur, color: dossierLie.statut_atelier_text_color || getContrastColor(statutCouleur), borderLeftColor: statutCouleur }
                                : undefined;
                              const statut = fileEntry?.statut || 'pas_arrive';
                              const clientLabel = r.client
                                || [r.client_prenom, r.client_nom].filter(Boolean).join(' ')
                                || '—';
                              const immat = r.immat || r.immatriculation || '';
                              // « Arrivé à 08:12 » plutôt que « En attente » — même
                              // vocabulaire que le panneau Réception de la vue Atelier.
                              const statutLabel = statut === 'pas_arrive' ? 'Pas arrivé'
                                : statut === 'en_attente' ? `Arrivé à ${fileEntry?.heure_arrivee ? new Date(fileEntry.heure_arrivee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}`
                                : statut === 'en_cours' ? 'En cours'
                                : 'Reçu';
                              // `traite` partage le style de `termine` (classe sinon absente du CSS)
                              const statutCls = statut === 'traite' ? 'termine' : statut;
                              const canToggle = isCurrentDay && fileEntry && (statut === 'en_attente' || statut === 'en_cours');
                              const nextStatut = statut === 'en_attente' ? 'en_cours' : 'termine';
                              // Les shims (dossiers sans rdv explicite — la majorité des
                              // importés Mécaplanning) se déplacent aussi : le drop écrit
                              // alors heure/poste_reception du dossier (cf. performMove).
                              const canDrag = true;
                              return (
                                <div
                                  key={r.id}
                                  className={`reception-semaine-card${statutCouleur ? ' rsc--statut' : ''}${dossierLie?.client_annule_at ? ' is-annule-client' : ''}${confirmeVisible(dossierLie) ? ' is-confirme-client' : ''}`}
                                  style={rscStyle}
                                  draggable={canDrag && !lectureSeule}
                                  onDragStart={canDrag ? (e) => handleDragStart(e, r, 'reception') : undefined}
                                  onDragEnd={(e) => e.target.classList.remove('dragging')}
                                  onClick={() => handleEditReception(r)}
                                  title={`${heure} · ${clientLabel}${immat ? ' · ' + immat : ''}`}
                                >
                                  {/* Vue Réception : pas de bande (cartes trop basses, libellé illisible) mais
                                      contour épais + pastille ✓ / ✕ devant l'heure (retours 29/10). */}
                                  <div className="rsc-top">
                                    <span className="rsc-heure">
                                      {dossierLie?.client_annule_at
                                        ? <span className="rsc-reponse rsc-reponse--ko" title="Annulé par le client via le lien du SMS">✕</span>
                                        : confirmeVisible(dossierLie)
                                          ? <span className="rsc-reponse rsc-reponse--ok" title="Confirmé par le client via le lien du SMS">✓</span>
                                          : null}
                                      {heure}
                                    </span>
                                    <span className="rsc-client">{clientLabel}</span>
                                  </div>
                                  <div className="rsc-bottom">
                                    {immat && <span className="rsc-immat">{immat}</span>}
                                    <button
                                      type="button"
                                      className={`rsc-status rsc-status--${statutCls}${!canToggle ? ' is-disabled' : ''}`}
                                      onClick={async (e) => {
                                        // Pastille "vitrine" (pas_arrive / termine) :
                                        // on laisse le clic remonter à la carte pour ouvrir la modale d'édition.
                                        if (!canToggle) return;
                                        e.stopPropagation();
                                        try {
                                          await api.put(`/planning/file-attente/${fileEntry.id}`, { statut: nextStatut });
                                          refreshFileAttente();
                                        } catch (err) {
                                          console.error('[file-attente] changement statut', err);
                                        }
                                      }}
                                    >
                                      {statutLabel}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* ── Colonne « Non affectés » ─────────────────────────────────
                  Ces dossiers n'ont pas de fiche réceptionnaire en face (import
                  Mécaplanning : Cédric, Laurina(pro), Dylan (part)…) : aucune
                  colonne ne pouvait les montrer. Ils sont ici, à droite pour ne
                  pas décaler les colonnes que l'équipe connaît, et se glissent
                  vers le bon réceptionnaire — le drop écrit heure ET poste.
                  Colonne SOURCE uniquement : on n'y dépose pas (le retour à
                  « non affecté » n'a pas de sens métier). Masquée si vide. */}
              {rdvNonAffectes.length > 0 && (
                <div className="resource-column resource-column--nonaffecte">
                  <div className="header-cell header-cell--staff">
                    <div className="staff-top">
                      <div className="staff-identity">
                        <strong className="staff-name">Non affectés</strong>
                        <span className="staff-specialite">à répartir</span>
                      </div>
                    </div>
                    <div className="charge-row">
                      <div className="charge-bar-wrapper" title={`${rdvNonAffectes.length} rendez-vous sans réceptionnaire`}>
                        <div className="charge-bar-fill" style={{ width: '100%', background: '#a855f7' }} />
                      </div>
                      <span className="resource-count">{rdvNonAffectes.length}</span>
                    </div>
                  </div>
                  <div className="slots-container" style={{ height: GRID_SLOTS * 38 }}>
                    {heures.map((h, idx) => (<div key={idx} className={`empty-slot${h.endsWith(':00') ? ' full-hour' : ''}`} />))}
                    {Object.entries(rdvNonAffectes.reduce((acc, r) => {
                      (acc[r.heure || ''] = acc[r.heure || ''] || []).push(r);
                      return acc;
                    }, {})).map(([heure, rdvs]) => {
                      const startIdx = clampSlot(getHeureIndex(heure));
                      if (startIdx < 0) return null;
                      return (
                        <div key={heure} className="reception-semaine-stack" style={{ top: `${startIdx * 38}px` }}>
                          {rdvs.map(r => {
                            const clientLabel = r.client || [r.client_prenom, r.client_nom].filter(Boolean).join(' ') || '—';
                            const immat = r.immat || r.immatriculation || '';
                            return (
                              <div
                                key={r.id}
                                className="reception-semaine-card reception-semaine-card--nonaffecte"
                                draggable={!lectureSeule}
                                onDragStart={(e) => handleDragStart(e, r, 'reception')}
                                onDragEnd={(e) => e.target.classList.remove('dragging')}
                                onClick={() => handleEditReception(r)}
                                title={`${heure} · ${clientLabel}${immat ? ' · ' + immat : ''}${r._nomMeca ? ` — Mécaplanning : ${r._nomMeca}` : ''}\nGlisser sur un réceptionnaire pour l'affecter`}
                              >
                                <div className="rsc-top">
                                  <span className="rsc-heure">{heure}</span>
                                  <span className="rsc-client">{clientLabel}</span>
                                </div>
                                <div className="rsc-bottom">
                                  {immat && <span className="rsc-immat">{immat}</span>}
                                  {r._nomMeca && <span className="rsc-source">{r._nomMeca}</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        </div>
        )}

      </>)}
      {/* ── Fin du contenu principal conditionné par la concession ── */}

      {/* ── IA-3 : Popover conflits + suggestions ────────────────────────────── */}
      {conflictPopover && (
        <>
          <div className="conflict-popover-overlay" onClick={() => { setConflictPopover(null); setSuggestions(null); }} />
          <div className="conflict-popover">
            <div className="conflict-popover-header">
              <strong>⚠️ Conflits — {conflictPopover.label}</strong>
              <button onClick={() => { setConflictPopover(null); setSuggestions(null); }}>✕</button>
            </div>

            {conflictPopover.conflicts.map(({ a, b }, i) => (
              <div key={i} className="conflict-item">
                <div className="conflict-item-info">
                  <span className="conflict-times">{a.heure_debut} ({a.duree}h) ↔ {b.heure_debut} ({b.duree}h)</span>
                  <span className="conflict-clients">{a.client || a.client_nom} / {b.client || b.client_nom}</span>
                </div>
                <button
                  className="btn-suggest"
                  onClick={() => handleSuggestReassignment(conflictPopover.staffId, b)}
                  disabled={suggestionsLoading}
                >
                  {suggestionsLoading ? '⏳' : '💡 Suggérer'}
                </button>
              </div>
            ))}

            {suggestions?.staffId === conflictPopover.staffId && suggestions.items.length > 0 && (
              <div className="suggestions-list">
                <div className="suggestions-title">Suggestions de réaffectation :</div>
                {suggestions.items.map((s, i) => (
                  <button key={i} className="suggestion-card" onClick={() => applySuggestion(s)}>
                    <span className="suggestion-meca">{s.mecanicien}</span>
                    <span className="suggestion-heure">à {s.heure}</span>
                    <span className="suggestion-raison">{s.raison}</span>
                  </button>
                ))}
              </div>
            )}

            {suggestions?.staffId === conflictPopover.staffId && suggestions.items.length === 0 && !suggestionsLoading && (
              <div className="suggestions-empty">Aucune réaffectation possible trouvée.</div>
            )}
          </div>
        </>
      )}

      {undoMove && !moveWarning && (
        <div className="planning-undo-toast" role="status">
          <span className="planning-undo-text">↔ {undoMove.label || 'Déplacement appliqué'}</span>
          <button type="button" className="planning-undo-btn" onClick={undoLastMove}>Annuler</button>
          <button type="button" className="planning-undo-close" onClick={() => { clearTimeout(undoTimer.current); setUndoMove(null); }} aria-label="Fermer">✕</button>
        </div>
      )}
      {moveWarning && (
        <div className="planning-move-warning" role="alert">⛔ {moveWarning}</div>
      )}

      {/* Flèches de navigation dossier précédent/suivant (colonne Kanban).
          Rendues au-dessus de la modale, sur ses bords G/D. Sans refermer. */}
      {showModal && idxFrere !== -1 && modalFreres.length > 1 && (
        <>
          <button
            type="button"
            className="modal-nav-arrow modal-nav-prev"
            disabled={idxFrere <= 0}
            onClick={() => naviguerDossier(-1)}
            title="Dossier précédent"
            aria-label="Dossier précédent"
          >‹</button>
          <button
            type="button"
            className="modal-nav-arrow modal-nav-next"
            disabled={idxFrere >= modalFreres.length - 1}
            onClick={() => naviguerDossier(1)}
            title="Dossier suivant"
            aria-label="Dossier suivant"
          >›</button>
          <div className="modal-nav-count">{idxFrere + 1} / {modalFreres.length}</div>
        </>
      )}
      <InterventionModal
        calculerRepartition={calculerRepartition}
        horairesDuMeca={horairesDuMeca}
        lectureSeule={lectureSeule || !peut('dossier.modifier')}
        peut={peut}
        analyseIaEnabled={planningRole?.analyseIaEnabled === true}
        show={showModal || !!confirmOnlineRdv}
        onClose={() => { handleCloseModal(); setConfirmOnlineRdv(null); }}
        onSaved={(infos) => {
          skipNextSSE.current++;
          loadData();
          refreshAPlanifier();
          // Proposition d'un SMS de confirmation — à la création d'un RDV
          // uniquement, jamais sur l'enregistrement d'un dossier existant.
          if (infos?.creation && infos.interventionId) {
            proposerSmsConfirmationRdv(infos.interventionId, infos.clientNom, infos.avecRecap);
          }
        }}
        onStatutAtelierChange={(intvId, statutId) => { recolorerDossier(intvId, statutId); if (estStatutFacture(statutId)) proposerSmsVehiculePret(intvId); }}
        confirmOnlineRdv={confirmOnlineRdv}
        onOnlineRdvConfirmed={() => { setConfirmOnlineRdv(null); loadData(); }}
        intervention={modalIntervention}
        linkedReception={editingId ? rdvReception.find(r => r.intervention_id === editingId.interventionId) || null : null}
        user={user}
        editMode={editMode}
        statutsAtelier={statutsAtelier}
        travauxTypes={travauxTypes}
        mecaniciens={mecaniciens}
        /* Sélecteurs d'édition : liste complète groupée par atelier — l'aperçu
           MiniAtelierGrid, lui, garde la liste filtrée (32 colonnes sinon). */
        groupesMecaniciens={groupesMecaniciens}
        mecaniciensTous={mecaniciensTous}
        receptionnaires={staffReceptionnaires}
        interventions={interventions}
        rdvReception={rdvReception}
        /* Créneaux réellement ouverts : la modale et les mini-aperçus s'alignaient
           sur la constante HEURES (07:00→16:45 en dur) alors que la grille suit
           depuis le 2026-07-28 la plage d'ouverture des ateliers. Un atelier
           ouvrant à 06:00 ne pouvait donc pas recevoir de créneau 06:00 depuis la
           modale, et des créneaux hors ouverture y restaient proposés. */
        heures={heures}
        onQuickMoveIntervention={handleQuickMoveIntervention}
        onQuickMoveReception={handleQuickMoveReception}
        initialFormOverrides={!modalIntervention ? {
          mecanicien: formData.mecanicien,
          mecanicien_id: formData.mecanicien_id,
          heure_debut: formData.heure_debut,
          duree: formData.duree,
          date_intervention: formData.date_intervention,
          // Sans elle, la modale gardait date_reception = aujourd'hui : un RDV
          // créé pour un autre jour partait avec sa réception datée du jour
          // (constaté en réel — « le RDV s'enregistre à la date du jour »).
          date_reception: formData.date_intervention,
        } : null}
        onDelete={() => {
          skipNextSSE.current++;
          loadData();
        }}
        onSummary={(summary) => setSummaryModal(summary)}
      />



      {pendingMove && (() => {
        const moveConflicts = getMoveConflicts(pendingMove);
        return (
        <div className="modal-overlay">
          <div className="modal-content confirm-move-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{pendingMove.type === 'resize' ? 'Confirmer la modification de durée' : pendingMove.type === 'a_planifier' ? 'Planifier ce véhicule' : pendingMove.type === 'non_honore' ? 'Replanifier ce rendez-vous' : 'Confirmer le déplacement'}</h2>

            <div className="move-summary">
              <div className="move-item-info">
                <strong>{pendingMove.item.client}</strong>
                <span className="move-immat">{pendingMove.item.immat}</span>
                {pendingMove.item.type && <span className="move-type">{pendingMove.item.type}</span>}
              </div>

              <div className="move-details">
                <div className="move-from">
                  <div className="move-label">Avant</div>
                  <div className="move-value">
                    {pendingMove.from.heure}
                    {pendingMove.from.dureeLabel && ` — ${pendingMove.from.dureeLabel}`}
                    {pendingMove.from.mecanicien && ` — ${pendingMove.from.mecanicien}`}
                    {pendingMove.from.date && ` — ${pendingMove.from.date}`}
                    {pendingMove.from.poste && ` — Poste ${pendingMove.from.poste}`}
                  </div>
                </div>
                <div className="move-arrow">→</div>
                <div className="move-to">
                  <div className="move-label">Après</div>
                  <div className="move-value">
                    {pendingMove.to.heure}
                    {pendingMove.to.dureeLabel && ` — ${pendingMove.to.dureeLabel}`}
                    {pendingMove.to.mecanicien && ` — ${pendingMove.to.mecanicien}`}
                    {pendingMove.to.date && ` — ${pendingMove.to.date}`}
                    {pendingMove.to.poste && ` — Poste ${pendingMove.to.poste}`}
                  </div>
                </div>
              </div>
            </div>

            {moveConflicts.length > 0 && (
              <div className="move-conflict-warning">
                <span className="move-conflict-icon" aria-hidden="true">⚠️</span>
                <span>
                  Chevauche {moveConflicts.map(c => {
                    const nom = c.client_nom || c.client || 'Client';
                    return `${nom}${c.client_prenom ? ' ' + c.client_prenom : ''} à ${c.heure_debut}`;
                  }).join(', ')}
                </span>
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={cancelMove}>
                Annuler
              </button>
              <button
                type="button"
                className={`btn-primary${moveConflicts.length > 0 ? ' btn-confirm-conflict' : ''}`}
                onClick={confirmMove}
              >
                {moveConflicts.length > 0 ? 'Confirmer malgré le conflit' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>

      {/* ── Recevoir un client de la file : confirmation + SMS ─────────────────
          Le numéro et le texte sont affichés AVANT l'envoi : le message part chez
          un vrai client, on ne le déclenche pas à l'aveugle. Le texte reste
          modifiable, et « Recevoir sans SMS » garde la porte ouverte quand le
          client est déjà devant le comptoir. */}
      {recevoirModal && (() => {
        const nom = recevoirModal.entree?.nom || 'Client';
        const tel = recevoirModal.telephone;
        const trop = (recevoirModal.message || '').length > 160;
        return (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📣 Recevoir {nom}</h2>
                <button className="modal-close" onClick={() => setRecevoirModal(null)} disabled={recevoirBusy}>✕</button>
              </div>
              <div className="modal-body">
                {tel ? (
                  <>
                    <div className="rec-tel">
                      <span className="rec-tel-label">SMS à</span>
                      <span className="rec-tel-num">{tel}</span>
                    </div>
                    <textarea
                      className="rec-message"
                      rows={4}
                      value={recevoirModal.message}
                      onChange={(e) => setRecevoirModal(m => ({ ...m, message: e.target.value }))}
                    />
                    <div className={`rec-compteur${trop ? ' rec-trop' : ''}`}>
                      {(recevoirModal.message || '').length}/160 caractères
                      {trop && ' — au-delà, l’envoi est refusé : raccourcissez le texte'}
                    </div>
                  </>
                ) : (
                  <p className="rec-sans-tel">Aucun numéro de téléphone sur cette entrée : le client sera pris en charge sans SMS.</p>
                )}

                {/* Rattrapage de la marque au comptoir. Le client de la borne a
                    pu répondre « je ne sais pas », et un dossier importé peut
                    n'en porter aucune : c'est ici, le client en face, que le
                    trou se comble le plus sûrement. Un clic, pas de validation
                    séparée — le PATCH part seul. */}
                {marqueFileActive && !recevoirModal.entree?.intervention_id && marquesConcession.length > 0 && (
                  <div className="rec-marque">
                    <span className="rec-marque-lab">Marque du véhicule</span>
                    <div className="rec-marque-choix">
                      {marquesConcession.map(m => {
                        const actif = String(recevoirModal.marque ?? recevoirModal.entree?.marque_effective ?? '').toLowerCase() === String(m.nom).toLowerCase();
                        return (
                          <button
                            key={m.id || m.nom}
                            className={`rec-marque-chip${actif ? ' rec-marque-chip--on' : ''}`}
                            disabled={recevoirBusy}
                            onClick={async () => {
                              const valeur = actif ? '' : m.nom;
                              setRecevoirModal(x => ({ ...x, marque: valeur }));
                              try {
                                await api.patch(`/planning/file-attente/${recevoirModal.entree.id}/marque`, { marque: valeur });
                                await refreshFileAttente();
                              } catch (err) {
                                console.error('[file-attente marque]', err);
                              }
                            }}
                          >
                            <BadgeMarque nom={m.nom} logoUrl={m.logo_url} couleur={m.couleur} taille={18} />
                            {m.nom}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer rec-footer">
                <button className="btn-secondary" onClick={() => setRecevoirModal(null)} disabled={recevoirBusy}>Annuler</button>
                {/* « Déjà reçu » : le client était au comptoir avant qu'on ouvre
                    l'écran. Il va droit au statut final — le serveur horodate
                    quand même la prise en charge, sans quoi la carte afficherait
                    « Reçu » sans heure. */}
                <button className="btn-secondary" onClick={() => confirmerRecevoir(false, 'termine')} disabled={recevoirBusy}>
                  Déjà reçu
                </button>
                <button className="btn-secondary" onClick={() => confirmerRecevoir(false)} disabled={recevoirBusy}>
                  Recevoir sans SMS
                </button>
                {tel && (
                  <button className="btn-primary" onClick={() => confirmerRecevoir(true)}
                    disabled={recevoirBusy || !(recevoirModal.message || '').trim()}>
                    {recevoirBusy ? 'Envoi…' : 'Recevoir avec SMS'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── SMS après déplacement d'une réception ──────────────────────────────
          Le créneau vient de bouger : on PROPOSE de prévenir le client, sans
          jamais envoyer seul. « Ne pas prévenir » est un choix de premier rang. */}
      {smsProposition && (() => {
        const trop = (smsProposition.message || '').length > 160;
        const fermer = () => !smsPropositionBusy && setSmsProposition(null);
        const envoyer = async () => {
          if (smsPropositionBusy) return;
          setSmsPropositionBusy(true);
          try {
            await api.post(`/planning/interventions/${smsProposition.interventionId}/sms`, {
              message: smsProposition.message.trim(),
            });
            setSmsProposition(null);
          } catch (err) {
            console.error('[sms-proposition]', err);
            setSmsProposition(m => m ? { ...m, envoi: err.response?.data?.error || 'Envoi impossible.' } : m);
          } finally {
            setSmsPropositionBusy(false);
          }
        };
        return (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{smsProposition.titre}</h2>
                <button className="modal-close" onClick={fermer} disabled={smsPropositionBusy}>✕</button>
              </div>
              <div className="modal-body">
                <div className="rec-tel">
                  <span className="rec-tel-label">{smsProposition.sousTitre}</span>
                  <span className="rec-tel-num">{smsProposition.telephone}</span>
                </div>
                <textarea
                  className="rec-message"
                  rows={4}
                  value={smsProposition.message}
                  onChange={(e) => setSmsProposition(m => ({ ...m, message: e.target.value }))}
                />
                <div className={`rec-compteur${trop ? ' rec-trop' : ''}`}>
                  {(smsProposition.message || '').length}/160 caractères
                  {trop && ' — au-delà, l’envoi est refusé : raccourcissez le texte'}
                </div>
                {smsProposition.envoi && <p className="rec-sans-tel">⚠️ {smsProposition.envoi}</p>}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={fermer} disabled={smsPropositionBusy}>Ne pas prévenir</button>
                <button className="btn-primary" onClick={envoyer}
                  disabled={smsPropositionBusy || !(smsProposition.message || '').trim()}>
                  {smsPropositionBusy ? 'Envoi…' : '✉️ Envoyer le SMS'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Popover post-lasso : nouveau RDV ou absence collaborateur ────────── */}
      {creneauChoix && (
        <>
          <div className="creneau-choix-fond" onClick={() => setCreneauChoix(null)} />
          <div className="creneau-choix" style={{ left: creneauChoix.x, top: creneauChoix.y }}>
            <div className="creneau-choix-titre">
              {getStaffLabel(creneauChoix.staff)} · {creneauChoix.heure}
              {creneauChoix.heureFin && ` → ${creneauChoix.heureFin}`}
            </div>
            <button type="button" className="creneau-choix-btn" onClick={ouvrirRdvDepuisCreneau}>
              📅 Nouveau RDV
            </button>
            {/* Proposé seulement s'il y a matière : un menu qui ouvre sur une
                liste vide n'aide personne. Le compteur annonce ce qu'on va trouver. */}
            {aPlanifier.length > 0 && (
              <button type="button" className="creneau-choix-btn" onClick={ouvrirChoixDossierDepuisCreneau}>
                🪝 Dossier à planifier <span className="creneau-choix-compte">{aPlanifier.length}</span>
              </button>
            )}
            <button type="button" className="creneau-choix-btn" onClick={ouvrirAbsenceDepuisCreneau}>
              🏖️ {creneauChoix.heureFin ? 'Absence sur ce créneau' : 'Absence collaborateur'}
            </button>
          </div>
        </>
      )}

      {/* ── Choix d'un dossier « à planifier » pour le créneau tracé ────────── */}
      {choixDossier && (() => {
        const liste = filtrerAPlanifier(aPlanifier, filtreAPlanifier);
        return (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>🪝 Planifier un dossier</h2>
                <button className="modal-close" onClick={fermerChoixDossier}>✕</button>
              </div>
              <div className="modal-body">
                <p className="cd-cible">
                  Créneau choisi : <strong>{getStaffLabel(choixDossier.staff)}</strong> · <strong>{choixDossier.heure}</strong>
                </p>
                <input
                  type="text"
                  className="stampyt-immat-input cd-recherche"
                  placeholder="🔍 Nom, immat ou OR…"
                  autoFocus
                  value={filtreAPlanifier}
                  onChange={(e) => setFiltreAPlanifier(e.target.value)}
                />
                {liste.length === 0 ? (
                  <div className="pa-empty">Aucun dossier ne correspond à « {filtreAPlanifier} ».</div>
                ) : (
                  <div className="cd-liste">
                    {liste.map(item => (
                      <button
                        type="button"
                        key={item.id}
                        className="cd-item"
                        onClick={() => planifierDossierSurCreneau(item)}
                      >
                        <span className="cd-item-l1">
                          <strong>{item.immatriculation || '—'}</strong>
                          <span className="cd-item-client">{item.client_nom || item.client || 'Client'}</span>
                        </span>
                        <span className="cd-item-l2">
                          {item.type_intervention || 'Sans RDV'}
                          {item.numero_or ? ` · OR ${item.numero_or}` : ''}
                          {item.heure_reception ? ` · arrivé ${item.heure_reception}` : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Absence rapide depuis la grille ──────────────────────────────────── */}
      {/* [11] Suppression d'une absence ou d'une indisponibilité, au clic droit
          sur son bloc. Deux portées possibles quand elle appartient à une série
          (« tous les vendredis ») : cette occurrence seule, ou la série entière.
          ⚠️ La route est un DELETE, et le verrou par méthode HTTP du Planning
          refuse DELETE à tout non-super-admin : un réceptionnaire ne peut donc
          pas supprimer. C'est le comportement existant, pas une régression —
          mais c'est le premier écran où il se voit. */}
      {suppressionAbsence && (() => {
        const { absence, staff } = suppressionAbsence;
        const indispo = absence.type === 'indisponibilite';
        const serie = !!absence.serie_id;
        const supprimer = async (toute) => {
          setSuppressionAbsence(a => ({ ...a, busy: true, erreur: null }));
          try {
            await api.delete(toute
              ? `/planning/absences/serie/${absence.serie_id}`
              : `/planning/absences/${absence.id}`);
            setSuppressionAbsence(null);
            skipNextSSE.current++;
            await loadData();
          } catch (err) {
            console.error('[suppression-absence]', err);
            setSuppressionAbsence(a => a ? { ...a, busy: false,
              erreur: err.response?.data?.error || 'Suppression impossible — droits insuffisants ?' } : a);
          }
        };
        return (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{indispo ? '🚧 Supprimer l\'indisponibilité' : '🏖️ Supprimer l\'absence'}</h2>
                <button className="modal-close" onClick={() => setSuppressionAbsence(null)} disabled={suppressionAbsence.busy}>✕</button>
              </div>
              <div className="modal-body">
                {/* Rappel du créneau COMPLET : on supprime souvent depuis une
                    journée quelconque de la plage, et le seul jour de début ne
                    dit pas ce qu'on s'apprête à retirer. Une absence posée
                    « jusqu'au 30/10 » doit s'annoncer entière. */}
                {(() => {
                  const fr = (v) => String(v).slice(0, 10).split('-').reverse().join('/');
                  const d = String(absence.date_debut).slice(0, 10);
                  const f = String(absence.date_fin || absence.date_debut).slice(0, 10);
                  const jours = Math.round((new Date(f) - new Date(d)) / 86400000) + 1;
                  return (
                    <p className="absence-rapide-note">
                      <strong>{getStaffLabel(staff)}</strong> — {indispo ? 'Indisponible' : libelleMotifAbsence(absence.motif)}
                      <br />
                      {f !== d
                        ? <>du <strong>{fr(d)}</strong> au <strong>{fr(f)}</strong> · {jours} jours</>
                        : <>le <strong>{fr(d)}</strong></>}
                      {absence.heure_debut ? ` · ${libelleAbsence(absence)}` : ' · journée entière'}
                      {absence.commentaire && <><br /><em>{absence.commentaire}</em></>}
                    </p>
                  );
                })()}
                {serie && (
                  <p className="absence-rapide-aide">
                    Cette occurrence fait partie d'une <strong>série récurrente</strong>.
                    Vous pouvez ne retirer que ce jour, ou toute la série.
                  </p>
                )}
                {suppressionAbsence.erreur && <p className="rec-sans-tel">⚠️ {suppressionAbsence.erreur}</p>}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setSuppressionAbsence(null)} disabled={suppressionAbsence.busy}>Annuler</button>
                {serie && (
                  <button className="btn-secondary" onClick={() => supprimer(true)} disabled={suppressionAbsence.busy}>
                    Toute la série
                  </button>
                )}
                <button className="btn-primary" onClick={() => supprimer(false)} disabled={suppressionAbsence.busy}>
                  {suppressionAbsence.busy ? 'Suppression…' : (serie ? 'Ce jour seulement' : 'Supprimer')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {absenceRapide && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{absenceRapide.type === 'indisponibilite' ? '🚧 Indisponibilité' : '🏖️ Absence'} — {getStaffLabel(absenceRapide.staff)}</h2>
              <button className="modal-close" onClick={() => setAbsenceRapide(null)} disabled={absenceRapide.busy}>✕</button>
            </div>
            <div className="modal-body">
              <p className="absence-rapide-note">
                Le <strong>{absenceRapide.date.split('-').reverse().join('/')}</strong> —
                gérable ensuite dans Configuration ▸ Absences.
              </p>
              <div className="absence-rapide-form">
                {/* [10] Une indisponibilité N'EST PAS une absence : elle accepte
                    qu'on planifie par-dessus. C'est le premier choix du
                    formulaire parce qu'il change le sens de tout le reste. */}
                <div className="absence-rapide-duree">
                  <button type="button"
                    className={`cfg-periode${absenceRapide.type !== 'indisponibilite' ? ' actif' : ''}`}
                    onClick={() => setAbsenceRapide(a => ({ ...a, type: 'absence', motif: a.motif === 'autre' ? 'conges' : a.motif }))}>Absence</button>
                  <button type="button"
                    className={`cfg-periode${absenceRapide.type === 'indisponibilite' ? ' actif' : ''}`}
                    onClick={() => setAbsenceRapide(a => ({ ...a, type: 'indisponibilite', motif: 'autre' }))}>Indisponibilité</button>
                </div>
                <p className="absence-rapide-aide">
                  {absenceRapide.type === 'indisponibilite'
                    ? 'Le créneau reste planifiable : on peut y poser une intervention, qui s\'affichera en parallèle. Sert à réserver quelqu\'un pour les imprévus.'
                    : 'Le créneau est bloqué : aucune intervention ne peut y être placée.'}
                </p>
                <div className="absence-rapide-duree">
                  <button type="button"
                    className={`cfg-periode${absenceRapide.journee ? ' actif' : ''}`}
                    onClick={() => setAbsenceRapide(a => ({ ...a, journee: true }))}>Journée entière</button>
                  <button type="button"
                    className={`cfg-periode${absenceRapide.journee ? '' : ' actif'}`}
                    onClick={() => setAbsenceRapide(a => ({
                      ...a, journee: false,
                      // Rebasculer sans plage tracée doit proposer quelque chose
                      // de saisissable, pas deux champs vides.
                      heure_debut: a.heure_debut || '08:00',
                      heure_fin: a.heure_fin || '10:00',
                    }))}>Créneau</button>
                </div>
                {/* Plage continue, réservée à la JOURNÉE ENTIÈRE : « congés
                    jusqu'au 30/10 » se pose d'un geste depuis le planning, au
                    lieu d'ouvrir Configuration ▸ Absences. Le serveur savait
                    déjà écrire une ligne `date_debut → date_fin` ; le
                    formulaire l'ignorait et renvoyait toujours le même jour.
                    ⚠️ Masqué dès qu'une récurrence est demandée : une plage
                    continue ET un « tous les vendredis » sur la même saisie ne
                    veulent rien dire ensemble. */}
                {absenceRapide.journee && !absenceRapide.repete && (
                  <label>
                    Jusqu'au (optionnel)
                    <input type="date" value={absenceRapide.dateFin || ''}
                      min={absenceRapide.date}
                      title="Laisser vide pour ce seul jour"
                      onChange={(e) => setAbsenceRapide(a => ({ ...a, dateFin: e.target.value, erreur: null }))} />
                  </label>
                )}
                {!absenceRapide.journee && (
                  <div className="cfg-creneau">
                    <input type="time" step="900" value={absenceRapide.heure_debut}
                      onChange={(e) => setAbsenceRapide(a => ({ ...a, heure_debut: e.target.value }))} />
                    <span>→</span>
                    <input type="time" step="900" value={absenceRapide.heure_fin}
                      onChange={(e) => setAbsenceRapide(a => ({ ...a, heure_fin: e.target.value }))} />
                  </div>
                )}
                {/* ⚠️ Le motif est propre aux ABSENCES : « Congé », « Arrêt
                    maladie », « RTT » ne veulent rien dire pour une
                    indisponibilité, qui n'explique pas pourquoi quelqu'un
                    manque mais pourquoi on lui garde du temps. Le sélecteur
                    disparaît donc, et c'est la précision libre qui porte le
                    « pourquoi » — « réservé aux imprévus », « formation
                    interne ». En base, `motif` reste NOT NULL : on y écrit
                    'autre', valeur neutre du référentiel. */}
                {absenceRapide.type !== 'indisponibilite' && (
                  <label>
                    Motif
                    <select value={absenceRapide.motif}
                      onChange={(e) => setAbsenceRapide(a => ({ ...a, motif: e.target.value }))}>
                      {MOTIFS_ABSENCE.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </label>
                )}
                <label>
                  {absenceRapide.type === 'indisponibilite' ? 'Précision (optionnel)' : 'Commentaire (optionnel)'}
                  <input value={absenceRapide.commentaire}
                    placeholder={absenceRapide.type === 'indisponibilite' ? 'ex. : réservé aux imprévus' : ''}
                    onChange={(e) => setAbsenceRapide(a => ({ ...a, commentaire: e.target.value }))} />
                </label>
                {/* [11] Récurrence. Le serveur savait déjà la traiter
                    (`recurrence.jours` + `recurrence.jusqua`, série identifiée
                    par un `serie_id`) : il ne manquait que ce formulaire. */}
                {!absenceRapide.dateFin && (
                <label className="absence-repete">
                  <input type="checkbox" checked={!!absenceRapide.repete}
                    onChange={(e) => setAbsenceRapide(a => ({ ...a, repete: e.target.checked, erreur: null }))} />
                  Répéter chaque semaine
                </label>
                )}
                {absenceRapide.repete && (
                  <div className="absence-recurrence">
                    <div className="absence-jours">
                      {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'].map(j => {
                        const on = (absenceRapide.jours || []).includes(j);
                        return (
                          <button key={j} type="button"
                            className={`absence-jour${on ? ' actif' : ''}`}
                            title={j.charAt(0).toUpperCase() + j.slice(1)}
                            onClick={() => setAbsenceRapide(a => ({
                              ...a, erreur: null,
                              jours: on ? a.jours.filter(x => x !== j) : [...(a.jours || []), j],
                            }))}>
                            {j === 'mardi' ? 'Ma' : j === 'mercredi' ? 'Me' : j.slice(0, 1).toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                    <label>
                      Jusqu'au
                      <input type="date" value={absenceRapide.jusqua || ''}
                        min={absenceRapide.date}
                        onChange={(e) => setAbsenceRapide(a => ({ ...a, jusqua: e.target.value, erreur: null }))} />
                    </label>
                  </div>
                )}
              </div>
              {absenceRapide.erreur && <p className="rec-sans-tel">⚠️ {absenceRapide.erreur}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setAbsenceRapide(null)} disabled={absenceRapide.busy}>Annuler</button>
              <button className="btn-primary" onClick={enregistrerAbsenceRapide} disabled={absenceRapide.busy}>
                {absenceRapide.busy ? 'Enregistrement…'
                  : (() => {
                      const quoi = absenceRapide.type === 'indisponibilite' ? 'l\'indisponibilité' : 'l\'absence';
                      if (absenceRapide.dateFin) {
                        const j = Math.round((new Date(absenceRapide.dateFin) - new Date(absenceRapide.date)) / 86400000) + 1;
                        return `Poser ${quoi} · ${j} jours`;
                      }
                      return `Poser ${quoi}`;
                    })()}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale Demandes clients (réutilisée telle quelle) ──────────────── */}
      {demandeOuverte && demandesRefs && (
        <DemandeModal
          key={demandeOuverte.id}
          demande={demandeOuverte}
          prefill={null}
          responsables={demandesRefs.responsables}
          marques={demandesRefs.marques}
          modeles={demandesRefs.modeles}
          concessions={concessions}
          sujets={demandesRefs.sujets}
          user={user}
          onSave={async (donnees) => {
            try {
              await demandesApi.update(demandeOuverte.id, donnees);
              setDemandeOuverte(null);
              rafraichirDemandes();
            } catch (e) {
              console.error('[panneau demandes/save]', e);
              alert(e.response?.data?.error || "Erreur lors de l'enregistrement.");
            }
          }}
          onOpenExisting={(d) => setDemandeOuverte(d)}
          onArchiver={(d) => { setDemandeOuverte(null); setDemandeAArchiver(d); }}
          onOuvrirDossier={(intervention) => {
            // Dans l'environnement Planning : fermer la demande et rejoindre le
            // dossier comme un résultat de recherche (bon jour, bon atelier).
            setDemandeOuverte(null);
            ouvrirDossierDepuisRecherche(intervention);
          }}
          onClose={() => { setDemandeOuverte(null); rafraichirDemandes(); }}
        />
      )}

      {/* ── Archivage d'une demande depuis le panneau latéral ─────────────────
          Même modale de justification que la page Demandes clients : le
          composant est partagé, les motifs types sont donc identiques. */}
      {demandeAArchiver && (
        <ArchiveDemandeModal
          busy={archivageEnCours}
          onClose={() => setDemandeAArchiver(null)}
          onConfirm={async (motif) => {
            setArchivageEnCours(true);
            try {
              await demandesApi.archive(demandeAArchiver.id, motif);
              setDemandeAArchiver(null);
              await rafraichirDemandes();
            } catch (e) {
              console.error('[panneau demandes/archive]', e);
              alert(e.response?.data?.error || "Erreur lors de l'archivage.");
            } finally {
              setArchivageEnCours(false);
            }
          }}
        />
      )}

      {/* ── Vider la journée (super-admin) ─────────────────────────────────────
          Suppression en masse : la modale annonce le décompte AVANT d'agir, et
          distingue les dossiers importés (regénérables par une resynchro) des
          dossiers saisis à la main, qui eux sont définitivement perdus. */}
      {videJourModal && (() => {
        const c = videJourModal.compte || {};
        const total = Number(c.total || 0);
        const saisies = Number(c.saisies || 0);
        const portee = videJourModal.portee;
        // Au-delà de la journée, un clic ne suffit plus : la portée n'est pas
        // visible à l'écran (on ne voit qu'un jour de la grille) et la
        // suppression est définitive. Recopier le mot engage le geste.
        const confirmationExigee = portee !== 'jour' && total > 0;
        const saisieOk = !confirmationExigee || videJourModal.saisie.trim().toUpperCase() === 'SUPPRIMER';
        return (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>🧹 Vider le planning</h2>
                <button className="modal-close" onClick={() => setVideJourModal(null)} disabled={videJourBusy}>✕</button>
              </div>
              <div className="modal-body">
                <div className="portee-choix">
                  {PORTEES.map(p => (
                    <button
                      key={p.cle}
                      type="button"
                      className={`portee-btn${portee === p.cle ? ' is-active' : ''}`}
                      disabled={videJourBusy}
                      onClick={() => chargerCompteVidage(p.cle)}
                    >
                      <span className="portee-icone">{p.icone}</span>
                      <span className="portee-libelle">{p.libelle}</span>
                      <span className="portee-plage">{libellePortee(p.cle)}</span>
                    </button>
                  ))}
                </div>

                {videJourModal.chargement ? (
                  <p className="vj-chargement">Comptage en cours…</p>
                ) : total === 0 ? (
                  <p>Aucune intervention sur cette période (<strong>{libellePortee(portee)}</strong>) pour cette concession.</p>
                ) : (
                  <>
                    <p>
                      <strong>{total}</strong> intervention{total > 1 ? 's' : ''}
                      {Number(c.jours_peuples) > 1 && <> réparties sur <strong>{c.jours_peuples} jours</strong></>}
                      {' '}— <strong>{libellePortee(portee)}</strong> — vont être supprimées pour cette concession.
                    </p>
                    <ul className="vj-detail">
                      <li>{c.importees || 0} issue{Number(c.importees) > 1 ? 's' : ''} de Mécaplanning — un nouvel import les recrée</li>
                      <li className={saisies > 0 ? 'vj-alerte' : undefined}>
                        {saisies} saisie{saisies > 1 ? 's' : ''} à la main
                        {saisies > 0 ? ' — définitivement perdue' + (saisies > 1 ? 's' : '') : ''}
                      </li>
                      {Number(c.commentaires) > 0 && <li>{c.commentaires} commentaire(s) interne(s) supprimé(s) avec elles</li>}
                      {Number(c.complements) > 0 && <li>{c.complements} planification(s) complémentaire(s) supprimée(s) avec elles</li>}
                    </ul>
                    <p className="vj-avert">
                      Cette action est <strong>définitive</strong>
                      {portee === 'jour'
                        ? ' et ne concerne que la journée affichée.'
                        : ' et porte sur des jours que vous ne voyez pas à l\'écran.'}
                    </p>
                    {confirmationExigee && (
                      <label className="vj-confirm-saisie">
                        <span>Tapez <strong>SUPPRIMER</strong> pour confirmer</span>
                        <input
                          className="page-search"
                          value={videJourModal.saisie}
                          disabled={videJourBusy}
                          onChange={e => setVideJourModal(m => ({ ...m, saisie: e.target.value }))}
                          placeholder="SUPPRIMER"
                          autoFocus
                        />
                      </label>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setVideJourModal(null)} disabled={videJourBusy}>Annuler</button>
                {total > 0 && (
                  <button className="btn-primary vj-confirmer" onClick={confirmerVideJour} disabled={videJourBusy || !saisieOk}>
                    {videJourBusy ? 'Suppression…' : `🗑️ Supprimer les ${total} interventions`}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Import Mécaplanning : choix de la portée puis progression ────────
          La boucle est côté client, jour par jour (cf. lancerMecaSync). */}
      {mecaModal && (() => {
        const a = mecaModal.avance;
        const tagsEnCours = !!mecaModal.avanceTags && !mecaModal.avanceTags.termine;
        const enCours = (!!a && !a.termine) || tagsEnCours;
        const jours = a ? a.total : joursOuvresDePlage(mecaModal.portee).length;
        // Concession CIBLE de l'import, affichée en permanence : un import lancé
        // « en croyant être ailleurs » a purgé la mauvaise concession (2026-08-10).
        const concessionIdImport = user?.concession_id || Number(localStorage.getItem('crm-global-concession')) || null;
        const concessionImport = concessions.find(c => c.id === concessionIdImport)?.nom || null;
        const nomNormalise = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const confirmationValide = !!mecaModal.confirmImport && !!concessionImport
          && nomNormalise(mecaModal.confirmImport.saisie) === nomNormalise(concessionImport);
        const lancerApresConfirmation = () => {
          if (!confirmationValide) return;
          const portee = mecaModal.portee;
          setMecaModal(m => (m ? { ...m, confirmImport: null } : m));
          lancerMecaSync(portee);
        };
        return (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>🔄 Importer depuis Mécaplanning</h2>
                <button className="modal-close" onClick={() => setMecaModal(null)} disabled={enCours}>✕</button>
              </div>
              <div className="modal-body">
                <div className={`mi-concession${concessionImport ? '' : ' mi-concession--absente'}`}>
                  🏢 Concession&nbsp;: <strong>{concessionImport || 'aucune sélectionnée'}</strong>
                </div>
                {/* Deux modes : rejouer une période, ou aller chercher UN dossier
                    qui manque — sans purger le jour, donc sans perdre les statuts
                    et commentaires saisis sur les autres cartes. */}
                {!a && !mecaModal.avanceTags && !mecaModal.confirmImport && (
                  <div className="mi-modes">
                    <button type="button" className={`mi-mode${mecaModal.mode !== 'dossier' && mecaModal.mode !== 'tags' ? ' is-active' : ''}`}
                      onClick={() => setMecaModal(m => ({ ...m, mode: 'periode' }))}>📅 Par période</button>
                    <button type="button" className={`mi-mode${mecaModal.mode === 'dossier' ? ' is-active' : ''}`}
                      onClick={() => setMecaModal(m => ({ ...m, mode: 'dossier' }))}>🎯 Un dossier précis</button>
                    <button type="button" className={`mi-mode${mecaModal.mode === 'tags' ? ' is-active' : ''}`}
                      onClick={() => setMecaModal(m => ({ ...m, mode: 'tags' }))}>🔖 Statuts → tags</button>
                  </div>
                )}

                {/* ── Mode Statuts → tags : portée puis progression ── */}
                {mecaModal.mode === 'tags' && !mecaModal.avanceTags && (
                  <>
                    <div className="portee-choix">
                      {PORTEES.map(p => {
                        const n = joursOuvresDePlage(p.cle).length;
                        return (
                          <button key={p.cle} type="button"
                            className={`portee-btn${mecaModal.portee === p.cle ? ' is-active' : ''}`}
                            onClick={() => setMecaModal(m => ({ ...m, portee: p.cle }))}>
                            <span className="portee-icone">{p.icone}</span>
                            <span className="portee-libelle">{p.libelle}</span>
                            <span className="portee-plage">{libellePortee(p.cle)}</span>
                            <span className="portee-compte">{n} jour{n > 1 ? 's' : ''}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="mi-note">
                      Pose les indicateurs Mécaplanning (« ATTENTE ACCORD CLIENT », « VEHICULE IMMOBILISE PIECE »…)
                      comme <strong>tags</strong> sur les dossiers <strong>déjà importés</strong> — y compris ceux dont
                      vous avez corrigé la date à la main.
                    </p>
                    <p className="mi-note">
                      <strong>Ne modifie aucun dossier</strong> : ni date, ni heure, ni mécanicien, ni statut.
                      Un indicateur doit avoir un tag du même nom pour être posé (Configuration ▸ Tags) ;
                      un indicateur retiré côté Mécaplanning ne retire pas le tag ici.
                    </p>
                  </>
                )}

                {mecaModal.mode === 'tags' && mecaModal.avanceTags && (() => {
                  const t = mecaModal.avanceTags;
                  return (
                    <>
                      <div className="mi-barre"><div className="mi-barre-jauge" style={{ width: `${t.total ? (t.fait / t.total) * 100 : 0}%` }} /></div>
                      <p className="mi-avance">
                        {t.termine
                          ? (t.interrompu ? 'Import interrompu.' : (t.aucunTag ? 'Aucun tag actif dans cette concession — rien à poser.' : 'Import terminé.'))
                          : <>Jour {t.fait + 1} sur {t.total}{t.jour ? <> — {formatDateFr(t.jour)}</> : null}…</>}
                      </p>
                      <ul className="vj-detail">
                        <li><strong>{t.poses.length}</strong> tag{t.poses.length > 1 ? 's' : ''} posé{t.poses.length > 1 ? 's' : ''}</li>
                        <li>{t.dejaPoses} déjà posé{t.dejaPoses > 1 ? 's' : ''} (rien à faire)</li>
                        {t.echecs.length > 0 && (
                          <li className="vj-alerte">{t.echecs.length} jour{t.echecs.length > 1 ? 's' : ''} en échec — {t.echecs[0].motif}</li>
                        )}
                      </ul>
                      {t.poses.length > 0 && (
                        <div className="mi-poses">
                          {t.poses.slice(0, 12).map((x, i) => (
                            <div key={i} className="mi-pose-ligne">
                              <span className="mi-pose-tag">{x.tag}</span>
                              <span className="mi-pose-dossier">{x.client}{x.immat ? ` · ${x.immat}` : ''}</span>
                            </div>
                          ))}
                          {t.poses.length > 12 && <div className="mi-note">… et {t.poses.length - 12} autre(s)</div>}
                        </div>
                      )}
                      {/* Ce bloc dit à l'utilisateur QUEL tag créer : c'est lui qui
                          transforme un indicateur ignoré en indicateur importé. */}
                      {Object.keys(t.sansTag).length > 0 && (
                        <div className="mi-sans-tag">
                          <strong>Indicateurs sans tag correspondant</strong> — créez le tag du même nom pour les importer :
                          <ul className="vj-detail">
                            {Object.entries(t.sansTag).map(([lib, n]) => (
                              <li key={lib}>« {lib} » — vu {n > 1 ? `sur ${n} dossiers` : 'sur 1 dossier'}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  );
                })()}

                {!a && mecaModal.mode === 'dossier' && (
                  <>
                    <p className="mi-note">
                      Recherche dans la journée Mécaplanning du <strong>{formatDateFr(formatDate(selectedDate))}</strong> —
                      nom du client, immatriculation ou n° d'OR. Seul le dossier choisi est importé,
                      le reste de la journée n'est pas touché.
                    </p>
                    <div className="mi-recherche-ligne">
                      <input
                        className="page-search"
                        autoFocus
                        placeholder="🔍 ROGLY, GD523SD, 011040…"
                        value={mecaModal.q}
                        onChange={e => setMecaModal(m => ({ ...m, q: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') rechercherDossierMeca(); }}
                      />
                      <button className="btn-primary" onClick={rechercherDossierMeca}
                        disabled={mecaModal.rechercheEnCours || (mecaModal.q || '').trim().length < 2}>
                        {mecaModal.rechercheEnCours ? '⏳' : 'Chercher'}
                      </button>
                    </div>
                    {mecaModal.erreurRecherche && (
                      <p className="mi-note" style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>❌ {mecaModal.erreurRecherche}</p>
                    )}
                    {mecaModal.resultats && !mecaModal.resultats.length && !mecaModal.erreurRecherche && (
                      <p className="mi-note">
                        Aucun dossier ne correspond côté Mécaplanning ce jour-là
                        {typeof mecaModal.totalJour === 'number' ? ` (${mecaModal.totalJour} rendez-vous dans la journée)` : ''}.
                        Vérifiez la date affichée : la recherche porte sur elle.
                      </p>
                    )}
                    {(mecaModal.resultats || []).map(r => (
                      <div key={r.rdvId} className="mi-dossier">
                        <div className="mi-dossier-infos">
                          <strong>{r.client}</strong>
                          {r.immat && <span className="mi-dossier-immat">{r.immat}</span>}
                          <span className="mi-dossier-detail">
                            {r.debut}{r.fin ? `–${r.fin}` : ''} · {r.mecaniciens.join(', ') || 'mécanicien inconnu'}
                            {r.or ? ` · OR ${r.or}` : ''}
                          </span>
                          {/* La question qu'on se pose vraiment quand « un dossier ne
                              remonte pas » : absent, ou déjà là mais mal placé ? */}
                          {r.importe ? (
                            r.importe.echec
                              ? <span className="mi-dossier-etat" style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>❌ L'import a échoué</span>
                              : <span className="mi-dossier-etat" style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))' }}>
                                  ✓ Importé à {r.importe.heure} chez {r.importe.mecanicien}
                                  {r.importe.secours ? ' (affectation de secours — à corriger sur la carte)' : ''}
                                </span>
                          ) : r.deja_dans_crm ? (
                            <span className="mi-dossier-etat">
                              ⚠️ Déjà dans le CRM à {r.deja_dans_crm.heure} chez {r.deja_dans_crm.mecanicien || '—'} —
                              réimporter le remplacera par la version Mécaplanning
                            </span>
                          ) : (
                            <span className="mi-dossier-etat">Absent du CRM</span>
                          )}
                        </div>
                        {!r.importe && (
                          <button className="btn-primary btn-sm"
                            onClick={() => importerUnDossierMeca(r)}
                            disabled={!!mecaModal.importEnCours}>
                            {mecaModal.importEnCours === r.rdvId ? '⏳' : (r.deja_dans_crm ? 'Réimporter' : 'Importer')}
                          </button>
                        )}
                      </div>
                    ))}
                  </>
                )}

                {!a && mecaModal.confirmImport && (
                  <div className="mi-confirmation">
                    <p className="mi-confirm-avert">
                      ⚠️ Import <strong>destructif</strong> : les dossiers importés des <strong>{jours} jour{jours > 1 ? 's' : ''}</strong> de
                      la période seront <strong>purgés puis recréés</strong> depuis Mécaplanning, pour la concession :
                    </p>
                    <p className="mi-confirm-nom">{concessionImport}</p>
                    <label className="mi-confirm-label">
                      Pour confirmer, tapez le nom de la concession ci-dessus :
                      <input
                        className="mi-confirm-input"
                        autoFocus
                        value={mecaModal.confirmImport.saisie}
                        onChange={e => setMecaModal(m => (m ? { ...m, confirmImport: { ...m.confirmImport, saisie: e.target.value } } : m))}
                        onKeyDown={e => { if (e.key === 'Enter') lancerApresConfirmation(); }}
                        placeholder={concessionImport || ''}
                      />
                    </label>
                  </div>
                )}

                {!a && mecaModal.mode !== 'dossier' && mecaModal.mode !== 'tags' && !mecaModal.confirmImport && (
                  <>
                    <div className="portee-choix">
                      {PORTEES.map(p => {
                        const n = joursOuvresDePlage(p.cle).length;
                        return (
                          <button
                            key={p.cle}
                            type="button"
                            className={`portee-btn${mecaModal.portee === p.cle ? ' is-active' : ''}`}
                            onClick={() => setMecaModal(m => ({ ...m, portee: p.cle }))}
                          >
                            <span className="portee-icone">{p.icone}</span>
                            <span className="portee-libelle">{p.libelle}</span>
                            <span className="portee-plage">{libellePortee(p.cle)}</span>
                            <span className="portee-compte">{n} jour{n > 1 ? 's' : ''}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="mi-note">
                      Les dimanches sont écartés — l'atelier est fermé. Comptez environ{' '}
                      <strong>{Math.max(1, Math.round(jours * 2.5))} s</strong> pour {jours} jour{jours > 1 ? 's' : ''}.
                    </p>
                    <p className="mi-note">
                      Un dossier déjà importé est <strong>mis à jour</strong>, jamais dupliqué — mais son
                      statut atelier n'est <strong>pas</strong> réécrit, pour ne pas effacer une saisie faite ici.
                    </p>
                  </>
                )}

                {a && (
                  <>
                    <div className="mi-barre"><div className="mi-barre-jauge" style={{ width: `${a.total ? (a.fait / a.total) * 100 : 0}%` }} /></div>
                    <p className="mi-avance">
                      {a.termine
                        ? (a.interrompu ? 'Import interrompu.' : 'Import terminé.')
                        : <>Jour {a.fait + 1} sur {a.total}{a.jour ? <> — {formatDateFr(a.jour)}</> : null}…</>}
                    </p>
                    <ul className="vj-detail">
                      <li><strong>{a.importes}</strong> intervention{a.importes > 1 ? 's' : ''} créée{a.importes > 1 ? 's' : ''}</li>
                      <li>{a.maj} mise{a.maj > 1 ? 's' : ''} à jour</li>
                      {a.echecs.length > 0 && (
                        <li className="vj-alerte">
                          {a.echecs.length} jour{a.echecs.length > 1 ? 's' : ''} en échec :{' '}
                          {a.echecs.slice(0, 3).map(e => formatDateFr(e.jour)).join(', ')}
                          {a.echecs.length > 3 ? '…' : ''}
                          <div className="mi-motif">{a.echecs[0].motif}</div>
                        </li>
                      )}
                    </ul>
                  </>
                )}
              </div>
              <div className="modal-footer">
                {!a && mecaModal.mode === 'dossier' && (
                  <button className="btn-secondary" onClick={() => setMecaModal(null)} disabled={!!mecaModal.importEnCours}>Fermer</button>
                )}
                {mecaModal.mode === 'tags' && !mecaModal.avanceTags && <>
                  <button className="btn-secondary" onClick={() => setMecaModal(null)}>Annuler</button>
                  <button className="btn-primary" onClick={() => lancerSyncIndicateurs(mecaModal.portee)}
                    disabled={joursOuvresDePlage(mecaModal.portee).length === 0}>
                    🔖 Importer les statuts ({joursOuvresDePlage(mecaModal.portee).length} jour{joursOuvresDePlage(mecaModal.portee).length > 1 ? 's' : ''})
                  </button>
                </>}
                {mecaModal.mode === 'tags' && mecaModal.avanceTags && !mecaModal.avanceTags.termine && (
                  <button className="btn-secondary" onClick={() => { mecaAnnule.current = true; }}>
                    Interrompre après ce jour
                  </button>
                )}
                {mecaModal.mode === 'tags' && mecaModal.avanceTags?.termine && (
                  <button className="btn-primary" onClick={() => setMecaModal(null)}>Fermer</button>
                )}
                {!a && mecaModal.mode !== 'dossier' && mecaModal.mode !== 'tags' && !mecaModal.confirmImport && <>
                  <button className="btn-secondary" onClick={() => setMecaModal(null)}>Annuler</button>
                  <button className="btn-primary" onClick={() => setMecaModal(m => (m ? { ...m, confirmImport: { saisie: '' } } : m))}
                    disabled={jours === 0 || !concessionImport}>
                    🔄 Importer {jours} jour{jours > 1 ? 's' : ''}
                  </button>
                </>}
                {!a && mecaModal.confirmImport && <>
                  <button className="btn-secondary" onClick={() => setMecaModal(m => (m ? { ...m, confirmImport: null } : m))}>Retour</button>
                  <button className="btn-primary" onClick={lancerApresConfirmation} disabled={!confirmationValide}>
                    🔄 Lancer l'import ({jours} jour{jours > 1 ? 's' : ''})
                  </button>
                </>}
                {enCours && (
                  <button className="btn-secondary" onClick={() => { mecaAnnule.current = true; }}>
                    Interrompre après ce jour
                  </button>
                )}
                {a && a.termine && (
                  <button className="btn-primary" onClick={() => setMecaModal(null)}>Fermer</button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ Menu contextuel — panneau à colonnes ═════════════════════════════
          Colonne 1 : les actions. Colonne 2 : le contenu de l'action SURVOLÉE
          (aucun clic nécessaire). Colonne 3 : « Et ensuite ? », proposée après
          une écriture pour enchaîner les gestes qui la suivent naturellement.
          Le menu ne se referme plus après une action : seuls Échap, un clic à
          l'extérieur ou « Terminer » le ferment. */}
      {contextMenu && (() => {
        const intv = contextMenu.intervention;
        const action = CTX_ACTIONS.find(a => a.cle === ctxCol2);
        const suitesProposees = ctxSuites ? (CTX_ACTIONS.find(a => a.cle === ctxSuites.action)?.suites || []) : [];
        const montreSuites = !!ctxSuites && suitesProposees.length > 0;
        const profondeur = montreSuites ? 3 : (action?.colonne ? 2 : 1);
        // Tablette en portrait : 740 px ne tiennent pas. On n'affiche alors que
        // la colonne la plus profonde, avec un retour arrière.
        const colonnes = contextMenu.etroit
          ? [profondeur]
          : [1, action?.colonne ? 2 : 0, montreSuites ? 3 : 0].filter(Boolean);

        const filtre = ctxFiltre.trim().toLowerCase();
        const garde = (txt) => !filtre || (txt || '').toLowerCase().includes(filtre);
        const zones = ctxParking?.zones || [];
        const position = ctxParking?.position || null;
        const messages = ctxMessages || [];

        const retour = () => { if (montreSuites) setCtxSuites(null); else setCtxCol2(null); };

        // ── Contenus réutilisés par les colonnes 2 et 3 ─────────────────────
        const blocParking = (depuisSuites) => (
          <>
            <div className="ctx-note">
              {!ctxParking ? 'Chargement…'
                : position?.zone
                  ? `${position.zone} · maj ${formatMajParking(position.maj)}${position.par ? ` par ${position.par}` : ''}`
                  : 'Aucune position connue'}
            </div>
            {ctxParking && zones.length === 0 && (
              <div className="ctx-ligne is-disabled">Aucun parking configuré pour cette concession</div>
            )}
            {zones.filter(z => garde(z.nom)).map(z => (
              <div
                key={z.id}
                className={`ctx-ligne${position?.zone === z.nom ? ' is-active' : ''}${ctxBusy === `parking-${z.nom}` ? ' is-busy' : ''}`}
                onClick={() => declarerParking(intv, z.nom, { depuisSuites })}
              >
                <span className="ctx-ligne-icone">📍</span>
                <span className="ctx-ligne-txt">{z.nom}</span>
              </div>
            ))}
          </>
        );

        const blocCommentaire = (depuisSuites) => (
          <div className="ctx-form">
            <textarea
              className="ctx-textarea"
              rows={3}
              placeholder="Note interne sur le dossier…"
              value={ctxCommentaire}
              onChange={(e) => setCtxCommentaire(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  enregistrerCommentaire(intv, ctxCommentaire, { depuisSuites });
                }
              }}
            />
            <button
              type="button"
              className="ctx-btn-valider"
              disabled={!ctxCommentaire.trim() || ctxBusy === 'commentaire'}
              onClick={() => enregistrerCommentaire(intv, ctxCommentaire, { depuisSuites })}
            >
              {ctxBusy === 'commentaire' ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        );

        const blocClient = () => (
          <>
            {intv.client_email ? (
              <a
                className="ctx-ligne ctx-lien"
                href={`mailto:${intv.client_email}?subject=${encodeURIComponent('Votre rendez-vous atelier')}`}
              >
                <span className="ctx-ligne-icone">📧</span>
                <span className="ctx-ligne-txt">{intv.client_email}</span>
              </a>
            ) : (
              <div className="ctx-ligne is-disabled"><span className="ctx-ligne-icone">📧</span><span className="ctx-ligne-txt">Aucun email renseigné</span></div>
            )}
            {intv.client_email && messages.filter(m => m.canal !== 'sms').filter(m => garde(m.libelle)).map(m => (
              <a
                key={`mail-${m.id}`}
                className="ctx-ligne ctx-lien"
                href={`mailto:${intv.client_email}?subject=${encodeURIComponent(m.objet || m.libelle)}&body=${encodeURIComponent(m.contenu)}`}
              >
                <span className="ctx-ligne-icone">📧</span>
                <span className="ctx-ligne-txt">{m.libelle}</span>
              </a>
            ))}
            {intv.client_tel ? (
              <>
                <div className="ctx-note">💬 SMS · {intv.client_tel}</div>
                {ctxMessages === null && <div className="ctx-ligne is-disabled">Chargement des modèles…</div>}
                {ctxMessages && messages.filter(m => m.canal !== 'email').length === 0 && (
                  <div className="ctx-ligne is-disabled">Aucun message type — à configurer dans Planning ▸ Messages types</div>
                )}
                {messages.filter(m => m.canal !== 'email').filter(m => garde(m.libelle)).map(m => (
                  <div key={m.id} className="ctx-ligne" onClick={() => ouvrirSms(intv, m)}>
                    <span className="ctx-ligne-icone">💬</span>
                    <span className="ctx-ligne-txt">{m.libelle}</span>
                  </div>
                ))}
                <div className="ctx-ligne" onClick={() => ouvrirSms(intv, null)}>
                  <span className="ctx-ligne-icone">✏️</span>
                  <span className="ctx-ligne-txt">Message libre…</span>
                </div>
              </>
            ) : (
              <div className="ctx-ligne is-disabled"><span className="ctx-ligne-icone">💬</span><span className="ctx-ligne-txt">Aucun téléphone renseigné</span></div>
            )}
          </>
        );

        // ── Colonne 2 : le contenu de l'action survolée ─────────────────────
        const contenuColonne2 = () => {
          switch (action?.colonne) {
            case 'statuts':
              return (
                <>
                  {/* Sans « tous les statuts », le compte ne peut ni effacer un
                      statut ni sortir des trois du compagnon (verrou serveur identique). */}
                  {peut('dossier.statut_atelier_tous') && (
                    <div
                      className={`ctx-ligne${!intv.statut_atelier_id ? ' is-active' : ''}`}
                      onClick={() => handleCtxStatutAtelier(null)}
                    >
                      <span className="ctx-pastille ctx-pastille--vide" />
                      <span className="ctx-ligne-txt">— Non défini —</span>
                    </div>
                  )}
                  {statutsAtelier.filter(s => garde(s.nom))
                    .filter(s => peut('dossier.statut_atelier_tous') || estStatutTechnicien(s.nom))
                    .map(s => (
                    <div
                      key={s.id}
                      className={`ctx-ligne${intv.statut_atelier_id === s.id ? ' is-active' : ''}${ctxBusy === `statut-${s.id}` ? ' is-busy' : ''}`}
                      onClick={() => handleCtxStatutAtelier(s.id)}
                    >
                      <span className="ctx-pastille" style={{ background: s.couleur }} />
                      <span className="ctx-ligne-txt">{s.nom}</span>
                    </div>
                  ))}
                </>
              );
            case 'tags': {
              const poses = intv.tags || [];
              return (
                <>
                  <div className="ctx-note">
                    {poses.length === 0
                      ? 'Aucun tag sur ce dossier'
                      : `${poses.length} tag${poses.length > 1 ? 's' : ''} · cliquez pour ajouter ou retirer`}
                  </div>
                  {tags.filter(t => garde(t.nom)).map(t => {
                    const actif = poses.some(p => p.id === t.id);
                    return (
                      <div
                        key={t.id}
                        className={`ctx-ligne${actif ? ' is-active' : ''}`}
                        onClick={() => handleCtxToggleTag(t)}
                      >
                        <span className="ctx-pastille" style={{ background: t.couleur }} />
                        <span className="ctx-ligne-txt">{t.nom}</span>
                        {/* La coche est le seul retour visible : la colonne ne se
                            referme pas et la carte ne porte volontairement aucune
                            marque, il faut donc voir ici ce qui est posé. */}
                        <span className="ctx-tag-coche">{actif ? '✓' : ''}</span>
                      </div>
                    );
                  })}
                </>
              );
            }
            // Déplacer et Ajouter : UN SEUL formulaire, deux effets. Ce qui les
            // sépare est écrit en tête du panneau plutôt que laissé à deviner —
            // c'est la confusion la plus probable entre les deux entrées.
            case 'planif':
            case 'ajouter': {
              const ajout = ctxCol2 === 'ajouter';
              const dateDe = (n) => {
                const d = new Date((intv.date_intervention || intv.date) + 'T00:00:00');
                d.setDate(d.getDate() + n);
                return toYmd(d);
              };
              const raccourcis = [
                { label: 'Demain', date: toYmd(lendemainOuvertDe(intv).jour) },
                { label: '+ 1 sem.', date: dateDe(7) },
              ];
              return (
                <div className="ctx-form">
                  <div className={`ctx-effet ctx-effet--${ajout ? 'ajout' : 'deplacement'}`}>
                    <span className="ctx-effet-ic">{ajout ? '＋' : '↔'}</span>
                    <span>
                      {ajout
                        ? <><strong>Une seconde intervention</strong> est créée sur le même OR. Celle d'aujourd'hui <strong>reste en place</strong>.</>
                        : <><strong>L'intervention part en entier</strong> sur le nouveau créneau. Rien ne reste sur celui d'aujourd'hui.</>}
                    </span>
                  </div>
                  <div className="ctx-raccourcis">
                    {raccourcis.map(r => (
                      <button key={r.label} type="button"
                        className={`ctx-raccourci${ctxPlanifForm.date === r.date ? ' is-active' : ''}`}
                        onClick={() => setCtxPlanifForm(f => ({ ...f, date: r.date }))}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                  <label className="ctx-champ">
                    <span>Date</span>
                    <input type="date" value={ctxPlanifForm.date}
                      onChange={e => setCtxPlanifForm(f => ({ ...f, date: e.target.value }))} />
                  </label>
                  <label className="ctx-champ">
                    <span>Heure</span>
                    <input type="time" step="900" value={ctxPlanifForm.heure}
                      onChange={e => setCtxPlanifForm(f => ({ ...f, heure: e.target.value }))} />
                  </label>
                  <label className="ctx-champ">
                    <span>Durée</span>
                    <select value={ctxPlanifForm.duree}
                      onChange={e => setCtxPlanifForm(f => ({ ...f, duree: e.target.value }))}>
                      {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7, 8].map(d => (
                        <option key={d} value={d}>{formatDuree(d)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="ctx-champ">
                    <span>Mécanicien</span>
                    {/* Liste COMPLÈTE groupée par atelier : programmer doit pouvoir
                        changer d'atelier, pas seulement d'heure. */}
                    <select value={ctxPlanifForm.mecanicien_id}
                      onChange={e => setCtxPlanifForm(f => ({ ...f, mecanicien_id: e.target.value }))}>
                      {groupesMecaniciens.map(g => (
                        <optgroup key={g.atelier} label={g.atelier}>
                          {g.mecas.map(m => <option key={m.id} value={m.id}>{getStaffLabel(m)}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </label>
                  {ctxDispo && (
                    <div className={`ctx-dispo ctx-dispo--${ctxDispo.etat}`}>
                      <span>{ctxDispo.etat === 'ok' ? '✔' : ctxDispo.etat === 'load' ? '…' : '⚠'}</span>
                      <span>{ctxDispo.texte}</span>
                    </div>
                  )}
                  <label className="ctx-champ ctx-champ--motif">
                    <span>Motif</span>
                    <input type="text" value={ctxMotif} maxLength={200}
                      placeholder={ajout ? 'Pièces en attente…' : 'Facultatif…'}
                      onChange={e => setCtxMotif(e.target.value)} />
                  </label>
                  <button
                    type="button"
                    className={`ctx-btn-valider${ajout ? ' ctx-btn-valider--ajout' : ''}`}
                    disabled={ctxBusy === 'ajouter'}
                    onClick={ajout ? handleCtxAjoutSubmit : handleCtxPlanifSubmit}
                  >
                    {ajout ? "Ajouter l'intervention" : "Déplacer l'intervention"}
                  </button>
                </div>
              );
            }
            case 'lendemain':
              return (
                <div className="ctx-durees">
                  {[0.25, 0.5, 0.75, 1, 1.5, 2].map(d => (
                    <button
                      key={d}
                      type="button"
                      className="ctx-duree-btn"
                      disabled={ctxBusy === `lendemain-${d}`}
                      onClick={() => handleCtxLendemain(d)}
                    >
                      {formatDuree(d)}
                    </button>
                  ))}
                </div>
              );
            case 'nonHonore':
              return (
                <>
                  <div className="ctx-note">
                    Le dossier quitte la grille et rejoint la file de rappel —
                    retrouvez-le dans <strong>Tableaux ▸ RDV non honorés</strong>.
                  </div>
                  <div className="ctx-durees">
                    {[['non_presente', "Client ne s'est pas présenté"], ['annule', 'Client a annulé'], ['autre', 'Autre']].map(([cle, libelle]) => (
                      <button
                        key={cle}
                        type="button"
                        className={`ctx-duree-btn${ctxNonHonore.motif === cle ? ' is-active' : ''}`}
                        onClick={() => setCtxNonHonore(f => ({ ...f, motif: cle }))}
                      >{libelle}</button>
                    ))}
                  </div>
                  <div className="ctx-form">
                    <textarea
                      className="ctx-textarea"
                      rows={2}
                      placeholder="Commentaire (optionnel) — ex. injoignable, boîte vocale…"
                      value={ctxNonHonore.commentaire}
                      onChange={(e) => setCtxNonHonore(f => ({ ...f, commentaire: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="ctx-btn-valider"
                      disabled={!ctxNonHonore.motif || ctxBusy === 'non_honore'}
                      onClick={declarerNonHonore}
                    >{ctxBusy === 'non_honore' ? '⏳' : 'Déclarer non honoré'}</button>
                  </div>
                </>
              );
            case 'parking':      return blocParking(false);
            case 'commentaire':  return blocCommentaire(false);
            case 'contact':      return blocClient();
            default:             return null;
          }
        };

        // ── Colonne 3 : « Et ensuite ? » ────────────────────────────────────
        const contenuSuites = () => suitesProposees.map(suite => {
          if (suite === 'parking') {
            return (
              <div className="ctx-suite" key="parking">
                <div className="ctx-suite-titre">
                  📍 Où est le véhicule ?
                  {ctxSuites.fait.parking && <span className="ctx-suite-ok">✓ {ctxSuites.fait.parking}</span>}
                </div>
                {blocParking(true)}
              </div>
            );
          }
          if (suite === 'commentaire') {
            return (
              <div className="ctx-suite" key="commentaire">
                <div className="ctx-suite-titre">
                  💬 Laisser un commentaire
                  {ctxSuites.fait.commentaire && <span className="ctx-suite-ok">✓ enregistré</span>}
                </div>
                {ctxSuites.fait.commentaire
                  ? <div className="ctx-note">{ctxSuites.fait.commentaire}</div>
                  : blocCommentaire(true)}
              </div>
            );
          }
          return (
            <div className="ctx-suite" key="client">
              <div className="ctx-suite-titre">📞 Prévenir le client</div>
              {blocClient()}
            </div>
          );
        });

        return (
          <div
            ref={ctxPanelRef}
            className={`ctx-panel ctx-panel--${contextMenu.dir}${contextMenu.etroit ? ' ctx-panel--etroit' : ''}`}
            style={{
              top: contextMenu.top,
              left: contextMenu.left,
              right: contextMenu.right,
              '--ctx-max-h': `${contextMenu.maxH}px`,
            }}
            onMouseMove={suivrePointeur}
            onMouseLeave={annulerCommutation}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            {/* ── Colonne 1 : les actions ─────────────────────────────────── */}
            {colonnes.includes(1) && (
              <div className="ctx-col ctx-col--actions">
                {/* Client à gauche, immat au milieu, cravate à droite : le menu
                    contextuel est l'endroit où l'on agit sans ouvrir le dossier,
                    donc l'endroit où l'on cherche le trousseau. Même étiquette
                    ambre que dans la modale, en lecture seule. */}
                {/* Deux lignes : le nom du client dispose de toute la largeur
                    (à trois mots il était déjà tronqué), immat et cravate se
                    rangent dessous — l'immat à gauche, la cravate contre le bord
                    droit comme dans la modale. Le menu contextuel est l'endroit
                    où l'on agit sans ouvrir le dossier, donc celui où l'on
                    cherche le trousseau. */}
                <div className="ctx-col-tete ctx-identite">
                  <span className="ctx-client">{intv.client_nom || intv.client || 'Client'}</span>
                  {(intv.immat || intv.numero_cravate) && (
                    <span className="ctx-identite-l2">
                      {intv.immat && <span className="ctx-immat">{intv.immat}</span>}
                      {intv.numero_cravate && (() => {
                        // Même teinte que la carte et que la modale : la couleur
                        // fait partie de l'identification du trousseau, elle ne
                        // peut pas changer d'un écran à l'autre.
                        const t = teinteCravate(intv.cravate_couleur);
                        return (
                          <span className="ctx-cravate" style={{ background: t.fond, color: t.texte }}
                            title={`Cravate ${intv.numero_cravate} (${t.nom.toLowerCase()})`}>
                            <span className="ctx-cravate-hole" />
                            {intv.numero_cravate}
                          </span>
                        );
                      })()}
                    </span>
                  )}
                </div>
                <div className="ctx-col-corps">
                  {CTX_ACTIONS.filter(a => !a.siTags || tags.length > 0)
                    .filter(a => a.separateur || !a.cap || peut(a.cap))
                    .map((a, i) => a.separateur ? (
                    <div key={`sep-${i}`} className="ctx-separateur" />
                  ) : (
                    <div
                      key={a.cle}
                      className={`ctx-item${ctxCol2 === a.cle ? ' is-open' : ''}${a.danger ? ' danger' : ''}`
                        + (a.cle === 'prepicking'
                            && (interventions.find(i => idDossier(i) === idDossier(intv)) || intv).prepicking_at
                          ? ' ctx-item--coche' : '')}
                      onPointerEnter={(e) => { if (e.pointerType === 'mouse') survolerAction(a.colonne ? a.cle : null); }}
                      onClick={() => choisirAction(a)}
                    >
                      <span className="ctx-item-icone">{a.icone}</span>
                      <span className="ctx-item-libelle">
                        {a.cle === 'parking' && position?.zone
                          ? `Position parking : ${position.zone}`
                          : a.cle === 'prepicking'
                            /* État lu dans la liste VIVANTE : `contextMenu.intervention`
                               est un instantané pris à l'ouverture du menu, il ne
                               refléterait pas la bascule qu'on vient de faire. */
                            ? ((interventions.find(i => idDossier(i) === idDossier(intv)) || intv).prepicking_at
                                ? '✓ Pièces préparées' : 'Prépicking')
                            : a.libelle}
                      </span>
                      {a.colonne && <span className="ctx-item-fleche">›</span>}
                    </div>
                  ))}
                </div>
                <button type="button" className="ctx-terminer" onClick={closeContextMenu}>Terminer</button>
              </div>
            )}

            {/* ── Colonne 2 : le contenu de l'action survolée ──────────────── */}
            {colonnes.includes(2) && action?.colonne && (
              <div
                className={`ctx-col ctx-col--detail${['commentaire', 'nonHonore'].includes(action.colonne) ? ' ctx-col--saisie' : ''}`}
                ref={ctxCol2Ref}
                onPointerEnter={annulerCommutation}
              >
                <div className="ctx-col-tete">
                  {contextMenu.etroit && <button type="button" className="ctx-retour" onClick={retour}>‹</button>}
                  <span>{action.titre}</span>
                  {filtre && <span className="ctx-filtre">{ctxFiltre}</span>}
                </div>
                <div className="ctx-col-corps">{contenuColonne2()}</div>
              </div>
            )}

            {/* ── Colonne 3 : et ensuite ? ─────────────────────────────────── */}
            {colonnes.includes(3) && montreSuites && (
              <div className="ctx-col ctx-col--suites" onPointerEnter={annulerCommutation}>
                <div className="ctx-col-tete ctx-tete-ok">
                  {contextMenu.etroit && <button type="button" className="ctx-retour" onClick={retour}>‹</button>}
                  <span>✓ {ctxSuites.label}</span>
                </div>
                <div className="ctx-col-corps">{contenuSuites()}</div>
              </div>
            )}
          </div>
        );
      })()}


      {/* ── Modale d'envoi de SMS ──────────────────────────────────────────────
          Le message arrive pré-rempli par le modèle choisi, variables déjà
          substituées côté serveur, et reste modifiable avant envoi. */}
      {smsModal && (() => {
        const intv = smsModal.intervention;
        const trop = smsModal.texte.length > 160;
        return (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>💬 Envoyer un SMS</h2>
                <button className="modal-close" onClick={() => setSmsModal(null)} disabled={smsModal.envoi === 'encours'}>✕</button>
              </div>
              <div className="modal-body">
                <div className="pk-delete-recap" style={{ marginBottom: '12px' }}>
                  <div className="pk-delete-line"><span>Client</span><strong>{intv.client_nom || intv.client || '—'}</strong></div>
                  <div className="pk-delete-line"><span>Destinataire</span><strong>{intv.client_tel}</strong></div>
                  <div className="pk-delete-line"><span>Véhicule</span><strong>{intv.immat || intv.immatriculation || '—'}</strong></div>
                </div>
                <textarea
                  className="page-search" rows={4} autoFocus
                  style={{ width: '100%', resize: 'vertical' }}
                  value={smsModal.texte}
                  onChange={e => setSmsModal(m => ({ ...m, texte: e.target.value, envoi: null, erreur: null }))}
                  placeholder="Votre message…"
                />
                <div style={{ fontSize: '12px', marginTop: '4px', color: trop ? 'var(--color-error)' : 'var(--text-muted)' }}>
                  {smsModal.texte.length} / 160 caractères{trop && ' — un SMS ne peut pas dépasser 160 caractères'}
                  <span style={{ marginLeft: '8px' }}>· les accents sont retirés à l'envoi</span>
                </div>
                {smsModal.envoi === 'ok' && (
                  <p style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))', fontWeight: 600, marginTop: '10px' }}>✅ SMS envoyé.</p>
                )}
                {smsModal.envoi === 'erreur' && (
                  <p style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', marginTop: '10px' }}>❌ {smsModal.erreur}</p>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setSmsModal(null)} disabled={smsModal.envoi === 'encours'}>Annuler</button>
                <button className="btn-primary" onClick={envoyerSms}
                  disabled={!smsModal.texte.trim() || trop || smsModal.envoi === 'encours' || smsModal.envoi === 'ok'}>
                  {smsModal.envoi === 'encours' ? 'Envoi…' : '💬 Envoyer'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modale de confirmation de suppression ──────────────────────────────
          Seconde confirmation explicite : elle rappelle l'identité du dossier
          (client · immat · OR · date) pour qu'on ne supprime jamais à l'aveugle. */}
      {deleteModal && (() => {
        const d = deleteModal.intervention;
        const clientFull = [d.client_prenom, d.client_nom ? String(d.client_nom).toUpperCase() : '']
          .filter(Boolean).join(' ') || d.client || '—';
        const dateLabel = d.date_intervention || d.date
          ? new Date((d.date_intervention || d.date) + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
          : '—';
        return (
          <div className="modal-overlay">
            <div className="modal-content small pk-delete-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Supprimer ce dossier ?</h2>
                <button className="modal-close" onClick={() => setDeleteModal(null)} disabled={deleteBusy}>✕</button>
              </div>
              <div className="modal-body">
                <div className="pk-delete-recap">
                  <div className="pk-delete-line"><span>Client</span><strong>{clientFull}</strong></div>
                  <div className="pk-delete-line"><span>Véhicule</span><strong>{d.immat || d.immatriculation || '—'}</strong></div>
                  <div className="pk-delete-line"><span>OR</span><strong>{d.numero_or || '—'}</strong></div>
                  <div className="pk-delete-line"><span>Rendez-vous</span><strong>{dateLabel} à {(d.heure_debut || '').substring(0, 5)}</strong></div>
                  <div className="pk-delete-line"><span>Mécanicien</span><strong>{d.mecanicien || '—'}</strong></div>
                </div>
                <p className="pk-delete-warning">
                  {d.is_complement
                    ? 'Seule cette planification complémentaire sera supprimée — le dossier principal est conservé.'
                    : 'Cette action est définitive : le dossier, son historique et son RDV de réception seront supprimés.'}
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setDeleteModal(null)} disabled={deleteBusy}>Annuler</button>
                <button className="btn-danger" onClick={handleDeleteConfirmed} disabled={deleteBusy}>
                  {deleteBusy ? 'Suppression…' : '🗑️ Supprimer définitivement'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modale récap après création d'un RDV ───────────────────────────── */}
      {summaryModal && (() => {
        const s = summaryModal;
        const clientFull = [s.client_prenom, s.client_nom ? s.client_nom.toUpperCase() : ''].filter(Boolean).join(' ') || '—';
        const vehiculeText = [s.marque, s.modele].filter(Boolean).join(' ') || '—';
        const dateLabel = s.date_intervention
          ? new Date(s.date_intervention + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
          : '—';
        return (
          <div className="modal-overlay">
            <div className="modal-content small rdv-summary-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header rdv-summary-header">
                <div className="rdv-summary-check">✅</div>
                <h2>Rendez-vous créé</h2>
                <button className="modal-close" onClick={() => setSummaryModal(null)}>✕</button>
              </div>
              <div className="modal-body rdv-summary-body">
                <div className="rdv-summary-row">
                  <span className="rdv-summary-icon">👤</span>
                  <div className="rdv-summary-text">
                    <div className="rdv-summary-label">Client</div>
                    <div className="rdv-summary-value">{clientFull}{s.client_tel ? ` · ${s.client_tel}` : ''}</div>
                  </div>
                </div>
                <div className="rdv-summary-row">
                  <span className="rdv-summary-icon">🚗</span>
                  <div className="rdv-summary-text">
                    <div className="rdv-summary-label">Véhicule</div>
                    <div className="rdv-summary-value">
                      {s.immatriculation && <span className="rdv-summary-immat">{s.immatriculation}</span>}
                      {vehiculeText !== '—' && <span>{vehiculeText}</span>}
                    </div>
                  </div>
                </div>
                <div className="rdv-summary-row">
                  <span className="rdv-summary-icon">📅</span>
                  <div className="rdv-summary-text">
                    <div className="rdv-summary-label">Rendez-vous</div>
                    <div className="rdv-summary-value">
                      {dateLabel}
                      {s.heure_reception && <> · réception <strong>{s.heure_reception}</strong></>}
                      {s.heure_debut && s.heure_debut !== s.heure_reception && <> · atelier {s.heure_debut}</>}
                      {s.duree && <> · {s.duree}h</>}
                    </div>
                  </div>
                </div>
                <div className="rdv-summary-row">
                  <span className="rdv-summary-icon">🔧</span>
                  <div className="rdv-summary-text">
                    <div className="rdv-summary-label">Mécanicien</div>
                    <div className="rdv-summary-value">{s.mecanicien_label}</div>
                  </div>
                </div>
                <div className="rdv-summary-row">
                  <span className="rdv-summary-icon">🚪</span>
                  <div className="rdv-summary-text">
                    <div className="rdv-summary-label">Réceptionnaire</div>
                    <div className="rdv-summary-value">{s.receptionnaire_label}</div>
                  </div>
                </div>
                {(s.type_intervention || s.numero_or) && (
                  <div className="rdv-summary-row">
                    <span className="rdv-summary-icon">📋</span>
                    <div className="rdv-summary-text">
                      <div className="rdv-summary-label">Type</div>
                      <div className="rdv-summary-value">
                        {s.type_intervention || '—'}
                        {s.numero_or && <span className="rdv-summary-or"> · OR {s.numero_or}</span>}
                      </div>
                    </div>
                  </div>
                )}
                {s.travaux.length > 0 && (
                  <div className="rdv-summary-row">
                    <span className="rdv-summary-icon">✓</span>
                    <div className="rdv-summary-text">
                      <div className="rdv-summary-label">Travaux planifiés</div>
                      <ul className="rdv-summary-travaux">
                        {s.travaux.map((t, i) => (
                          <li key={i}>
                            {t.libelle || 'Travail'}
                            {t.duree ? ` · ${t.duree}h` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {smsConfirmPret && (
                  <button
                    className="btn-secondary"
                    style={{ marginRight: 'auto' }}
                    onClick={() => { setSmsProposition(smsConfirmPret); setSmsConfirmPret(null); setSummaryModal(null); }}
                    title={`SMS de confirmation au ${smsConfirmPret.telephone}`}
                  >
                    ✉️ Envoyer la confirmation
                  </button>
                )}
                <button className="btn-primary" onClick={() => { setSmsConfirmPret(null); setSummaryModal(null); }}>Fermer</button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}


export default PlanningAtelier;

// Taux de charge d'un atelier dans son onglet : pastille colorée par rapport à
// l'objectif de 100 %. Sous 90 % = heures vides à remplir (rouge),
// 90-99 % orange, 100-110 % vert, au-delà = surbooking (rouge). L'infobulle
// donne les heures et le mode de calcul : un chiffre qu'on challenge doit
// pouvoir s'expliquer.
function TauxCharge({ charge }) {
  if (!charge) return null;
  const h = (x) => `${String(Math.round(x * 10) / 10).replace('.', ',')} h`;
  if (!charge.ouvert || !(charge.disponibles > 0)) {
    return <span className="taux-charge niv-neutre" title="Aucune heure disponible ce jour (atelier fermé, férié ou sans mécanicien présent)">—</span>;
  }
  const t = charge.taux || 0;
  const niv = t > 1.1 ? 'surbooking' : t >= 1 ? 'ok' : t >= 0.9 ? 'proche' : 'bas';
  return (
    <span className={`taux-charge niv-${niv}`}
      title={`${h(charge.planifiees)} planifiées / ${h(charge.disponibles)} disponibles — horaires de l'atelier, pauses, jours fériés et absences déduits (calcul de Remplissage atelier). Objectif : 100 %.${niv === 'surbooking' ? ' Au-delà de 110 % : surbooking.' : ''}`}>
      {Math.round(t * 100)} %
    </span>
  );
}
